/**
 * The English catalogue, T-5.0.2. One flat map, because a key is an address
 * and not a tree: the validator asks "does this key resolve", nothing more.
 *
 * The contract of Section 1.1: a spec, a row, and a screen carry keys; this
 * file carries the words. A key never changes once shipped, only its
 * translation does.
 *
 * **A counted noun is an i18next plural pair.** `noun.record_one` and
 * `noun.record_other` are the two forms; `noun.record` is the STEM, which is
 * what a row stores in `entry_noun_key` and what `t()` is called with beside a
 * `count`. The stem is deliberately not a key of its own: i18next picks the
 * form from the count and the language's CLDR category, which is the whole
 * reason the `_plural` spelling had to go — Hindi puts zero in `one` and
 * English does not, and no hand-written sibling can know that.
 */
import { GLOSSARY_WORDS } from './glossary';

export const en = {
  // -------------------------------------------------------------- nouns
  // The nouns a record and its entries are called, as plural pairs. The STEM
  // — `noun.record`, `noun.entry` — is what `app.record.noun_key` and
  // `entry_noun_key` hold in every tenant's rows and on every installed
  // phone, so the stem is the name that may never change; the two forms
  // under it are what i18next picks between.
  'noun.record_one': 'record',
  'noun.record_other': 'records',
  'noun.entry_one': 'entry',
  'noun.entry_other': 'entries',
  'noun.line_one': 'line',
  'noun.line_other': 'lines',
  'noun.sale_one': 'sale',
  'noun.sale_other': 'sales',
  'noun.expense_one': 'expense',
  'noun.expense_other': 'expenses',
  'noun.transaction_one': 'transaction',
  'noun.transaction_other': 'transactions',
  'noun.payout_one': 'payout',
  'noun.payout_other': 'payouts',
  'noun.register_one': 'register',
  'noun.register_other': 'registers',
  'noun.movement_one': 'movement',
  'noun.movement_other': 'movements',
  'noun.mark_one': 'mark',
  'noun.mark_other': 'marks',

  // ----------------------------------------------------------- glossary
  // The nouns spelled once, spread in from `glossary.ts` so the server can
  // spread the same object into its own namespace. A value below says
  // `$t(glossary.record_one)` and never the word, so a noun is translated in
  // one place; `nesting.enforcement.test.ts` is what holds that.
  ...GLOSSARY_WORDS,

  // ------------------------------------------------------------ records
  'record.daily_sales.sales': 'Sales',
  'record.daily_sales.items': 'Items',
  'record.daily_sales.customers': 'Customers',
  'record.shop_expenses.expenses': 'Expenses',
  'record.shop_expenses.categories': 'Expense categories',
  'record.shop_expenses.payees': 'Payees',
  'record.party_dues.dues': 'Dues',
  'record.party_dues.parties': 'Parties',
  'record.staff_payouts.payouts': 'Payouts',
  'record.staff_payouts.staff': 'Staff',
  'record.staff_attendance.attendance': 'Attendance',
  'record.staff_attendance.staff': 'Staff',
  'record.stock_register.movements': 'Stock movements',
  'record.stock_register.items': 'Items',
  'record.stock_register.locations': 'Locations',
  'record.common.payment_modes': 'Payment modes',

  // ------------------------------------------------------------- fields
  'field.common.date': 'Date',
  'field.common.amount': 'Amount',
  'field.common.note': 'Note',
  'field.common.name': 'Name',
  'field.common.phone': 'Phone',
  'field.common.payment_mode': 'Payment mode',
  // The two a blank record seeds that no template needed a word for.
  'field.blank.quantity': 'Quantity',
  'field.blank.direction': 'In or out',
  'field.daily_sales.item': 'Item',
  'field.daily_sales.qty': 'Quantity',
  'field.daily_sales.customer': 'Customer',
  'field.shop_expenses.category': 'Category',
  'field.shop_expenses.paid_to': 'Paid to',
  'field.shop_expenses.bill_photo': 'Bill photo',
  'field.party_dues.party': 'Party',
  'field.party_dues.direction': 'Direction',
  'field.party_dues.due_date': 'Due date',
  'field.party_dues.status': 'Status',
  'field.staff_payouts.staff': 'Staff member',
  'field.staff_payouts.type': 'Payout type',
  'field.staff_attendance.staff': 'Staff member',
  'field.staff_attendance.status': 'Status',
  'field.staff_attendance.hours': 'Hours',
  'field.stock_register.item': 'Item',
  'field.stock_register.movement': 'Movement',
  'field.stock_register.qty': 'Quantity',
  'field.stock_register.batch': 'Batch',
  'field.stock_register.location': 'Location',

  // ------------------------------------------- words an answer asks for
  // Keyed off `labelKeyBy` and `nameKeyBy` in the specs. One word per row,
  // and every row is a real word a shopkeeper of that trade already uses.
  'field.daily_sales.item.dish': 'Dish',
  'field.daily_sales.item.job': 'Job',
  'field.daily_sales.item.trip': 'Trip',
  'field.daily_sales.item.material': 'Material',
  'record.daily_sales.items.dishes': 'Dishes',
  'record.daily_sales.items.jobs': 'Jobs',
  'record.daily_sales.items.trips': 'Trips',
  'record.daily_sales.items.materials': 'Materials',
  'field.party_dues.party.client': 'Client',
  'field.party_dues.party.customer': 'Customer',
  'record.party_dues.parties.clients': 'Clients',
  'record.party_dues.parties.customers': 'Customers',
  'field.stock_register.item.ingredient': 'Ingredient',
  'field.stock_register.item.material': 'Material',
  'record.stock_register.items.ingredients': 'Ingredients',
  'record.stock_register.items.materials': 'Materials',

  // ------------------------------------------------------------ options
  'option.party_dues.you_gave': 'You gave',
  'option.party_dues.you_got': 'You got',
  'option.party_dues.open': 'Open',
  'option.party_dues.settled': 'Settled',
  'option.staff_payouts.salary': 'Salary',
  'option.staff_payouts.advance': 'Advance',
  'option.staff_payouts.overtime': 'Overtime',
  'option.staff_payouts.bonus': 'Bonus',
  'option.staff_attendance.present': 'Present',
  'option.staff_attendance.half_day': 'Half day',
  'option.staff_attendance.absent': 'Absent',
  'option.staff_attendance.leave': 'Leave',
  'option.stock_register.stock_in': 'Stock in',
  'option.stock_register.stock_out': 'Stock out',
  'option.blank.in': 'Money in',
  'option.blank.out': 'Money out',

  // ---------------------------------------------------------- help text
  'help.party_dues.direction':
    'You gave means they owe you more. You got means the due comes down.',
  'help.stock_register.movement':
    'Stock in adds to the count. Stock out takes from it.',

  // -------------------------------------------------------------- views
  'view.common.all_entries': 'All $t(glossary.entry_other)',
  'view.common.summary': 'Summary',

  // ------------------------------------------------------ summary tiles
  'stat.today': 'Today',
  'stat.this_month': 'This month',
  'stat.units_sold': 'Units sold',
  'stat.top_item': 'Top item',
  'stat.avg_sale': 'Average sale',
  'stat.top_category': 'Top category',
  'stat.net_balance': 'Net balance',
  'stat.top_party': 'Top party',
  'stat.avg_payout': 'Average payout',
  'stat.top_staff': 'Top paid',
  'stat.on_hand': 'On hand',
  'stat.moved_today': 'Moved today',
  'stat.present_today': 'Present today',
  'stat.absent_today': 'Absent today',
  'stat.marks_this_month': 'Marks this month',
  // The insights a template pins with a condition on them.
  'stat.still_open': 'Still open',
  'stat.advances_month': 'Advances this month',
  'stat.taken_in_month': 'Taken in this month',
  // The charts a template pins beside the numbers.
  'stat.last_14_days': 'The last two weeks',
  'stat.where_money_went': 'Where the money went',
  'stat.by_staff': 'By staff member',
  'stat.by_status': 'By status',
  'stat.days_present': 'Days present, by $t(glossary.person_one)',
  'stat.this_against_last': 'This month against last',
  'stat.net_by_day': 'Net movement, by day',

  // ---------------------------------------------------------- insights
  // The screen, and the six shapes an insight takes, as the library lists
  // them. `insight.card.*` is the KEY domain for those shapes and does not
  // rename; the word a person reads is insight.
  'insight.section': 'Insights',
  // The grid says how many it holds, on a record and on the Dashboard alike.
  'insight.section_count_one': 'Insights · {{count, number}}',
  'insight.section_count_other': 'Insights · {{count, number}}',
  // Not "Show all 1 insight": with one hidden there is nothing to be "all" of.
  'insight.show_all_one': 'Show {{count, number}} $t(glossary.insight_one)',
  'insight.show_all_other': 'Show all {{count, number}} $t(glossary.insight_other)',
  'insight.show_fewer': 'Show fewer',
  'insight.edit': 'Edit',
  // The button, as the boards say it: "Add insight", no article.
  'insight.add': 'Add $t(glossary.insight_one)',
  'insight.gallery_title': 'Add an $t(glossary.insight_one)',
  'insight.gallery_body':
    'Every number below is yours, worked out on this phone. Tap one to set it up and keep it on top.',
  // The RECORD's gallery says the same thing about sizes, because its
  // candidates are drawn at the size they would be added at and a tap opens a
  // size sheet. `insight.gallery_body` stays exactly as it is: the business
  // gallery reads it, and a key never changes once it has shipped.
  'insight.gallery_sizes_body':
    'Every number below is yours, worked out on this phone. Tap one to see its sizes.',
  // "Added" is the business gallery's tag. The record's is `insight.on_record`
  // below: a standing state, named and neutral, not a tone.
  'insight.added': 'Added',
  'insight.manage_title': 'Arrange $t(glossary.insight_other)',
  'insight.empty_title': 'Put your numbers on top',
  // The offer: three of this record's own numbers, dashed, one tap from being
  // kept. `insight.empty_body` stays where it is and unread — it is abstract,
  // and the instruction line below says the same thing about what is on screen.
  'insight.suggest_hint': 'Tap one to keep it on top of {{record}}.',
  'insight.suggest_add': 'Add {{name}} to {{record}}',
  'insight.browse_all': 'See all $t(glossary.insight_other) for {{record}}',
  'insight.not_now': 'Not now',
  'insight.empty_body':
    'Add an $t(glossary.insight_one) that totals a month, tracks a trend, or breaks a total down by item, party, or payment mode.',
  // The gallery's tag on a candidate already on this record, under its tile.
  // It names the record, because the record is on screen and "On Sales" is
  // what the person is looking at.
  'insight.on_record': 'On {{record}}',
  'insight.no_data': 'Nothing yet',
  // Above the sample strip on a business with no entries yet: what these
  // numbers are, and when they become the person's own. Two sentences; the
  // second is the promise, not an order — the get-going tile above the strip
  // is the door to the first entry. docs/design/dashboard-sample-insights.
  'insight.sample_line': 'Sample numbers. Your own appear once you add an $t(glossary.entry_one).',
  // A card's time line (owner decision, 29 Oct 2026): a server answer says when it was worked out, a card answered on the device when it last synced.
  'insight.stamp.worked_out': 'Worked out at {{time}}',
  'insight.stamp.worked_out_on': 'Worked out {{date}} at {{time}}',
  'insight.stamp.synced': 'Synced at {{time}}',
  'insight.stamp.synced_on': 'Synced {{date}} at {{time}}',
  // The tag on each sampled insight's label line, at full strength over the
  // faded tile. One word, so it fits a half width beside a long label.
  'insight.sample_tag': 'Sample',
  'insight.card.stat': 'Number',
  'insight.card.delta': 'Number with change',
  'insight.card.progress': 'Against last period',
  'insight.card.trend': 'Trend line',
  'insight.card.bars': 'Bars by day',
  'insight.card.breakdown': 'Top breakdown',

  // The gallery's sections.
  'insight.group.suggested': 'Ready for this $t(glossary.record_one)',
  // The same group on a RECORD, which has a name to use. The generic one above
  // stays for the business gallery, where there is no one record to name.
  'insight.group.suggested_for': 'Ready for {{record}}',
  'insight.group.number': 'One number',
  'insight.group.time': 'Over time',
  'insight.group.category': 'By category',

  // The two widths an insight can sit at.

  // ------------------------------------------------- the insight editor
  // One screen for both jobs: setting up an insight from the library, and building
  // one from nothing. The rows are the same either way, so there is one thing
  // to learn.
  'insight.editor_title': 'This $t(glossary.insight_one)',
  'insight.editor_new_title': 'Build your own',
  'insight.preview': 'Preview',
  'insight.build_own': 'Build your own',
  'insight.build_own_body': 'Start from nothing and choose every part yourself.',

  'insight.row.name': 'Name',
  'insight.row.name_placeholder': 'Written for you',
  'insight.row.card': 'Show as',
  // The record's grid sizes, as words beside their pictures. Labels, so no
  // full stop; a kind that cannot take a size simply does not offer it.
  'insight.row.size': 'Size',
  // The size sheet's way on to everything else an insight is.
  'insight.change_counts': 'Change what it counts',
  'insight.size.small': 'Small',
  'insight.size.wide': 'Wide',
  'insight.size.large': 'Large',
  'insight.row.measure': 'Measure',
  'insight.row.group': 'Break down by',
  'insight.row.show': 'Show',
  'insight.row.period': 'Over what period',
  'insight.row.dated_by': 'Dated by',
  'insight.dated_by_entry': 'The date of the $t(glossary.entry_one)',
  'insight.row.conditions': 'Only count $t(glossary.entry_other) where',

  'insight.group_none': 'Nothing',
  'insight.show_top': 'Top {{limit, number}}, then Other',
  'insight.add_condition': 'Add a condition',
  'insight.no_conditions': 'Every $t(glossary.entry_one) counts',
  'insight.add_to': 'Add to {{record}}',
  'insight.save_changes': 'Save changes',
  'insight.remove_card': 'Take this off the $t(glossary.entry_one) list',

  // Picking a field, an operator and a value, one step per screen.
  'insight.condition_title': 'Add a condition',
  'insight.condition_field': 'Which $t(glossary.field_one)',
  'insight.condition_op': 'What to check',
  'insight.condition_value': 'Which value',
  'insight.condition_amount': 'Amount',
  'insight.condition_search': 'Search',
  'insight.condition_add': 'Add this condition',
  'insight.condition_empty':
    'Nothing has been recorded in this $t(glossary.field_one) yet, so there is no value to choose.',
  'insight.condition_no_match': 'No value here matches what you typed.',
  'insight.yes': 'Yes',
  'insight.no': 'No',

  // The periods, as chips. Capitalised, because a chip is not mid-sentence.
  'insight.period.all': 'All time',
  'insight.period.day': 'Today',
  'insight.period.week': 'This week',
  'insight.period.month': 'This month',
  'insight.period.year': 'This year',

  // What a condition reads as. The field's own label supplies the noun, so
  // these are only ever the verb.
  'insight.op.is': 'is',
  // A multi select holding every one of the values: "Services has Cut and Colour".
  'insight.op.has': 'has',
  'insight.op.is_not': 'is not',
  'insight.op.in': 'is any of',
  'insight.op.not_in': 'is none of',
  'insight.op.gt': 'over',
  'insight.op.gte': 'at least',
  'insight.op.lt': 'under',
  'insight.op.lte': 'at most',
  'insight.op.set': 'is filled in',
  'insight.op.unset': 'is blank',
  'insight.op.is_today': 'is today',
  'insight.op.before_today': 'is before today',
  'insight.op.after_today': 'is after today',
  'insight.op.in_next_days': 'is in the next days',
  'insight.op.in_last_days': 'is in the last days',
  'insight.op.on_or_before': 'is on or before',
  'insight.op.on_or_after': 'is on or after',
  'insight.and': 'and',
  'insight.more_conditions': '+{{count, number}} more',
  'insight.more_values': '+{{count, number}} more',
  // Under a breakdown by a multi select, so nobody adds up the groups.
  'insight.groups_overlap': 'An entry can be in more than one group',
  'insight.a_field': 'a $t(glossary.field_one)',

  // ------------------------------------------------------------ sync state
  // A working mirror with a dead network is normal and says nothing. These are
  // for the two cases that are NOT normal: nothing local and no way to get it,
  // and a server that is refusing.
  'sync.offline_empty.title': 'Nothing here yet, and this phone is offline',
  'sync.offline_empty.body':
    'Your $t(glossary.record_other) live on this phone once they have synced. Connect once, and everything after that works offline.',
  'sync.banner.title': 'Not syncing',
  'sync.banner.body':
    'Everything you see is saved on this phone, and new $t(glossary.entry_other) will sync when the server recovers.',

  // What an insight says when nothing named it. Built from a measure, a window,
  // and sometimes a group, so one the user assembled still reads as English.
  'insight.scope.day': 'today',
  'insight.scope.week': 'this week',
  'insight.scope.month': 'this month',
  'insight.scope.year': 'this year',
  'insight.scope.all': 'all time',
  'insight.agg.sum': 'Total',
  'insight.agg.avg': 'Average',
  'insight.agg.min': 'Lowest',
  'insight.agg.max': 'Highest',
  'insight.agg.count': 'Filled',
  'insight.agg.rows': 'Count',
  // A measure that is a recipe rather than a column. The same two words
  // `insight.worked_out` and `field.starts.worked_out` already use, because a
  // person meets one word for this idea and should not meet two.
  'insight.agg.formula': 'Worked out',
  'insight.top_of': 'Top',
  'insight.by': 'by',
  /*
   * How many conditions narrow ONE total inside a combined card.
   *
   * It used to borrow `link.fires`, which is a LINK's trigger conditions and
   * says so on the Links screen. One sentence for two ideas is one sentence
   * that cannot be translated for both: a language that inflects "condition"
   * by what it belongs to would have to pick.
   */
  'insight.filters_one': '{{count, number}} condition',
  'insight.filters_other': '{{count, number}} conditions',
  'insight.other': 'Other',
  // The tail row of a tall breakdown when the ranking really did end: the
  // divider under the top five always means "this is all of it", and this is
  // what it says when there is no netted Other to name.
  'insight.not_recorded': 'Not recorded',
  'insight.vs_day': 'vs yesterday',
  'insight.vs_week': 'vs last week',
  'insight.vs_month': 'vs last month',
  'insight.vs_year': 'vs last year',
  'insight.no_compare': 'no earlier period',
  'insight.last_days': 'last {{days, number}} days',

  // ------------------------------------------- business-wide insights
  // The home screen's strip, and the vocabulary a business-wide insight speaks.
  // It is deliberately short: two records in one business share no field, so
  // the only things every entry can be asked about are its money, its day, its
  // record, who wrote it, and how it arrived.
  'insight.biz.gallery_title': 'Add a $t(glossary.business_one) $t(glossary.insight_one)',
  'insight.biz.gallery_body':
    'Every number below is yours, worked out on this phone from all your $t(glossary.record_other). Tap one to set it up and keep it on top.',
  'insight.biz.add_to': 'Add to {{business}}',
  'insight.biz.row.only_count': 'Only count',
  'insight.biz.row.only_count_none': 'Every $t(glossary.record_one) counts',

  // What a measure is called. A sum needs no word of its own: "Money in"
  // already means all of it.
  'insight.biz.flow.in': 'Money in',
  'insight.biz.flow.out': 'Money out',
  'insight.biz.flow.net': 'Net',
  'insight.biz.agg.avg': 'Average',
  'insight.biz.agg.min': 'Smallest',
  'insight.biz.agg.max': 'Largest',
  'insight.biz.entries': 'Entries',
  'insight.biz.list_rows': 'Entries in your $t(glossary.list_other)',
  'insight.biz.days_quiet': 'Days quiet',

  // What a business breaks down by. Never a field.
  'insight.biz.by.record': 'by $t(glossary.record_one)',
  'insight.biz.by.creator': 'by who wrote it',
  'insight.biz.by.channel': 'by how it arrived',
  'insight.biz.by.day': 'by day',
  'insight.biz.top.record': 'Top $t(glossary.record_one)',
  'insight.biz.top.creator': 'Who wrote the most',
  'insight.biz.top.channel': 'Top source',
  'insight.biz.top.day': 'Busiest day',
  'insight.biz.group_none': 'Nothing',

  // The names the default strip and the gallery give their insights.
  'insight.biz.profit_month': 'Profit, this month',
  // L51 of docs/plans/ledger.md: the net of every money record is not profit, which is income less expenses.
  'insight.biz.net_month': 'Money in less money out, this month',
  'insight.biz.entries_today': 'Entries today',
  'insight.biz.busiest_day': 'Busiest day',
  'insight.biz.quiet_records': 'Records gone quiet',
  'insight.biz.by_writer': 'Who wrote them',
  'insight.biz.my_records': 'What you wrote, by $t(glossary.record_one)',

  // People. A name arrives the day the members route does; until then the
  // signed-in person is "You" and anybody else is honest about being unnamed.
  'insight.biz.you': 'You',
  'insight.biz.someone': 'Someone else',

  // How an entry reached the record.
  'insight.biz.channel.typed': 'typed here',
  'insight.biz.channel.import': 'brought in from a file',
  'insight.biz.channel.api': 'sent by another system',

  // A condition, in the words a shopkeeper would use.
  'insight.biz.cond.record.is': 'only {{record}}',
  'insight.biz.cond.record.is_not': 'without {{record}}',
  'insight.biz.cond.creator.is': 'written by {{creator}}',
  'insight.biz.cond.creator.is_not': 'not written by {{creator}}',
  'insight.biz.cond.channel.is': 'from {{channel}}',
  'insight.biz.cond.channel.is_not': 'not from {{channel}}',


  // Days quiet, as an insight writes them.
  'insight.biz.today': 'Today',
  'insight.biz.days': '{{days, number}} days',

  // The gallery's sections.
  'insight.biz.group.suggested': 'Already on top',
  'insight.biz.group.money': 'Across every $t(glossary.record_one)',
  'insight.biz.group.time': 'Over time',
  'insight.biz.group.records': 'By $t(glossary.record_one)',
  'insight.biz.group.activity': 'Activity',

  // ------------------------------------------ worked out, hopped, combined
  // An insight may add up a RECIPE rather than a column, read a field on the row a
  // reference points at, and combine totals from two records. The words below are
  // the design's own: docs/design/one-formula, artboards 9 to 12.

  // What to add up, when it is not a column.
  'insight.measure_expr': 'A worked-out value',
  'insight.measure_expr_note': 'Any $t(glossary.recipe_one), added up. Nothing is stored on the $t(glossary.entry_one).',
  'insight.row.agg': 'How to add it up',
  'insight.worked_out': 'Worked out',

  // A field on the row a reference points at. No leading "the": this one is a
  // row's label and an insight's name, where "Customer’s Area" is how it is said.
  // `link.condition_hop` is the same fact inside a sentence, and keeps its "the".
  'insight.hop_field': '{{via}}’s {{field}}',

  // Combining totals. A leaf names what it adds up and the record it reads, and
  // the operators between them are the four the recipe builder already offers.
  'insight.combine.title': 'Combine',
  'insight.combine.start': 'Combine with another total',
  'insight.combine.start_note': 'Take one total away from another, or add them up',
  'insight.combine.add': 'Add a total',
  'insight.combine.leaf': '{{agg}} of {{source}} in {{record}}',
  'insight.combine.leaf_rows': 'Count of {{record}}',
  'insight.combine.a_recipe': 'a $t(glossary.recipe_one)',
  'insight.combine.round': 'Round the answer',
  'insight.combine.round_none': 'Not rounded',
  'insight.combine.round_places': 'To {{places, number}} places',
  'insight.combine.undo': 'Stop combining',
  'insight.combine.fixed': 'This one was built another way, so its totals cannot be rearranged here.',
  'insight.combine.full': 'An $t(glossary.insight_one) can hold {{count, number}} totals.',
  'insight.combine.same_list':
    'Both $t(glossary.record_other) point at the same $t(glossary.list_one), so the names line up and the $t(glossary.insight_one) may group by them.',

  // One total, set up on its own page.
  'insight.leaf.title': 'One total',
  'insight.leaf.record': 'Which $t(glossary.record_one)',
  'insight.leaf.record_note': 'Any $t(glossary.record_one) of this $t(glossary.business_one).',
  'insight.leaf.measure': 'What to add up',
  'insight.leaf.group': 'Line up by',
  'insight.leaf.group_note':
    'An $t(glossary.insight_one) that breaks down needs every total to point at the same $t(glossary.list_one).',
  'insight.leaf.no_group':
    'Nothing on this $t(glossary.record_one) points at the $t(glossary.list_one) this $t(glossary.insight_one) breaks down by, so it cannot be one of its totals.',
  'insight.leaf.conditions': 'Only count $t(glossary.entry_other) where',
  'insight.leaf.save': 'Use this total',
  'insight.leaf.remove': 'Take this total off',
  'insight.leaf.only_here':
    'An $t(glossary.insight_one) on a $t(glossary.record_one) can only add up that $t(glossary.record_one). An $t(glossary.insight_one) that reads two $t(glossary.record_other) lives on the $t(glossary.dashboard).',

  // The Dashboard's door into a record's own question.
  'insight.biz.from_record': 'From a $t(glossary.record_one)\u2019s $t(glossary.field_other)',
  'insight.biz.from_record_body': 'Any $t(glossary.record_one), any $t(glossary.field_one), any $t(glossary.recipe_one). May reach another $t(glossary.record_one).',
  'insight.biz.pick_record': 'Which $t(glossary.record_one)?',
  'insight.biz.hosted_add': 'Add to the $t(glossary.dashboard)',
  'insight.a11y.take_off': 'Take off {{record}}',
  'insight.edit.add_here': 'Add',
  'insight.edit.fold_line': '{{record}} opens showing the $t(glossary.insight_other) above this line.',
  'insight.edit.reset': 'Reset to the usual $t(glossary.insight_other)',
  'insight.a11y.label_one': '{{name}}, {{value}}, {{size}}, {{position, number}} of {{count, number}}',
  'insight.a11y.label_other': '{{name}}, {{value}}, {{size}}, {{position, number}} of {{count, number}}',
  'insight.edit.added': '{{name}} added.',
  'insight.edit.coach': 'Drag the dots to move an $t(glossary.insight_one), and its corner to change its size.',
  'insight.edit.count_one': '{{count, number}} of {{max, number}}',
  'insight.edit.count_other': '{{count, number}} of {{max, number}}',
  'insight.edit.full': '{{max, number}} of {{max, number}}. Take one off to add another.',
  'insight.edit.moved_one': '{{name}} moved to {{position, number}} of {{count, number}}.',
  'insight.edit.moved_other': '{{name}} moved to {{position, number}} of {{count, number}}.',
  'insight.edit.removed': '{{name}} taken off. Your $t(glossary.entry_other) are safe.',
  'insight.edit.reset_done': 'Back to the usual $t(glossary.insight_other).',
  'insight.edit.resized': '{{name}} is now {{size}}.',
  'insight.edit.undone': 'Undone.',
  'insight.a11y.bigger': 'Make bigger',
  'insight.a11y.move_earlier': 'Move earlier',
  'insight.a11y.move_later': 'Move later',
  'insight.a11y.smaller': 'Make smaller',
  'insight.a11y.take_off_dashboard': 'Take off the $t(glossary.dashboard)',

  // -------------------------------------------------- filtering a list
  // The sheet that cuts one record's entry list down, and the words the cut
  // wears afterwards. No key here says condition, operator or predicate: a
  // person picks a field and taps values, and the operator is implied by what
  // the field is.
  'filter.title': 'Filter {{entries}}',
  'filter.clear_all': 'Clear all',
  'filter.clear': 'Clear',
  'filter.days': 'Days',
  'filter.fields': 'Fields',
  // "Any" and never "All": "All items" reads as a filter that is switched on.
  'filter.any': 'Any',
  'filter.show': 'Show {{entries}}',
  'filter.show_none': 'No matches',
  'filter.use': 'Use {{entries}}',
  'filter.apply': 'Apply',
  'filter.open': 'Filter',

  // The day presets. They are the windows insight-query.ts already computes, so
  // a pill called "This month" and an insight scoped to the month mean the same days.
  'filter.preset.all': 'All time',
  'filter.preset.today': 'Today',
  'filter.preset.yesterday': 'Yesterday',
  'filter.preset.week': 'This week',
  'filter.preset.month': 'This month',
  'filter.preset.last_month': 'Last month',
  'filter.preset.custom': 'Custom',
  'filter.range_days': '{{from}} to {{to}}',

  // The value pane. The rows are what the record has actually recorded, so the
  // note under them says so rather than leaving a person wondering what is missing.
  'filter.no_value': 'No {{field}}',
  'filter.no_value_hint': 'Entries where this was left empty',
  'filter.only_recorded': 'Only what this $t(glossary.record_one) has recorded.',
  'filter.values_more': '{{count, number}} recorded',
  'filter.values_search': 'Find',
  'filter.values_search_in': 'Find in {{field}}',
  'filter.values_none': 'Nothing recorded in this $t(glossary.field_one) yet',
  'filter.values_no_match': 'No value here matches what you typed',
  'filter.note_search': 'Words in a note are not asked about here.',

  // A range is two conditions, and a person who fills one box deserves to know
  // what happened to the other.
  'filter.at_least': 'At least',
  'filter.at_most': 'At most',
  'filter.range_hint': 'Leave one side empty and it stays open on that side.',
  'filter.date.is_today': 'Today',
  'filter.date.before_today': 'Before today',
  'filter.date.after_today': 'After today',
  'filter.date.in_next_days': 'In the next few days',
  'filter.date.in_last_days': 'In the last few days',
  'filter.date.on_or_before': 'On or before a date',
  'filter.date.on_or_after': 'On or after a date',
  'filter.date.next_days_one': 'Next {{count, number}} day',
  'filter.date.next_days_other': 'Next {{count, number}} days',
  'filter.date.last_days_one': 'Last {{count, number}} day',
  'filter.date.last_days_other': 'Last {{count, number}} days',
  'filter.date.on_or_before_day': 'On or before {{day}}',
  'filter.date.on_or_after_day': 'On or after {{day}}',
  'filter.date.days': 'Days',
  'filter.date.day': 'Date',
  'filter.date.hint': 'Today counts as one of the days.',
  'filter.range_crossed': 'This is less than the first figure, so nothing would match',
  'filter.range_not_a_figure': 'Type a number here',
  'filter.from': 'From',
  'filter.to': 'To',
  'filter.more': 'More',
  'filter.less': 'Less',

  // Yes/no and filled/blank, for the two fields that are asked those.
  'filter.yes': 'Yes',
  'filter.no': 'No',
  'filter.filled': 'Filled in',
  'filter.blank': 'Blank',

  // What sits over the list once a filter is on.
  // No verb: "34 sales" works where "34 sales match" and "1 sale match" cannot
  // both be right, and the design's own line has no verb either.
  'filter.summary_net': '{{total}} net',
  'filter.or': 'or',
  'filter.and': 'and',
  // A multi select is asked whether an entry holds any of the ticked rows, or every one.
  'filter.match_any': 'Any of these',
  'filter.match_all': 'All of these',
  'filter.all_of_answer': 'All of {{values}}',
  // A pair insight is one period against the period before it, and an outside
  // window leaves no "before". The line says what they need, never what they are.
  'filter.window_hides_one': '{{count, number}} $t(glossary.insight_one) needs a period of its own, so it is off while a date filter is on.',
  'filter.window_hides_other': '{{count, number}} $t(glossary.insight_other) need a period of their own, so they are off while a date filter is on.',
  // Two insights asking the same total over different months are ONE once the
  // days are fixed. Said out loud, because an insight somebody placed vanishing
  // without a word reads as a defect.
  'filter.window_merged_one': '{{count, number}} $t(glossary.insight_one) asks the same question once the days are fixed.',
  'filter.window_merged_other': '{{count, number}} $t(glossary.insight_other) ask the same question once the days are fixed.',
  'filter.empty.title': 'Nothing matches these filters',
  'filter.empty.body': 'This $t(glossary.record_one) holds {{entries}}. None of them matches.',
  'filter.empty.clear': 'Clear filters',
  'filter.open_count_one': 'Filter · {{count, number}}',
  'filter.open_count_other': 'Filter · {{count, number}}',

  // ------------------------------------------------------------- search
  //
  // One box over every record and list on the device. The words here carry two promises
  // the screen must keep: it works with the radio off, and every row says why
  // it came back.
  'search.open': 'Search',
  'search.placeholder': 'Search everything',
  'search.cancel': 'Cancel',

  'search.recent': 'Recent',
  'search.recent_clear': 'Clear',
  // The one promise worth making out loud on an empty box.
  'search.promise':
    'Searches every $t(glossary.record_one) and $t(glossary.list_one) on this phone, with the radio off. It reads the words your $t(glossary.list_other) draw, never the ids underneath them.',

  // Every group of results is headed by the RECORD or LIST its rows came from, and the
  // heading is a door into it. There is no separate index of them: a
  // row on its own answered nothing, and tapping it left a person at the
  // top of it looking for the thing all over again.
  'search.entries': 'Entries',
  // A record or list whose NAME matched but whose rows are not on the page gets a row of
  // its own under this heading, so that an empty one is still a door and not
  // a silence. Home's own two words, because they are the same two sections.
  'search.records_and_lists': 'Records and $t(glossary.list_other)',
  // A group heading's count when the result cap cut it short: how many rows are
  // on the page, and how many match in that record. The bare total alone would
  // claim rows the page does not show; the shown figure alone was the cap,
  // printed as if it were the shop's number.
  'search.shown_of_total': '{{shown, number}} of {{total, number}}',
  // The last row of a cut group. It brings the next page of that record's
  // matches onto the same screen, in place.
  'search.show_more_one': 'Show {{count, number}} more',
  'search.show_more_other': 'Show {{count, number}} more',

  // Shown when nothing matched exactly and these are the closest rows instead.
  // Nothing is highlighted in that state, because no term actually matched.
  'search.near_title': 'Nothing spelt "{{typed}}". Closest matches:',

  'search.none_title': 'No match for "{{typed}}"',
  'search.none_body_one': 'Searched every word in {{count, number}} $t(glossary.entry_one).',
  'search.none_body_other': 'Searched every word in {{count, number}} $t(glossary.entry_other).',
  'search.none_body_indexing': 'This phone is still indexing, so more may match soon.',
  'search.none_scope': 'Dates and yes-or-no answers are not searched here. Use the filter inside a $t(glossary.record_one) for those.',

  // The index is built after launch, because a migration cannot render a
  // document. Records and lists, not rows: a record is what a person recognises.
  'search.building': 'Still reading your $t(glossary.record_other) and $t(glossary.list_other). {{done, number}} of {{total, number}} done.',
  'search.building_short': 'Still reading your $t(glossary.record_other) and $t(glossary.list_other).',
  'search.so_far': '{{entries}} so far',

  'search.too_short': 'Type two letters or more.',
  'search.everywhere': 'Search every $t(glossary.record_one) for "{{query}}"',
  'search.everywhere_short': 'All $t(glossary.record_other)',
  'search.indexing': 'Getting {{record}} ready to search',
  'search.clear': 'Clear search',
  'search.no_match': 'No $t(glossary.entry_other) in {{record}} match "{{query}}".',

  // ---------------------------------------------------- directory seeds
  'seed.payment_mode.cash': 'Cash',
  'seed.payment_mode.upi': 'UPI',
  'seed.payment_mode.card': 'Card',
  'seed.payment_mode.bank_transfer': 'Bank transfer',
  'seed.category.rent': 'Rent',
  'seed.category.electricity': 'Electricity',
  'seed.category.supplies': 'Supplies',
  'seed.category.transport': 'Transport',
  'seed.category.tea_snacks': 'Tea and snacks',
  'seed.category.other': 'Other',
  'seed.payment_mode.cheque': 'Cheque',
  'seed.category.ingredients': 'Ingredients',
  'seed.category.gas': 'Gas cylinder',
  'seed.category.fuel': 'Fuel',
  'seed.category.repairs': 'Repairs',
  'seed.category.tolls': 'Tolls and parking',
  'seed.category.raw_material': 'Raw material',
  'seed.category.tools': 'Tools',
  'seed.location.counter': 'Counter',
  'seed.location.store_room': 'Store room',
  'seed.location.kitchen': 'Kitchen',
  'seed.party.walk_in': 'Walk-in customer',

  // ------------------------------------------------------------ samples
  'sample.daily_sales.item_1': 'Tea',
  'sample.daily_sales.item_2': 'Biscuits',
  'sample.daily_sales.item_3': 'Gift box',
  'sample.daily_sales.customer_1': 'Regular customer',
  'sample.daily_sales.note_1': 'Morning rush',
  'sample.shop_expenses.payee_1': 'Electricity board',
  'sample.shop_expenses.payee_2': 'Shop landlord',
  'sample.shop_expenses.note_1': 'Monthly bill',
  'sample.party_dues.party_1': 'Sharma Traders',
  'sample.party_dues.party_2': 'Gupta and Sons',
  'sample.party_dues.party_3': 'New Star Agencies',
  'sample.party_dues.note_1': 'Part payment received',
  'sample.staff_payouts.staff_1': 'Counter staff',
  'sample.staff_payouts.staff_2': 'Delivery staff',
  'sample.staff_payouts.staff_3': 'Helper',
  'sample.staff_payouts.note_1': 'Festival advance',
  'sample.staff_attendance.staff_1': 'Day shift worker',
  'sample.staff_attendance.staff_2': 'Machine operator',
  'sample.staff_attendance.staff_3': 'Helper',
  'sample.staff_attendance.note_1': 'Left after lunch',
  'sample.stock_register.item_1': 'Rice, 5 kg bag',
  'sample.stock_register.item_2': 'Cooking oil, 1 litre',
  'sample.stock_register.item_3': 'Wheat flour',
  'sample.stock_register.batch_1': 'Batch B-104',
  'sample.stock_register.batch_2': 'Batch B-107',
  'sample.stock_register.note_1': 'Weekly restock',

  // -------------------------------------------------------------- chips
  'chip.retail_shop': 'Retail shop',
  'chip.restaurant': 'Restaurant or food stall',
  'chip.wholesale_trading': 'Wholesale or trading',
  'chip.services': 'Salon, repair, or services',
  'chip.manufacturing': 'Workshop or manufacturing',
  'chip.transport': 'Transport or logistics',
  'chip.construction': 'Construction or contracting',
  'chip.freelancer': 'Freelancer or home business',

  // ---------------------------------------------------------- questions
  // The three asked before the chip. An answer shapes the words, the fields,
  // and the seeded rows of every template the person is about to create.
  'question.trade.label': 'What do you mostly sell or do?',
  'question.trade.help': 'This picks the words your $t(glossary.record_other) use.',
  'answer.trade.goods': 'Goods',
  'answer.trade.food': 'Food',
  'answer.trade.services': 'Services',
  'answer.trade.transport': 'Transport',
  'answer.trade.materials': 'Materials',
  'answer.trade.other': 'Something else',
  'answer.trade.none': 'Nothing to sell',
  'question.payments.label': 'How do $t(glossary.person_other) pay you?',
  'question.payments.help': 'Tick every one. Each becomes a one-tap choice.',
  'answer.payments.cash': 'Cash',
  'answer.payments.upi': 'UPI',
  'answer.payments.card': 'Card',
  'answer.payments.bank_transfer': 'Bank transfer',
  'answer.payments.cheque': 'Cheque',
  'question.tracking.label': 'What else do you want to keep?',
  'question.tracking.help': 'Each one adds a $t(glossary.record_one). Skip any you do not need.',
  'answer.tracking.credit': 'Credit and dues',
  'answer.tracking.staff': 'Staff payouts',
  'answer.tracking.stock': 'Stock counts',

  // --------------------------------------------------------- onboarding
  // Three steps, in this order: the questions about the work, the business
  // name, then the shop type and the records it creates. The last screen is
  // the only write, so its button names what it makes.
  'onboarding.questions.title': 'A few things about your work',
  'onboarding.questions.subtitle':
    'Three quick answers. They set the words and the $t(glossary.field_other) in your $t(glossary.record_other).',
  'onboarding.questions.continue': 'Continue',
  'onboarding.questions.needed': 'Answer this one to carry on.',
  'onboarding.name.title': 'Name your $t(glossary.business_one)',
  'onboarding.name.subtitle': 'This is the name on everything you keep. You can change it later.',
  'onboarding.name.name_label': 'Workspace name',
  'onboarding.name.name_placeholder': 'Sharma Traders',
  'onboarding.name.currency_label': 'Currency',
  'onboarding.name.currency_help': 'Every amount is kept in this currency.',
  'onboarding.name.continue': 'Continue',
  'onboarding.goal.title': 'What do you run?',
  'onboarding.goal.subtitle':
    'Pick the closest match. The $t(glossary.record_other) shops like yours keep are set up ready.',
  'onboarding.goal.needed': 'Pick one so the setup matches the shop.',
  'onboarding.goal.change': 'Change',
  'onboarding.goal.card_title': 'What gets created',
  'onboarding.goal.record_one': 'record',
  'onboarding.goal.record_other': 'records',
  'onboarding.goal.from_answers': 'From your answers',
  'onboarding.goal.empty': 'Keep at least one $t(glossary.record_one).',
  'onboarding.goal.continue': 'Create these $t(glossary.record_other)',
  'onboarding.goal.start_blank': 'Start from blank',
  'onboarding.blank.title': 'Start from blank',
  'onboarding.blank.subtitle': 'Name your first $t(glossary.record_one). You pick its $t(glossary.field_other) next.',
  // -------------------------------------------------------- home, Stage 5
  'home.records': 'Records',
  'home.lists': 'Lists',
  'home.today_in': 'In today',
  'home.today_out': 'Out today',
  'home.new_record': 'New $t(glossary.record_one)',
  'home.no_records': 'No $t(glossary.record_other) yet.',
  /*
   * Home shows the first few of each section and puts this at the end of the
   * section label, where the insight strip already puts Edit.
   *
   * NO COUNT. The total rides the label itself - "Records · 12" - so the link
   * keeps only the verb and the number is said once. The two arrive and leave
   * together: a section that fits shows neither, because a link onto a screen
   * carrying the same four rows is a lie about there being more.
   *
   * The link never announces itself as "See all" to a screen reader; home
   * passes `home.all_records` or `home.all_lists`, which names where it goes.
   */
  'home.see_all': 'See all',
  'home.all_records': 'All $t(glossary.record_other)',
  'home.all_lists': 'All $t(glossary.list_other)',
  /*
   * The line under a list's name on its tile, when the list holds nothing.
   *
   * The one word direction B adds. The line normally names the first three
   * things in the list and then counts the rest as "+3" — digits and a plus
   * read the same in every language, so the tail needs no key and the empty
   * case is the only sentence here. The count beside the name is left BLANK on
   * an empty list rather than drawn as a zero, because this already said it.
   */
  'home.list_empty': 'Nothing in it yet',
  'home.empty.title': 'No $t(glossary.record_other) or $t(glossary.list_other) yet',
  'home.empty.body': 'Add a $t(glossary.record_one), and everything you keep lands in it.',
  'home.add.title': 'Add a $t(glossary.record_one)',
  'home.add.blank': 'Start from blank',
  'home.add.blank_body': 'You pick every $t(glossary.field_one).',
  'home.add.voice': 'Create with voice',
  'home.add.voice_body': 'Say what it should hold. Confirm before it is saved.',
  'home.add.new_list': 'New $t(glossary.list_one)',
  'home.add.new_list_body': 'Names you pick from in a form. Customers, items.',
  /*
   * What home teaches about itself. design/explorations/lists-links-education, board D1.
   *
   * The tip names both halves in one breath, because the pair is the lesson:
   * a record holds what happened, a list holds the words several records write
   * it down with. "Shared names" is an abstraction until it is named things.
   *
   * SO `examples` IS THE BUSINESS'S OWN LISTS, three of them, from
   * `components/home/tip-examples.ts` — the same three the reader can see on
   * this screen, which is what makes the sentence checkable rather than
   * decorative. It is names a person typed, so it arrives cased as they typed
   * it and this sentence must not put it anywhere a capital would be wrong.
   *
   * `_none` is the same lesson for a business with no lists yet: the clause is
   * dropped rather than filled with invented examples, because a name this
   * reader has never seen teaches nothing and reads as a feature they missed.
   * It is not a plural form and is never reached by a `count` —
   * `interpolation.enforcement.test.ts`, *no key wears a CLDR category*.
   *
   * "Records" and "Lists" open their sentences, so they are spelled here; the
   * nouns inside a sentence go through the glossary like every other.
   */
  'home.tip.title': 'Quick tip',
  'home.tip.lists':
    'Records hold $t(glossary.entry_other). Lists are shared names many $t(glossary.record_other) pick from — {{examples}}.',
  'home.tip.lists_none':
    'Records hold $t(glossary.entry_other). Lists are shared names many $t(glossary.record_other) pick from.',
  'home.lists_caption': 'Shared across $t(glossary.record_other).',
  /*
   * The Lists section with nothing in it yet. It says what a list is FOR
   * rather than that there are none, which the empty section already shows.
   * The record names are sample values and keep their capitals.
   */
  'home.lists_empty.title': 'One $t(glossary.list_one), many $t(glossary.record_other)',
  'home.lists_empty.body':
    'Payment modes on Sales and Expenses. Cities on Customers and Parties. Name it once, and every $t(glossary.record_one) picks from the same $t(glossary.list_one).',

  // ------------------------------------------------------- start from blank
  'blank.title': 'Start from blank',
  'blank.name_label': 'Record name',
  'blank.name_placeholder': 'Daily milk round',
  'blank.counts_label': 'What should this $t(glossary.record_one) total?',
  'blank.counts_caption':
    'This decides the totals only. You pick the $t(glossary.field_other) next whatever you answer, and this one answer cannot be changed later.',
  'blank.counts.money_in': 'Money coming in',
  'blank.counts.money_in_hint': 'Sales, collections. Every $t(glossary.entry_one) adds up.',
  'blank.counts.money_out': 'Money going out',
  'blank.counts.money_out_hint': 'Expenses, payouts. Every $t(glossary.entry_one) takes away.',
  'blank.counts.money_both': 'Money both ways',
  'blank.counts.money_both_hint': 'Each $t(glossary.entry_one) says in or out.',
  'blank.counts.count': 'A count, not money',
  'blank.counts.count_hint': 'Units, boxes, litres. No money $t(glossary.field_one) at all.',
  'blank.counts.none': 'Nothing. Just a date',
  'blank.counts.none_hint': 'No amount and no total. You add every $t(glossary.field_one) yourself.',
  'blank.footnote': 'Every $t(glossary.record_one) gets a date. Nothing else is decided here.',
  'blank.create': 'Create and pick $t(glossary.field_other)',
  'blank.name_taken': 'You already keep a $t(glossary.record_one) with this name.',
  'blank.list_title': 'New $t(glossary.list_one)',
  'blank.list_name_label': 'List name',
  'blank.list_name_placeholder': 'Suppliers',
  'blank.list_footnote': 'A $t(glossary.list_one) holds names you pick from in a form. Add them any time.',
  'blank.create_list': 'Create $t(glossary.list_one)',
  // The columns a list is made with. A list of customers that can hold only a
  // name is a list a shopkeeper has to leave to make useful, so the question is
  // asked where the list is made.
  'new_list.columns_label': 'What does each one hold?',
  'new_list.columns_caption': 'Every $t(glossary.entry_one) has a name. Tick what else you keep.',
  'new_list.name_fixed': 'Always on. A $t(glossary.list_one) is picked from by its name.',
  'new_list.columns_failed':
    'The $t(glossary.list_one) was made, but the extra columns were not. Add them on the $t(glossary.list_one) itself, from home.',
  'tabs.records': 'Records',
  'tabs.account': 'Account',
  'tabs.dashboard': 'Dashboard',
  'tabs.settings': 'Settings',
  // ------------------------------------------------- the dashboard
  // The business-wide insight strip, on its own tab since 3 Sep 2026. Only a
  // person holding view_business_insights sees the tab at all.
  'dashboard.empty.title': 'Nothing to show yet',
  'dashboard.empty.body': 'Add a $t(glossary.record_one), and its numbers land here.',
  // The get-going card, THE-DASHBOARD-ELEMENTS.md row E1: the work setup could
  // not do for the person, as tiles above the strip on day zero. Steps are
  // derived from the records and lists; only a skip and Hide are written down. The list
  // names and the entry noun inside these are the business's own words.
  // docs/design/dashboard-get-going, direction C.
  'dashboard.setup.label': 'Get going',
  // Steps done over the steps that remain. One string, so Hindi can say
  // "5 में से 0".
  'dashboard.setup.count': '{{done, number}} of {{total, number}}',
  'dashboard.setup.hide': 'Hide',
  // The spoken word for the X on a tile. Nothing prints it.
  'dashboard.setup.skip': 'Skip',
  'dashboard.setup.invite.line': 'Only you here so far',
  'dashboard.setup.list.title': 'Add {{record}}',
  'dashboard.setup.first.title': 'Add your first {{entry}}',
  'dashboard.setup.first.line': 'Your numbers start with it',
  'dashboard.setup.done': 'Done',
  // ------------------------------------------------- view as
  // An owner or partner seeing the app as one person on the team. The screen
  // is exactly what that person sees, word for word; the band above it is the
  // only thing that is the owner's. Nothing tapped in this mode writes.
  'view_as.open': 'View as',
  'view_as.sheet.title': 'View as',
  'view_as.sheet.subtitle':
    'See the $t(glossary.business_one) the way one $t(glossary.person_one) on your team sees it. Nothing you tap will change anything.',
  'view_as.banner.title': 'Viewing as {{person}}',
  'view_as.banner.body': 'Read only. Nothing you tap will change anything.',
  'view_as.exit': 'Exit',
  'view_as.blocked': 'You are viewing as {{person}}. Exit to make changes.',
  'view_as.nobody': 'Nobody else is in this $t(glossary.business_one) yet.',
  'account.title': 'Your settings',
  'account.profile': 'Profile',
  // Your settings' two sections between the profile and About
  // (docs/plans/workspace-menu.md, Decided 17).
  'account.section_account': 'Account',
  'account.preferences': 'Preferences',
  'account.edit_profile': 'Edit profile',
  'account.name': 'Name',
  'account.email': 'Email',
  'account.verified_label': 'Email verified',
  'account.verified': 'Yes',
  'account.unverified': 'Not yet',
  'account.member_since': 'Member since',
  'account.photo': 'Photo',
  'account.photo_set': 'Set',
  'account.businesses': 'Workspaces',
  // The interim door into somebody else's business. Invitations will travel as
  // links later; until then the code a person was handed is typed in here.
  'account.join_business': 'Join a $t(glossary.business_one) with a code',
  'account.appearance': 'Appearance',
  'account.dark_mode': 'Dark mode',
  'account.language': 'Language',
  'account.language_footer': 'The app downloads this language. Anything not translated yet stays in English.',
  'account.language_unavailable': 'Other languages are not ready yet.',
  'account.voice': 'OS’s voice',
  // The mobile row and page that hold how OS opens, when it answers and its
  // voice (docs/plans/workspace-menu.md step 5.3). `account.voice` above
  // stays: the web still reads it.
  'account.agent': 'OS',
  'account.voice_female': 'Female',
  'account.voice_male': 'Male',
  'account.voice_female_detail': 'Calm and clear. The default.',
  'account.voice_male_detail': 'Low and steady.',
  'account.voice_footer': 'Used from your next call, and in the chat too.',
  'account.voice_preview': 'Play a sample',
  'account.voice_stop_preview': 'Stop the sample',
  'account.voice_samples': 'Samples play in the app’s language.',
  'account.voice_sample_failed': 'The sample did not play. Try again.',
  // Account → Voice, live mode's rows (os-voice-conversation.md, J24 and J32)
  'account.voice_keep_awake': 'Keep the screen on in live mode',
  'account.voice_keep_awake_detail': 'The screen stays on until you stop live mode.',
  'account.voice_live_sound': 'Sound in live mode',
  // When OS answers in live mode (option B, step 6): three choices, phone and web.
  'account.voice_sensitivity': 'When OS answers',
  'account.voice_sensitivity_name_only': 'Only when I say “OS”',
  'account.voice_sensitivity_name_only_detail': 'Say its name, tap the orb or answer its question. Nothing else is taken as for OS.',
  'account.voice_sensitivity_normal': 'Normal',
  'account.voice_sensitivity_normal_detail': 'Right after OS speaks, a follow-up needs no “OS”. Otherwise, say its name.',
  'account.voice_sensitivity_everything': 'Everything near me',
  'account.voice_sensitivity_everything_detail': 'OS answers what is said near the phone. Amounts and dues still need “OS”.',
  // How OS opens for a new conversation: live mode (the default) or chat mode, phone and web.
  'account.voice_opens_in': 'Open OS in',
  'account.voice_opens_in_live': 'Live mode',
  'account.voice_opens_in_live_detail': 'OS starts listening as soon as you open it.',
  'account.voice_opens_in_chat': 'Chat mode',
  'account.voice_opens_in_chat_detail': 'OS opens ready to type. Talk live starts live mode when you want it.',
  // the voice sample: what each voice says when the picker plays it (C81),
  // read by the clip route as data (`clipKeys`, `preview`). English is the bare
  // key, every other language sits under it by tag, as the fillers do. No
  // language genders these words, so every voice says the one key, and none
  // has a `_female` or `_male` form.
  'account.voice_sample': 'Ask anything about your $t(glossary.record_other), or say what to add.',
  'account.voice_sample.hi': 'अपने रिकॉर्ड के बारे में कुछ भी पूछें, या बताएँ क्या जोड़ना है।',
  'account.voice_sample.hi-Latn': 'Apne records ke baare mein kuch bhi poochhiye, ya bataiye kya jodna hai.',
  'account.voice_sample.mr': 'तुमच्या रेकॉर्डबद्दल काहीही विचारा, किंवा काय जोडायचं ते सांगा.',
  'account.voice_sample.gu': 'તમારા રેકોર્ડ વિશે કંઈ પણ પૂછો, અથવા શું ઉમેરવું છે તે કહો.',
  'account.voice_sample.pa': 'ਆਪਣੇ ਰਿਕਾਰਡਾਂ ਬਾਰੇ ਕੁਝ ਵੀ ਪੁੱਛੋ, ਜਾਂ ਦੱਸੋ ਕੀ ਜੋੜਨਾ ਹੈ।',
  'account.voice_sample.bn': 'আপনার রেকর্ড নিয়ে যা খুশি জিজ্ঞেস করুন, বা বলুন কী যোগ করতে হবে।',
  'account.voice_sample.ta': 'உங்கள் பதிவுகளைப் பற்றி எதையும் கேளுங்கள், அல்லது எதைச் சேர்க்க வேண்டும் என்று சொல்லுங்கள்.',
  'account.voice_sample.te': 'మీ రికార్డుల గురించి ఏదైనా అడగండి, లేదా ఏమి జోడించాలో చెప్పండి.',
  'account.voice_sample.kn': 'ನಿಮ್ಮ ದಾಖಲೆಗಳ ಬಗ್ಗೆ ಏನು ಬೇಕಾದರೂ ಕೇಳಿ, ಅಥವಾ ಏನು ಸೇರಿಸಬೇಕು ಎಂದು ಹೇಳಿ.',
  'account.voice_sample.ml': 'നിങ്ങളുടെ റെക്കോർഡുകളെക്കുറിച്ച് എന്തും ചോദിക്കൂ, അല്ലെങ്കിൽ എന്ത് ചേർക്കണമെന്ന് പറയൂ.',
  'account.about': 'About',
  'account.privacy': 'Privacy Policy',
  'account.terms': 'Terms of Use',
  'account.privacy_body':
    'This page is a placeholder. The privacy policy that describes how RecordOS handles your data will live here.',
  'account.terms_body':
    'This page is a placeholder. The terms of use that govern the product will live here.',
  'account.sign_out': 'Sign out',
  'account.signing_out': 'Signing out…',
  'account.storybook': 'Open Storybook',
  // The delete screen. The one place the app is deliberately hard to use.
  'account.delete_account': 'Delete account',
  'account.delete_title': 'Delete account',
  'account.delete_body':
    'This permanently deletes your account and every $t(glossary.business_one) you own — on the server and on this phone. You are signed out straight away. There is no undo.',
  'account.delete_prompt': 'Type delete to confirm',
  'account.delete_placeholder': 'delete',
  'account.delete_confirm': 'Delete my account',
  'account.delete_working': 'Deleting…',
  // ------------------------------------------------- levels and jobs
  // Two words that must never merge. A LEVEL is one of app.authority's closed
  // five: what a person may change about the business itself. A JOB is
  // app.role.name, the business's own word for the work. The screen says
  // "level" and "job" and never "role", because a shopkeeper already uses
  // "role" for both and the confusion is the whole risk.
  // Both nouns themselves are `glossary.level_*` and `glossary.job_*`: they
  // are counted on the jobs list and in the impact sheet, so they belong
  // where a count picks the form.
  'level.help': 'How much of the $t(glossary.business_one) they can change.',
  // app.entry_status. Only the one a job can grant today has a word here.
  'status.submitted': 'Submitted',
  'job.help': 'The work you give them, in your own word.',
  'job.none': 'No $t(glossary.job_one) yet',
  'job.new': 'New $t(glossary.job_one)',
  'job.ready_made': 'Ready made',
  'job.yours': 'Your $t(glossary.job_other)',
  'job.lowest_level': 'Lowest $t(glossary.level_one) allowed',
  'job.lowest_level.help':
    'A $t(glossary.person_one) below this $t(glossary.level_one) is refused the $t(glossary.job_one) the moment you give it, not later.',
  'job.holders_one': '{{count, number}} $t(glossary.person_one)',
  'job.holders_other': '{{count, number}} $t(glossary.person_other)',
  'job.rules_one': '{{count, number}} rule',
  'job.rules_other': '{{count, number}} rules',
  'job.rename_changes_nothing': 'Renaming a $t(glossary.job_one) changes nobody\u2019s access.',

  // ------------------------------------------- the business switcher
  // The card at the top of Home, and the sheet it opens. One door, so the
  // caption promises one thing. The business itself is behind the people
  // icon on the Dashboard (business.people.door).
  'business.switch.caption': 'Tap to switch',
  'business.sheet.title': 'Your $t(glossary.business_other)',
  'business.sheet.subtitle':
    'One at a time. Everything on screen belongs to the one you pick.',
  'business.sheet.new': 'New $t(glossary.business_one)',
  'business.records_one': '{{count, number}} $t(glossary.record_one)',
  'business.records_other': '{{count, number}} $t(glossary.record_other)',
  'business.switching': 'Switching\u2026',

  // --------------------------------------------------- the workspace menu
  // The side menu the workspace name opens (docs/plans/workspace-menu.md):
  // the workspaces at its top, the person at its foot. Its heading is
  // account.businesses, its New workspace is business.sheet.new, its "You"
  // is settings.profile.card, and its two tiles are account.appearance and
  // account.language.
  'workspace_menu.label': 'Workspaces and your settings',
  'workspace_menu.lede': 'One at a time. Everything you see belongs to the one you pick',
  'workspace_menu.join': 'Join with a code',
  'workspace_menu.your_settings': 'Your settings',
  'workspace_menu.profile_hint': 'Opens your profile',

  // ------------------------------------------------- the business itself
  'business.title': 'Workspace',
  'business.name': 'Name',
  'business.currency': 'Currency',
  'business.records': 'Records',
  'business.started': 'Started {{day}}',
  'business.the_business': 'The $t(glossary.business_one)',
  'business.the_people': 'The $t(glossary.person_other)',
  // The icon top right of the Dashboard. Spoken, never drawn.
  'business.people.door': 'The $t(glossary.person_other) in this $t(glossary.business_one)',
  'business.the_plan': 'The plan',
  'business.setup_conversation.title': 'How it was set up',
  'business.setup_conversation.description': 'The conversation this workspace was built from. Read only.',
  'business.setup_conversation.you': 'You',
  'business.setup_conversation.empty': 'The setup had no conversation to show.',
  'business.setup_conversation.failed': 'The setup conversation did not load.',
  'business.people': 'People',
  'business.edit_details': 'Edit details',
  'business.team': 'Team',
  'business.team.help': 'Who is in the $t(glossary.business_one), and at what $t(glossary.level_one)',
  'business.jobs': 'Jobs',
  'business.jobs.help': 'Your own word for the work, and what it may do',
  'business.branches': 'Branches',
  'business.branches.help': 'A site, a till, or a vehicle a $t(glossary.record_one) can be split by',
  'business.branches.none': 'None yet',
  'business.plan': 'Plan',
  // app.tenant.plan_id, which is a key and not a word.
  'plan.trial': 'Trial',
  'business.plan.ends': 'Ends {{day}}',
  'business.billing': 'Billing',
  'business.billing.owner_only': 'Owner only',
  'business.billing.help': 'Only the Owner can see the plan, change it, or pay for it.',
  'business.billing.primary_only': 'Primary admin only',
  'business.billing.primary_help': 'Only the primary admin can see the plan, change it, or pay for it.',
  'business.leave': 'Leave this $t(glossary.business_one)',
  'business.saved': 'Saved',

  // -------------------------------------------------------------- the team
  'team.title': 'Team',
  'team.count_one': '{{count, number}} $t(glossary.person_one)',
  'team.count_other': '{{count, number}} $t(glossary.person_other)',
  'team.you': 'you',
  // The menu on a person's row: View as (view_as.open) and this. The trigger's
  // spoken name carries the person, so two rows never announce alike.
  'team.menu.open': 'Open',
  'team.menu.label': 'Options for {{person}}',
  'team.invite': 'Invite a $t(glossary.person_one)',
  'team.invited_waiting': 'Invited, not opened yet',
  'team.set_by_hand': 'Set by hand on {{day}}',
  'team.synced': 'Synced {{day}}',
  'team.never_synced': 'Not synced yet',

  // The invitation. Nothing here sends email, so the token is shown and the
  // inviter shares it. The screen must say that rather than imply a message.
  'invite.title': 'Invite',
  'invite.who': 'Who',
  'invite.their_level': 'Their $t(glossary.level_one)',
  'invite.their_job': 'Their $t(glossary.job_one), if you have one',
  // Two buttons, because they promise different things. One mints a code you
  // then share yourself; nothing here sends a message.
  'invite.make_code': 'Make the code',
  'invite.send': 'Send the invitation',
  'invite.branch': 'Branch',
  'invite.branch.every': 'Every branch',
  'invite.branch.help': 'Leave it at every branch unless the work is at one place',
  'invite.level_opens_nothing': 'A $t(glossary.level_one) on its own opens nothing',
  'invite.level_opens_nothing.body':
    'Until you give them a $t(glossary.job_one), or open a $t(glossary.record_one) for them by hand, they sign in and see an empty list. That is deliberate: nothing is shared until you share it.',
  'invite.owner_refused': 'Hand the $t(glossary.business_one) over instead. One owner at a time.',
  'invite.code_title': 'Share this code',
  'invite.code_body':
    'They enter it once, on their own phone, and they are in. It works for seven days and once only.',
  'invite.copy': 'Copy the code',
  'invite.copied': 'Copied',
  'invite.revoke': 'Withdraw the invitation',
  'invite.accept.title': 'Join a $t(glossary.business_one)',
  'invite.accept.body': 'Enter the code somebody gave you.',
  'invite.accept.label': 'Invitation code',
  'invite.accept.button': 'Join',

  // Inviting in three steps (web-redesign-04 phase 4): their details, their
  // role, what they can open and how it reaches them. An email invitation is
  // sent; a phone invitation is handed over, because nothing sends a text.
  'invite.step_of': 'Step {{step}} of 3',
  'invite.step.details': 'Their details',
  'invite.step.role': 'Their role',
  'invite.step.open': 'What they can open',
  'invite.details.lede': 'Who they are, and how the invitation reaches them.',
  'invite.name': 'Name',
  'invite.name.placeholder': 'How the team will see them',
  'invite.phone': 'Phone number',
  'invite.phone.placeholder': '+91 98450 12345',
  'invite.phone_bad': 'Start with + and the country code, like +91 98450 12345.',
  'invite.email': 'Email',
  'invite.email.placeholder': 'name@example.com',
  'invite.email_bad': 'That does not look like an email address.',
  'invite.one_needed': 'A phone number or an email is needed: it is where the invitation goes.',
  'invite.reports_to': 'Reports to (optional)',
  'invite.reports_to.help': 'Who their team is. It opens nothing; their role and access do.',
  'invite.they': 'this $t(glossary.person_one)',
  'invite.role.lede': 'What {{person}} may do in the $t(glossary.business_one).',
  'invite.role.above_yours': 'It allows more than your own role, so an admin invites at this one.',
  'invite.role.primary_note': 'Primary admin is not a role. It moves only when the $t(glossary.business_one) is handed over.',
  'invite.open.lede': 'Which $t(glossary.record_other) {{person}} may use, and how the invitation reaches them.',
  'invite.open.follow':
    'Every $t(glossary.field_one), and the $t(glossary.list_other) a $t(glossary.record_one) uses, follow the $t(glossary.record_one). Set them one by one on their page once they join.',
  'invite.open.admin': 'An admin opens every $t(glossary.record_one), so there is nothing to choose for {{person}}.',
  'invite.open.not_yours': 'An admin opens $t(glossary.record_other) to them once they join.',
  'invite.how': 'How it reaches them',
  'invite.how.link': 'A link',
  'invite.how.link.help': 'A link goes to {{to}}. They tap it and they are in.',
  'invite.how.link.help_phone': 'A link you hand to them. They tap it and they are in.',
  'invite.how.both': 'A link and a code',
  'invite.how.both.help': 'The same link, and a code they can type on their phone, or you can hand over in person.',
  'invite.next': 'Next',
  'invite.back': 'Back',
  'invite.sent.title': 'Invitation sent',
  'invite.sent.lede': '{{person}} gets it at {{to}}. When they open it, they are in.',
  'invite.sent.mail_failed': 'The email to {{to}} could not be sent. Hand it over yourself, or send it again.',
  'invite.ready.title': 'Invitation ready',
  'invite.ready.lede': 'Hand it to {{person}} yourself: the code or the link. When they open it, they are in.',
  'invite.sent.works': 'Works once, until {{day}}',
  'invite.sent.again': 'Send it again',
  'invite.sent.hand_over': 'Or hand it over yourself',
  'invite.sent.hand_over.help': 'Standing with them? They can enter this code, or open the link. It only works for {{to}}.',
  'invite.hand_over': 'Hand it over',
  'invite.hand_over.help_phone': 'They can enter this code in the app, or open the link. It works once.',
  'invite.share_code': 'Share the code',
  'invite.share_link': 'Share the link',
  'invite.code_label': 'The code: {{code}}',
  'invite.sent.role': 'Role',
  'invite.sent.can_open': 'Can open',
  'invite.sent.can_open_count_one': '{{count, number}} $t(glossary.record_one)',
  'invite.sent.can_open_count_other': '{{count, number}} $t(glossary.record_other)',
  'invite.row.sent_to': 'Sent to {{to}}',
  'invite.row.for': 'For {{to}}',
  'invite.row.expires': 'Expires {{day}}',
  'invite.row.expired': 'Expired {{day}}',
  'invite.withdrawn': 'Invitation withdrawn',
  'invite.gone': 'This invitation was used or withdrawn.',

  // ------------------------------------------------------------ one person
  'person.level': 'Level',
  'person.level.help': 'What they may change about the $t(glossary.business_one) itself',
  'person.jobs.help': 'What they do, in your own word',
  'person.can_open': 'What they can open',
  'person.can_open.none': 'Nothing yet. Give them a $t(glossary.job_one), or open one by hand.',
  'person.remove': 'Remove from the $t(glossary.business_one)',
  'person.remove.title': 'Remove {{person}}?',
  // Leads with what survives, because it does. data.entry.created_by carries
  // no key, so every row they wrote keeps their name on it.
  'person.remove.body':
    '{{person}} stays where it is, with their name on it. They lose the key, not the work.',
  'person.remove.entries_one': '{{count, number}} $t(glossary.entry_one)',
  'person.remove.entries_other': '{{count, number}} $t(glossary.entry_other)',
  'person.remove.entries_any': 'Every $t(glossary.entry_one) they wrote',
  'person.remove.softer': 'Make them a Viewer instead',
  'person.last_owner': 'A $t(glossary.business_one) must always have one owner.',
  'person.last_owner.fix': 'Make somebody else the owner first.',
  'person.choose_new_owner': 'Choose a new owner',

  // ---------------------------------------------------------- the job editor
  'job.title': 'Job',
  // The Jobs screen's own name, and the row on a person that lists theirs.
  // `glossary.job_*` is the mid-sentence word and is lower case; a label is
  // capitalised by being a label, so the two cannot be the same string.
  'job.section': 'Jobs',
  'job.name': 'Name',
  'job.business_permissions': 'What they may do to the $t(glossary.business_one)',
  'job.record_permissions': 'What they may do in $t(glossary.record_other) and $t(glossary.list_other)',
  'job.add_rule': 'Add a rule',
  'job.holders': 'Who holds this $t(glossary.job_one)',
  'job.give': 'Give this $t(glossary.job_one)',
  'job.archive': 'Retire this $t(glossary.job_one)',
  'job.none_yet': 'No $t(glossary.job_other) yet. A $t(glossary.job_one) is your word for the work.',
  'job.business_permissions.none': 'Nothing about the $t(glossary.business_one) itself',
  'job.business_permissions_one': '{{count, number}} thing about the $t(glossary.business_one)',
  'job.business_permissions_other': '{{count, number}} things about the $t(glossary.business_one)',

  // The impact sheet. It is shown BEFORE the save, never after.
  'impact.title': 'This change reaches {{people}}',
  'impact.across_one': 'Across {{count, number}} $t(glossary.record_one) or $t(glossary.list_one).',
  'impact.across_other': 'Across {{count, number}} $t(glossary.record_other) and $t(glossary.list_other).',
  'impact.skipped_one': 'One is set by hand and will not move: {{names}}',
  'impact.skipped_other': 'These are set by hand and will not move: {{names}}',
  'impact.refused': 'These $t(glossary.person_other) cannot hold this $t(glossary.job_one) yet: {{names}}',
  'impact.confirm_one': 'Change access for {{count, number}} $t(glossary.person_one)',
  'impact.confirm_other': 'Change access for {{count, number}} $t(glossary.person_other)',
  'impact.rename_only': 'A rename reaches nobody. Nothing else changes.',
  'impact.nobody': 'This reaches nobody yet. No one holds this $t(glossary.job_one).',
  // The held-back half of the impact sentence. It counts PEOPLE, not grants:
  // JobImpactResponse.skippedCustomised holds one entry per person and record.
  'impact.held_back_one': '{{count, number}} $t(glossary.person_one) is set by hand and will not move.',
  'impact.held_back_other': '{{count, number}} $t(glossary.person_other) are set by hand and will not move.',
  'impact.not_a_member': 'No longer in this $t(glossary.business_one), so they are skipped.',
  'impact.below_level': 'Their $t(glossary.level_one) is below what this $t(glossary.job_one) needs, so they are skipped.',

  // A grant somebody edited away from its job. app.apply_role() steps over it
  // for good, so it is never silent: it is badged, and it has one way back.
  'grant.customised': 'Set by hand',
  'grant.customised.help': 'Somebody changed this by hand, so the $t(glossary.job_one) no longer moves it.',
  'grant.refollow': 'Follow the $t(glossary.job_one) again',

  // No tenant is open. A management screen cannot guess which business.
  'business.none_open': 'No $t(glossary.business_one) is open. Pick one first.',
  // The management screens are NEVER kept on this phone, unlike the records and lists.
  // sync.offline_empty.* promises "everything after that works offline", which
  // is true of a record list and a lie here, so these screens say their own
  // thing — and say why, because the reason is a decision and not a shortfall.
  'team.offline.title': 'This one needs a connection',
  'team.offline.body':
    'Who is in the $t(glossary.business_one), and what each $t(glossary.person_one) may do, is never kept on this phone. It is read fresh every time, so nobody is shown an old answer about who can open what.',

  // ------------------------------------------------------- the rule editor
  'rule.title': 'A rule',
  'rule.in_words': 'The rule, in words',
  'rule.which_records': 'Which $t(glossary.record_other) and $t(glossary.list_other)',
  'rule.which_records.single': 'One $t(glossary.record_one) or $t(glossary.list_one)',
  'rule.which_records.kind': 'All of one kind',
  // The same four said mid-sentence, for the rule sentence. The four above are
  // row copy: they stand alone as the value of a row and open with a capital.
  // A kind says WHICH kind here — the picker's own rows already do that, and
  // "In all of one kind they may" names nothing a person can picture, while
  // "In every list they may" does. A record matched by id needs no word at
  // all: its name is a name, and a name keeps its capital wherever it sits.
  'rule.which_records.single.in_sentence': 'one $t(glossary.record_one) or $t(glossary.list_one)',
  'rule.which_records.kind_record.in_sentence': 'every $t(glossary.record_one)',
  'rule.which_records.kind_directory.in_sentence': 'every $t(glossary.list_one)',
  // A pattern that names no match key at all reaches no record: app.apply_role()
  // matches on record_id OR kind and an empty match answers neither.
  // It keeps the old generic word rather than claiming a kind.
  'rule.which_records.kind.in_sentence': 'all of one kind',
  'rule.what_they_may_do': 'What they may do',
  'rule.the_limits': 'The limits',
  'rule.money': 'Money',
  'rule.money.both': 'money in and out',
  'rule.money.in': 'money in only',
  'rule.money.out': 'money out only',
  'rule.rows': 'Entries they see',
  'rule.rows.all': 'every $t(glossary.entry_one) there is',
  'rule.rows.own': 'only $t(glossary.entry_other) they wrote',
  'rule.write': 'A write',
  'rule.write.direct': 'goes in straight away',
  'rule.write.draft': 'waits for approval',
  'rule.write.request': 'is a request somebody else writes',
  'rule.transitions': 'They may move an $t(glossary.entry_one) to',
  'rule.balance': 'They may see the balance',
  'rule.field_rules': 'Field rules',
  'rule.field_rules.help': 'Hide a $t(glossary.field_one), lock it to one value, or make it needed',
  'rule.field_rules.none': 'No $t(glossary.field_one) rules. They see every $t(glossary.field_one) on the $t(glossary.entry_one).',
  'rule.nothing_yet': 'do nothing here yet',
  // The glue. Without it the sentence joins on punctuation and reads as a
  // caption; with it, it closes up: "In Sales, they may see totals and
  // add entries — money in and out, only rows they wrote, and every entry they
  // write goes in straight away."
  'rule.sentence_opening': 'In',
  'rule.sentence_they_may': 'they may',
  'rule.sentence_every_write': 'and every $t(glossary.entry_one) they write',

  // ---------------------------------------------------------- field rules
  'field_rule.title': 'Field rules',
  'field_rule.open': 'Open',
  'field_rule.hidden': 'Hidden',
  'field_rule.locked': 'Locked',
  'field_rule.must_fill': 'Must fill',
  'field_rule.hidden.help': 'Stripped on the way out, before the $t(glossary.entry_one) reaches their phone.',
  'field_rule.locked.help': 'Always {{value}}. They never choose.',
  'field_rule.must_fill.help': 'They cannot save the $t(glossary.entry_one) without it.',
  'field_rule.above.help': 'Needed only above {{amount}}. Below that they may skip it.',
  'field_rule.above.label': 'Only above',
  'field_rule.count_one': '{{count, number}} $t(glossary.field_one) is stripped before the $t(glossary.entry_one) leaves the server.',
  'field_rule.count_other': '{{count, number}} $t(glossary.field_other) are stripped before the $t(glossary.entry_one) leaves the server.',
  // A field the form REQUIRES cannot also be hidden, or nobody can save.
  'field_rule.required_clash':
    'This $t(glossary.field_one) is required, so hiding it would make a save impossible for them.',

  // ---------------------------------------------------- from a record's side
  'record_access.open': 'Who can use this',
  'record_access.fields_hidden': '{{count, number}} $t(glossary.field_other) hidden from them',
  'record_access.writes_fields': 'writes {{count, number}} $t(glossary.field_other) only',
  'record_access.title': 'Who can use {{record}}',
  'record_access.owner': 'An owner holds everything, with no rule written anywhere',
  'record_access.set_by_hand': 'Set by hand here',
  'record_access.set_by_hand.body':
    'You changed this $t(glossary.person_one) here yourself, so a change to the {{job}} $t(glossary.job_one) will step over them. The exception you made survives the next edit, on purpose.',
  'record_access.refollow': 'Follow the {{job}} $t(glossary.job_one) again',
  'record_access.offline': 'It lands when their phone next syncs',
  'record_access.offline.body':
    'A $t(glossary.person_one) whose access you changed keeps what is already on their phone until it reaches the server again. Nothing they wrote in between is thrown away.',

  'level_and_job.help':
    'Level is how much of the $t(glossary.business_one) they can change. Job is the daily work you give them. A $t(glossary.person_one) has one $t(glossary.level_one) and can hold many $t(glossary.job_other).',

  // app.authority, seeded by migration 0006. The label is the rung; the help
  // line says what that rung actually permits, in a shopkeeper's words.
  'authority.owner': 'Primary admin',
  'authority.partner': 'Partner',
  'authority.admin': 'Admin',
  'authority.member': 'Member',
  'authority.viewer': 'Viewer',
  'authority.owner.help':
    'Complete access, plus the two things kept for a primary admin: removing or demoting admins, and — when that day comes — shutting the $t(glossary.business_one) down. A $t(glossary.business_one) can have more than one.',
  'authority.partner.help':
    'Runs it like a primary admin. Cannot remove or demote admins, and shutting the $t(glossary.business_one) down is not theirs either.',
  'authority.admin.help':
    'Complete access to run the $t(glossary.business_one). Cannot remove or demote other admins, and shutting the $t(glossary.business_one) down is kept for a primary admin.',
  'authority.member.help': 'Does the daily work. Adds and fixes $t(glossary.entry_other) in the $t(glossary.record_other) and $t(glossary.list_other) they hold.',
  'authority.viewer.help': 'Only looks. Sees what they are shown, and changes nothing.',

  // A role's permissions (packages/shared/src/roles.ts, web-redesign-02
  // Decided 1 and 2). Reached as role.permission.<key>, role.group.<group>
  // and role.reach.<reach>; the words are the role page's
  // (business-14-roles.mjs, business-16-reporting.mjs).
  'role.permission.see_entries': 'See $t(glossary.entry_other)',
  'role.permission.sees_new_records': 'See $t(glossary.record_other) made later, from their role',
  'role.permission.add_entries': 'Add $t(glossary.entry_other)',
  'role.permission.change_entries': 'Change $t(glossary.entry_other)',
  'role.permission.delete_entries': 'Delete $t(glossary.entry_other)',
  'role.permission.download_entries': 'Download $t(glossary.entry_other)',
  'role.permission.manage_records': 'Make $t(glossary.record_other) and change their $t(glossary.field_other)',
  'role.permission.see_insights': 'See $t(glossary.insight_other)',
  'role.permission.see_money': 'See money totals, accounts and what each party owes',
  'role.permission.change_insights': 'Add and change $t(glossary.insight_other)',
  'role.permission.change_dashboard': 'Change the $t(glossary.dashboard)',
  'role.permission.see_team': 'See the team',
  'role.permission.set_reports_to': 'Set who reports to whom in their team',
  'role.permission.invite_people': 'Invite $t(glossary.person_other)',
  'role.permission.change_access': 'Change a $t(glossary.person_one)\u2019s role and access',
  'role.permission.remove_people': 'Remove $t(glossary.person_other)',
  'role.permission.manage_roles': 'Make and change roles',
  'role.permission.change_workspace': 'Change the $t(glossary.business_one) details',
  'role.permission.download_workspace': 'Download the whole $t(glossary.business_one)',
  'role.permission.ask_os': 'Ask OS about what they can open',
  'role.permission.os_changes': 'Let OS make changes, once they say yes',
  'role.permission.talk_live': 'Talk live',
  'role.permission.keep_facts': 'Keep facts for the whole $t(glossary.business_one)',
  'role.group.records': 'Records and $t(glossary.entry_other)',
  'role.group.records.line': 'What they may see and do in the $t(glossary.record_other) they are given.',
  'role.group.insights': 'Insights',
  'role.group.insights.line': 'Totals and charts, on a $t(glossary.record_one) and on the $t(glossary.dashboard).',
  'role.group.team': 'Team and roles',
  'role.group.team.line': 'The $t(glossary.person_other) in the $t(glossary.business_one), and what each may do.',
  'role.group.workspace': 'Workspace settings',
  'role.group.workspace.line': 'The $t(glossary.business_one) details, and all of its data.',
  'role.group.os': 'OS',
  'role.group.os.line': 'What they may ask OS, and let it do for them.',
  'role.reach.off': 'Off',
  'role.reach.own': 'Own',
  'role.reach.team': 'Their team',
  'role.reach.everyone': 'Everyone',

  // The pieces Workspace settings shares on the web (web-redesign-03 phase 1):
  // the role badge, a permission group, the reach control, the Reports to
  // picker, the access editor and a person's panel. Words from
  // business-14-roles.mjs, business-15-access.mjs, business-16-reporting.mjs
  // and person-detail.mjs.
  'role_editor.count_one': '{{on, number}} of {{count, number}} on',
  'role_editor.count_other': '{{on, number}} of {{count, number}} on',
  'role_editor.turn_all_on': 'Turn all on',
  'role_editor.turn_all_off': 'Turn all off',
  'role_editor.changed': 'Changed',
  'role_editor.needs': 'Needs {{need}}',
  'role_editor.needs_and': 'Needs {{need}} and {{other}}',
  'reports_to.label': 'Reports to',
  'reports_to.nobody': 'Nobody',
  'reports_to.find': 'Find someone',
  'reports_to.to_nobody': 'Reports to nobody',
  'reports_to.no_match': 'Nobody by that name',
  'reports_to.loop': 'Reports to {{person}}: that would make a loop',
  'reports_to.sees.off': '{{role}} · sees no $t(glossary.entry_other)',
  'reports_to.sees.own': '{{role}} · sees only their own $t(glossary.entry_other); their team’s will not show to them',
  'reports_to.sees.team': '{{role}} · sees their team’s $t(glossary.entry_other)',
  'reports_to.sees.everyone': '{{role}} · sees every $t(glossary.entry_one)',
  'reports_to.sees.unknown': '{{role}}',
  'access.level.none': 'None',
  'access.level.see': 'See',
  'access.level.change': 'Change',
  'access.level.label': 'Access to {{thing}}',
  'access.mixed': 'Mixed',
  'access.fields': 'Fields',
  'access.lists_used': 'Lists it uses',
  'access.sub.none': 'Not open to them',
  'access.sub.same': 'Every $t(glossary.field_one) the same',
  'access.sub.mixed': 'Some $t(glossary.field_other) differ',
  'access.sub.everything': 'Opens every $t(glossary.record_one)',
  'access.show_fields': 'Show the $t(glossary.field_other) of {{thing}}',
  'access.lock.opens_everything': 'An admin opens every $t(glossary.record_one), so there is nothing to set.',
  'access.lock.role_sees_only': 'Their role does not add or change $t(glossary.entry_other).',
  'access.lock.record_not_given': 'Give the $t(glossary.record_one) first.',
  'access.lock.record_at_see': 'Needs the $t(glossary.record_one) set to change.',
  'access.quiet': 'Their role does not allow this. It is kept, and comes back if the role allows it again.',
  'access.nothing_open': 'Nothing is open to change.',
  'access.save.title_person': 'Change what {{person}} can open?',
  'access.save.title_record': 'Change who can open {{thing}}?',
  'access.save.body': 'It reaches them the next time their phone syncs.',
  'access.save.action_one': 'Save {{count, number}} change',
  'access.save.action_other': 'Save {{count, number}} changes',
  'access.save.move': '{{from}} → {{to}}',
  'access.save.move_every': '{{from}} → {{to}}, every $t(glossary.field_one)',
  'access.save.inside': '{{thing}}, in {{record}}',
  'access.save.nothing': 'Nothing changed.',
  'access.empty.title': 'Nothing yet',
  'access.empty.body': '{{person}} signs in to an empty list until a $t(glossary.record_one) is opened to them.',
  'access.ask_for': 'Ask for access',
  'access.ask_for.sent': 'Asked. Everyone who may change roles and access will see it on Team.',
  'access.give': 'Give access',
  'access.admin.title': 'Every $t(glossary.record_one), $t(glossary.field_one) and $t(glossary.list_one)',
  'access.admin.body': '{{person}} is an admin: an admin opens everything in the $t(glossary.business_one), so there is nothing to set here.',
  'access.lock.record_sees_only': 'The $t(glossary.record_one) is at See, so nothing inside it can be at Change.',
  'access.title': 'What {{person}} can open',
  'access.lede': 'Tap a $t(glossary.record_one) to set each $t(glossary.field_one), and the $t(glossary.list_other) it uses. None · See · Change.',
  'access.records': 'Records',
  'access.records_count': 'Records · {{on, number}} of {{all, number}}',
  'access.lists': 'Lists',
  'access.lists.lede': 'The $t(glossary.list_other) they can pick from, or change.',
  'access.changed': 'Changed',
  'access.summary.see_all': 'Sees every $t(glossary.field_one)',
  'access.summary.change_all': 'Changes every $t(glossary.field_one)',
  'access.summary.see': 'Sees {{on, number}} of {{all, number}} $t(glossary.field_other)',
  'access.summary.change': 'Changes {{on, number}} of {{all, number}} $t(glossary.field_other)',
  'access.summary.hidden_one': '{{count, number}} hidden',
  'access.summary.hidden_other': '{{count, number}} hidden',
  'access.record_title': '{{record}} for {{person}}',
  'access.step.record': 'The $t(glossary.record_one)',
  'access.step.record.help': '{{record}} and its $t(glossary.entry_other)',
  'access.step.fields': 'The $t(glossary.field_other) inside it',
  'access.step.fields.help': 'See, change, or none',
  'access.step.lists': 'The $t(glossary.list_other) it picks from',
  'access.step.lists.help': 'What they see or change in them',
  'access.reach.sees': 'Sees: {{reach}}',
  'access.reach.changes': 'Changes: {{reach}}',
  'access.reach.from_role': 'Whose $t(glossary.entry_other) they see and change comes from the {{role}} role. Change their role to change it.',
  'access.reach.narrowed': 'Narrower here than the {{role}} role: this $t(glossary.record_one) was set up before roles, and keeps what it was set to.',
  'access.reach.narrowed_adds':
    'Narrower here than the {{role}} role: this $t(glossary.record_one) was set up before roles, and keeps what it was set to. At Change they add $t(glossary.entry_other) here but change none already written.',
  'access.none_hides': 'None hides the $t(glossary.field_one): it is taken out before the $t(glossary.entry_one) reaches their phone.',
  'access.was': 'Was {{level}}',
  'access.no_changes': 'No changes yet',
  'access.unsaved_one': '{{count, number}} change not saved',
  'access.unsaved_other': '{{count, number}} changes not saved',
  'access.discard': 'Discard',
  'access.save_changes': 'Save changes',
  'access.saved': 'Saved. It reaches {{person}} when their phone next syncs.',
  'access.give_person': 'Give {{person}} access',
  'access.starts_from': 'Starts from the {{role}} role. Not given yet.',
  'access.from_role': 'From the {{role}} role, and set here',
  'person.change_access': 'Change what they can open',
  'record_access.people': 'People',
  'record_access.count_one': '{{count, number}} $t(glossary.person_one) · tap one to set what they may do here',
  'record_access.count_other': '{{count, number}} $t(glossary.person_other) · tap one to set what they may do here',
  'record_access.primary': 'Holds everything as the primary admin. Nothing is set here.',
  'record_access.admin': 'Every $t(glossary.entry_one) and $t(glossary.field_one), as an admin. Nothing is set here.',
  'record_access.nobody_else': 'Nobody but the admins can open this $t(glossary.record_one) yet. Give someone access to start.',
  'record_access.hint': 'A $t(glossary.person_one) starts with what their role gives. What you set here is theirs alone, for {{record}}. It lands when their phone next syncs.',
  'record_access.give': 'Give someone access',
  'record_access.admins_note': 'Admins hold every $t(glossary.record_one) already, so they are not here.',
  'record_access.not_in_yet': 'Not in {{record}} yet',
  'record_access.everyone_in': 'Everyone in the $t(glossary.business_one) can open it already.',
  'record_access.invite_new': 'Invite someone new',
  'record_access.invite_new.help': 'By their phone or email',
  'person.panel.title': 'Person',
  'person.call': 'Call',
  'person.email': 'Email',
  'person.card.contact': 'Contact',
  'person.card.team': 'Team',
  'person.card.role': 'Role',
  'person.card.can_open_one': 'What they can open · {{count, number}}',
  'person.card.can_open_other': 'What they can open · {{count, number}}',
  'person.contact.edit': 'Edit',
  'person.contact.add_phone': 'Add a phone number',
  'person.contact.add_email': 'Add an email',
  'person.contact.add_address': 'Add an address',
  'person.contact.joined': 'Joined {{day}}',
  'person.team.nobody': 'Nobody reports to them',
  'person.team.count_one': '{{count, number}} $t(glossary.person_one)',
  'person.team.count_other': '{{count, number}} $t(glossary.person_other)',
  'person.team.see_chart': 'See them on the chart',
  'person.role.primary': 'Every permission, and every $t(glossary.record_one). Handing it over is the only change.',
  'person.role.sees.off': '{{role}}: sees no $t(glossary.entry_other).',
  'person.role.sees.own': '{{role}}: sees their own $t(glossary.entry_other) only, in the $t(glossary.record_other) below.',
  'person.role.sees.team': '{{role}}: sees their own $t(glossary.entry_other) and their team’s, in the $t(glossary.record_other) below.',
  'person.role.sees.everyone': '{{role}}: sees every $t(glossary.entry_one), in the $t(glossary.record_other) below.',
  'person.open.record': 'Record',
  'person.open.list': 'List',
  'person.open.access': 'Access',
  'person.open.fields': 'Fields',
  'person.open.all_fields': 'All',
  'person.open.some_fields_one': '{{shown, number}} of {{count, number}}',
  'person.open.some_fields_other': '{{shown, number}} of {{count, number}}',
  'person.open.nothing': 'Nothing yet',
  'person.active.now': 'Active now',
  'person.active.minutes_one': 'Active {{count, number}} min ago',
  'person.active.minutes_other': 'Active {{count, number}} min ago',
  'person.active.hours_one': 'Active {{count, number}} hour ago',
  'person.active.hours_other': 'Active {{count, number}} hours ago',
  'person.active.yesterday': 'Active yesterday',
  'person.active.day': 'Active {{day}}',
  'person.active.never': 'Not active yet',
  'person.notice.title_one': '{{count, number}} $t(glossary.person_one) reports to {{person}}',
  'person.notice.title_other': '{{count, number}} $t(glossary.person_other) report to {{person}}',
  'person.notice.body':
    'Their role, {{role}}, lets them see only their own $t(glossary.entry_other), so their team’s $t(glossary.entry_other) do not show to them. That is fine if they only assign the work.',
  'person.notice.change_role': 'Change their role',
  'person.notice.leave': 'Leave it',

  // Roles on the web (web-redesign-03 phase 2): the Roles page, a role's page,
  // a new role and removing one. The role.* domain is the permission words, so
  // these are roles_page.*, role_page.* and role_new.*.
  'roles_page.title': 'Roles',
  'roles_page.hint.title': 'A role is what a $t(glossary.person_one) may do.',
  'roles_page.hint.body':
    'A $t(glossary.business_one) starts with two, Admin and Member; make any others you need. Which $t(glossary.record_other), $t(glossary.field_other) and $t(glossary.list_other) a $t(glossary.person_one) may use is their access, set on each $t(glossary.person_one) on Team.',
  'roles_page.meta.count_one': '{{count, number}} role',
  'roles_page.meta.count_other': '{{count, number}} roles',
  'roles_page.meta.holders_one': '{{count, number}} $t(glossary.person_one) holds one',
  'roles_page.meta.holders_other': '{{count, number}} $t(glossary.person_other) hold one',
  'roles_page.meta.what': 'What a $t(glossary.person_one) may do',
  'roles_page.meta.you_hold': 'You hold {{role}}',
  'roles_page.primary.title': '{{person}} is the primary admin',
  'roles_page.primary.body':
    'The owner, not a role: everything, and the three things kept for the owner alone. The plan and billing, deleting the $t(glossary.business_one), and handing it over.',
  'roles_page.primary.hand_over': 'Hand over the $t(glossary.business_one)',
  'roles_page.col.role': 'Role',
  'roles_page.col.sees': 'Sees $t(glossary.entry_other)',
  'roles_page.col.holders': 'Who holds it',
  'roles_page.col.does': 'What it lets them do',
  'roles_page.col.made': 'Made',
  'roles_page.sees.off': 'None',
  'roles_page.sees.own': 'Their own',
  'roles_page.sees.team': 'Their team’s',
  'roles_page.sees.everyone': 'Everyone’s',
  'roles_page.holders_one': '{{count, number}} $t(glossary.person_one)',
  'roles_page.holders_other': '{{count, number}} $t(glossary.person_other)',
  'roles_page.holders.none': 'Nobody yet',
  'roles_page.does_one': '{{on, number}} of {{count, number}} permission',
  'roles_page.does_other': '{{on, number}} of {{count, number}} permissions',
  'roles_page.does.every_group': 'Every group',
  'roles_page.does.nothing': 'Nothing on',
  'roles_page.group.records': 'Records',
  'roles_page.group.insights': 'Insights',
  'roles_page.group.team': 'Team',
  'roles_page.group.workspace': 'Workspace',
  'roles_page.group.os': 'OS',
  'roles_page.made.system': 'System role',
  'roles_page.made.by': 'Made by {{person}}',
  'roles_page.made.here': 'Made in this $t(glossary.business_one)',
  'roles_page.new_chip': 'New',
  'roles_page.new_role': 'New role',
  'roles_page.new_role.line': 'Tell OS what the $t(glossary.person_other) in it do, or pick the permissions yourself',
  'roles_page.yours.title': 'Your role: {{role}}',
  'roles_page.yours.no_purpose': 'Nothing is written yet about what it is for.',
  'roles_page.yours.body': 'Only an admin or the primary admin can change roles.',
  'roles_page.menu.label': 'More for {{role}}',
  'roles_page.menu.open': 'Open',
  'roles_page.menu.copy': 'Make a copy',
  'roles_page.menu.remove': 'Remove this role',
  'roles_page.menu.system_note': 'Admin and Member are system roles. Change them as you like, but they stay.',
  'roles_page.copied': '{{role}} made.',
  'roles_page.remove.title': 'Remove {{role}}?',
  'roles_page.remove.body_one':
    '{{count, number}} $t(glossary.person_one) holds it. Pick the role they move to, and it goes. Their access to $t(glossary.record_other) does not change.',
  'roles_page.remove.body_other':
    '{{count, number}} $t(glossary.person_other) hold it. Pick the role they move to, and it goes. Their access to $t(glossary.record_other) does not change.',
  'roles_page.remove.body_none': 'Nobody holds it, so nobody moves. It goes at once.',
  'roles_page.remove.moves_to': 'Moves to',
  'roles_page.remove.action_person': 'Move {{person}}, then remove',
  'roles_page.remove.action_people_one': 'Move {{count, number}} $t(glossary.person_one), then remove',
  'roles_page.remove.action_people_other': 'Move {{count, number}} $t(glossary.person_other), then remove',
  'roles_page.remove.action_none': 'Remove {{role}}',
  'roles_page.remove.done_nobody': '{{role}} removed.',
  'roles_page.remove.done_person': '{{role}} removed. {{person}} holds {{to}} now.',
  'roles_page.remove.done_people_one': '{{role}} removed. {{count, number}} $t(glossary.person_one) holds {{to}} now.',
  'roles_page.remove.done_people_other': '{{role}} removed. {{count, number}} $t(glossary.person_other) hold {{to}} now.',
  'roles_page.remove.undone': '{{role}} is back.',
  'roles_page.remove.undo_failed': '{{role}} could not be put back. Make it again from New role.',
  'role_page.about': 'About this role',
  'role_page.made_by': 'Made by {{person}}.',
  'role_page.system': 'A system role: it comes with every $t(glossary.business_one) and stays.',
  'role_page.name': 'Name',
  'role_page.name_help': 'Renaming a role changes nobody’s access.',
  'role_page.purpose': 'What it is for',
  'role_page.purpose_help': 'Shown beside the role wherever it is picked.',
  'role_page.holders': 'Who holds it · {{count, number}}',
  'role_page.holders.none': 'Nobody holds it yet.',
  'role_page.holders.unseen_one': '{{count, number}} $t(glossary.person_one) your role does not show.',
  'role_page.holders.unseen_other': '{{count, number}} $t(glossary.person_other) your role does not show.',
  'role_page.holder_team_one': '{{count, number}} $t(glossary.person_one) reports to them',
  'role_page.holder_team_other': '{{count, number}} $t(glossary.person_other) report to them',
  'role_page.give': 'Give this role',
  'role_page.give.everyone': 'Everyone who can be given it already holds it.',
  'role_page.give.done': '{{person}} now holds {{role}}.',
  'role_page.does': 'What it lets $t(glossary.person_other) do',
  'role_page.does_line':
    'In the $t(glossary.record_other), $t(glossary.field_other) and $t(glossary.list_other) each $t(glossary.person_one) is given. Their access is set on each $t(glossary.person_one) on Team. For $t(glossary.entry_other), say whose: their own, their team’s (everyone who reports to them, however deep), or everyone’s.',
  'role_page.expand_all': 'Expand all',
  'role_page.collapse_all': 'Collapse all',
  'role_page.kept.title': 'Kept for the primary admin.',
  'role_page.kept.body':
    'No role can hold these: the plan and billing, deleting the $t(glossary.business_one), and handing it over.',
  'role_page.meta.sees': 'Sees: {{sees}}',
  'role_page.read_only': 'Only an admin or the primary admin can change roles.',
  'role_page.admin_primary_only': 'Only the primary admin changes Admin, since that changes every admin.',
  'role_page.not_found': 'This role is not here any more.',
  'role_page.all_roles': 'All roles',
  'role_page.impact.title_one': 'This change reaches {{count, number}} $t(glossary.person_one)',
  'role_page.impact.title_other': 'This change reaches {{count, number}} $t(glossary.person_other)',
  'role_page.impact.body': 'Everyone who holds {{role}}, in the $t(glossary.record_other) they can open.',
  'role_page.impact.raised': 'Can now: {{permissions}}',
  'role_page.impact.lowered': 'Can no longer: {{permissions}}',
  'role_page.impact.reach': '{{permission}} ({{reach}})',
  'role_page.impact.quiet': 'Goes quiet and is kept: {{records}}',
  'role_page.impact.note':
    'A role never opens a $t(glossary.record_one) on its own. What it allows shows only in the $t(glossary.record_other) each $t(glossary.person_one) can already open.',
  'role_page.impact.confirm_one': 'Change it for {{count, number}} $t(glossary.person_one)',
  'role_page.impact.confirm_other': 'Change it for {{count, number}} $t(glossary.person_other)',
  'role_page.impact.nobody_title': 'Nobody holds {{role}}',
  'role_page.impact.nobody_body': 'So the change reaches nobody yet. It reaches whoever is given the role later.',
  'role_page.impact.rename_title': 'Rename {{from}} to {{to}}?',
  'role_page.impact.rename_body': 'Renaming changes nobody’s access. Everyone who holds it sees the new name.',
  'role_page.impact.rename': 'Rename',
  'role_page.impact.words_title': 'Save what {{role}} is for?',
  'role_page.impact.words_body': 'Nobody’s access changes.',
  'role_page.impact.loading': 'Finding who it reaches',
  'role_new.title': 'New role',
  'role_new.meta': 'Pick what $t(glossary.person_other) in it may do',
  'role_new.about_line': 'Name it for the work, not the $t(glossary.person_one). You give it to $t(glossary.person_other) after it is made.',
  'role_new.name_help': 'Renaming a role later changes nobody’s access.',
  'role_new.with_os': 'Ask OS to set it up',
  'role_new.with_os_later': 'Asking OS to set up a role comes later. For now, pick the permissions yourself.',
  'role_new.with_os_phone': 'Tap Ask OS at the top and say what the role is for. Nothing is made until you tap Make the role. Or pick the permissions yourself below.',
  'role_new.make': 'Make the role',
  'role_new.name_needed': 'Give the role a name.',
  'role_new.name_taken': 'There is already a role called {{role}}. Names ignore capitals, so pick another.',
  'role_new.nothing': 'Tick at least one permission. A role has to let $t(glossary.person_other) do something.',
  'role_new.made': '{{role}} made. Give it to $t(glossary.person_other) from Team, or from its page.',

  // Team and a person on the phone (web-redesign-04 phase 2), from the
  // roles-and-access-phone boards' Words board, with "tap" for the web's "click".
  'team.tab.joined_one': 'Joined · {{count, number}}',
  'team.tab.joined_other': 'Joined · {{count, number}}',
  'team.tab.invited_one': 'Invited · {{count, number}}',
  'team.tab.invited_other': 'Invited · {{count, number}}',
  'team.joined': 'Joined',
  'team.invited': 'Invited',
  'team.search': 'Search $t(glossary.person_other)',
  'team.reports_to_name': 'Reports to {{person}}',
  'team.their_team_one': '{{count, number}} in their team',
  'team.their_team_other': '{{count, number}} in their team',
  'team.nobody_else.title': 'Nobody else yet',
  'team.nobody_else.body':
    'Invite the $t(glossary.person_other) who work with you. Each gets a role, and the $t(glossary.record_other) you open to them.',
  'team.removed_toast': '{{person}} removed. Their $t(glossary.entry_other) stay.',
  'team.member_note': 'Your role lets you see the team. Only an admin can change it.',
  'team.invites_none.title': 'No invitations waiting',
  'team.invites_none.body': 'An invitation you send waits here until they join.',
  'role_badge.primary': 'Primary admin',
  'role_list.permissions_on': '{{on, number}} of {{total, number}}',
  'person.reports_to.top': 'Nobody. The primary admin is the top of the team.',
  'person.their_team_one': 'Their team · {{count, number}}',
  'person.their_team_other': 'Their team · {{count, number}}',
  'person.fields_of': '{{on, number}} of {{all, number}} $t(glossary.field_other)',
  'person.role_sheet.title': 'Their role',
  'person.role_sheet.help':
    'A new role changes what they may do, not what they can open. Their $t(glossary.record_other), $t(glossary.field_other) and $t(glossary.list_other) stay as they are.',
  'person.remove.lose': 'They lose',
  'person.remove.lose_change': '{{record}} · changes {{on, number}} of {{all, number}} $t(glossary.field_other)',
  'person.remove.lose_see': '{{record}} · sees {{on, number}} of {{all, number}} $t(glossary.field_other)',
  'person.remove.lose_everything': 'Every $t(glossary.record_one), $t(glossary.field_one) and $t(glossary.list_one)',
  'person.remove.lose_role': 'The {{role}} role, and signing in to {{workspace}}',
  'person.remove.stays': 'What stays',
  'person.remove.stays_entries': 'Every $t(glossary.entry_one) they wrote, with their name on it',
  'person.remove.team_moves_one':
    'Their {{count, number}} $t(glossary.person_one) will report to {{person}}, their manager, until you move them.',
  'person.remove.team_moves_other':
    'Their {{count, number}} $t(glossary.person_other) will report to {{person}}, their manager, until you move them.',
  'person.remove.team_tops_one':
    'Their {{count, number}} $t(glossary.person_one) will report to nobody until you move them.',
  'person.remove.team_tops_other':
    'Their {{count, number}} $t(glossary.person_other) will report to nobody until you move them.',
  'person.remove.confirm': 'Remove {{person}}',
  'person.remove.softer_role': 'Make them a {{role}} instead',
  'reports_to.lede': 'Who {{person}} answers to. It opens nothing; it says who a team is.',
  'reports_to.own_team_only':
    'Only $t(glossary.person_other) in your team are here. Moving someone to another team needs an admin.',

  // Roles on the phone (web-redesign-04 phase 5), beside the web's roles_page.*,
  // role_page.* and role_new.*, which the phone reads too: what only the phone
  // says, and handing over the primary admin.
  'role_phone.saved': '{{role}} saved.',
  'role_phone.give.title': 'Give {{role}}',
  // The ceiling on roles (roles-parity phase 5): a permission past the
  // caller's own role is locked and says so, as the route refuses it
  // (ABOVE_YOUR_ROLE); and a role above it is read, never changed.
  'role_phone.above_own': 'Your own role does not allow it',
  'role_phone.above_own_reach': 'Your own role reaches {{reach}}, so no further',
  'role_phone.role_above_own': 'It allows more than your own role does, so only someone whose role allows that can change it.',
  'role_phone.move_above_own': 'Above your own role',
  'role_phone.remove_needs_access':
    'Its $t(glossary.person_other) move to another role, which needs Change a $t(glossary.person_one)\u2019s role and access. Ask an admin.',
  'handover.pick.title': 'Hand over {{workspace}}',
  'handover.pick.lede': 'Pick the $t(glossary.person_one) who becomes the primary admin. They accept it before anything changes.',
  'handover.pick.nobody': 'Nobody else has joined yet. Invite someone first.',
  'handover.title': 'Hand {{workspace}} to {{person}}?',
  'handover.lede': 'There is one primary admin at a time. Once they accept, the $t(glossary.business_one) is theirs.',
  'handover.gets': '{{person}} gets',
  'handover.gets.body': 'The plan and billing, deleting the $t(glossary.business_one), and handing it over again',
  'handover.keep': 'You keep',
  'handover.your_role': 'Your role, from then on',
  'handover.your_role.admin': 'Admin lets you do everything else, as now. Your $t(glossary.entry_other) keep your name.',
  'handover.your_role.other': 'You do what {{role}} allows. Your $t(glossary.entry_other) keep your name.',
  'handover.type_name': 'Type the $t(glossary.business_one) name to confirm',
  'handover.confirm': 'Hand it over',
  'handover.offered': '{{person}} is asked to accept. Until they do, you are the primary admin.',
  'handover.waiting': 'Waiting for {{person}} to accept the $t(glossary.business_one).',
  'handover.take_back': 'Take it back',
  'handover.taken_back': 'The hand-over is taken back.',
  'handover.to_you':
    '{{person}} is handing {{workspace}} to you. You would become the primary admin: the plan and billing, deleting the $t(glossary.business_one), and handing it over.',
  'handover.accept': 'Accept',
  'handover.not_now': 'Not now',
  'handover.accepted': 'You are the primary admin of {{workspace}} now.',
  'handover.asked_alert': '{{person}} asked to become the primary admin.',
  'handover.open': 'Open',
  'handover.give': 'Hand the $t(glossary.business_one) to {{person}}',
  'handover.give.help':
    'They become the owner: the plan and billing, deleting the $t(glossary.business_one), and handing it over. There is one primary admin at a time, so you pick a role for yourself first.',
  'handover.ask': 'Ask to be the primary admin',
  'handover.ask.help':
    'The primary admin owns the $t(glossary.business_one): the plan and billing, deleting it, and handing it over. Asking puts your name in front of the primary admin, who can hand it to you.',
  'handover.asked': 'Asked. The primary admin will see it on Team.',
  // Team on the web (web-redesign-03 phase 3): Joined and Invited, the list
  // and the chart of who reports to whom, a person's panel, removing people.
  'team_page.tab.joined': 'Joined',
  'team_page.tab.invited': 'Invited',
  'team_page.more': 'More',
  'team_page.export': 'Export CSV',
  'team_page.hint.title': 'Role and access.',
  'team_page.hint.body':
    'A role is what a $t(glossary.person_one) may do. Their access is which $t(glossary.record_other), $t(glossary.field_other) and $t(glossary.list_other) they may do it in, and is set on each $t(glossary.person_one).',
  'team_page.column.person': 'Person',
  'team_page.column.role': 'Role',
  'team_page.column.their_team': 'Their team',
  'team_page.column.can_open': 'Can open',
  'team_page.column.last_active': 'Last active',
  'team_page.column.joined': 'Joined',
  'team_page.opens.everything': 'Everything',
  'team_page.opens.nothing': 'Nothing yet',
  'team_page.opens.count_one': '{{count, number}} $t(glossary.record_one)',
  'team_page.opens.count_other': '{{count, number}} $t(glossary.record_other)',
  'team_page.opens.more_one': '{{names}} and {{count, number}} more',
  'team_page.opens.more_other': '{{names}} and {{count, number}} more',
  'team_page.filter.opens_anything': 'Anything',
  'team_page.filter.reports_to_set': 'Reports to {{person}}',
  'team_page.filter.reports_to_line_one': '{{count, number}} of {{total, number}} $t(glossary.person_other) reports to {{person}}',
  'team_page.filter.reports_to_line_other': '{{count, number}} of {{total, number}} $t(glossary.person_other) report to {{person}}',
  'team_page.filter.reports_to_nobody_line_one': '{{count, number}} of {{total, number}} $t(glossary.person_other) reports to nobody',
  'team_page.filter.reports_to_nobody_line_other': '{{count, number}} of {{total, number}} $t(glossary.person_other) report to nobody',
  'team_page.sort.last_active': 'Last active',
  'team_page.sort.name': 'Name',
  'team_page.sort.joined': 'Joined',
  'team_page.view.label': 'View',
  'team_page.view.list': 'List',
  'team_page.view.chart': 'Chart',
  'team_page.loading': 'Reading the team',
  'team_page.failed': 'The team could not be read',
  'team_page.offline': 'No connection',
  'team_page.offline_body': 'The team is read from the server. It comes back when the connection does.',
  'team_page.no_match': 'Nobody matches the search and the filters. Clear a filter to see more.',
  'team_page.chart.label': 'Who reports to whom',
  'team_page.chart.expand_all': 'Expand all',
  'team_page.chart.collapse_all': 'Collapse all',
  'team_page.chart.fold': 'Fold {{person}}’s team',
  'team_page.chart.unfold': 'Open {{person}}’s team',
  'team_page.chart.nobody_below': 'Nobody reports to {{person}}',
  'team_page.chart.drop': '{{person}} reports to {{manager}}',
  'team_page.chart.loop': '{{manager}} is in {{person}}’s team, so {{person}} cannot report to them',
  'team_page.chart.empty_title': 'Nobody reports to anyone yet',
  'team_page.chart.empty_body':
    'Set who each $t(glossary.person_one) reports to, in their panel or by dragging them here. It only says who a team is; what they may see stays with their role.',
  'team_page.chart.set_for': 'Set it for {{person}}',
  'team_page.reports_to.now': '{{person}} reports to {{manager}} now',
  'team_page.reports_to.nobody_now': '{{person}} reports to nobody now',
  'team_page.role.given': '{{person}} has the {{role}} role now',
  'team_page.bulk.change_role': 'Change their role',
  'team_page.bulk.change_role_to': 'Give them',
  'team_page.bulk.role_given_one': '{{count, number}} $t(glossary.person_one) has the {{role}} role now',
  'team_page.bulk.role_given_other': '{{count, number}} $t(glossary.person_other) have the {{role}} role now',
  'team_page.give.title_person': 'Give {{person}} access',
  'team_page.give.title_one': 'Give {{count, number}} $t(glossary.person_one) access',
  'team_page.give.title_other': 'Give {{count, number}} $t(glossary.person_other) access',
  'team_page.give.body':
    'Open one $t(glossary.record_one) or $t(glossary.list_one) to them. Its $t(glossary.field_other) and $t(glossary.list_other) follow it, and their role decides whose $t(glossary.entry_other) they see.',
  'team_page.give.record': 'What to open',
  'team_page.give.record_pick': 'Pick one',
  'team_page.give.no_records': 'There is no $t(glossary.record_one) to open yet.',
  'team_page.give.admins_left_out_one': '{{count, number}} of them is an admin and opens everything already, so is left out.',
  'team_page.give.admins_left_out_other': '{{count, number}} of them are admins and open everything already, so are left out.',
  'team_page.give.holders_left_out_one': '{{count, number}} of them holds it already and keeps their access as it is. Change it on their page.',
  'team_page.give.holders_left_out_other': '{{count, number}} of them hold it already and keep their access as it is. Change it on their page.',
  'team_page.give.holds_all': 'Everything there is to open is open to them already. Change their access on their page.',
  'team_page.give.done_person': '{{record}} is open to {{person}} now',
  'team_page.give.done_one': '{{record}} is open to {{count, number}} $t(glossary.person_one) now',
  'team_page.give.done_other': '{{record}} is open to {{count, number}} $t(glossary.person_other) now',
  'team_page.contact.title': 'Edit {{person}}’s contact',
  'team_page.contact.body': 'Their email is their account’s, and only they can change it.',
  'team_page.contact.name': 'Name in this $t(glossary.business_one)',
  'team_page.contact.phone': 'Phone',
  'team_page.contact.address': 'Address',
  'team_page.contact.saved': '{{person}}’s contact is saved',
  'team_page.remove.body': 'Every $t(glossary.entry_one) they wrote stays where it is, with their name on it. They lose the key, not the work.',
  'team_page.remove.manager_body_one':
    'Every $t(glossary.entry_one) they wrote stays, with their name on it. The {{count, number}} $t(glossary.person_one) who reports to them will report to {{person}}, their manager, until you move them.',
  'team_page.remove.manager_body_other':
    'Every $t(glossary.entry_one) they wrote stays, with their name on it. The {{count, number}} $t(glossary.person_other) who report to them will report to {{person}}, their manager, until you move them.',
  'team_page.remove.manager_body_nobody_one':
    'Every $t(glossary.entry_one) they wrote stays, with their name on it. The {{count, number}} $t(glossary.person_one) who reports to them will report to nobody until you move them.',
  'team_page.remove.manager_body_nobody_other':
    'Every $t(glossary.entry_one) they wrote stays, with their name on it. The {{count, number}} $t(glossary.person_other) who report to them will report to nobody until you move them.',
  'team_page.remove.names_more_one': '{{names}} and {{count, number}} more',
  'team_page.remove.names_more_other': '{{names}} and {{count, number}} more',
  'team_page.remove.softer': 'Make them a {{role}} instead',
  'team_page.remove.confirm_person': 'Remove {{person}}',
  'team_page.remove.done_person': '{{person}} is removed',
  'team_page.remove.entries_stay': 'Every $t(glossary.entry_one) they wrote stays, with their name on it.',
  'team_page.remove.team_up_one': '{{count, number}} $t(glossary.person_one) reports to {{person}} now.',
  'team_page.remove.team_up_other': '{{count, number}} $t(glossary.person_other) report to {{person}} now.',
  'team_page.remove.team_up_nobody_one': '{{count, number}} $t(glossary.person_one) reports to nobody now.',
  'team_page.remove.team_up_nobody_other': '{{count, number}} $t(glossary.person_other) report to nobody now.',
  'team_page.invited.column.invitation': 'Invitation',
  'team_page.invited.column.sent_to': 'Sent to',
  'team_page.invited.column.sent': 'Sent',
  'team_page.invited.column.expires': 'Expires',
  'team_page.invited.by_hand': 'A code to hand over',
  'team_page.invited.expired': 'Expired',
  'team_page.invited.withdraw': 'Withdraw',
  'team_page.invited.withdrawn': 'The invitation is withdrawn',
  'team_page.invited.search': 'Search invitations',
  'team_page.invited.empty_title': 'No invitations waiting',
  'team_page.invited.empty_body': 'An invitation works for seven days and once only. When it is used, the $t(glossary.person_one) moves to Joined.',
  'team_page.invited.foot_one':
    '{{count, number}} open invitation. An invitation works for seven days and once only. When it is used, the $t(glossary.person_one) moves to Joined.',
  'team_page.invited.foot_other':
    '{{count, number}} open invitations. An invitation works for seven days and once only. When it is used, the $t(glossary.person_one) moves to Joined.',

  // A person's page (web-redesign-03 phase 4): Information, Access, Team and
  // Activity under one head. Said of the person as "them", never a pronoun
  // the app would have to guess.
  'person_page.tab.information': 'Information',
  'person_page.tab.access': 'Access',
  'person_page.tab.team': 'Team',
  'person_page.tab.activity': 'Activity',
  'person_page.meta.reports_to': 'Reports to {{person}}',
  'person_page.more': 'More for {{person}}',
  'person_page.not_found': 'This $t(glossary.person_one) is not in the $t(glossary.business_one)',
  'person_page.not_found_body': 'They may have been removed, or the link is an old one.',
  'person_page.back_to_team': 'See the whole team',
  'person_page.contact.desc': 'How to reach {{person}}.',
  'person_page.reports_to.desc': 'Who they answer to. Change it here or drag them on the chart.',
  'person_page.reports_to.manager': 'Manager',
  'person_page.reports_to.hidden': 'Someone your role does not show',
  'person_page.role.desc': 'What they may do, in the $t(glossary.record_other) they can open.',
  'person_page.role.count': '{{on, number}} of {{count, number}}',
  'person_page.role.help': 'A new role changes what they may do, not what they can open. Their $t(glossary.record_other), $t(glossary.field_other) and $t(glossary.list_other) stay as they are.',
  'person_page.access.records': 'Records',
  'person_page.access.records_desc': 'The $t(glossary.record_other) they can open, and whose $t(glossary.entry_other) in each.',
  'person_page.access.lists': 'Lists',
  'person_page.access.lists_desc': 'The $t(glossary.list_other) they can pick from, or change.',
  'person_page.access.give_record': 'Give a $t(glossary.record_one)',
  'person_page.access.give_list': 'Give a $t(glossary.list_one)',
  'person_page.access.whose': 'Whose $t(glossary.entry_other)',
  'person_page.access.given_by': 'Given by',
  'person_page.access.no_records': 'No $t(glossary.record_one) is open to them yet.',
  'person_page.access.no_lists': 'No $t(glossary.list_one) is open to them yet.',
  'person_page.access.saved': 'What {{person}} can open is saved',
  'person_page.access.hidden': 'What somebody can open is shown to a $t(glossary.person_one) whose role changes access. Ask an admin.',
  'person_page.team.title': 'Their team',
  'person_page.team.desc': 'Everyone who reports to them, however deep.',
  'person_page.team.search': 'Search their team',
  'person_page.team.empty_title': 'Nobody reports to {{person}}',
  'person_page.team.empty_body': 'Set Reports to on somebody’s page, or drag them under {{person}} on the chart.',
  'person_page.team.no_match': 'Nobody in their team matches.',
  'person_page.activity.title': 'Activity',
  'person_page.activity.filter.all': 'All',
  'person_page.activity.filter.entries': 'Entries',
  'person_page.activity.filter.team': 'Team and role',
  'person_page.activity.filter.access': 'Access',
  'person_page.activity.today': 'Today',
  'person_page.activity.yesterday': 'Yesterday',
  'person_page.activity.empty': 'Nothing here yet',
  'person_page.activity.empty_body': 'What they do, and what is done to them, shows here.',
  'person_page.activity.older': 'Show older',
  'person_page.activity.entries_added_one': 'Added {{count, number}} $t(glossary.entry_one) to {{record}}',
  'person_page.activity.entries_added_other': 'Added {{count, number}} $t(glossary.entry_other) to {{record}}',
  'person_page.activity.entries_changed_one': 'Changed {{count, number}} $t(glossary.entry_one) in {{record}}',
  'person_page.activity.entries_changed_other': 'Changed {{count, number}} $t(glossary.entry_other) in {{record}}',
  'person_page.activity.entries_removed_one': 'Removed {{count, number}} $t(glossary.entry_one) from {{record}}',
  'person_page.activity.entries_removed_other': 'Removed {{count, number}} $t(glossary.entry_other) from {{record}}',
  'person_page.activity.joined': 'Joined the $t(glossary.business_one)',
  'person_page.activity.joined_by': 'Joined the $t(glossary.business_one), invited by {{actor}}',
  'person_page.activity.role': 'Given the {{role}} role',
  'person_page.activity.role_by': '{{actor}} gave them the {{role}} role',
  'person_page.activity.role_moved': 'Moved to the {{role}} role when their old role was removed',
  'person_page.activity.level.see': 'see',
  'person_page.activity.level.change': 'change',
  'person_page.activity.gave': 'Given {{record}} to {{level}}',
  'person_page.activity.gave_by': '{{actor}} gave them {{record}} to {{level}}',
  'person_page.activity.took': '{{record}} was taken away',
  'person_page.activity.took_by': '{{actor}} took {{record}} away',
  'person_page.activity.access': 'What they can open in {{record}} changed',
  'person_page.activity.access_by': '{{actor}} changed what they can open in {{record}}',
  'person_page.activity.reports_to': 'Now reports to {{person}}',
  'person_page.activity.reports_to_by': '{{actor}} set them to report to {{person}}',
  'person_page.activity.reports_to_nobody': 'Now reports to nobody',
  'person_page.activity.reports_to_nobody_by': '{{actor}} set them to report to nobody',
  'person_page.activity.details': 'Their contact changed',
  'person_page.activity.details_by': '{{actor}} changed their contact',
  'person_page.activity.invite': 'Invited',
  'person_page.activity.invite_by': '{{actor}} invited them',
  'person_page.activity.removed': 'Removed from the $t(glossary.business_one)',
  'person_page.activity.removed_by': '{{actor}} removed them from the $t(glossary.business_one)',
  'person_page.activity.primary': 'Became the primary admin',
  'person_page.activity.handed_over': 'Handed the $t(glossary.business_one) over to {{person}}',
  'person_page.activity.gone_record': 'a removed $t(glossary.record_one)',
  'person_page.activity.role_gone': 'Given a role that was removed later',
  'person_page.activity.role_gone_by': '{{actor}} gave them a role that was removed later',
  'person_page.activity.gone_person': 'somebody who has left',
  'access.form_needs': 'The form needs it',
  'access.form_needs.refused': '{{field}} is needed to save an $t(glossary.entry_one), so it cannot be hidden from somebody who adds them. Make it See, or take away Add $t(glossary.entry_other) from their role.',
  'access.form_needs.refused_see': '{{field}} is needed to save an $t(glossary.entry_one), so somebody who adds them must be able to change it. Leave it at Change, or take away Add $t(glossary.entry_other) from their role.',

  // Inviting a person (web-redesign-03 phase 5; web-app.md section 12): a
  // right sheet in three steps, then the sheet that says where it went.
  'invite_sheet.step.details': 'Their details',
  'invite_sheet.step.role': 'Their role',
  'invite_sheet.step.open': 'What they can open',
  'invite_sheet.steps': 'Steps',
  'invite_sheet.step_of': 'Step {{step, number}} of {{count, number}}',
  'invite_sheet.lede.details': 'Who they are. The invitation goes to their phone or email.',
  'invite_sheet.lede.role': 'What {{person}} may do in the $t(glossary.business_one).',
  'invite_sheet.lede.open': 'Which $t(glossary.record_other) {{person}} may use, and how the invitation reaches them.',
  'invite_sheet.name': 'Name',
  'invite_sheet.name.placeholder': 'How the team will see them',
  'invite_sheet.name.needed': 'Write their name, up to 120 letters.',
  'invite_sheet.phone': 'Phone number',
  'invite_sheet.phone.bad': 'That does not look like a phone number. Check its digits.',
  'invite_sheet.email': 'Email',
  'invite_sheet.email.bad': 'That email does not look right.',
  'invite_sheet.contact.help': 'A phone number or an email is needed: the invitation goes there, and it is how RecordOS knows it is them when they join.',
  'invite_sheet.contact.needed': 'Add a phone number or an email. The invitation needs somewhere to go.',
  'invite_sheet.taken': '{{person}} is already in {{workspace}}, with the {{role}} role. Change their role on their page instead.',
  'invite_sheet.reports_to': 'Reports to',
  'invite_sheet.optional': '(optional)',
  'invite_sheet.reports_to.help': 'Who their team is. It opens nothing; their role and access do.',
  'invite_sheet.role.primary_note': 'Primary admin is not a role. It moves only when the $t(glossary.business_one) is handed over.',
  'invite_sheet.role.above': 'It lets them do more than your own role does.',
  'invite_sheet.open.help': 'Every $t(glossary.field_one), and the $t(glossary.list_other) a $t(glossary.record_one) uses, follow the $t(glossary.record_one). Set them one by one on their page once they join.',
  'invite_sheet.open.nothing_title': 'A role on its own opens nothing',
  'invite_sheet.open.nothing_body': 'Until a $t(glossary.record_one) is open to them, they sign in and see an empty list. That is deliberate: nothing is shared until you share it.',
  'invite_sheet.open.not_yours': 'Opening $t(glossary.record_other) to somebody needs a role that changes access. Once they join, ask an admin to open what they need.',
  'invite_sheet.open.no_records': 'There are no $t(glossary.record_other) to open yet.',
  'invite_sheet.reach.legend': 'How it reaches them',
  'invite_sheet.reach.link': 'A link',
  'invite_sheet.reach.link.body': '{{to}} gets a link. They click it and they are in.',
  'invite_sheet.reach.code': 'A link and a code',
  'invite_sheet.reach.code.body': 'The same link, and a code they can type on their phone, or you can hand over in person.',
  'invite_sheet.next': 'Next',
  'invite_sheet.send': 'Send the invitation',
  'invite_sheet.sent.title': 'Invitation sent',
  'invite_sheet.sent.title_ready': 'Invitation ready',
  'invite_sheet.sent.lede': '{{person}} gets it at {{to}}. When they open it, they are in.',
  'invite_sheet.sent.lede_phone': 'Messages to a phone are not sent yet, so hand {{person}} the code or the link yourself. When they open it, they are in.',
  'invite_sheet.sent.lede_phone_link': 'Messages to a phone are not sent yet, so hand {{person}} the link yourself. When they open it, they are in.',
  'invite_sheet.sent.lede_failed': 'The email to {{to}} could not be sent. Send it again, or hand {{person}} the code or the link yourself.',
  'invite_sheet.sent.to': 'Sent to {{to}}',
  'invite_sheet.sent.for': 'For {{to}}',
  'invite_sheet.sent.works': 'Works once, until {{day}}',
  'invite_sheet.sent.again': 'Send it again',
  'invite_sheet.sent.again_done': 'Sent again. The old code no longer works.',
  'invite_sheet.sent.again_ready': 'A new code is ready. The old one no longer works.',
  'invite_sheet.sent.hand_title': 'Or hand it over yourself',
  'invite_sheet.sent.hand_body': 'Standing with them? They can enter this code, or open the link. It only works for {{to}}.',
  'invite_sheet.sent.hand_body_link': 'Standing with them? They can open the link. It only works for {{to}}.',
  'invite_sheet.sent.code_once': 'The code is shown once, when it is made. Send it again for a new one.',
  'invite_sheet.sent.no_link': 'Its link is kept for a $t(glossary.person_one) whose role gives at least as much as this one.',
  'invite_sheet.sent.role': 'Role',
  'invite_sheet.sent.can_open': 'Can open',
  'invite_sheet.sent.gone': 'That invitation was used or withdrawn.',

  // A record's Access tab (web-redesign-03 phase 6): every person with their
  // role and what they may do in this one record, one person in the right
  // panel in three parts, and Give someone access.
  'record_access.people_line_one': '{{count, number}} $t(glossary.person_one) · click a $t(glossary.person_one) to set what they may do here',
  'record_access.people_line_other': '{{count, number}} $t(glossary.person_other) · click a $t(glossary.person_one) to set what they may do here',
  'record_access.column.entries': 'Entries',
  'record_access.column.lists': 'Lists it picks from',
  'record_access.open_person': 'Open {{person}}’s access',
  'record_access.nobody':
    'Nobody but the primary admin can open this $t(glossary.record_one) yet. Give someone access to start.',
  'record_access.entries.primary': 'Holds everything as the primary admin. Nothing is set here.',
  'record_access.entries.admin': 'Opens every $t(glossary.record_one) as an admin. Nothing is set here.',
  'record_access.entries.not_given': 'Not given yet · starts from the {{role}} role',
  'record_access.entries.line': '{{sees}} · {{changes}}',
  'record_access.entries.sees.everyone': 'Sees every $t(glossary.entry_one)',
  'record_access.entries.sees.team': 'Sees their team’s $t(glossary.entry_other)',
  'record_access.entries.sees.own': 'Sees their own $t(glossary.entry_other)',
  'record_access.entries.sees.off': 'Sees no $t(glossary.entry_other)',
  'record_access.entries.changes.everyone': 'changes any',
  'record_access.entries.changes.team': 'changes their team’s',
  'record_access.entries.changes.own': 'changes their own',
  'record_access.entries.adds': 'adds',
  'record_access.entries.changes_nothing': 'changes nothing',
  'record_access.and': '{{names}} and {{last}}',
  'record_access.fields.all_one': 'All {{count, number}}',
  'record_access.fields.all_other': 'All {{count, number}}',
  'record_access.fields.some': '{{shown, number}} of {{total, number}}',
  'record_access.fields.mixed': 'Changes {{change, number}} · sees {{see, number}}',
  'record_access.fields.hidden_named': '{{field}} hidden',
  'record_access.fields.hidden_one': '{{count, number}} hidden',
  'record_access.fields.hidden_other': '{{count, number}} hidden',
  'record_access.lists.changes': 'Changes {{lists}}',
  'record_access.lists.sees': 'Sees them',
  'record_access.lists.none': 'Sees none of them',
  'record_access.panel.title': '{{person}} in {{record}}',
  'record_access.panel.all_records': 'All their $t(glossary.record_other)',
  'record_access.part.record': 'The $t(glossary.record_one)',
  'record_access.part.record_line': '{{record}}, its $t(glossary.entry_other)',
  'record_access.part.fields': 'The $t(glossary.field_other) inside it',
  'record_access.part.fields_line': 'See, change, or none',
  'record_access.part.lists': 'The $t(glossary.list_other) it picks from',
  'record_access.part.lists_line': 'What they see or change in them',
  'record_access.ability.see': 'Sees',
  'record_access.ability.add': 'Adds $t(glossary.entry_other)',
  'record_access.ability.change': 'Changes',
  'record_access.ability.remove': 'Removes $t(glossary.entry_other)',
  'record_access.ability.yes': 'Yes',
  'record_access.ability.no': 'No',
  'record_access.reach.everyone': 'Every $t(glossary.entry_one)',
  'record_access.reach.team': 'Their team’s',
  'record_access.reach.own': 'Their own',
  'record_access.reach.off': 'None',
  'record_access.lock.role.see': 'Their role does not see $t(glossary.entry_other)',
  'record_access.lock.role.add': 'Their role does not add $t(glossary.entry_other)',
  'record_access.lock.role.change': 'Their role does not change $t(glossary.entry_other)',
  'record_access.lock.role.remove': 'Their role does not remove $t(glossary.entry_other)',
  'record_access.lock.needs_change': 'Needs this $t(glossary.record_one) set to change',
  'record_access.part.from_role': 'Whose $t(glossary.entry_other) they reach comes from the {{role}} role, and is changed on its page.',
  'record_access.foot.from_role': 'From the {{role}} role, and set here',
  'record_access.foot.not_given': 'Starts from the {{role}} role. Not given yet.',
  'record_access.foot.read_only': 'Only a $t(glossary.person_one) who may change access sets it',
  'record_access.foot.yours': 'Your own access is set by somebody else',
  'record_access.save': 'Save {{person}}’s access',
  'record_access.saved': '{{person}}’s access saved',
  'record_access.saved_over': 'Somebody else changed {{person}}’s access while it was open. Yours is saved over it; Undo puts theirs back.',
  'record_access.give.action': 'Give {{person}} access',
  'record_access.given': '{{person}} can use {{record}}',
  'record_access.give.open': 'Give someone access',
  'record_access.give.find': 'Find someone in {{workspace}}',
  'record_access.give.group': 'Not in {{record}} yet',
  'record_access.give.invite': 'Invite someone new',
  'record_access.give.invite_line': 'By their phone or email, with an invitation',
  'record_access.give.admins': 'Admins hold every $t(glossary.record_one) already, so they are not here.',
  'record_access.give.everyone': 'Everyone in the $t(glossary.business_one) can use it already.',
  'record_access.give.no_match': 'Nobody by that name',
  'record_access.view_as': 'View as {{person}}',

  // The primary-admin model, migration 0010. Two powers stay with the primary
  // admins and every sentence below names both, so nobody grants or asks for
  // the level without being told what it is. Removing or demoting admins is
  // enforced today (PRIMARY_ONLY in members.ts). Shutting the business down is
  // RESERVED for the level and not yet a button anywhere — so the copy says
  // "kept for" and never "can", until a route exists to make "can" true.
  'primary.request': 'Request primary admin',
  'primary.request.help':
    'A primary admin can remove or demote admins, and shutting the $t(glossary.business_one) down is kept for them alone. Asking puts your name in front of every primary admin.',
  'primary.requested': 'Asked. Every primary admin will see it.',
  'primary.pending': '{{person}} asked to become a primary admin.',
  'primary.grant': 'Make primary admin',
  'primary.grant.help':
    'They gain the two primary powers: removing or demoting admins, and — when that day comes — shutting the $t(glossary.business_one) down. A $t(glossary.business_one) can have more than one primary admin.',
  'primary.dismiss': 'Put the ask away',

  // app.capability, all sixteen: fifteen seeded by migration 0006 and one by
  // 0007. The server reads
  // label_key and help_key off that table, so a key here must match a row
  // there: db:verify fails when the two lists disagree.
  //
  // A help line says what turning it on actually permits, in one sentence. It
  // never restates the label, and it never names a table.
  //
  // An in_sentence line is the SAME permission said mid-sentence, for the rule
  // sentence in apps/mobile/src/components/permissions/rule-sentence.ts: "they
  // may see totals and add entries". The label cannot do that job — it is chip
  // copy and starts with a capital, so the closed-up sentence read "they may
  // See totals" — and lower-casing the label in code would be the app editing
  // the catalogue's English. Record scope only: no tenant capability is ever
  // said in a sentence. There is no server column for it, unlike label_key and
  // help_key; en.test.ts holds the list complete against RECORD_CAPABILITIES.
  //
  // Eight of record scope, granted per record through app.record_grant.
  'cap.view_summaries.label': 'See totals',
  'cap.view_summaries.help': 'They see the totals, but not the $t(glossary.entry_other) inside.',
  'cap.view_summaries.in_sentence': 'see totals',
  'cap.view_all_entries.label': 'See every $t(glossary.entry_one)',
  'cap.view_all_entries.help': 'They open every $t(glossary.entry_one), not only the ones they wrote.',
  'cap.view_all_entries.in_sentence': 'see every $t(glossary.entry_one)',
  'cap.export.label': 'Download',
  'cap.export.help': 'They take the $t(glossary.entry_other) out of the app as a file.',
  // Not bare "download": the chip sits under a heading that says what is being
  // downloaded and the sentence does not.
  'cap.export.in_sentence': 'download the $t(glossary.record_one)',
  'cap.add_entry.label': 'Add $t(glossary.entry_other)',
  'cap.add_entry.help': 'They write new $t(glossary.entry_other).',
  'cap.add_entry.in_sentence': 'add $t(glossary.entry_other)',
  'cap.edit_own_entry.label': 'Fix their own $t(glossary.entry_other)',
  'cap.edit_own_entry.help': 'They change an $t(glossary.entry_one) they wrote themselves.',
  'cap.edit_own_entry.in_sentence': 'fix their own $t(glossary.entry_other)',
  'cap.edit_any_entry.label': 'Fix anyone\u2019s $t(glossary.entry_other)',
  'cap.edit_any_entry.help': 'They change an $t(glossary.entry_one) written by anybody.',
  'cap.edit_any_entry.in_sentence': 'fix anyone\u2019s $t(glossary.entry_other)',
  'cap.delete_entry.label': 'Delete $t(glossary.entry_other)',
  'cap.delete_entry.help': 'They remove an $t(glossary.entry_one) for good.',
  'cap.delete_entry.in_sentence': 'delete $t(glossary.entry_other)',
  'cap.edit_structure.label': 'Change the $t(glossary.field_other)',
  'cap.edit_structure.help': 'They add, rename, or remove $t(glossary.field_other).',
  'cap.edit_structure.in_sentence': 'change the $t(glossary.field_other)',
  // Seven of tenant scope, held by the job and covering the whole business.
  'cap.manage_members.label': 'Add and remove $t(glossary.person_other)',
  'cap.manage_members.help': 'They invite $t(glossary.person_other) to the $t(glossary.business_one), and take them out.',
  'cap.define_roles.label': 'Make $t(glossary.job_other)',
  'cap.define_roles.help': 'They create a $t(glossary.job_one), and decide what it may do.',
  'cap.manage_units.label': 'Manage branches',
  'cap.manage_units.help': 'They add and change a branch, a till, or a vehicle.',
  'cap.manage_policies.label': 'Set $t(glossary.business_one) rules',
  'cap.manage_policies.help': 'They change a rule that covers the whole $t(glossary.business_one).',
  'cap.close_period.label': 'Close the month',
  'cap.close_period.help': 'They lock a finished month so nobody can change it.',
  'cap.manage_billing.label': 'Handle the plan',
  'cap.manage_billing.help': 'They see the plan, change it, and pay for it.',
  'cap.view_business_insights.label': 'See the $t(glossary.business_one) numbers',
  'cap.view_business_insights.help': 'They open the $t(glossary.dashboard), with the totals across everything they may total.',
  // Shown under a permission the job's own level may not hold. min_rank on
  // app.capability decides it, and app.apply_role() refuses at assignment.
  'cap.needs_level': 'Needs the {{level}} $t(glossary.level_one).',

  // ------------------------------------------------ inside a record, Stage 2
  'record.empty.title': 'Nothing here yet',
  /**
   * The same empty record, read by somebody who may not write in it. "Add the
   * first one" would be an instruction they cannot follow, and the button that
   * goes with it is not drawn for them either.
   */
  'record.empty.read_only': 'Nothing has been added here yet.',
  /**
   * Under the list when `row_scope` is `own_entries`. Without it a person sees
   * four of their own rows in a record the shop put four hundred in, and has no
   * way to tell that from one that lost its data.
   */
  'record.own_rows_only': 'You see the $t(glossary.entry_other) you added.',
  /**
   * The same record, empty, for somebody on `row_scope: own_entries`.
   *
   * `record.empty.read_only` would be a lie here: things HAVE been added, and
   * this person may not see them. This says only what is true of THEM, which
   * is also the most it may say — naming other people's rows would give away
   * the very thing the row scope withholds.
   */
  'record.empty.own_rows_only': 'You have not added anything here yet.',
  /*
   * The phone's two ways into an empty record or list (docs/plans/empty-pages.md,
   * E1 and E6). The phone runs no import itself, so its Import card says where
   * one is brought in, or, without OS, why it cannot run.
   */
  'record.start.record_line': 'Each $t(glossary.entry_one) you add shows up here.',
  'record.start.list_line': 'The names a $t(glossary.record_one) picks from are kept here.',
  'record.start.import': 'Import from a file',
  'record.start.import_elsewhere': 'Excel, CSV, or photos of a register, from RecordOS on a computer.',
  'record.start.import_no_os': 'Importing needs OS, which is not on here.',
  'record.start.add': 'Add manually',
  'record.start.add_first': 'Add the first one',
  /**
   * The device's copy of the server's refusal, shown BEFORE the typing rather
   * than after it. `link.derived_no_edit` is the precedent: a person with no
   * network must be told the same thing, in the same words, rather than typing
   * a change that is rejected on the next sync.
   */
  'entry.no_edit': 'You cannot change this $t(glossary.entry_one). Ask an admin for the right.',
  'entry.no_edit_others': 'You can change only the $t(glossary.entry_other) you added.',
  'entry.no_add': 'You cannot add to this $t(glossary.record_one). Ask an admin for the right.',
  /**
   * The Customize preview draws the real form over a record that is not saved
   * yet, so there is nothing to attach a file to. Said plainly, because the
   * button is there and tapping it has to do something.
   */
  'entry.attach_needs_record': 'Save this $t(glossary.record_one) first, then you can attach a file.',
  /** The one line a failed upload shows under the field. */
  'entry.attach_failed': 'That file did not upload. Check your connection and try again.',
  /**
   * A file an entry names that could not be looked up: deleted, restricted, or
   * simply a lookup that failed. The row still stands, and saving keeps it.
   *
   * It must NOT read like an error. The file is probably fine; what failed is
   * one request. A person who sees "Attached file" and saves loses nothing.
   */
  'entry.attach_unnamed': 'Attached file',
  /**
   * How many files an entry carries, on its card. A bare "2" beside a quantity
   * reads as a quantity, so the card says the noun as well. Two keys because
   * English counts one thing differently from two.
   */
  'entry.attachments_one': '{{count, number}} attachment',
  'entry.attachments_other': '{{count, number}} attachments',
  /**
   * A write the server refused, shown after it is gone. A 4xx is final — no
   * retry mends it — so the honest thing is to say the row did not save and
   * why, not to leave a person to notice it missing days later.
   */
  'sync.refused.title_one': '{{count, number}} $t(glossary.entry_one) did not save',
  'sync.refused.title_other': '{{count, number}} $t(glossary.entry_other) did not save',
  'sync.refused.dismiss': 'Got it',
  'sync.refused.open': 'Put it right',
  'sync.download.title': 'Getting your $t(glossary.business_one) ready on this phone',
  'sync.download.percent': '{{percent, number}}%',
  'sync.download.size': '{{done, number}} MB of about {{total, number}} MB downloaded',
  'sync.download.saved_one': '{{saved, number}} of {{count, number}} $t(glossary.entry_one) saved on this phone',
  'sync.download.saved_other': '{{saved, number}} of {{count, number}} $t(glossary.entry_other) saved on this phone',
  'sync.download.body': 'Saving takes longer than downloading. Search is ready after this. You can keep working, and new entries save as usual.',
  'search.waiting_download': 'Your data is still arriving. Search finds everything once this phone has indexed it.',
  'record.loading': 'Opening',
  'entry.new': 'New',
  'entry.save': 'Save',
  /** Confirmation after a new row lands locally. A label, not a sentence. */
  'entry.saved': 'Saved',
  /** Caption under the tick on Save & add new. The form is already reset underneath. */
  'entry.saved_next': 'Ready for the next one',
  /**
   * `app.record_grant.write_mode`. On `submit_draft` and `request` the button
   * does not save: it starts an approval, and a button that says Save would
   * promise something that has not happened.
   */
  'entry.submit': 'Send for approval',
  'entry.submitting': 'Sending\u2026',
  'entry.save_add_another': 'Save & add new',
  /**
   * The row at the end of the fields, which goes to Customize.
   *
   * It promises less than the screen it opens, and that is deliberate. The
   * person reading it is filling a form in and has just found it does not ask
   * for something. "Add more fields" is that want in their words. Reordering
   * and renaming are on the other side too, and nobody arrives wanting those.
   */
  'entry.add_fields': 'Add more $t(glossary.field_other)',
  'entry.saving': 'Saving',
  'entry.pending': 'Syncing',
  // On a total of other entries (a booking's room bill) that counts one still
  // waiting to reach the server.
  'entry.rollup_unsaved': 'Not yet saved',
  // "Total of other entries" in Add a field: a booking's Room Bill adds up the
  // Guest Service Orders that pick it. `field` is a field of choices on that
  // record, `choice` one of its choices.
  'rollup.settings.over': 'Add up the $t(glossary.entry_other) of',
  'rollup.settings.via': 'That pick this $t(glossary.entry_one) in',
  'rollup.settings.of': 'Add up',
  'rollup.settings.count': 'How many there are',
  'rollup.settings.pick': 'Take',
  'rollup.settings.pick.sum': 'The total',
  'rollup.settings.pick.min': 'The smallest, or the earliest date',
  'rollup.settings.pick.max': 'The largest, or the latest date',
  'rollup.settings.of_read': 'Of',
  'rollup.settings.min_of': 'Smallest {{field}}',
  'rollup.settings.max_of': 'Largest {{field}}',
  'rollup.settings.when': 'Only when',
  'rollup.settings.when_every': 'Every one',
  'rollup.settings.when_pair': '{{field}} is {{choice}}',
  'rollup.settings.when_hint': 'Only $t(glossary.entry_other) saved with this choice are added.',
  'rollup.settings.choose': 'Choose',
  // A saved total, in words: "Total Amount of Guest Service Orders, by Booking".
  'rollup.settings.summary': '{{of}} of {{over}}, by {{via}}',
  'rollup.settings.summary_when': '{{of}} of {{over}}, by {{via}}, only when {{field}} is {{choice}}',
  'rollup.settings.none':
    'No other $t(glossary.record_one) picks an $t(glossary.entry_one) of this one yet. Add a pick to one first, then add its total here.',
  'entry.sync_failed': 'Will sync when online',
  /** The row's one overflow marker: "+3 more". The count is drawn beside it. */
  'entry.more': 'more',
  /** The row's footer: who added the entry. `{{person}}` is the writer's first name. */
  'entry.by': 'Entry by {{person}}',
  'entry.by_you': 'Entry by you',
  /** A writer the device cannot name: a removed member, or names not fetched yet. */
  'entry.by_someone': 'Entry by someone',
  /** Beside the author: when the entry was typed. `{{time}}` is a clock, "12:46 pm". */
  'entry.at': 'at {{time}}',
  'entry.show_more': 'Show more',
  'entry.show_less': 'Show less',
  'entry.primary': 'Primary',
  'entry.secondary': 'More details',
  /** The row opened, and the entry behind it is no longer in the record. */
  'entry.gone': 'This $t(glossary.entry_one) is not here any more.',
  'entry.day_today': 'Today',
  'entry.day_yesterday': 'Yesterday',
  /**
   * The caption under a field a default just filled, in place of the field's
   * own hint. `{{field}}` is the reference field the recipe reads through, so the line
   * reads "From Customer" and names where the value came from.
   *
   * It does NOT say "usual". A recipe may be anything, and a form that called
   * every filled value a usual would be wrong the first time somebody wrote
   * one that is not. It goes the moment the person types into the field, which
   * is the moment the value stops being the list's and becomes theirs.
   */
  'entry.from_default': 'From {{field}}',
  'entry.from_open_row': 'Still open for this {{field}}',
  /** The same caption for a recipe with no hop in it: a fixed value, or a sum. */
  'entry.starts_with': 'Filled in for you',

  // ------------------------------------------ the entry sheet, docs/plans/done/entry-sheet.md
  /*
   * A record whose entries are written from a sheet — every row of a list on
   * one day, one mark each, one save.
   *
   * Every word here is GENERIC and takes the record's own nouns through its variables: the
   * record says "mark" or "delivery", the list says "Staff members", and the
   * options say "Present" or "Absent". Nothing below names attendance, because
   * the same screen walks a delivery round and a machine check per shift.
   */
  /** The floating button on a sheet record, before anything is marked today. */
  'sheet.mark_today': 'Mark today',
  /** The same button once today already carries marks. */
  'sheet.edit_today': 'Edit today',
  /** The line over the rows: how many of the list carry a mark. "3 of 8 marked". */
  'sheet.marked_of': '{{marked, number}} of {{total, number}} marked',
  /** Under a name with no cell chosen yet. */
  'sheet.not_marked': 'Not marked',
  /**
   * The one-tap shortcut beside the count line, gone the moment every row is
   * marked. `{{option}}` is the option the record named as its quick one, lower-cased:
   * "All present".
   */
  'sheet.all_option': 'All {{option}}',
  /** The save bar when the sheet writes new marks. "Save 8 marks". */
  'sheet.save_count': 'Save {{count, number}} {{noun}}',
  /** The save bar when every mark already exists and only some changed. */
  'sheet.save_changes': 'Save changes',
  /**
   * Above Save, while anybody is left without a cell. Not marked means no
   * entry, and silence must not read as Present or as Absent.
   * "5 not marked. No mark is saved for them."
   */
  'sheet.left_out': '{{count, number}} not marked. No {{entry}} is saved for them.',
  /** The day row on the record: how many marks that day holds. "8 marked". */
  'sheet.day_count': '{{count, number}} marked',
  /** One count on the day row. "6 present". The option's word, lower-cased. */
  'sheet.count_option': '{{count, number}} {{option}}',
  /** The sheet over a list with nothing in it yet. */
  'sheet.empty_title': 'No one to mark yet',
  /** `{{entries}}` is the list the sheet walks, lower-cased: "Add your staff members…". */
  'sheet.empty_body': 'Add your {{entries}} and they will be listed here.',
  /** The button under it, into the list's own form. `{{record}}` is that list's name. */
  'sheet.empty_add': 'Add {{record}}',

  // ------------------------------ adding a directory row from a picker, T-5.3.2
  /**
   * The header of the directory's own form when a picker sent somebody to it.
   * The record's name follows: "Add to" + "Payees". A directory carries the
   * default entry_noun_key, so the ordinary "New entry" would say nothing about
   * why the person is on this screen.
   */
  'reference.add_to': 'Add to',
  /**
   * The line under the field once a row has been created for it. The record's
   * name follows: "Added to" + "Payees". A tap in the picker writes a row into
   * ANOTHER record, and a person who is not told will not think to look for it
   * there, and will add it a second time next week.
   */
  'reference.added_to': 'Added to',

  // ------------------- dependent choices and multi select on the entry form
  // docs/plans/dependent-choices-and-multi-select.md, Decisions 2 and 6. The
  // picker's own lines ("Only Sub-categories under Utilities", "3 of 5") are
  // `ui.reference.*` and `ui.multi.*`; these are the form's.
  /**
   * Under a child the form emptied because the field it follows changed, until
   * the person picks again. `{{field}}` is that field's label: "Cleared because
   * Category changed."
   */
  'choice.cleared_because': 'Cleared because {{field}} changed.',
  /**
   * Under a worked-out field on the entry form: how its answer is worked out.
   * `{{sentence}}` is the formula said in words: "Worked out · the total of
   * Price over Services."
   */
  'entry.worked_out_line': 'Worked out · {{sentence}}',
  /**
   * Under a multi select the form trimmed when rows were taken out of the field
   * it follows. `{{from}}` is the trimmed field's own label and `{{field}}`
   * the one it follows, both as the owner typed them (writing.md: a value is
   * the person's own, never reworded): "Removed 1 from Services, not under the
   * chosen Service types."
   */
  'choice.removed_children_one': 'Removed {{count, number}} from {{from}}, not under the chosen {{field}}.',
  'choice.removed_children_other': 'Removed {{count, number}} from {{from}}, not under the chosen {{field}}.',
  /**
   * Why a new row cannot be made yet: it is filed under the field it follows,
   * and that field holds nothing. `{{field}}` is its label.
   */
  'choice.pick_parent_first': 'Pick the {{field}} first to add a new one.',
  /**
   * A saved pair that no longer matches, because a row moved to another parent
   * since the entry was saved. `{{row}}` is the row's title, `{{field}}` the
   * label of the field it follows: "“Water” is not under the Category chosen."
   */
  'choice.mismatch': '“{{row}}” is not under the {{field}} chosen. Pick again.',
  /** The same, for a row this phone cannot name. */
  'choice.mismatch_unnamed': 'This is not under the {{field}} chosen. Pick again.',
  /**
   * A row an old entry still holds after it was removed from its list. The
   * picker no longer offers it; the entry keeps it, named so. `{{title}}` is
   * the row's own title: "Godown rent (archived)".
   */
  'choice.archived': '{{title}} (archived)',

  // ----------------------------------------- customize the entry flow, T-10.11
  // The two doors on the entry list are one word each. A person walks past them
  // on every visit, so the explaining moved to the screen behind each one.
  'record.door.customize': 'Customize',
  'record.door.links': 'Links',
  // The same two doors, now behind the header's gear, with a line under each
  // saying what is there. Settled 22 Sep 2026: a sheet, not a menu.
  'record.settings.title': 'Settings for {{record}}',
  'record.settings.fields_one': '{{count, number}} $t(glossary.field_one)',
  'record.settings.fields_other': '{{count, number}} $t(glossary.field_other)',
  'record.settings.insights_one': '{{count, number}} $t(glossary.insight_one)',
  'record.settings.insights_other': '{{count, number}} $t(glossary.insight_other)',
  'record.search_placeholder': 'Search {{record}}',
  'customize.intro': 'This is the form somebody fills to record one $t(glossary.entry_one). Add a $t(glossary.field_one), take one out, or set the order they come in.',
  'customize.title': 'Customize $t(glossary.entry_one) flow',
  'customize.fields_label': 'In the $t(glossary.entry_one) form',
  'customize.fields_caption': 'Drag to set the order. Tap a $t(glossary.field_one) to change how it is filled.',
  'customize.add_field': 'Add a $t(glossary.field_one)',
  'customize.record_label': 'The $t(glossary.record_one)',
  'customize.record_name': 'Name',
  /*
   * Which glyph this record wears on home, and the word for having none.
   *
   * "Default" is a real choice and not an absence: a record that was never
   * given an icon keeps the glyph it already draws — a list is a list, a record
   * that only takes money away is an arrow pointing down — and the picker has
   * to be able to put it back. The twenty-three words are under `icon.` below.
   */
  'customize.icon': 'Icon',
  'customize.icon_default': 'Default',
  'customize.entry_called': 'One $t(glossary.entry_one) is called',
  'customize.totals': 'How totals work',
  'customize.totals_none': 'This $t(glossary.record_one) counts nothing',
  'customize.money_in': 'money in',
  'customize.money_out': 'money out',
  'customize.money_both': 'in and out, by the $t(glossary.entry_one)',
  'customize.save': 'Save changes',
  'customize.leave.title': 'Leave without saving?',
  'customize.leave.body': 'Your changes to this $t(glossary.record_one) are not saved. They go if you leave now.',
  'customize.leave.keep': 'Keep editing',
  'customize.leave.discard': 'Discard changes',
  'customize.save_caption': 'New $t(glossary.entry_other) only. Past $t(glossary.entry_other) keep every value they hold.',
  'customize.saving': 'Saving',
  'customize.preview': 'Preview the $t(glossary.entry_one) form',
  'customize.preview_only': 'This is a preview. Nothing is saved from this screen.',
  'customize.read_only': 'You may look at this, and not change it.',
  'customize.stale': 'This $t(glossary.record_one) changed while you were here. Open it again and make the change on top.',
  'customize.off_meta': 'Off, so it is not in the form',
  'customize.opens_first': 'Opens first',
  'customize.badge_new': 'New',
  'customize.badge_off': 'Off',
  'customize.required': 'Required',
  'customize.keeps_last': 'Keeps the last value',
  'customize.starts_with': 'Starts with',
  'customize.from_list': 'From the $t(glossary.list_one)',
  'customize.locked': 'From the template',

  /*
   * The record icon vocabulary: one word per name in `RECORD_ICON_IDS`.
   *
   * TWENTY-THREE WORDS AND TWENTY-THREE NAMES, and `en.test.ts` proves the two
   * lists are the same length and the same members. The name is what the record
   * stores and the phone draws a glyph from; this is what a person reads
   * in the picker, so a name and its word may differ — `things` is drawn with a
   * box and read as "Things" — and neither is ever shown in place of the other.
   *
   * Plural where a list of them is the usual thing ("People", "Places") and
   * singular where one is ("Person", "Bank", "Calendar"), because that is how a
   * person names the list they are looking at.
   */
  'icon.people': 'People',
  'icon.person': 'Person',
  'icon.places': 'Places',
  'icon.buildings': 'Buildings',
  'icon.trips': 'Trips',
  'icon.things': 'Things',
  'icon.stock': 'Stock',
  'icon.shopping': 'Shopping',
  'icon.money': 'Money',
  'icon.payments': 'Payments',
  'icon.bank': 'Bank',
  'icon.categories': 'Categories',
  'icon.work': 'Work',
  'icon.tasks': 'Tasks',
  'icon.documents': 'Documents',
  'icon.health': 'Health',
  'icon.tools': 'Tools',
  'icon.devices': 'Devices',
  'icon.power': 'Power',
  'icon.water': 'Water',
  'icon.calendar': 'Calendar',
  'icon.book': 'Ledger',
  'icon.list': 'List',

  // one field, up close
  'field.settings.title': 'Field',
  'field.settings.name': 'Field name',
  'field.settings.hint': 'Hint under the $t(glossary.field_one)',
  'field.settings.hint_placeholder': 'Nothing shown',
  'field.settings.hint_help': 'A short line. It helps a new helper at the counter.',
  'field.settings.in_form': 'In the form',
  'field.settings.show': 'Show in the $t(glossary.entry_one) form',
  'field.settings.show_off_help': 'Off keeps every value already saved.',
  'field.settings.must_fill': 'Must be filled',
  'field.settings.must_fill_attrs': 'This kind of $t(glossary.field_one) lives outside the typed slots, so it can be asked for, not required.',
  'field.settings.opens_first': 'Opens first, with the keypad',
  'field.settings.keeps_last': 'Keeps the last value',
  'field.settings.filled': 'How it is filled',
  'field.settings.worked_out': 'How it is worked out',
  'field.settings.limits': 'Limits',
  'field.settings.units': 'Units',
  'field.settings.choices': 'Choices',
  'field.settings.remove': 'Remove this $t(glossary.field_one)',
  'field.settings.remove_body': 'It leaves new $t(glossary.entry_other). Old ones keep the values they hold.',
  'field.settings.removed': 'Being removed on save',
  'field.settings.undo_remove': 'Keep it after all',
  'field.settings.primary_note': 'This $t(glossary.field_one) comes from the template. It stays in the form, and its type stays as it is.',
  'field.settings.bound_note': 'The $t(glossary.record_one) reads this $t(glossary.field_one), so it stays in the form.',
  'field.settings.type': 'Type',
  'field.settings.reads': 'Reads',
  /**
   * What the field holds the moment a new entry opens, before anybody types.
   *
   * It sits beside "Keeps the last value" because the two answer questions
   * that sound alike and are not: this one is about a NEW entry, that one is
   * about the entry after "Save and add new". A field may answer both.
   */
  'field.settings.starts_with': 'Starts with',
  'field.starts.hint': 'What the $t(glossary.field_one) holds when a new $t(glossary.entry_one) opens. The $t(glossary.person_one) may type over it, and what they type is what saves.',
  'field.starts.nothing': 'Nothing',
  'field.starts.nothing_hint': 'The $t(glossary.person_one) types it every time',
  'field.starts.fixed': 'A fixed value',
  'field.starts.fixed_hint': 'The same on every new $t(glossary.entry_one)',
  /** `{{field}}` is the list a reference field on this record points at: "the Customer". */
  'field.starts.from_list': 'A value on the {{field}} it points at',
  'field.starts.from_list_hint': 'It fills when that $t(glossary.field_one) is picked',
  'field.starts.worked_out': 'Worked out',
  'field.starts.worked_out_hint': 'Any $t(glossary.recipe_one): the rate on the $t(glossary.list_one), less a discount',
  'field.starts.pick_row': 'Which $t(glossary.entry_one)?',
  'field.starts.pick_choice': 'Which choice?',
  'field.starts.pick_yes_no': 'Yes or no?',
  'field.starts.yes': 'Yes',
  'field.starts.no': 'No',
  'field.starts.no_rows': 'This $t(glossary.list_one) has nothing in it on this device yet.',
  /**
   * The builder's own line for a default: which fields make it fill again.
   *
   * Worked out from the recipe rather than chosen, which is why it is a fact
   * and not a door.
   */
  'formula.follows': 'Follows',
  'formula.follows_none': 'Nothing — it fills once, when the $t(glossary.entry_one) opens',
  'formula.follows_hint': 'Whenever one of these changes and nobody has typed into the $t(glossary.field_one), it fills again. The moment somebody types, it stops following for that $t(glossary.entry_one).',

  // adding one
  'add_field.title': 'Add a $t(glossary.field_one)',
  'add_field.question': 'What do you want to record?',
  // The second section. A total is not a twelfth kind of typing, so its heading
  // says the one thing that separates it: nobody fills it in.
  'add_field.computed_question': 'Or let one work itself out',
  'add_field.computed_caption': 'Nobody fills this one in. It reads the other $t(glossary.field_other) on the $t(glossary.entry_one) and gives the answer.',
  'add_field.caption': 'Pick the kind of value. You name it on the next step.',
  'add_field.lines_after_save': 'To put lines inside each line, save first, then open this $t(glossary.field_one) again.',
  // A removed field keeps its place, so its old entries keep their values:
  // removing one makes no room, and the words do not say it would.
  'add_field.full': 'This $t(glossary.record_one) has no room left for that kind of $t(glossary.field_one), counting the ones removed from it.',
  'new_field.title': 'New $t(glossary.field_one)',
  'new_field.add': 'Add $t(glossary.field_one)',
  'new_field.choices_caption': 'The chips a $t(glossary.person_one) taps, in the order they see them.',
  'new_field.add_choice': 'Add a choice',
  'new_field.choice_placeholder': 'Cash',
  'new_field.note': 'A new $t(glossary.field_one) starts on the next $t(glossary.entry_one). Past $t(glossary.entry_other) stay as they were, and read as nothing for it.',
  'new_field.list': 'Which $t(glossary.list_one) does it read?',
  // Headings in that picker when a reference may also pick one entry of another record.
  'new_field.lists': 'Lists',
  'new_field.records': 'Or one $t(glossary.entry_one) of a $t(glossary.record_one)',
  'new_field.new_list': 'New $t(glossary.list_one)',
  'new_field.new_list_note': 'A new $t(glossary.list_one) is created straight away, and shows on home under Lists.',
  // Naming a list is not stocking it. A field that reads an empty list is a
  // field nobody can answer on the first entry, so the rows are added here too.
  'new_field.list_contents': 'What is in this $t(glossary.list_one)?',
  'new_field.add_row': 'Add to',
  'new_field.list_empty': 'Nothing in it yet. Add the first one.',
  'new_field.list_more': 'more, not shown here',
  'new_field.list_rows_note': 'Adding one opens the $t(glossary.list_one)\'s own form, so every column is asked for. The $t(glossary.list_one) has its own page, where an $t(glossary.entry_one) can be changed or removed.',
  'new_field.list_no_title': 'This $t(glossary.list_one) has no name column, so an $t(glossary.entry_one) cannot be added here. Open the $t(glossary.list_one) instead.',
  'new_field.list_unread': 'This $t(glossary.list_one) could not be read just now.',
  'new_field.name_placeholder': 'Payment status',
  'new_field.pending': 'Not saved yet. Save the flow to keep it.',
  'new_field.next': 'Next',
  // A total has one look, so the line above the button says there is nothing
  // left to choose. The button still goes somewhere, and this says where.
  'new_field.formula_next': 'Next: what it is worked out from',
  'new_field.keeps_last': 'Keeps the last answer',
  'new_field.keeps_last_help': 'It carries over to the next $t(glossary.entry_one).',
  'new_field.must_fill_help': 'An $t(glossary.entry_one) cannot be saved without it.',
  'new_field.step_first': 'Step 1 of 2',
  'new_field.one_shape': 'This kind of $t(glossary.field_one) has one look.',

  // how a field looks: the shape gallery, and the row that names the shape
  'field.shapes.title': 'How it looks',
  'field.shapes.caption': 'Tap a look. Each picture is the real control, at the size the form draws it.',
  // "Step 2 of 2" is an ordinal, not a count of two. `_two` is a CLDR plural
  // category — Arabic and Welsh use it — so the word is spelled out here for
  // the same reason `new_field.step_first` is.
  'field.shapes.step_second': 'Step 2 of 2',
  'field.shapes.rename': 'Rename',
  'field.shapes.apply': 'Use this look',
  'field.shapes.hand_tuned': 'Tuned by hand',
  'field.shapes.hand_tuned_note': 'Set up knob by knob, so no look on the list matches it. The picture below is exactly what it does.',
  'field.shapes.stepper_note': 'The plus and minus keys are 48 across, so they stay tappable on a small phone.',
  // Over a card whose picture is a row: what opens when that row is tapped.
  'field.shapes.when_tapped': 'When tapped',
  // The name the peek pretends somebody typed into the search box. A real
  // name, so the "Add" row underneath reads as it would at the counter.
  'field.shapes.peek_typed': 'Ramesh',
  // Under a greyed card. Read as `${count} ${t(...)}`.
  'field.shapes.too_many_choices': 'choices is more than this look can show. Pick another, or take some choices out.',

  // The docked keypad comes with every money look, so it is not what tells
  // this one apart. What does is that nothing sits beside the number.
  'field.shapes.money_keypad': 'Just the amount',
  'field.shapes.money_keypad_why': 'The number on its own. The keypad docks at the bottom whichever look you choose.',
  'field.shapes.money_quick': 'Quick amounts',
  'field.shapes.money_quick_why': 'One tap for the figures you take all day.',
  'field.shapes.money_sign': 'Money in or out',
  'field.shapes.money_sign_why': 'A plus and a minus, so one $t(glossary.field_one) records both directions.',

  'field.shapes.number_type': 'Type it',
  'field.shapes.number_type_why': 'A plain box. Best when the number is large, or rarely the same twice.',
  'field.shapes.number_stepper': 'Plus and minus',
  'field.shapes.number_stepper_why': 'Tap up and down. No keyboard opens at all.',
  'field.shapes.number_units': 'Type it, with units',
  'field.shapes.number_units_why': 'A box and a unit beside it, for kilos one day and pieces the next.',

  'field.shapes.text_line': 'One line',
  'field.shapes.text_line_why': 'A single box. This is the only look a line of words has.',

  'field.shapes.long_three': 'Three lines',
  'field.shapes.long_three_why': 'Room for a sentence, without taking over the form.',
  'field.shapes.long_five': 'Five lines',
  'field.shapes.long_five_why': 'Room for a paragraph. Five is as tall as a note goes.',

  'field.shapes.date_empty': 'Empty until tapped',
  'field.shapes.date_empty_why': 'The date waits for an answer. Best when the day is rarely today.',
  'field.shapes.date_today': 'Starts on today',
  'field.shapes.date_today_why': 'Already filled when the form opens. One tap less on almost every $t(glossary.entry_one).',

  'field.shapes.datetime_moment': 'Date and time',
  'field.shapes.datetime_moment_why': 'The day and the clock together, for a shift, a slot, or a trip.',

  'field.shapes.boolean_switch': 'Switch',
  'field.shapes.boolean_switch_why': 'On or off, beside the name. This is the only look a yes or no has.',

  'field.shapes.select_buttons': 'Buttons in a row',
  'field.shapes.select_buttons_why': 'Up to four choices, all readable at once. One tap, and nothing moves.',
  'field.shapes.select_chips': 'Chips',
  'field.shapes.select_chips_why': 'Wraps on the form, up to a dozen choices. Past four, this is the look that keeps them in view.',
  'field.shapes.select_sheet': 'Search list',
  'field.shapes.select_sheet_why': 'One row that opens a sheet with a search box. Best past four choices.',

  'field.shapes.multi_list': 'Tick several',
  'field.shapes.multi_list_why': 'Several rows from another list, shown as chips.',
  'field.shapes.lines_table': 'Lines',
  'field.shapes.lines_table_why': 'One line for each item, each with its own fields.',
  // A number series (Phase 8 of docs/plans/workbook-map.md): the form, the phone and the field editor.
  'field.series.given_when_saved': 'Given when saved',
  'field.series.given_when_synced': 'Number given when saved',
  'field.series.title': 'Numbering',
  'field.series.prefix': 'Letters before the number',
  'field.series.prefix_hint': 'Capital letters, such as VCH/. Leave it empty for the number alone.',
  'field.series.digits': 'Digits',
  'field.series.start': 'First number',
  'field.series.yearly': 'Start again each financial year',
  'field.series.yearly_help': 'The year shows before the number, such as 26-27/7.',
  'field.series.per': 'Counted separately for each',
  'field.series.per_none': 'Nothing: one series',
  'field.series.once': 'Each number once',
  'field.series.once_help': 'Off lets one number repeat, for one voucher over several days.',
  'field.series.frozen': 'Once a number is given, only Each number once can change.',
  'field.shapes.series_number': 'Number',
  'field.shapes.series_number_why': 'Given when saved, or typed from a paper voucher.',

  'field.shapes.reference_create': 'Search, and add new',
  'field.shapes.reference_create_why': 'Find an $t(glossary.entry_one), or add a new one right there without leaving the form.',
  'field.shapes.reference_search': 'Search only',
  'field.shapes.reference_search_why': 'Find an $t(glossary.entry_one) that already exists. Nobody can add one from the form.',

  'field.shapes.file_any': 'Camera or gallery',
  'field.shapes.file_any_why': 'The $t(glossary.person_one) chooses where the picture comes from.',
  'field.shapes.file_camera': 'Camera only',
  'field.shapes.file_camera_why': 'Straight to the camera, for a photo that must be taken there and then.',
  'field.shapes.file_three': 'Up to three',
  'field.shapes.file_three_why': 'Room for a few pages of one bill.',

  'field.shapes.phone_plain': 'Plain',
  'field.shapes.phone_plain_why': 'A number box, and nothing beside it.',
  'field.shapes.phone_call': 'Tap to call',
  'field.shapes.phone_call_why': 'A Call button in the box, so a chase is one tap from the $t(glossary.entry_one).',

  'field.shapes.location_pin': 'Map pin',
  'field.shapes.location_pin_why': 'One button that drops a pin where the phone is.',
  'field.shapes.location_manual': 'Pin, or typed in',
  'field.shapes.location_manual_why': 'The same button, plus two boxes for a place the phone cannot reach.',

  'field.shapes.readonly_value': 'Read only',
  'field.shapes.readonly_value_why': 'Worked out from other $t(glossary.field_other). Nobody types into it.',
  // "Max choices" on a multi select: the most rows one entry may hold.
  'field.settings.max_chosen': 'Max choices',
  'field.settings.max_chosen_bad': 'Type a number from 1 to 20, or leave it empty.',
  'field.settings.max_chosen_help': 'Leave it empty to allow any number.',
  'field.settings.max_chosen_placeholder': 'No limit',
  // "Only offer rows that match" on a field that points at a list: a dependent choice.
  'field.settings.filter': 'Only offer rows that match',
  'field.settings.filter_any': 'Every row',
  'field.settings.filter_bad': 'These two $t(glossary.field_other) cannot be matched. Pick another.',
  'field.settings.filter_equals': 'Must equal this $t(glossary.entry_one)\'s',
  'field.settings.filter_help': 'Offer every row of {{list}}, or only the rows that match a $t(glossary.field_one) on this $t(glossary.entry_one).',
  'field.settings.filter_nothing': 'No $t(glossary.field_one) on {{list}} points at a $t(glossary.list_one) this $t(glossary.record_one) also uses, so there is nothing to match.',
  'field.settings.filter_ring': 'These $t(glossary.field_other) would filter each other in a ring. Pick another.',
  'field.settings.filter_sentence': 'Offers only the rows of {{list}} whose {{field}} is the {{equals}} on this $t(glossary.entry_one).',
  'field.settings.filter_where': 'Its $t(glossary.field_one) on {{list}}',
  'field.editable_when.always': 'Always',
  'field.editable_when.always_hint': 'Anyone who may write the $t(glossary.entry_one) can change it.',
  'field.editable_when.empty_locks': 'Lock the field',
  'field.editable_when.hint': 'The field can be changed only when this says yes. While it is locked it keeps its value, and its starting value still fills it.',
  'field.editable_when.label': 'Editable when',
  'field.editable_when.only_when': 'Only when…',
  'field.editable_when.only_when_hint': 'Say when it can be changed. While it is locked, its starting value still fills it.',
  'field.editable_when.title': 'When can it be changed?',
  'field.must_be.add': 'Add a rule',
  'field.must_be.breaks_one': '{{count, number}} saved $t(glossary.entry_one) breaks this rule. It stays as it is until someone edits it.',
  'field.must_be.breaks_other': '{{count, number}} saved $t(glossary.entry_other) break this rule. They stay as they are until someone edits them.',
  'field.must_be.breaks_none': 'No saved $t(glossary.entry_one) breaks this rule.',
  'field.must_be.default_message': 'This does not keep its rule.',
  'field.must_be.edit': 'Change the check',
  'field.must_be.empty_passes': 'Let it through',
  'field.must_be.hint': 'Checked last, when the $t(glossary.entry_one) is saved. A rule that says no shows its words under the $t(glossary.field_one).',
  'field.must_be.label': 'Must be',
  'field.must_be.level': 'When it says no',
  'field.must_be.level_block': 'Stop the save',
  'field.must_be.level_warn': 'Only warn',
  'field.must_be.message': 'Words to show',
  'field.must_be.message_hint': 'Put a $t(glossary.field_one) name in braces to show its value.',
  'field.must_be.record_hint': 'Rules that read several $t(glossary.field_other) at once. A rule that says no shows its words at the top of the form.',
  'field.must_be.record_label': 'Rules for the whole $t(glossary.entry_one)',
  'field.must_be.remove': 'Remove the rule',
  'field.must_be.title': 'What must it be?',
  'field.show_when.always': 'Always',
  'field.show_when.always_hint': 'The field is on every $t(glossary.entry_one).',
  'field.show_when.empty_hides': 'Hide the field',
  'field.show_when.label': 'Show when',
  'field.show_when.only_when': 'Only when…',
  'field.show_when.only_when_hint': 'Say when it shows. While it is hidden, nothing typed in it is kept.',
  'field.show_when.title': 'When does it show?',
  'field.show_when.hint': 'The field shows only when this says yes. While it is hidden it is never required, and nothing typed in it is kept.',

  // the save sheet
  'save_sheet.title': 'Save these changes?',
  'save_sheet.rule': 'Past $t(glossary.entry_other) are never rewritten. Totals read the same values as before.',
  'save_sheet.keep_editing': 'Keep editing',
  'change.added': 'is new. It starts on the next $t(glossary.entry_one).',
  'change.removed': 'leaves the form. Values already saved stay.',
  'change.switched_off': 'leaves the form. Values already saved stay.',
  'change.switched_on': 'joins the form.',
  'change.renamed': 'has a new name.',
  'change.reordered': 'The order of the form changed.',
  'change.tuned': 'is filled a different way.',
  'change.record_renamed': 'The $t(glossary.record_one) has a new name.',
  'change.record_icon': 'The $t(glossary.record_one) has a new icon.',
  'change.focus': 'opens first from now on.',
  'change.layout': 'The sections of the form changed.',
  'change.layout_removed': 'The form drops its sections and shows every field in order.',
  'change.record_rules': 'The rules every $t(glossary.entry_one) must keep changed.',

  // the kinds of value, in words a shopkeeper uses
  // Three words per kind: what it is, an example of the VALUE it holds, and
  // an example of what somebody would CALL such a field. The third is the
  // placeholder in the name box, so a person naming a photo field is not
  // shown a name for a yes-or-no one.
  'field_type.money': 'Amount of money',
  'field_type.money_hint': 'The keypad, in your currency',
  'field_type.money_example': 'Advance paid',
  'field_type.number': 'A number',
  'field_type.number_hint': 'Count or weight',
  'field_type.number_example': 'Boxes',
  'field_type.quantity': 'A quantity',
  'field_type.quantity_hint': 'A number with a unit',
  'field_type.quantity_example': 'Weight',
  'field_type.text': 'Words',
  'field_type.text_hint': 'One short line',
  'field_type.text_example': 'Bill number',
  'field_type.long_text': 'A note',
  'field_type.long_text_hint': 'A few lines',
  'field_type.long_text_example': 'Note',
  'field_type.date': 'A date',
  'field_type.date_hint': 'One day',
  'field_type.date_example': 'Due date',
  'field_type.datetime': 'A date and time',
  'field_type.datetime_hint': 'A day and a clock time',
  'field_type.datetime_example': 'Picked up at',
  'field_type.boolean': 'Yes or no',
  'field_type.boolean_hint': 'One switch',
  'field_type.boolean_example': 'Delivered',
  'field_type.select': 'One of a few',
  'field_type.select_hint': 'Cash, UPI, Card',
  'field_type.select_example': 'Payment mode',
  'field_type.reference': 'From a $t(glossary.list_one)',
  'field_type.reference_hint': 'Items, Customers',
  'field_type.reference_example': 'Customer',
  'field_type.attachment': 'A photo',
  'field_type.attachment_hint': 'Camera or gallery',
  'field_type.attachment_example': 'Bill photo',
  'field_type.phone': 'A phone number',
  'field_type.phone_hint': 'Tap to call',
  'field_type.phone_example': 'Mobile',
  'field_type.location': 'A place',
  'field_type.location_hint': 'A pin on a map',
  'field_type.location_example': 'Delivered at',
  'field_type.formula': 'A total',
  'field_type.formula_hint': 'Worked out from other $t(glossary.field_other)',
  'field_type.formula_example': 'Total',
  'field_type.rollup': 'Total of other $t(glossary.entry_other)',
  'field_type.rollup_hint': 'Adds up the $t(glossary.entry_other) that pick this one',
  'field_type.rollup_example': 'Room bill',
  'field_type.lookup': 'A value from a $t(glossary.list_one)',
  'field_type.lookup_hint': 'Not yet',
  'field_type.lookup_example': 'Customer phone',
  'field_type.lines': 'Lines',
  'field_type.lines_hint': 'Items on a bill, rooms on a stay',
  'field_type.lines_example': 'Items',
  'field_type.series': 'A number series',
  'field_type.series_hint': 'Numbers each $t(glossary.entry_one) in turn',
  'field_type.series_example': 'Voucher no',
  // The formula builder. A chain of terms, joined left to right.
  'formula.title': 'What is it worked out from?',
  'formula.caption': 'Tap to add a value. It runs left to right, so there is nothing to bracket.',
  'formula.nothing_yet': 'Nothing yet',
  'formula.hop_arrow': 'from',
  'formula.op.add': '+',
  'formula.op.subtract': '\u2212',
  'formula.op.multiply': '\u00d7',
  'formula.op.divide': '\u00f7',
  'formula.op.add_word': 'plus',
  'formula.op.subtract_word': 'minus',
  'formula.op.multiply_word': 'times',
  'formula.op.divide_word': 'divided by',
  'formula.add_value': 'Add a value',
  'formula.source_here': 'A $t(glossary.field_one) on this $t(glossary.entry_one)',
  'formula.source_here_hint': 'Quantity, Amount, Date',
  'formula.source_list': 'A $t(glossary.field_one) on a $t(glossary.list_one) it points at',
  'formula.source_list_hint': 'The price on the item',
  'formula.source_fixed': 'A fixed number',
  'formula.source_fixed_hint': 'The same on every $t(glossary.entry_one)',
  'formula.fixed_choice': 'One of the choices of {{field}}',
  'formula.pick_field': 'Which $t(glossary.field_one)?',
  'formula.pick_hop': 'Through which one?',
  'formula.pick_hop_field': 'Read which $t(glossary.field_one)?',
  'formula.pick_op': 'Joined how?',
  'formula.fixed_number': 'The number',
  'formula.shows_as': 'Shows as',
  'formula.result.money': 'An amount',
  'formula.result.number': 'A plain number',
  'formula.result.quantity': 'A quantity',
  'formula.result.text': 'Words',
  'formula.when_empty': 'When a value is empty',
  'formula.when_empty_blank': 'Show a dash',
  'formula.when_empty_blank_hint': 'Nothing is counted, so no total is quietly short',
  'formula.when_empty_zero': 'Read it as zero',
  'formula.when_empty_zero_hint': 'An empty value counts as 0, so the rest still adds up',
  'formula.preview': 'On the last $t(glossary.entry_one)',
  'formula.preview_none': 'No $t(glossary.entry_one) to try it on yet',
  'formula.remove': 'Take it out',
  'formula.built_elsewhere': 'This one was built another way, so it cannot be changed here.',
  'formula.left_to_right': 'Runs left to right',
  'formula.name': 'What is it called?',

  // The recipe said out loud, by `formulaSentence`. One wording for the
  // builder, the link row, the card caption and the assistant's proposal.
  'formula.words.or_else': 'or else',
  'formula.words.if': 'if',
  'formula.words.then': 'then',
  'formula.words.otherwise': 'otherwise',
  'formula.words.yes': 'yes',
  'formula.words.no': 'no',
  'formula.words.round': 'round',
  'formula.words.to_places': 'to {{places, number}} places',
  'formula.words.days_from': 'days from',
  'formula.words.to': 'to',
  'formula.words.joined_with': 'joined with',
  'formula.words.unknown': 'something this version cannot say',
  'formula.words.plus': 'plus',
  'formula.words.valid_till': 'as the last day it covers',
  'formula.words.today': 'today',
  'formula.unit.days': 'days',
  'formula.unit.weeks': 'weeks',
  'formula.unit.months': 'months',
  'formula.unit.years': 'years',
  'formula.test.eq': 'is',
  'formula.test.ne': 'is not',
  'formula.test.gt': 'is more than',
  'formula.test.gte': 'is at least',
  'formula.test.lt': 'is less than',
  'formula.test.lte': 'is at most',
  'formula.test.filled': 'is filled',
  'formula.test.empty': 'is empty',

  // The recipe, as rows a finger opens. A piece may hold pieces, so the words
  // here name the SHAPES ("the first that is filled") and the LEAD-INS ("or
  // else", "then", "otherwise") rather than the node kinds behind them.
  // Words from docs/design/one-formula, artboards 1 to 4.
  'formula.recipe': 'The $t(glossary.recipe_one)',
  'formula.gap': '…',
  'formula.nothing_here': 'Nothing yet. The first piece is a value.',
  'formula.add_piece': 'Add a piece',
  'formula.add_another': 'Add another',
  'formula.depth': '{{depth, number}} of {{max_depth, number}} levels deep · {{pieces, number}} of {{max_pieces, number}} pieces',
  'formula.bracket_open': '(',
  'formula.bracket_close': ')',
  'formula.result.boolean': 'A yes or no',
  'formula.result.date': 'A date',
  'formula.result_fixed': 'The answer has to fit the $t(glossary.field_one) it lands in, so this one is settled.',
  'formula.use_it': 'Use this $t(glossary.recipe_one)',
  'formula.wraps_note': 'A way to combine or shape takes this piece as its first input.',
  'formula.pick_test': 'What does it ask?',
  'formula.fixed_word': 'A fixed word',
  'formula.fixed_word_hint': 'The same words on every $t(glossary.entry_one)',
  // The lead-in on the left of a row, in small capitals.
  'formula.key.first': 'First',
  'formula.key.or_else': 'or else',
  'formula.key.if': 'If',
  'formula.key.asks': 'is',
  'formula.key.than': 'than',
  'formula.key.then': 'then',
  'formula.key.otherwise': 'otherwise',
  'formula.key.round': 'round',
  'formula.key.days': 'days',
  'formula.key.days_from': 'days from',
  'formula.key.days_to': 'to',
  'formula.key.join': 'join',
  'formula.key.with': 'with',
  // The row that owns a shape, so the whole shape can be tapped.
  'formula.head.or_else': 'the first that is filled',
  'formula.head.if': 'one answer or another',
  'formula.head.join': 'joined into words',
  'formula.head.days': 'days between two dates',
  'formula.head.date_add': 'a date moved on',
  'formula.key.date_add': 'date',
  'formula.key.date_from': 'from',
  'formula.key.date_by': 'plus',
  'formula.key.date_unit': 'unit',
  'formula.key.date_ends': 'ends',
  'formula.pick_unit': 'Moved on by',
  'formula.pick_valid_till': 'Which day it ends on',
  'formula.valid_till.same_day': 'On the same day',
  'formula.valid_till.last_day': 'On the last day it covers',
  'formula.pick.date_add': 'A date moved on',
  'formula.pick.date_add_note': 'Start date plus 1 month',
  'formula.pick.today': 'Today',
  'formula.pick.today_note': 'The day the form opens',
  // What can be done to one piece.
  'formula.piece.title': 'This piece',
  'formula.piece.change': 'Change this piece',
  'formula.piece.change_note': 'Pick another value',
  'formula.piece.or_else': 'The first that is filled',
  'formula.piece.or_else_note': 'This value, or else that one',
  'formula.piece.if': 'If this, then that',
  'formula.piece.if_note': 'One answer or another, by a test',
  'formula.piece.round': 'Round to',
  'formula.piece.round_note': '2 places, or none',
  'formula.piece.round_now': 'Now to {{places, number}} places. Tap to change',
  'formula.piece.remove': 'Take it out',
  'formula.piece.remove_note': 'The rest of the $t(glossary.recipe_one) closes up',
  // Add a piece: three groups, and the copy of artboard 2.
  'formula.group.value': 'A value',
  'formula.group.combine': 'A way to combine',
  'formula.group.shape': 'A way to shape it',
  'formula.pick.here': 'A $t(glossary.field_one) on this $t(glossary.entry_one)',
  'formula.pick.hop': 'A $t(glossary.field_one) on the {{field}} it points at',
  'formula.pick.brackets': 'Brackets',
  'formula.pick.brackets_note': 'Work this part out first',
  'formula.pick.days': 'Days between two dates',
  'formula.pick.days_note': 'Due date minus today',
  'formula.pick.join': 'Join words',
  'formula.pick.join_note': 'Name, a dash, the area',
  'formula.words.agg_count': 'the number of',
  'formula.words.agg_max': 'the highest',
  'formula.words.agg_min': 'the lowest',
  'formula.words.agg_over': 'over',
  'formula.words.agg_sum': 'the total of',
  'formula.pick.agg_count': 'Number of {{lines}}',
  'formula.pick.agg_count_note': 'How many lines',
  'formula.pick.agg_field': 'Add up which number on {{lines}}',
  'formula.pick.agg_sum': 'Total of a number on {{lines}}',
  // A line's hop up the tree, read with the row it lands on: "Check in of the stay", or by the
  // record's name when it has no noun of its own: "Check in of Stays".
  'formula.words.of_the_row': '{{field}} of the {{row}}',
  'formula.words.of_record': '{{field}} of {{record}}',
  // The nudge on a record's own entry list. Earned, named, and dismissed for good.
  'nudge.formula.title': '{{field}} could work itself out',
  'nudge.formula.body': '{{record}} holds a {{price}}, and every $t(glossary.entry_one) holds a {{quantity}}. Keep one and stop typing the other.',
  'nudge.formula.yes': 'Set it up',
  'nudge.formula.no': 'Not now',
  'nudge.formula.dismiss': 'Hide this',
  'field_type.multi_select': 'Many from a $t(glossary.list_one)',
  'field_type.multi_select_hint': 'Services, Tags',
  'field_type.multi_select_example': 'Services',

  // how a field is drawn, and the settings behind it
  'component.text.plain': 'One line',
  'component.text.multiline': 'A few lines',
  'component.text.phone': 'A phone box',
  'component.number.plain': 'Type the number',
  'component.number.stepper': 'Plus and minus',
  'component.money.calculator': 'Calculator keypad',
  'component.date.picker': 'A calendar',
  'component.boolean.switch': 'A switch',
  'component.select.chips': 'Chips to tap',
  'component.reference.search_picker': 'Search a $t(glossary.list_one)',
  'component.reference.multi_picker': 'Pick many from a $t(glossary.list_one)',
  'component.file.picker': 'Camera or gallery',
  'component.map.picker': 'A place',
  'component.value.readonly': 'Read only',
  'component.lines.table': 'Lines',
  'ui.lines.count_one': '{{count}} line',
  'ui.lines.count_other': '{{count}} lines',
  'ui.lines.none': 'No lines yet',
  // A line of a lines field, said about itself. The noun is the lines record's
  // own word for one of its entries ("item", "guest"), already translated.
  'ui.lines.untitled': '{{noun}} {{number, number}}',
  'ui.lines.add': 'Add {{noun}}',
  'ui.lines.remove': 'Remove this {{noun}}',
  'ui.lines.empty': 'No {{nouns}} yet.',
  'ui.lines.empty_count': 'No {{nouns}} yet. Type the number of {{nouns}}, or add one.',
  'ui.lines.inside_empty': 'No {{nouns}} in this {{parent}} yet.',
  'ui.lines.open_hint': 'Shows its details',
  // The entry form's lines (docs/plans/entry-lines-and-layout.md). The noun is
  // the lines record's own word for one of its entries, mid-sentence ("guest"),
  // and nouns the word for several.
  // The count could not go as low as asked: these lines hold something (Decision 9).
  // `what` is the lines record's word for one line or for several, as the count asks.
  'lines.count_note_one': '{{count, number}} {{what}} has details. Remove it by hand to go lower.',
  'lines.count_note_other': '{{count, number}} {{what}} have details. Remove one by hand to go lower.',
  // A save stopped by a line with a gap: the first one is open below.
  'lines.fix_lines': 'Some {{nouns}} need a detail. They are open below.',
  // On a closed card: what stops this line saving.
  'lines.required_pick': 'Pick the {{field}} first.',
  'lines.required_fill': '{{field}} is needed.',
  'lines.inside_gap': 'Some {{nouns}} need a detail.',
  // A depth-2 line's sheet: "Guest 2 · Deluxe room".
  'lines.sheet_title': '{{noun}} · {{parent}}',
  // How many lines a run holds, on a card and above Save: "3 guests".
  'lines.how_many_one': '{{count, number}} {{what}}',
  'lines.how_many_other': '{{count, number}} {{what}}',
  // On a card whose amount is one typed field times another: "3 × ₹40", quantity × rate.
  // {{left}} and {{right}} arrive formatted, a number or an amount.
  'lines.times': '{{left}} × {{right}}',
  'lines.labelled': '{{label}} {{value}}',
  // The caps on one save (Decision 5). The server refuses past them.
  'lines.over_cap': 'One entry holds at most {{max, number}} lines in all. Remove some before you save.',
  'lines.over_max': '{{field}} holds at most {{max, number}} {{nouns}}. Remove some before you save.',
  'lines.count_from.label': 'Count comes from',
  'lines.count_from.none': 'None',
  'lines.count_from.none_hint': 'Lines are added one at a time.',
  'lines.count_from.hint': 'Type a number in that field and the form adds that many lines to fill.',
  'lines.settings.add_field': 'Add a $t(glossary.field_one) to each line',
  'lines.settings.holds': 'Each line holds',
  'lines.settings.holds_caption': 'More settings for these open once you save.',
  'lines.settings.holds_empty': 'Add at least one $t(glossary.field_one) each line holds.',
  // The two reference fields every lines record holds, as a composed setup names them.
  'field.lines_parent': 'Parent',
  'field.lines_root': 'Root',
  'lines.settings.label': 'Lines',
  'lines.settings.next': 'Next: what each line holds',
  'lines.customize.back': 'Back to {{record}}',
  'lines.customize.discard_and_back': 'Discard and go back',
  'lines.customize.save_and_back': 'Save and go back',
  'lines.customize.unsaved': 'Your changes to these fields are not saved yet. Save them, or leave them, before you go back.',
  'lines.customize.intro': 'Each line of {{field}} holds these. A line is saved with its $t(glossary.entry_one), and the fields here show on every line.',
  'lines.settings.fields': 'Fields on each line',
  'lines.settings.save_and_open': 'Save and open',
  'lines.settings.save_first': 'Your changes to this $t(glossary.record_one) are saved first, then the fields on each line open.',
  'component.stepper_note': 'Plus and minus needs a newer app. An older phone gets the number pad instead.',
  'prop.allow_expression': 'Allow plus and times',
  'prop.sign_control': 'Show the in and out switch',
  'prop.quick_amounts': 'Quick amounts',
  'prop.min': 'Smallest',
  'prop.max': 'Largest',
  'prop.decimals': 'Decimals',
  'prop.step': 'Step',
  'prop.allow_create': 'Let a $t(glossary.person_one) add a new $t(glossary.entry_one)',
  'prop.recent_first': 'Show the recent ones first',
  'prop.allow_search': 'Let a $t(glossary.person_one) search the choices',
  'prop.tap_to_call': 'Tap the number to call it',
  'prop.rows': 'Lines on screen',
  'prop.max_length': 'Longest',
  'prop.max_files': 'How many files',
  'prop.allow_manual': 'Let a $t(glossary.person_one) move the pin',
  'prop.none': 'No limit',
  'prop.add': 'Add',
  'prop.default': 'Filled with',
  'prop.default_none': 'Nothing',
  'prop.default_now': 'Now',
  'prop.default_today': 'Today',
  'prop.mode': 'What it asks for',
  'prop.mode_date': 'A day',
  'prop.mode_datetime': 'A day and a time',
  // ---------------------------------------------------------------- links
  // What posts one entry into another record. Section 5.7. No screen says
  // pattern, feed, trigger, field map or sign: a person says what happens.
  'link.list_caption': 'What this $t(glossary.record_one) posts into, and what posts into it, on its own.',
  'link.side.out': 'Posts into {{record}}',
  'link.side.in': 'Posted from {{record}}',
  'link.side.change_there': 'Change it on {{record}}',
  'link.side.other': 'Other $t(glossary.record_one)',
  'link.incoming_title': 'Posted into this $t(glossary.record_one)',
  'link.incoming_from': 'From {{record}}',
  'link.list_title': 'Links',
  'link.list_empty_title': 'Nothing is linked yet',
  'link.list_empty_body': 'A $t(glossary.link_one) posts an $t(glossary.entry_one) from this $t(glossary.record_one) into another one, on its own.',
  // The empty screen teaches with one worked example. Sales and Stock
  // movements are records the templates really create, and the pair Section
  // 5.7's own worked sequence uses, so the picture on the screen and the rows
  // a person will meet are the same story.
  'link.empty_example_from': 'Sales',
  'link.empty_example_to': 'Stock movements',
  'link.empty_example': 'A sale in Sales posts a matching $t(glossary.entry_one) into Stock movements.',
  'link.empty_fires': 'It fires in the same save. Every $t(glossary.entry_one) can fire it, or only the ones that meet a condition.',
  'link.add': 'Add a $t(glossary.link_one)',
  'link.count_on': '{{count, number}} on',
  'link.fired_count': 'Fired {{count, number}} times',
  'link.fired_once': 'Fired once',
  'link.fired_never': 'Not fired yet',
  'link.added_by': 'Added by {{person}}',
  'link.by_user': 'you',
  'link.by_onboarding': 'setting up',
  'link.by_suggestion': 'a suggestion you accepted',
  'link.made_at_setup': 'Made when you set up',
  'link.paused_note': 'Pausing a $t(glossary.link_one) stops the next firing. Every $t(glossary.entry_one) it already wrote stays.',
  'link.is_paused': 'Paused. Every $t(glossary.entry_one) it already wrote stays.',
  'link.writes_nothing': 'A read, so it writes nothing',
  'link.mode.auto': 'On its own',
  'link.mode.paused': 'Paused',
  'link.reads_only': 'This kind of $t(glossary.link_one) writes nothing yet.',

  // the builder
  'link.editor_title': 'Set up a $t(glossary.link_one)',
  'link.what_it_does': 'What this $t(glossary.link_one) does',
  'link.the_rule': 'The rule',
  'link.row.fires': 'What fires it',
  'link.row.lands': 'Where it lands',
  'link.row.carries': 'What carries over',
  'link.row.direction': 'Direction',
  'link.row.mode': 'When it runs',
  'link.direction.out': 'Takes out',
  'link.direction.in': 'Puts in',
  'link.fires_always': 'Every $t(glossary.entry_one)',
  'link.fires_one': '{{count, number}} condition',
  'link.fires_other': '{{count, number}} conditions',
  'link.carries_count': '{{answered, number}} of {{total, number}} $t(glossary.field_other)',
  'link.preview': 'The $t(glossary.entry_one) it will write',
  'link.preview_none': 'Answer every needed $t(glossary.field_one), and the $t(glossary.entry_one) appears here.',
  'link.turn_on': 'Turn this $t(glossary.link_one) on',
  'link.save_changes': 'Save changes',
  'link.remove': 'Remove this $t(glossary.link_one)',
  'link.pick_target': 'Where it lands',
  'link.no_targets': 'This $t(glossary.business_one) has no other $t(glossary.record_one) to post into yet.',
  'link.note_auto': 'It runs on its own, in the same save. Every $t(glossary.entry_one) it writes wears the Auto badge.',
  'link.note_paused': 'Nothing new is written. Every $t(glossary.entry_one) it already wrote stays.',

  // the condition screen
  'link.condition_title': 'When it fires',
  'link.condition_when': 'When this $t(glossary.link_one) fires',
  'link.fires_on': 'It fires on',
  'link.every_entry': 'Every $t(glossary.entry_one)',
  'link.some_entries': 'Only some $t(glossary.entry_other)',
  'link.all_must_be_true': 'All of these must be true',
  'link.and': 'and',
  'link.or': 'or',
  // What a save's links did (L3 of docs/plans/links-fixes.md): said on the save and on the entry.
  'link.posted.added': 'Added to {{records}}',
  'link.posted.changed': 'Changed in {{records}} too',
  'link.posted.removed': 'Taken out of {{records}}',
  'link.posted.names_and': '{{names}} and {{last}}',
  'link.refused.title': '{{record}} was not written',
  'link.refused.title_unseen': 'A $t(glossary.link_one) could not write its $t(glossary.entry_one)',
  'link.refused.saved': 'Your $t(glossary.entry_one) is saved.',
  'link.refused.where': 'The $t(glossary.link_one) is on the Links tab of {{record}}.',
  'link.refused.more_one': '{{count, number}} more $t(glossary.link_one) could not write',
  'link.refused.more_other': '{{count, number}} more $t(glossary.link_other) could not write',
  'link.entry.section': 'Posted by $t(glossary.link_other)',
  // Counts in money (L2): a record's money that moves nothing (a quote, an estimate, a budget) is kept out of every total.
  'money.counts.label': 'Counts in money',
  'money.counts.help': 'Turn this off for prices that move no money: quotes, estimates, budgets. Off takes its $t(glossary.entry_other) out of every money total.',
  'money.counts.not_in_totals': 'Not in money totals',
  'money.counts.turned_off': 'Its $t(glossary.entry_other) are out of every money total now.',
  'money.counts.turned_on': 'Its $t(glossary.entry_other) count in money again.',
  'link.or_another_field': 'or a different $t(glossary.field_one)',
  'link.add_condition': 'Add a condition',
  'link.add_condition_note':
    'A new condition must also be true. To accept one value or another, use "or a different $t(glossary.field_one)" inside a condition.',
  'link.any_one_enough': 'Any one of these values is enough.',
  'link.none_of_these': 'An $t(glossary.entry_one) is kept only when it is none of these.',
  'link.add_value': 'Add a value',
  'link.pick_field': 'Which $t(glossary.field_one) to check',
  'link.pick_field_note': 'Changing the $t(glossary.field_one) clears the values',
  'link.pick_op': 'It must be',
  'link.pick_value': 'Add a value to {{field}}',
  'link.value_one_only': 'One figure only. A range is two conditions.',
  'link.value_recorded': 'The values this $t(glossary.record_one) has really recorded',
  'link.recorded_count': '{{count, number}} $t(glossary.entry_other) carry it',
  'link.needs_a_value': 'One condition still needs a value, so nothing fires yet.',
  'link.match_count': '{{matched, number}} of the last {{total, number}} $t(glossary.entry_other) would fire this $t(glossary.link_one).',
  'link.match_all': 'Every $t(glossary.entry_one) fires it, with no condition at all.',
  'link.save_conditions': 'Save these conditions',
  'link.condition_empty': 'Nothing has been recorded in this $t(glossary.field_one) yet.',
  'link.condition_search': 'Search',
  'link.condition_no_match': 'Nothing matches that.',

  // the operators. Deliberately not shared with insight.op.*: that screen asks
  // a different question and its words are its own.
  'link.op.is': 'is',
  'link.op.is_not': 'is not',
  'link.op.filled': 'is filled',
  'link.op.empty': 'is empty',
  'link.op.at_least': 'is at least',
  'link.op.at_most': 'is at most',
  'link.op.exactly': 'is exactly',
  'link.op.contains': 'contains',
  'link.op.not_contains': 'does not contain',
  'link.op.is_today': 'is today',
  'link.op.before_today': 'is before today',
  'link.op.after_today': 'is after today',
  'link.op.in_next_days': 'is in the next days',
  'link.op.in_last_days': 'is in the last days',
  'link.op.on_or_before': 'is on or before',
  'link.op.on_or_after': 'is on or after',
  'link.op_note.is': 'One of the values you pick',
  'link.op_note.is_not': 'Anything but the values you pick',
  'link.op_note.filled': 'Any value at all. No value needed',
  'link.op_note.empty': 'Nothing was entered. No value needed',
  'link.op_note.at_least': 'That figure or more',
  'link.op_note.at_most': 'That figure or less',
  'link.op_note.exactly': 'That figure and no other',
  'link.op_note.contains': 'The word appears anywhere in the text',
  'link.op_note.not_contains': 'The word appears nowhere in the text',
  'link.op_note.is_today': 'The day it is now. No value needed',
  'link.op_note.before_today': 'Any day before today. No value needed',
  'link.op_note.after_today': 'Any day after today. No value needed',
  'link.op_note.in_next_days': 'Today and the days after it, as many as you say',
  'link.op_note.in_last_days': 'Today and the days before it, as many as you say',
  'link.op_note.on_or_before': 'That date or any day before it',
  'link.op_note.on_or_after': 'That date or any day after it',

  // the field map
  'link.map_title': 'What carries over',
  'link.map_every_field': 'Every $t(glossary.field_one) in {{record}}',
  'link.map_needed': 'needed',
  'link.map_ready': 'Every $t(glossary.field_one) it needs has an answer, so this $t(glossary.link_one) can be turned on.',
  'link.map_missing': '{{fields}} still has no answer, so nothing can be written yet.',
  'link.map_answered': '{{answered, number}} of {{total, number}} $t(glossary.field_other) answered. A $t(glossary.field_one) with no answer is left out of the $t(glossary.entry_one).',
  'link.map_pick': 'What fills {{field}}?',
  'link.map_from_field': 'A $t(glossary.field_one) on this $t(glossary.entry_one)',
  'link.map_const': 'The same value every time',
  'link.map_reference': 'A value read off the {{field}} $t(glossary.entry_one)',
  'link.map_empty': 'Leave it empty',
  'link.map_empty_note': 'The $t(glossary.entry_one) is written without this $t(glossary.field_one)',
  'link.map_empty_never': 'Not allowed',
  'link.map_empty_never_note': '{{record}} refuses an $t(glossary.entry_one) with no {{field}}',
  'link.map_no_fit': '“{{from}}” cannot fill “{{to}}”',
  'link.map_computed': 'Worked out from other $t(glossary.field_other), so nothing may write it',
  'link.map_from': 'this $t(glossary.entry_one)\u2019s {{field}}',
  'link.map_reads': 'the {{via}} $t(glossary.entry_one)\u2019s {{field}}',
  'link.map_left_empty': 'left empty',
  'link.map_fixed': 'always {{value}}',
  'link.map_typed': 'a value you type',
  'link.map_done': 'Done',
  'link.map_answer_first': 'Answer {{field}} first',

  // the sentence (packages/shared/src/link-sentence.ts): every surface says a rule this way
  'link.or_either': 'either',
  'link.sentence.when_a': 'When a {{record}} $t(glossary.entry_one) is saved',
  'link.sentence.when_an': 'When an {{record}} $t(glossary.entry_one) is saved',
  'link.sentence.when_a_with': 'When a {{record}} $t(glossary.entry_one) is saved with {{conditions}}',
  'link.sentence.when_an_with': 'When an {{record}} $t(glossary.entry_one) is saved with {{conditions}}',
  'link.sentence.adds': '{{when}}, adds its {{measure}} to {{record}}.',
  'link.sentence.takes': '{{when}}, takes its {{measure}} out of {{record}}.',
  'link.sentence.posts': '{{when}}, posts its {{measure}} to {{record}}.',
  'link.sentence.writes': '{{when}}, adds an $t(glossary.entry_one) to {{record}}.',
  // A row that takes from a total the sentence cannot name a figure for (QA rerun 1, E1).
  'link.sentence.writes_takes': '{{when}}, adds an $t(glossary.entry_one) to {{record}} that takes from it.',
  'link.clause.is': '{{field}} {{value}}',
  'link.clause.is_not': '{{field}} not {{value}}',
  'link.clause.filled': '{{field}} filled',
  'link.clause.empty': '{{field}} empty',
  'link.clause.at_least': '{{field}} at least {{value}}',
  'link.clause.at_most': '{{field}} at most {{value}}',
  'link.clause.exactly': '{{field}} exactly {{value}}',
  'link.clause.contains': '{{field}} containing {{value}}',
  'link.clause.not_contains': '{{field}} not containing {{value}}',
  'link.clause.is_today': '{{field}} today',
  'link.clause.before_today': '{{field}} before today',
  'link.clause.after_today': '{{field}} after today',
  'link.clause.in_next_days': '{{field}} in the next {{value}} days',
  'link.clause.in_last_days': '{{field}} in the last {{value}} days',
  'link.clause.on_or_before': '{{field}} on or before {{value}}',
  'link.clause.on_or_after': '{{field}} on or after {{value}}',

  // the row a link wrote
  'link.badge_auto': 'Auto',
  'link.derived_no_edit':
    'A $t(glossary.link_one) wrote this $t(glossary.entry_one), so it cannot be changed here. Change the $t(glossary.entry_one) it came from and this one follows it.',
  'link.provenance_title': 'Written by a $t(glossary.link_one)',
  // The link-only mark on a table row a link wrote: its tooltip and its name (UI quick fixes U1).
  'link.provenance_rule': 'The rule that wrote it',
  'link.provenance_source': 'The $t(glossary.entry_one) it came from',
  'link.provenance_open': 'Open it',
  'link.provenance_unknown': 'That $t(glossary.entry_one) is not on this device yet',
  'link.saved': 'Saved',
  'link.failed': 'The $t(glossary.link_one) could not be saved.',

  // A mapping that is worked out, and a condition that reads through a pointer.
  // Words from docs/design/one-formula, artboards 6 and 7.
  'link.map_formula': 'Worked out from this {{noun}}',
  'link.map_formula_note': 'Any $t(glossary.recipe_one) the formula builder can make',
  'link.condition_hop': 'the {{via}}’s {{field}}',
  'link.condition_group_own': 'On this {{noun}}',
  'link.condition_group_hop': 'On the {{field}} it points at',

  // --------------------------------------------------------- setup door
  // AI onboarding into a new business, and the proposal that ends it.
  // Words from docs/design/ai-setup-and-agent.

  // the conversation
  'setup.talk.title': 'Tell me about your work',
  'setup.talk.listening': 'Listening…',
  'setup.talk.speaking': 'Speaking…',
  'setup.talk.type_instead': 'Type instead',
  'setup.talk.placeholder': 'Type your answer',
  // The placeholder after the person picks "Other" in a chip list. It was
  // `placeholder_other`, which i18next reads as the plural form of
  // `setup.talk.placeholder` the moment anyone passes a count to it — `_one`
  // and `_other` are plural suffixes now and nothing else may wear them.
  'setup.talk.placeholder_own_words': 'Tell me in your own words',
  'setup.talk.so_far': 'What I have so far',
  'setup.talk.pending': 'Still to ask',
  'setup.talk.privacy': 'Your words are used to build your $t(glossary.record_other). Nothing is shared.',
  'setup.live.mic_blocked': 'Your browser blocked the microphone, so I switched to typing. To talk, allow the microphone from the lock icon in the address bar, then try again.',
  'setup.live.no_mic': 'No microphone found, so I switched to typing. Connect one to talk, then try again.',
  'setup.live.mic_failed': 'The microphone could not start, so I switched to typing. Try again to talk.',
  'setup.live.no_connect': 'I could not start the call: the connection dropped. Your answers above are kept. Type for now, or try the call again.',
  // The first question is drawn on the phone so the model is not asked until
  // the person has said something. Other is not sent: it only raises the
  // keyboard. build_now is the line the Build button sends into the thread.
  'setup.talk.first.question': 'What kind of work do you do?',
  'setup.talk.first.shop': 'Shop',
  'setup.talk.first.wholesale': 'Wholesale',
  'setup.talk.first.contractor': 'Contractor',
  'setup.talk.first.transport': 'Transport',
  'setup.talk.first.factory': 'Factory',
  'setup.talk.first.food': 'Food',
  'setup.talk.first.clinic': 'Clinic',
  'setup.talk.first.services': 'Services or freelance',
  'setup.talk.first.other': 'Other',
  'setup.opening.welcome': 'Welcome! Answer any of these, in any order, or skip them and just tell me about your work.',
  'setup.opening.cards': 'Questions about your work',
  'setup.opening.files.title': 'Share what you already keep',
  'setup.opening.files.sub': 'A register, Excel sheets, bills, a Tally export or your website. It saves the most questions.',
  'setup.opening.optional': 'Optional',
  'setup.opening.drop': 'Drop or choose files',
  'setup.opening.drop_now_one': 'Drop to share {{count, number}} file',
  'setup.opening.drop_now_other': 'Drop to share {{count, number}} files',
  'setup.opening.heard': 'Heard from you',
  'setup.opening.failed_one': '{{count, number}} could not be read',
  'setup.opening.failed_other': '{{count, number}} could not be read',
  'setup.opening.site_failed': 'Your website could not be read',
  'setup.opening.read_files_one': '{{count, number}} file read',
  'setup.opening.read_files_other': '{{count, number}} files read',
  'setup.opening.read_files_site_one': '{{count, number}} file and your website read',
  'setup.opening.read_files_site_other': '{{count, number}} files and your website read',
  'setup.opening.read_site': 'Your website read',
  'setup.opening.read_of_one': '{{read, number}} of {{count, number}} file read.',
  'setup.opening.read_of_other': '{{read, number}} of {{count, number}} files read.',
  'setup.opening.found': 'Found: {{what}}.',
  // One file's left-out line in the folded card, apart from its chip: the file's name, then the line (files-paging-and-fixes, Decided 7).
  'setup.opening.left_out': '{{name}}: {{line}}',
  'setup.opening.work.title': 'What kind of work is it?',
  'setup.opening.work.sub': 'Pick the closest, or say it in your words.',
  'setup.opening.pick_single': 'Pick one',
  'setup.opening.other_label': 'Tell me in a few words',
  'setup.opening.other_placeholder': 'For example, a mobile repair shop',
  'setup.opening.track.title': 'What do you need to keep track of?',
  'setup.opening.track.sub': 'Tick everything that applies, or tell me what a normal day looks like.',
  'setup.opening.pick_any': 'Pick any',
  'setup.opening.track.sales': 'Sales',
  'setup.opening.track.credit': 'Credit and dues',
  'setup.opening.track.stock': 'Stock',
  'setup.opening.track.expenses': 'Expenses',
  'setup.opening.track.staff': 'Staff pay',
  'setup.opening.track.orders': 'Orders',
  'setup.opening.from.heard': 'From what you said',
  'setup.opening.from.typed': 'From what you typed',
  'setup.opening.from.file': 'From your files',
  'setup.opening.none': 'Answer any of these, or just start talking',
  'setup.opening.answered_live_one': '{{count, number}} answered · or keep talking',
  'setup.opening.answered_live_other': '{{count, number}} answered · or keep talking',
  'setup.opening.answered_typed_one': '{{count, number}} answered · or keep typing',
  'setup.opening.answered_typed_other': '{{count, number}} answered · or keep typing',
  'setup.opening.continue': 'Continue',
  'setup.opening.say.work': 'Kind of work: {{work}}.',
  'setup.opening.say.track': 'I keep track of: {{things}}.',
  'setup.opening.say.files_only': 'I shared what I keep. Go on from that.',
  'setup.opening.skipped': 'Skipped: {{parts}}',
  'setup.opening.part.files': 'your files',
  'setup.opening.part.work': 'kind of work',
  'setup.opening.part.track': 'what to keep track of',
  'setup.opening.summary.work': 'Kind of work',
  'setup.opening.summary.track': 'Keeping track of',
  'setup.opening.summary.shared': 'Shared',
  'setup.opening.show_again': 'Show them again',
  'setup.opening.private': 'What you share stays in your workspace. Nothing is shared outside it.',
  'setup.talk.build_now':'Build it with what you have.',
  // The setup studio's gaps (docs/plans/onboarding-harness/phase-03.md, 3.2): held as data by setup-gaps.ts.
  'setup.studio.push.not_needed': 'Not needed',
  'setup.studio.q.other': 'Other…',
  'setup.studio.fail.changed': 'This needs a look: RecordOS changed since you drew it.',
  // A link the draft carries but cannot be made (16 Oct, Sharma Dairy): its reasons are the problem's own words.
  'setup.studio.unmade.link': 'This $t(glossary.link_one) can’t be made as it stands',
  'setup.studio.unmade.leave_out': 'Leave this $t(glossary.link_one) out',
  // The app the person uses, looked up (3.12): its chip and its own pages, never its name. Held as data by the Looked up part's topicKey.
  'setup.studio.looked.app': 'What the app you use keeps',
  'setup.studio.looked.app_site': 'The app’s website',
  'setup.studio.gap.name_q': 'What is this workspace called?',
  'setup.studio.gap.name_taken_q': 'You already have a workspace with this name. Use another?',
  'setup.studio.gap.other_name': 'Another name',
  'setup.studio.gap.no_record_q': 'What do you write down every day?',
  'setup.studio.gap.dangling_q': '{{record}} picks a {{field}}, but there is no {{list}} list yet. Add it?',
  'setup.studio.gap.add_list': 'Add {{list}}',
  'setup.studio.gap.credit_title': 'You give credit in {{record}}, but nothing records who paid back',
  'setup.studio.gap.credit_q': 'When a customer pays back, where should it go?',
  'setup.studio.gap.credit_fix': 'Add payments received',
  'setup.studio.gap.credit_both': 'Write it in {{record}} as got',
  // The money question of each money record (2.4 of docs/plans/ledger.md); the answer picks how its money counts. Never says ledger, posting or debit (L19).
  'setup.studio.gap.money_kind_q': 'Is {{record}} money in, money out, money owed to you, or money you owe?',
  'setup.studio.gap.money_kind_in': 'Money in',
  'setup.studio.gap.money_kind_out': 'Money out',
  'setup.studio.gap.money_kind_owed_to_you': 'Money owed to you',
  'setup.studio.gap.money_kind_you_owe': 'Money you owe',
  'setup.studio.gap.money_kind_both': 'Money both ways',
  'setup.studio.gap.money_kind_none': 'It moves no money',
  // Asked once (2.4): a yes puts For home at the top of Money out, so money taken home is never counted as a cost.
  'setup.studio.gap.for_home_q': 'Do you take money from the business for home?',
  'setup.studio.gap.for_home_yes': 'Yes, sometimes',
  'setup.studio.gap.for_home_no': 'No',
  // A payment mode whose name does not say what it is (2.7, L34): money the business has, or money it owes.
  'setup.studio.gap.account_type_q': 'Is {{account}} money you have, like a bank account, or money you owe, like an overdraft?',
  'setup.studio.gap.account_have': 'Money you have',
  'setup.studio.gap.account_owe': 'Money you owe',
  'setup.studio.gap.payment_q': 'How do customers pay for {{record}}?',
  'setup.studio.gap.payment_fix': 'Cash, UPI or card',
  'setup.studio.gap.rate_q': 'Should each line have a rate, so the total works itself out?',
  // The setup walk's blocks (docs/plans/setup-walk.md, phase 2): what the owner could not finish, asked once; a chip is words OS reads.
  'setup.studio.gap.walk_amount_title': '{{record}} cannot work out its {{field}}',
  'setup.studio.gap.walk_amount_q': '{{field}} on {{record}} reads something that is not there, so each entry would save with no amount. Where does the price come from?',
  'setup.studio.gap.walk_amount_fix': 'Show me where the price comes from',
  'setup.studio.gap.walk_no_price_title': '{{record}} has no price',
  'setup.studio.gap.walk_no_price_q': 'You can add to {{record}}, but nothing says what it costs or what is still owed. Add a price and a total?',
  'setup.studio.gap.walk_no_price_fix': 'Add a price and a total',
  'setup.studio.gap.walk_rows_title': 'Some of {{list}} have no {{field}}',
  'setup.studio.gap.walk_rows_q': '{{rows}} have no {{field}}, so an entry in {{record}} that picks one has no amount. Type their {{field}}?',
  'setup.studio.gap.walk_rows_fix': 'Ask me for each one',
  'setup.studio.gap.walk_party_title': 'Nobody owes what {{record}} leaves unpaid',
  'setup.studio.gap.walk_party_q': 'An entry in {{record}} can be paid in part, and nobody is named as owing the rest. Who owes it?',
  'setup.studio.gap.walk_party_fix': 'Name who owes it',
  'setup.studio.gap.walk_settle_title': 'Nothing takes what is owed in {{record}}',
  'setup.studio.gap.walk_settle_q': 'Money can be owed in {{record}}, and no record takes the rest when it is paid. Add a record of payments?',
  'setup.studio.gap.walk_settle_fix': 'Add a record of payments',
  'setup.studio.gap.rate_fix': 'Add rate and total',
  // Never says stock is not in RecordOS (R4 of docs/plans/qa-fixes-2.md): a one-item sale's stock is a link RecordOS builds.
  'setup.studio.gap.stock_q': 'Show stock received beside items sold, on one card?',
  'setup.studio.gap.stock_fix': 'Add that card',
  'setup.studio.gap.watch_q': 'You check {{what}}. Put it on top?',
  'setup.studio.gap.watch_fix': 'Put it on top',
  'setup.studio.gap.role_title': '{{role}} can see every entry in {{record}}',
  'setup.studio.gap.role_q': 'Should {{role}} see only {{filter}}?',
  'setup.studio.gap.role_fix': 'Only {{filter}}: note it as not possible yet',
  'setup.studio.gap.role_all': 'They see everything',
  'setup.studio.gap.conflict_use': 'Use {{name}}',
  'setup.studio.gap.unplaced_q': '{{name}} is read, but nothing on the canvas uses it yet. Where does it go?',
  'setup.studio.gap.place_it': 'Put it to use',
  'setup.studio.gap.rows_q': 'Where do the rows of {{name}} go?',
  'setup.studio.gap.rows_into': 'Into {{record}}',
  'setup.studio.gap.rows_leave': 'Leave them out',
  'setup.studio.gap.rows_leave_count_one': 'Leave out its {{count, number}} row',
  'setup.studio.gap.rows_leave_count_other': 'Leave out its {{count, number}} rows',
  'setup.studio.gap.rows_kinds_q_one': 'The rows of {{name}} hold {{count, number}} kind of entry. Where does it go?',
  'setup.studio.gap.rows_kinds_q_other': 'Each row of {{name}} holds up to {{count, number}} kinds of entries. Bring each kind into its own?',
  'setup.studio.gap.rows_sheet_q': 'Where do the rows of {{sheet}} in {{name}} go?',
  'setup.studio.gap.rows_kinds_sheet_q_one': 'The rows of {{sheet}} in {{name}} hold {{count, number}} kind of entry. Where does it go?',
  'setup.studio.gap.rows_kinds_sheet_q_other': 'Each row of {{sheet}} in {{name}} holds up to {{count, number}} kinds of entries. Bring each kind into its own?',
  'setup.studio.gap.leave_out': 'Leave it out',
  'setup.studio.gap.orphan_q': 'Nothing picks from {{list}} yet. Keep it?',
  // A party list already has a field named like a money figure Build adds (Billed, Received, Pending); Build keeps the person's field.
  'setup.studio.gap.figure_label_taken_q': '{{list}} already has a field called {{label}}. Build keeps it as it is, and gives the money figure from your accounts its own name.',
  'setup.studio.gap.remove_it': 'Remove it',
  'setup.studio.gap.card_q': '{{record}} has nothing on top yet. Show {{what}}?',
  'setup.studio.gap.documents_q': 'With your GSTIN, invoices from {{record}} will need a GST rate on the lines, GSTIN and State on the party list, and HSN on items. Add them now?',
  'setup.studio.gap.documents_fix': 'Add these fields',
  'setup.studio.gap.not_now': 'Not now',
  // Who else writes entries (S8): asked once; Only me leaves it, the other chip is words for OS.
  'setup.studio.gap.who_records_q': 'Does anyone else write $t(glossary.entry_other) here, like staff or a family member?',
  'setup.studio.gap.who_records_others': 'Others write $t(glossary.entry_other) too',
  'setup.studio.gap.who_records_only_me': 'Only me',
  'setup.studio.gap.fixed_fill_title': '{{from}} marks every {{field}} in {{to}} as {{value}}',
  'setup.studio.gap.fixed_fill_q': '{{from}} also takes {{others}}, and nothing on this $t(glossary.link_one) keeps it to {{value}}, so those $t(glossary.entry_other) land in {{to}} marked {{value}}. To keep each one as it was paid, open the $t(glossary.link_one) and copy {{field}} from {{from}}, or add the condition {{field}} is {{value}} back. Leave it if that is what you want.',
  'setup.studio.gap.sign_type_title': 'This $t(glossary.link_one) says it adds, but it writes {{value}}',
  'setup.studio.gap.sign_type_title_in': 'This $t(glossary.link_one) says it takes, but it writes {{value}}',
  'setup.studio.gap.sign_type_out_q': '{{from}} fills {{field}} in {{to}} with {{value}}, which takes money out, while the $t(glossary.link_one) is set to add. {{to}} goes by {{field}}, so its totals are right, but the $t(glossary.link_one) is described the wrong way round. Open the $t(glossary.link_one) and set it to take.',
  'setup.studio.gap.sign_type_in_q': '{{from}} fills {{field}} in {{to}} with {{value}}, which puts money in, while the $t(glossary.link_one) is set to take. {{to}} goes by {{field}}, so its totals are right, but the $t(glossary.link_one) is described the wrong way round. Open the $t(glossary.link_one) and set it to add.',
  // A record that counts no money is never said to take money out (18 Oct, Sharma Dairy: Milk Movement counts litres).
  'setup.studio.gap.sign_type_out_count_q': '{{from}} fills {{field}} in {{to}} with {{value}}, which takes away from {{to}}, while the $t(glossary.link_one) is set to add. {{to}} goes by {{field}}, so its totals are right, but the $t(glossary.link_one) is described the wrong way round. Set it to take.',
  'setup.studio.gap.sign_type_in_count_q': '{{from}} fills {{field}} in {{to}} with {{value}}, which adds to {{to}}, while the $t(glossary.link_one) is set to take. {{to}} goes by {{field}}, so its totals are right, but the $t(glossary.link_one) is described the wrong way round. Set it to add.',
  'setup.studio.gap.sign_type_take': 'Set it to take',
  'setup.studio.gap.sign_type_add': 'Set it to add',
  // A held link kept whole (D5 of docs/plans/qa-fixes-dairy.md): what its source holds that it does not copy.
  'setup.studio.gap.link_fill_title': 'This $t(glossary.link_one) leaves {{fields}} empty in {{to}}',
  'setup.studio.gap.link_fill_q': '{{from}} holds {{fields}} too. Copy {{fields}} from {{from}} on each $t(glossary.entry_one) this $t(glossary.link_one) posts into {{to}}?',
  'setup.studio.gap.link_fill_fix': 'Copy {{fields}}',
  'setup.studio.gap.link_carry_q': '{{from}} has its own {{choice}}, but this $t(glossary.link_one) writes {{value}} for every $t(glossary.entry_one), so the ones that go the other way land in {{to}} as {{value}} too. Copy {{choice}} into {{field}} instead?',
  'setup.studio.gap.link_carry_fix': 'Copy {{choice}}',
  // Two records doing one job (D10 of docs/plans/qa-fixes-dairy.md).
  'setup.studio.gap.same_job_title': '{{from}} and {{to}} keep the same thing',
  'setup.studio.gap.same_job_q': '{{from}} has its own {{choice}}, and each of its $t(glossary.entry_other) is copied into {{to}} as {{value}}, so the two $t(glossary.record_other) keep the same thing twice. Remove {{to}}, or keep both?',
  'setup.studio.gap.same_job_fields_q': '{{from}} and {{to}} hold the same $t(glossary.field_other), and each goes in and out by its own choice, so the two $t(glossary.record_other) keep the same thing twice. Remove {{to}}, or keep both?',
  // Two records fed the same way from one source: every one of its entries lands in both.
  'setup.studio.gap.same_job_fed_q': '{{source}} copies each of its $t(glossary.entry_other) into both {{from}} and {{to}} the same way, and the two hold the same $t(glossary.field_other), so they keep the same thing twice. Remove {{to}}, or keep both?',
  'setup.studio.gap.same_job_remove': 'Remove {{record}}',
  'setup.studio.gap.same_job_keep': 'Keep both',
  'setup.studio.edit.not_yet_type': 'Changing what {{field}} holds is not possible here yet: its $t(glossary.entry_other) would not fit. Add a new $t(glossary.field_one) instead.',
  'setup.studio.edit.not_yet_formula': 'Changing how {{field}} is worked out is not possible here yet. Change it in Customize on its $t(glossary.record_one) instead.',
  'setup.studio.edit.not_yet_lines': 'Changing the lines inside {{field}} is not possible here yet. Change them in Customize on its $t(glossary.record_one) instead.',
  'setup.studio.edit.not_yet_role': 'Roles are changed on Team.',
  'setup.studio.edit.not_yet_currency': 'The money a workspace counts in cannot change yet. Set it back to build the rest.',
  'setup.studio.edit.not_yet_kept': '{{name}} is set up in a way Edit cannot change yet. Change it in Customize on its $t(glossary.record_one) instead.',
  'setup.studio.edit.not_yet_money': 'How {{name}} counts money is not something Edit can build yet, so Build again leaves it as it is.',
  'setup.studio.edit.not_yet_approval': 'Approvals on {{name}} are not built here yet. Set them in the $t(glossary.record_one) settings, under Approvals, once it is built.',
  'setup.studio.edit.not_yet_task': 'The tasks {{name}} gives people are not built here yet, so Build again leaves them out.',
  'setup.studio.edit.not_yet_part.type_change': 'what it holds',
  'setup.studio.edit.not_yet_part.formula_change': 'how it is worked out',
  'setup.studio.edit.not_yet_part.lines_change': 'its lines',
  'setup.studio.edit.not_yet_part.role_change': 'a role',
  'setup.studio.edit.not_yet_part.currency_change': 'the money it counts in',
  'setup.studio.edit.not_yet_part.kept_part': 'how it is set up',
  'setup.studio.edit.not_yet_part.money_change': 'how it counts money',
  'setup.studio.edit.not_yet_part.approval_change': 'its approvals',
  'setup.studio.edit.not_yet_part.task_change': 'its tasks',
  'setup.studio.edit.cannot_build_count_one': '{{count, number}} change can’t be built here yet',
  'setup.studio.edit.cannot_build_count_other': '{{count, number}} changes can’t be built here yet',
  'setup.studio.edit.cannot_build_short': 'Can’t build yet · {{count, number}}',
  'setup.studio.edit.cannot_build_tip': '{{parts}}. Open it to see what to do instead.',
  'setup.studio.edit.cannot_build_title_one': 'This {{count, number}} change can’t be built here yet',
  'setup.studio.edit.cannot_build_title_other': 'These {{count, number}} changes can’t be built here yet',
  'setup.studio.edit.cannot_build_body': 'Nothing in {{name}} changes. Each one says what to do instead.',
  'setup.studio.gap.card_fix': 'Show {{what}}',
  'setup.studio.gap.what_total': 'today’s total',
  'setup.studio.gap.what_count': 'today’s count',
  'setup.talk.send': 'Send',
  'setup.talk.empty': 'Nothing came back for that. Try once more?',
  'setup.talk.proposal_open': 'Look it over',
  'setup.talk.repair_request': 'The setup could not be created: {{problems}} Fix each one and propose again.',

  // The call: voice as a mode of its own (docs/plans/done/voice-setup-call.md).
  // The greeting is spoken by the phone, so the model never sees it. It asks
  // about the work and what needs keeping track of, not about money: a
  // register, a stock count or a visit log has no money in it, and how they
  // are paid is one question the interviewer asks later, where it applies.
  // Every word under the mic says what a tap does at that moment, and nothing
  // here is a tool name, a slot or a count of anything the person did not say.
  'setup.call.greeting': 'Tell me about your work. What do you do all day, and what do you need to keep track of?',
  'setup.call.thinking': 'Thinking…',
  'setup.call.known': '{{known, number}} of {{needed, number}} known',
  'setup.call.cut_in': 'Tap to cut in',
  'setup.call.tap_when_done': 'Tap when done',
  'setup.call.tap_to_talk': 'Tap to talk',
  'setup.call.paused': 'Paused',
  'setup.call.fix': 'Fix',
  'setup.call.build': 'Build my setup',
  'setup.call.waiting': 'Closed for now',
  'setup.call.are_you_there': 'Are you still there?',
  'setup.call.no_words': 'I did not catch that. Say it again, or type it.',
  // The final screen, reached from the call: one sentence is spoken as the
  // screen opens, and the footnote counts what is switched off. `{{things}}` is a count in words: "One row", "Two rows".
  'setup.call.dropped': '{{things}} switched off. Nothing is created until you tap.',
  'setup.call.here_it_is': 'Here it is. Switch off anything you do not want, then build it.',
  // Spoken and shown once the gatherer has every slot the sector needs: it
  // asks nothing more, and the button under it is the next thing to do.
  'setup.call.ready': 'I have what I need. Tap Build my setup when you are ready.',

  // The language the agent speaks, asked before the call (docs/design/agent-language).
  // The picker's own words are English like the rest of the app; only what the
  // agent says and writes changes.
  'setup.language.title': 'Which language should I speak?',
  'setup.language.subtitle': 'Answer in the same one. You can change it during the call.',
  'setup.language.hear': 'Hear it',
  'setup.language.playing': 'Playing…',
  'setup.language.sample_failed': 'The sample could not play. The voice still works on the call.',
  'setup.language.start': 'Start talking',
  'setup.language.footnote': 'Only what I say and write changes. The rest of the app stays as it is.',
  'setup.language.your_phone': 'Your phone',
  'setup.language.change': 'Change language',

  // The five sentences the phone speaks WITHOUT a model call, once per language
  // the agent can be asked to speak. English is the key above; every other
  // language sits under it by its BCP 47 tag (agent-language.ts, spokenKey).
  // These are the only sentences translated by hand: everything else the
  // agent says comes from the model, in the language it was told.
  'setup.call.greeting.hi': 'अपने काम के बारे में बताइए। आप दिन भर क्या करते हैं, और किन चीज़ों का हिसाब रखना है?',
  'setup.call.are_you_there.hi': 'क्या आप अभी भी वहाँ हैं?',
  'setup.call.no_words.hi': 'मुझे सुनाई नहीं दिया। फिर से कहिए, या टाइप कर दीजिए।',
  'setup.call.here_it_is.hi': 'यह रहा। जो नहीं चाहिए उसे बंद कर दीजिए, फिर बनाइए।',
  'setup.call.ready.hi': 'मेरे पास ज़रूरी जानकारी है। तैयार हों तो Build my setup दबाइए।',
  'setup.call.greeting.hi-Latn': 'Apne kaam ke baare mein bataiye. Aap din bhar kya karte hain, aur kin cheezon ka hisaab rakhna hai?',
  'setup.call.are_you_there.hi-Latn': 'Kya aap abhi bhi wahan hain?',
  'setup.call.no_words.hi-Latn': 'Mujhe sunai nahi diya. Phir se kahiye, ya type kar dijiye.',
  'setup.call.here_it_is.hi-Latn': 'Yeh raha. Jo nahi chahiye use band kar dijiye, phir build kariye.',
  'setup.call.ready.hi-Latn': 'Mere paas zaroori jaankari hai. Taiyaar hon to Build my setup dabaiye.',
  'setup.call.greeting.mr': 'तुमच्या कामाबद्दल सांगा. तुम्ही दिवसभर काय करता, आणि कशाची नोंद ठेवायची आहे?',
  'setup.call.are_you_there.mr': 'तुम्ही अजून तिथे आहात का?',
  'setup.call.no_words.mr': 'मला ऐकू आलं नाही. पुन्हा सांगा, किंवा टाइप करा.',
  'setup.call.here_it_is.mr': 'हे घ्या. जे नको ते बंद करा, मग तयार करा.',
  'setup.call.ready.mr': 'मला हवी ती माहिती मिळाली. तयार असाल तेव्हा Build my setup दाबा.',
  'setup.call.greeting.gu': 'તમારા કામ વિશે કહો. તમે દિવસભર શું કરો છો, અને શેનો હિસાબ રાખવો છે?',
  'setup.call.are_you_there.gu': 'તમે હજી ત્યાં છો?',
  'setup.call.no_words.gu': 'મને સંભળાયું નહીં. ફરી કહો, અથવા ટાઇપ કરો.',
  'setup.call.here_it_is.gu': 'આ રહ્યું. જે ન જોઈએ તે બંધ કરો, પછી બનાવો.',
  'setup.call.ready.gu': 'મારી પાસે જરૂરી માહિતી છે. તૈયાર હો ત્યારે Build my setup દબાવો.',
  'setup.call.greeting.bn': 'আপনার কাজের কথা বলুন। আপনি সারাদিন কী করেন, আর কিসের হিসাব রাখতে চান?',
  'setup.call.are_you_there.bn': 'আপনি এখনও আছেন?',
  'setup.call.no_words.bn': 'আমি শুনতে পাইনি। আবার বলুন, বা টাইপ করুন।',
  'setup.call.here_it_is.bn': 'এই যে। যা চান না তা বন্ধ করুন, তারপর তৈরি করুন।',
  'setup.call.ready.bn': 'আমার যা দরকার তা আছে। প্রস্তুত হলে Build my setup চাপুন।',
  'setup.call.greeting.ta': 'உங்கள் வேலையைப் பற்றி சொல்லுங்கள். நீங்கள் நாள் முழுவதும் என்ன செய்கிறீர்கள், எதைக் கணக்கு வைக்க வேண்டும்?',
  'setup.call.are_you_there.ta': 'நீங்கள் இன்னும் இருக்கிறீர்களா?',
  'setup.call.no_words.ta': 'எனக்குக் கேட்கவில்லை. மீண்டும் சொல்லுங்கள், அல்லது தட்டச்சு செய்யுங்கள்.',
  'setup.call.here_it_is.ta': 'இதோ. வேண்டாததை அணைத்துவிடுங்கள், பிறகு உருவாக்குங்கள்.',
  'setup.call.ready.ta': 'எனக்குத் தேவையானது கிடைத்துவிட்டது. தயாரானதும் Build my setup ஐத் தட்டுங்கள்.',
  'setup.call.greeting.te': 'మీ పని గురించి చెప్పండి. మీరు రోజంతా ఏం చేస్తారు, ఏవేవి లెక్కలో ఉంచాలి?',
  'setup.call.are_you_there.te': 'మీరు ఇంకా ఉన్నారా?',
  'setup.call.no_words.te': 'నాకు వినిపించలేదు. మళ్ళీ చెప్పండి, లేదా టైప్ చేయండి.',
  'setup.call.here_it_is.te': 'ఇదిగో. వద్దనుకున్నవి ఆఫ్ చేయండి, తరువాత సృష్టించండి.',
  'setup.call.ready.te': 'నాకు కావలసినది ఉంది. సిద్ధమైనప్పుడు Build my setup నొక్కండి.',
  'setup.call.greeting.kn': 'ನಿಮ್ಮ ಕೆಲಸದ ಬಗ್ಗೆ ಹೇಳಿ. ನೀವು ದಿನವಿಡೀ ಏನು ಮಾಡುತ್ತೀರಿ, ಮತ್ತು ಯಾವುದರ ಲೆಕ್ಕ ಇಡಬೇಕು?',
  'setup.call.are_you_there.kn': 'ನೀವು ಇನ್ನೂ ಇದ್ದೀರಾ?',
  'setup.call.no_words.kn': 'ನನಗೆ ಕೇಳಿಸಲಿಲ್ಲ. ಮತ್ತೆ ಹೇಳಿ, ಅಥವಾ ಟೈಪ್ ಮಾಡಿ.',
  'setup.call.here_it_is.kn': 'ಇಲ್ಲಿದೆ. ಬೇಡವಾದದ್ದನ್ನು ಆಫ್ ಮಾಡಿ, ನಂತರ ರಚಿಸಿ.',
  'setup.call.ready.kn': 'ನನಗೆ ಬೇಕಾದದ್ದು ಸಿಕ್ಕಿದೆ. ಸಿದ್ಧವಾದಾಗ Build my setup ಒತ್ತಿ.',
  'setup.call.greeting.ml': 'നിങ്ങളുടെ ജോലിയെക്കുറിച്ച് പറയൂ. നിങ്ങൾ ദിവസം മുഴുവൻ എന്ത് ചെയ്യുന്നു, എന്തിന്റെ കണക്ക് സൂക്ഷിക്കണം?',
  'setup.call.are_you_there.ml': 'നിങ്ങൾ ഇപ്പോഴും അവിടെയുണ്ടോ?',
  'setup.call.no_words.ml': 'എനിക്ക് കേട്ടില്ല. വീണ്ടും പറയൂ, അല്ലെങ്കിൽ ടൈപ്പ് ചെയ്യൂ.',
  'setup.call.here_it_is.ml': 'ഇതാ. വേണ്ടാത്തത് ഓഫ് ചെയ്യൂ, എന്നിട്ട് സൃഷ്ടിക്കൂ.',
  'setup.call.ready.ml': 'എനിക്ക് വേണ്ടത് കിട്ടി. തയ്യാറായാൽ Build my setup ടാപ്പ് ചെയ്യൂ.',
  'setup.call.greeting.pa': 'ਆਪਣੇ ਕੰਮ ਬਾਰੇ ਦੱਸੋ। ਤੁਸੀਂ ਸਾਰਾ ਦਿਨ ਕੀ ਕਰਦੇ ਹੋ, ਅਤੇ ਕਿਸ ਦਾ ਹਿਸਾਬ ਰੱਖਣਾ ਹੈ?',
  'setup.call.are_you_there.pa': 'ਕੀ ਤੁਸੀਂ ਅਜੇ ਵੀ ਉੱਥੇ ਹੋ?',
  'setup.call.no_words.pa': 'ਮੈਨੂੰ ਸੁਣਿਆ ਨਹੀਂ। ਦੁਬਾਰਾ ਕਹੋ, ਜਾਂ ਟਾਈਪ ਕਰੋ।',
  'setup.call.here_it_is.pa': 'ਇਹ ਰਿਹਾ। ਜੋ ਨਹੀਂ ਚਾਹੀਦਾ ਉਹ ਬੰਦ ਕਰੋ, ਫਿਰ ਬਣਾਓ।',
  'setup.call.ready.pa': 'ਮੇਰੇ ਕੋਲ ਲੋੜੀਂਦੀ ਜਾਣਕਾਰੀ ਹੈ। ਤਿਆਰ ਹੋਵੋ ਤਾਂ Build my setup ਦਬਾਓ।',

  // The interview: a why under every question, a ticked set sent as one line,
  // and the understanding drawn as a card (docs/plans/onboarding-interview-and-experts.md).
  'setup.talk.why': 'Why I ask',
  'setup.talk.send_picks': 'Send these',
  'setup.talk.just_noted': 'Just noted',
  'setup.talk.from_expert': 'From the {{expert}} specialist',
  'setup.build.checking': 'Checking it against what you said',
  'agent.coming_next': 'Coming next',
  // The slots, in asking order: the business first, money after it and only
  // where money changes hands. Where the records are kept today is not asked.
  'setup.slot.does': 'The work',
  'setup.slot.name': 'Called',
  'setup.slot.things': 'You track',
  'setup.slot.actions': 'Each day',
  'setup.slot.watch': 'Watch',
  'setup.slot.money': 'Money',
  'setup.slot.volume': 'How busy',
  'setup.slot.currency': 'Currency',
  'setup.slot.does.ask': 'what kind of $t(glossary.business_one) this is',
  'setup.slot.name.ask': 'what your $t(glossary.business_one) is called',
  'setup.slot.things.ask': 'what you keep track of',
  'setup.slot.actions.ask': 'what you do in a day',
  'setup.slot.watch.ask': 'the number you want on top',
  'setup.slot.money.ask': 'how you are paid, if money changes hands',
  'setup.slot.volume.ask': 'how busy a day is',
  'setup.slot.currency.ask': 'the currency',
  // The counting words for a setup's parts are `glossary.record_*` and its
  // five siblings. They were the same six nouns twice over, so they moved to
  // the one place a noun is spelled.
  // One to nine in words, so a footnote reads as a sentence and not as a
  // count. Ten and up are digits, as they are in any register.
  'setup.words.number.1': 'One',
  'setup.words.number.2': 'Two',
  'setup.words.number.3': 'Three',
  'setup.words.number.4': 'Four',
  'setup.words.number.5': 'Five',
  'setup.words.number.6': 'Six',
  'setup.words.number.7': 'Seven',
  'setup.words.number.8': 'Eight',
  'setup.words.number.9': 'Nine',
  // The part a slot still lacks when it already holds something.
  'setup.slot.things.ask_more': 'which of those is a $t(glossary.list_one), a $t(glossary.record_one) or a $t(glossary.field_one)',
  'setup.slot.money.ask_more': 'whether $t(glossary.person_other) pay in part or later',
  'setup.slot.money.ask_flows': 'what is paid or collected',
  'setup.words.in': 'In: {{things}}',
  'setup.words.out': 'Out: {{things}}',
  'setup.words.part_paid': 'paid in part',
  'setup.words.paid_whole': 'paid in full',
  'setup.words.credit': 'some pay later',
  'setup.words.paid_by': 'by {{modes}}',
  'setup.words.no_money': 'No money changes hands',
  'setup.words.volume.few': 'A few $t(glossary.entry_other) a day',
  'setup.words.volume.daily': 'Every day',
  'setup.words.volume.busy': 'All day long',
  'setup.words.kind.list': 'list',
  'setup.words.kind.record': 'record',
  'setup.words.kind.field': 'field',
  'setup.words.kind.field_on': 'field on {{record}}',

  // building
  'setup.build.title': 'Building your $t(glossary.record_other)',
  'setup.build.subtitle': 'Nothing is saved yet. You see all of it before I create anything.',
  'setup.build.records': 'Records',
  'setup.build.fields': 'Fields',
  'setup.build.totals': 'Totals',
  'setup.build.lists': 'Lists, filled in',
  'setup.build.links': 'Links between $t(glossary.record_other)',
  'setup.build.cards': 'Insights',

  // the proposal
  'setup.plan.title': 'Here is what I would create',
  // The card's one write says Build on both platforms, as the web's does (r3
  // Setup, item 13); "Build my setup" stays the step before, which draws the
  // setup up and makes nothing. The key keeps its old name, as keys do.
  'setup.plan.create': 'Build',
  'setup.plan.change': 'Change something',
  'setup.plan.records_label': 'Records $t(glossary.person_other) write into',
  'setup.plan.lists_label': 'Lists $t(glossary.person_other) pick from',
  'setup.plan.cards_label': 'What you see on top',
  'setup.plan.money_in': 'Money in',
  'setup.plan.money_out': 'Money out',
  'setup.plan.count_only': 'A count',
  'setup.plan.seeded': 'filled in for you',
  'setup.plan.worked_out': 'Worked out',
  'setup.plan.no_money_in_list': 'A $t(glossary.list_one) holds no money $t(glossary.field_one). The $t(glossary.record_other) hold the money.',
  'setup.plan.recipe': 'Recipe',
  'setup.plan.fields_count': '{{count, number}} $t(glossary.field_other)',
  'setup.plan.one_row_is': 'Each row is one {{entry}}',
  'setup.plan.keep_record': 'Keep this $t(glossary.record_one)',
  'setup.plan.engine_title': 'What the $t(glossary.record_one) does with these',
  'setup.plan.engine.title': 'The headline of an $t(glossary.entry_one)',
  'setup.plan.engine.time': 'The day it lands under',
  'setup.plan.engine.measure': 'The number it totals',
  'setup.plan.engine.sign': 'Which way that number points',
  'setup.plan.engine.focus': 'The $t(glossary.field_one) that opens first',
  // The switches on the proposal: why a row cannot go, and the builder's notes.
  'setup.plan.cannot_drop_list': '{{record}} picks its {{field}} from it',
  'setup.plan.cannot_drop_last': 'The setup needs at least one $t(glossary.record_one)',
  'setup.plan.notes': 'Notes from the build',
  'setup.coach.build': 'Build',
  'setup.coach.landed': '{{name}} is ready. Want to add your first sale?',
  // Drafts of the landing greeting in the ten other languages, spoken by the
  // voice as the call opens in the new business (B12). Revisited in 9.2.
  'setup.coach.landed.hi': '{{name}} तैयार है। पहली बिक्री जोड़ना चाहेंगे?',
  'setup.coach.landed.hi-Latn': '{{name}} taiyaar hai. Pehli sale jodna chahenge?',
  'setup.coach.landed.mr': '{{name}} तयार आहे. पहिली विक्री जोडायची का?',
  'setup.coach.landed.gu': '{{name}} તૈયાર છે. પહેલું વેચાણ ઉમેરવું છે?',
  'setup.coach.landed.bn': '{{name}} তৈরি। প্রথম বিক্রি যোগ করবেন?',
  'setup.coach.landed.ta': '{{name}} தயார். முதல் விற்பனையைச் சேர்க்கலாமா?',
  'setup.coach.landed.te': '{{name}} సిద్ధంగా ఉంది. మొదటి అమ్మకాన్ని జోడించాలా?',
  'setup.coach.landed.kn': '{{name}} ಸಿದ್ಧವಾಗಿದೆ. ಮೊದಲ ಮಾರಾಟವನ್ನು ಸೇರಿಸಬೇಕೆ?',
  'setup.coach.landed.ml': '{{name}} തയ്യാറാണ്. ആദ്യ വിൽപ്പന ചേർക്കണോ?',
  'setup.coach.landed.pa': '{{name}} ਤਿਆਰ ਹੈ। ਪਹਿਲੀ ਵਿਕਰੀ ਜੋੜਨੀ ਹੈ?',
  'setup.coach.something_else': 'Something else',
  'setup.coach.shape.credit': 'Credit',
  'setup.coach.diff.added': '{{name}} added',
  'setup.coach.diff.added_picked': '{{name}} added, {{record}} picks from it',
  'setup.coach.diff.off': '{{name}} off',
  'setup.coach.diff.now_list': '{{name}} is now a $t(glossary.list_one)',
  'setup.coach.diff.now_record': '{{name}} is now a $t(glossary.record_one)',
  'setup.coach.diff.picks': '{{record}} picks from {{list}}',
  'setup.coach.diff.renamed': '{{from}} is now {{to}}',
  'setup.coach.diff.seeded': '{{name}} filled in for you',
  'setup.coach.diff.top_added': '{{name}} added on top',
  'setup.coach.diff.top_off': '{{name}} off the top',
  'setup.coach.diff.unseeded': '{{name}} starts empty',
  'setup.coach.diff.stops_picking': '{{record}} no longer picks from {{list}}',
  'setup.coach.card.links': 'Links',
  'setup.coach.card.lists': 'Lists',
  'setup.coach.card.records': 'Records',
  'setup.coach.shape.building': 'Building',
  'setup.coach.shape.empty': 'What I learn about your work shows up here, as I learn it.',
  'setup.coach.shape.not_yet': 'Not yet',
  'setup.coach.shape.ready': 'Ready to build',
  'setup.coach.shape.unnamed': 'Your $t(glossary.business_one)',
  'setup.coach.title': 'New $t(glossary.business_one)',
  'setup.coach.here': 'Setting up {{name}}',
  'setup.coach.here_blank': 'Setting up your $t(glossary.business_one)',
  'setup.coach.card.changed': 'Changed: {{what}}',
  'setup.coach.card.earlier': 'Earlier version · {{what}}',
  'setup.coach.card.on_top': 'On top',
  'setup.coach.couldnt_create': 'I couldn’t create it yet',
  'setup.coach.created': '{{name}} is ready.',
  'setup.coach.creating': 'Building…',
  'setup.coach.fix_it': 'Fix it for me',
  'setup.coach.open': 'Open {{name}}',
  'setup.coach.review.say': 'Switch off what you don’t want, or tell me what to change.',
  'setup.coach.language.restart': 'I carry on in the new language from here.',
  'setup.coach.landed.add': 'Add a {{entry}}',
  'setup.coach.landed.add_list': 'Add to {{list}}',
  'setup.coach.lost': 'The call dropped. Everything you said is kept.',
  'setup.coach.leave.body': 'Nothing is created yet. Come back, and I pick up from what you told me.',
  'setup.coach.leave.go': 'Leave',
  'setup.coach.leave.stay': 'Keep going',
  'setup.coach.leave.title': 'Leave setup?',
  'setup.coach.card_up': 'Your setup is in the card above. Build it, look it over, or tell me what to change.',


  // ---------------------------------------------------------- assistant
  // The agent behind the header door on every screen once a business exists.
  // The first block is the design's word table, verbatim
  // (docs/design/ai-setup-and-agent/parts.mjs, NEW_WORDS); the rest is what the
  // built screens needed beyond it.
  'agent.name': 'OS',
  'agent.open': 'Ask OS',
  'agent.here': 'In {{place}}',
  'agent.placeholder': 'Ask, or tell me what to do',
  'agent.suggest_label': 'Try',
  'agent.thinking': 'Working…',
  'agent.confirm': 'Do it',
  'agent.confirm_add_field': 'Add this $t(glossary.field_one)',
  'agent.confirm_link': 'Turn this on',
  'agent.confirm_entry': 'Save this $t(glossary.entry_one)',
  'agent.add_to_list': 'Add to {{record}}',
  'agent.confirm_card': 'Put it on top',
  'agent.edit_first': 'Change it first',
  'agent.cancel': 'No, leave it',
  'agent.undo': 'Undo',
  'agent.never_alone': 'Nothing is saved until you say yes or tap.',
  'agent.not_allowed': 'You cannot do that in this $t(glossary.business_one), so neither can I.',
  'agent.ask_owner': 'Ask an owner',
  'agent.pin_here': 'Pin this to {{place}}',
  'agent.spoke': 'You said',
  'agent.record_call.connecting': 'Getting your $t(glossary.business_one) ready…',
  'agent.record_call.draw_up': 'Draw it up',
  'agent.record_call.closed': 'The call ended before anything was drawn up.',
  'agent.record_call.failed': 'The call could not start. Try again, or start from blank.',
  // Create-with-voice opening line. Composed on the server from the brief's
  // real names, then quoted so the model says it (and translates it) as-is.
  'agent.record_call.greeting.empty': 'Nothing kept yet. What do you want to keep track of?',
  'agent.record_call.greeting.keeping': "You're keeping {{names}}. What do you want to add?",
  'agent.record_call.greeting.list': "You're keeping {{names}}. What list do you want?",
  'agent.record_call.and': 'and',
  'agent.voice.draw_up': 'Draw up what was said.',
  'agent.stop': 'Stop',
  'agent.not_saved_yet': 'Not saved yet',
  'agent.not_sent_yet': 'Not sent yet',
  'agent.new_entry': 'New {{entry}}',
  'agent.change_record': 'Change {{record}}',
  'agent.recipe_changes': 'Recipe changes',
  'agent.also_lands': 'Also lands in',
  // the sheet's own chrome
  'agent.send': 'Send',
  'agent.close': 'Close',
  // the orb: the door as a floating button, and the sheet it opens listening
  // (design/explorations/ai-orb, the Words board). The orb's own label is `agent.open`.
  'agent.voice.orb_hint': 'Opens OS, listening.',
  'agent.voice.listening': 'Listening…',
  // the call still opening at 5 s (C17): Type instead becomes the way on
  'agent.voice.still_connecting': 'Still connecting…',
  'agent.voice.still_connecting_hint': 'A slow network can take a moment. Type instead, and I’ll answer in the chat.',
  // Coach's own first person, in the chosen voice's gender: said with
  // `context: voice`, the same English, gendered where a language inflects it
  'agent.voice.still_connecting_hint_female': 'A slow network can take a moment. Type instead, and I’ll answer in the chat.',
  'agent.voice.still_connecting_hint_male': 'A slow network can take a moment. Type instead, and I’ll answer in the chat.',
  'agent.voice.tap_to_talk': 'Tap the orb to talk',
  'agent.voice.type_instead': 'Type instead',
  'agent.voice.mic_off_title': 'The microphone is off',
  'agent.voice.mic_off_body': 'Turn it on for RecordOS in Settings to talk to me, or type instead.',
  'agent.voice.open_settings': 'Open settings',
  // live mode opened and the recorder would not start (no input device, or it
  // would not open): the card's title and line, with Type instead and Try again
  'agent.voice.mic_unavailable_title': 'The microphone is not available',
  'agent.voice.mic_unavailable_body': 'It did not start on this phone. Type instead, or try again.',
  // the voice look's header: Expand to the conversation, and Minimize to the
  // floating orb (C11, C16)
  'agent.voice.expand': 'Expand',
  'agent.voice.minimize': 'Minimize',
  // the orb's live call (docs/plans/done/orb-live-call.md): the state line under the
  // orb and its controls. A control's label says what a tap will do, so it
  // changes with the state.
  'agent.live.connecting': 'Connecting…',
  'agent.live.speaking': 'Answering',
  // the work on a question, over its capsule and as the orb's label (C21);
  // it names no one, so it has no gendered forms
  'agent.live.coach_working': 'Working…',
  // a question left on the voice look without an answer (C28, C29), and the
  // button that sends it once more
  'agent.live.couldnt_answer': 'I couldn’t answer that',
  'agent.live.couldnt_answer_female': 'I couldn’t answer that',
  'agent.live.couldnt_answer_male': 'I couldn’t answer that',
  'agent.live.not_reached': 'I didn’t get to that',
  'agent.live.ask_again': 'Ask again',
  'agent.live.muted': 'Muted',
  'agent.live.muted_hint': 'The microphone is paused.',
  'agent.live.ended': 'The call ended',
  // under it when the day's questions are spent; the server's own sentence is
  // drawn when it sent one, and this when it did not
  'agent.live.quota_spent': 'I answer a set number of questions a day. Try again tomorrow.',
  // under "The call ended", why it ended when the app left it (S30), or when
  // it reached its length (C76) — and, while Coach still owes an answer, that
  // it will land
  'agent.live.ended.background': 'RecordOS left the screen, so the call ended.',
  'agent.live.ended.interrupted': 'A phone call interrupted it.',
  'agent.live.ended.cap_one': 'A call ends after {{count, number}} minute.',
  'agent.live.ended.cap_other': 'A call ends after {{count, number}} minutes.',
  'agent.live.ended.answer_lands': 'The answer will still land here.',
  // a call that could not be had, each in its own words, with the one
  // control that helps (C70–C74, C7b2)
  'agent.live.error.quota_title': 'That’s all for today',
  'agent.live.error.failed_title': 'The voice could not connect',
  'agent.live.error.failed_body': 'Try again, or type instead. I still answer what you type.',
  'agent.live.error.failed_body_female': 'Try again, or type instead. I still answer what you type.',
  'agent.live.error.failed_body_male': 'Try again, or type instead. I still answer what you type.',
  'agent.live.error.dropped_title': 'The voice dropped',
  'agent.live.error.lost_title': 'Connection lost',
  'agent.live.error.lost_body': 'The answer is kept. Reconnect to keep talking.',
  'agent.live.error.reconnect': 'Reconnect',
  'agent.live.error.offline_body': 'Talking to me needs the internet. Type your question now, and it goes when the phone is back online.',
  'agent.live.mute': 'Mute',
  'agent.live.unmute': 'Unmute',
  'agent.live.interrupt': 'Interrupt',
  'agent.live.talk': 'Talk',
  'agent.live.start': 'Talk live',
  'agent.live.end': 'Stop live',
  // Live mode on the web (os-live-mode.md, step 4.1): the live composer's controls and its live button, whose words
  // say what a click does.
  'agent.live.region': 'Live mode',
  'agent.live.sound_on': 'Sound on',
  'agent.live.sound_off': 'Sound off',
  'agent.live.talk_working': 'OS is working · Click to talk',
  'agent.live.talk_speaking': 'OS is speaking · Click to talk',
  'agent.live.sound_off_talk': 'Sound off · Click to talk',
  'agent.live.click_to_talk': 'Click to talk',
  'agent.live.muted_unmute': 'Muted by you · Click to unmute',
  'agent.live.still_listening': 'Still listening, in case you add something',
  'agent.live.mode_stopped': 'Live mode stopped.',
  // the web's line over the box when the server or the socket ended live mode (os-live-mode.md phase 6: the mode is live mode)
  'agent.live.mode_ended': 'Live mode ended.',
  'agent.live.ended_quiet': 'Live mode ended after 30 seconds with nothing said. Click Talk live to start again.',
  // Live mode on the phone (os-live-mode.md, phase 5): the orb's name and the
  // dock's line while the mic is muted — by live mode while OS works, while the
  // voice speaks and once the person's turn went by — said as what a tap does.
  'agent.live.tap_to_talk': 'Tap to talk',
  // Live mode, one conversation (docs/plans/os-voice-conversation.md, Appendix C): the caption's state word and the
  // detail after its "·", the chips and rows of the live composer, the cards' voice hints, the notes, and the setup
  // seat's words. "Next" is the shipped `agent.queued`; a lost connection is the shipped `agent.live.error.lost_title`.
  'agent.live.state.listening': 'Listening',
  'agent.live.state.working': 'Working',
  'agent.live.state.speaking': 'Speaking',
  'agent.live.state.quiet': 'Quiet',
  'agent.live.state.needs_tap': 'Needs your tap',
  'agent.live.state.needs_click': 'Needs a click',
  'agent.live.state.still_there': 'Still there?',
  'agent.live.state.interrupted': 'Paused',
  'agent.live.state.reconnecting': 'Reconnecting…',
  'agent.live.detail.say_bolo': 'say “bolo”',
  'agent.live.detail.tap_or_say': 'or say “save it”',
  'agent.live.detail.tap_or_say.hi': 'or say “सेव करो”',
  'agent.live.detail.tap_or_say.hi-Latn': 'or say “save karo”',
  'agent.live.detail.call_me': 'say “OS”, or tap the orb',
  // Listening, after speech was judged not for OS twice running: how to be heard (7 Oct evening, item 4).
  'agent.live.detail.say_name': 'say “OS” to ask',
  // The dimmed chip after an utterance ended unsure (option B, step 5): a tap says it was for OS. Never the words that were said.
  'agent.live.chip.claim': 'Was that for me?',
  'agent.live.detail.click_orb': 'say “OS”, or click the orb',
  'agent.live.detail.ends_in_one': 'stops in {{count, number}} min',
  'agent.live.detail.ends_in_other': 'stops in {{count, number}} min',
  'agent.live.detail.phone_call': 'for a phone call',
  'agent.live.detail.building': 'Building your setup',
  'agent.live.detail.ducked': 'quieter while you talk',
  'agent.live.detail.nothing_sent': 'nothing is sent',
  'agent.live.detail.typing_works': 'typing still works',
  'agent.live.detail.typing_works_web': 'Typing still works',
  'agent.live.im_here': 'I’m here',
  'agent.live.resume': 'Resume',
  'agent.live.carry_on': 'Carry on',
  'agent.live.not_now': 'Not now',
  'agent.live.mute_hint': 'Tap to mute',
  'agent.live.parked': 'Stopped. Say “bolo” or tap to get it.',
  'agent.live.edit_resend': 'Edit and resend',
  'agent.live.ended_divider': 'Live mode ended',
  // how long live mode ran, from ten minutes on (under ten it is m:ss)
  'agent.live.ran_minutes_one': '{{count, number}} min',
  'agent.live.ran_minutes_other': '{{count, number}} min',
  'agent.live.moved': 'Live mode moved to another device',
  'agent.live.chip.loud': 'Room is loud',
  'agent.live.chip.quiet_offer': 'Quiet?',
  'agent.live.chip.quiet': 'Quiet',
  'agent.live.chip.earphones': 'Earphones on. Talk over me any time.',
  'agent.live.chip.rollover': 'Live mode stops in a minute',
  'agent.live.half_duplex': 'Voice can’t interrupt while OS talks. Earphones fix this.',
  // the browser tab's title while live mode runs: the name, then the state word
  'agent.live.tab_title': '● OS · {{state}}',
  'agent.live.change.swapped': 'Changed {{from}} to {{to}}',
  'agent.live.change.dropped': 'Dropped the {{what}} total',
  'agent.live.change.added': 'Added {{what}}',
  // the divider's reason after "Live mode ended · m:ss"
  'agent.live.reason.idle': 'nothing said for 5 min',
  'agent.live.reason.moved': 'moved to your other device',
  'agent.live.reason.time': '30-minute limit',
  'agent.live.reason.background': 'the app left the screen',
  'agent.live.reason.interrupted': 'a phone call came in',
  'agent.live.card.hint': 'or say “save it”',
  'agent.live.card.hint.hi': 'or say “सेव करो”',
  'agent.live.card.hint.hi-Latn': 'or say “save karo”',
  'agent.live.card.hint_create': 'or say “create it”',
  'agent.live.card.hint_create.hi': 'or say “बना दो”',
  'agent.live.card.hint_create.hi-Latn': 'or say “bana do”',
  'agent.live.card.rule': 'Nothing is saved until you tap Save or say “save it”.',
  'agent.live.card.rule.hi': 'Nothing is saved until you tap Save or say “सेव करो”.',
  'agent.live.card.rule.hi-Latn': 'Nothing is saved until you tap Save or say “save karo”.',
  'agent.live.card.replaced': 'Replaced',
  'agent.live.card.voice_off': 'Voice can’t confirm this one.',
  'agent.live.privacy.title': 'What I hear in live mode',
  'agent.live.privacy.room': 'While live mode is on, I hear the room, customers too.',
  'agent.live.privacy.act': 'I only act when you talk to me.',
  'agent.live.privacy.kept': 'Only this conversation is kept, here in the chat. Nothing else is recorded.',
  'agent.live.privacy.ok': 'Got it',
  'agent.live.elsewhere.title': 'Live mode is running on another device',
  'agent.live.elsewhere.body': 'Live mode runs on one device at a time. Continue here and the other device stops live mode.',
  'agent.live.elsewhere.move': 'Continue live here',
  'agent.live.mic_blocked.title': 'Your browser blocked the microphone',
  'agent.live.mic_blocked.body': 'Allow it once and I can hear you. Until then, type and I answer here.',
  'agent.live.mic_blocked.step_lock': 'Click the lock beside the address',
  'agent.live.mic_blocked.step_allow': 'Turn Microphone on',
  'agent.live.mic_blocked.step_retry': 'Come back and click Try again.',
  'agent.live.setup.talk': 'Talk to OS',
  'agent.live.setup.talk_note': 'Starts live mode right here. Talk, or type if you like.',
  'agent.live.setup.language_note': 'Answer in the same one. You can change it as you talk.',
  'agent.live.setup.slot_staff': 'Staff',
  'agent.live.setup.build_hint': 'or say “build it”',
  'agent.live.setup.build_hint.hi': 'or say “बना दो”',
  'agent.live.setup.build_hint.hi-Latn': 'or say “bana do”',
  'agent.live.setup.create_hint': 'or say “bana do”',
  'agent.live.setup.create_rule': 'Nothing is created until you tap Build.',
  'agent.live.setup.created_one': 'Created · {{count, number}} $t(glossary.record_one)',
  'agent.live.setup.created_other': 'Created · {{count, number}} $t(glossary.record_other)',
  'agent.live.setup.switched_off': 'Switched off',
  'agent.live.setup.now_in': 'Now in {{name}}.',
  // live mode's fixed spoken lines, the server's own words (not the model's). English is the bare key; hi and
  // hi-Latn sit under it by tag (`liveSpokenKeys`), every other language falls back to English. A line whose first
  // person is gendered has a `_female` and a `_male` form, and its base keeps the female form, as the fillers do.
  // The hi and hi-Latn lines are drafts for Devendra's ear.
  'agent.live.say.idle_check': 'I’m still here. Need anything?',
  'agent.live.say.idle_check.hi': 'मैं सुन रही हूँ। कुछ चाहिए?',
  'agent.live.say.idle_check.hi_female': 'मैं सुन रही हूँ। कुछ चाहिए?',
  'agent.live.say.idle_check.hi_male': 'मैं सुन रहा हूँ। कुछ चाहिए?',
  'agent.live.say.idle_check.hi-Latn': 'Main sun rahi hoon. Kuch chahiye?',
  'agent.live.say.idle_check.hi-Latn_female': 'Main sun rahi hoon. Kuch chahiye?',
  'agent.live.say.idle_check.hi-Latn_male': 'Main sun raha hoon. Kuch chahiye?',
  'agent.live.say.goodbye': 'I’m stopping live mode. Tap Talk live when you need me.',
  'agent.live.say.goodbye.hi': 'लाइव मोड बंद कर रही हूँ। ज़रूरत हो तो Talk live दबाना।',
  'agent.live.say.goodbye.hi_female': 'लाइव मोड बंद कर रही हूँ। ज़रूरत हो तो Talk live दबाना।',
  'agent.live.say.goodbye.hi_male': 'लाइव मोड बंद कर रहा हूँ। ज़रूरत हो तो Talk live दबाना।',
  'agent.live.say.goodbye.hi-Latn': 'Live mode band kar rahi hoon. Zarurat ho toh Talk live dabana.',
  'agent.live.say.goodbye.hi-Latn_female': 'Live mode band kar rahi hoon. Zarurat ho toh Talk live dabana.',
  'agent.live.say.goodbye.hi-Latn_male': 'Live mode band kar raha hoon. Zarurat ho toh Talk live dabana.',
  'agent.live.say.quiet': 'Okay, I’ll wait until you call me.',
  'agent.live.say.quiet.hi': 'ठीक है, जब चाहिए तब बुलाना।',
  'agent.live.say.quiet.hi-Latn': 'Theek hai, bulana jab chahiye.',
  'agent.live.say.bare_yes': 'Tap Save, or say “save it”.',
  'agent.live.say.bare_yes.hi': 'Save दबाओ, या बोलो सेव करो।',
  'agent.live.say.bare_yes.hi-Latn': 'Save dabao, ya bolo save karo.',
  // A bare yes to a card that makes a record: its own button and create words (plan step 10 of docs/plans/links-rca.md).
  'agent.live.say.create_yes': 'Tap Create this, or say “create it”.',
  'agent.live.say.create_yes.hi': 'Create this दबाओ, या बोलो बना दो।',
  'agent.live.say.create_yes.hi-Latn': 'Create this dabao, ya bolo bana do.',
  'agent.live.say.tap_only': 'This one needs your tap. I can’t take it by voice.',
  'agent.live.say.tap_only.hi': 'इसमें बटन दबाना पड़ेगा, बोल के नहीं होता।',
  'agent.live.say.tap_only.hi-Latn': 'Isme button dabana padega, bol ke nahi hota.',
  'agent.live.say.parked': 'Stopped. Say “go on” when you want the rest.',
  'agent.live.say.parked.hi': 'रुक गई। आगे चाहिए तो बोलो “बोलो”।',
  'agent.live.say.parked.hi_female': 'रुक गई। आगे चाहिए तो बोलो “बोलो”।',
  'agent.live.say.parked.hi_male': 'रुक गया। आगे चाहिए तो बोलो “बोलो”।',
  'agent.live.say.parked.hi-Latn': 'Ruk gayi. Aage chahiye toh bolo “bolo”.',
  'agent.live.say.parked.hi-Latn_female': 'Ruk gayi. Aage chahiye toh bolo “bolo”.',
  'agent.live.say.parked.hi-Latn_male': 'Ruk gaya. Aage chahiye toh bolo “bolo”.',
  'agent.live.say.rollover': 'Live mode stops in a minute. Tap Carry on to keep going.',
  'agent.live.say.rollover.hi': 'एक मिनट में लाइव मोड बंद होगा। चालू रखना है तो Carry on दबाओ।',
  'agent.live.say.rollover.hi-Latn': 'Ek minute mein live mode band hoga. Chalu rakhna hai toh Carry on dabao.',
  'agent.live.say.quota': 'That’s all I can answer today. Let’s talk again tomorrow.',
  'agent.live.say.quota.hi': 'आज के सवाल पूरे हो गए। कल फिर बात करते हैं।',
  'agent.live.say.quota.hi-Latn': 'Aaj ke sawal poore ho gaye. Kal phir baat karte hain.',
  'agent.live.say.setup_ready': 'Ready. What’s the first entry?',
  'agent.live.say.setup_ready.hi': 'तैयार। पहली एंट्री बोलो?',
  'agent.live.say.setup_ready.hi-Latn': 'Ready. Pehli entry bolo?',
  'agent.live.say.create_tap': 'Tap Build when it looks right.',
  'agent.live.say.create_tap.hi': 'ठीक लगे तो Build दबाइए।',
  'agent.live.say.create_tap.hi-Latn': 'Theek lage toh Build dabaiye.',
  'agent.live.say.setup_built': 'Your workspace is ready. Open it when you are ready.',
  'agent.live.say.setup_built.hi': 'आपका वर्कस्पेस तैयार है। जब चाहें Open दबाइए।',
  'agent.live.say.setup_built.hi-Latn': 'Aapka workspace ready hai. Jab chahein Open dabaiye.',
  'agent.live.say.away': 'You’re on another tab. I’ll say the figures out loud; they’re in the chat when you come back.',
  'agent.live.say.away.hi': 'आप दूसरे टैब पे हो। आंकड़े बोल के बताऊँगी, चैट में भी रहेंगे।',
  'agent.live.say.away.hi_female': 'आप दूसरे टैब पे हो। आंकड़े बोल के बताऊँगी, चैट में भी रहेंगे।',
  'agent.live.say.away.hi_male': 'आप दूसरे टैब पे हो। आंकड़े बोल के बताऊँगा, चैट में भी रहेंगे।',
  'agent.live.say.away.hi-Latn': 'Aap doosre tab pe ho. Figures bol ke bataungi, chat mein bhi rahenge.',
  'agent.live.say.away.hi-Latn_female': 'Aap doosre tab pe ho. Figures bol ke bataungi, chat mein bhi rahenge.',
  'agent.live.say.away.hi-Latn_male': 'Aap doosre tab pe ho. Figures bol ke bataunga, chat mein bhi rahenge.',
  'agent.live.say.card_waiting': 'A card is waiting in the OS tab. Open it to save.',
  'agent.live.say.card_waiting.hi': 'OS टैब में कार्ड तैयार है। सेव करने के लिए टैब खोलो।',
  'agent.live.say.card_waiting.hi-Latn': 'OS tab mein card ready hai. Save karne ke liye tab kholo.',
  'agent.live.say.setup_ready_web': 'Your setup is ready. Tell me your first sale and I’ll write it down.',
  'agent.live.say.setup_ready_web.hi': 'सेटअप हो गया। पहली सेल बोलो, मैं लिख देती हूँ।',
  'agent.live.say.setup_ready_web.hi_female': 'सेटअप हो गया। पहली सेल बोलो, मैं लिख देती हूँ।',
  'agent.live.say.setup_ready_web.hi_male': 'सेटअप हो गया। पहली सेल बोलो, मैं लिख देता हूँ।',
  'agent.live.say.setup_ready_web.hi-Latn': 'Setup ho gaya. Pehli sale bolo, main likh deti hoon.',
  'agent.live.say.setup_ready_web.hi-Latn_female': 'Setup ho gaya. Pehli sale bolo, main likh deti hoon.',
  'agent.live.say.setup_ready_web.hi-Latn_male': 'Setup ho gaya. Pehli sale bolo, main likh deta hoon.',
  'agent.live.say.setup_ready_entry': '{{name}} is set up. Tell me today’s first {{entry}} and I’ll write it.',
  'agent.live.say.setup_ready_entry.hi': '{{name}} तैयार है। आज की पहली {{entry}} बोलो, मैं लिख देती हूँ।',
  'agent.live.say.setup_ready_entry.hi_female': '{{name}} तैयार है। आज की पहली {{entry}} बोलो, मैं लिख देती हूँ।',
  'agent.live.say.setup_ready_entry.hi_male': '{{name}} तैयार है। आज की पहली {{entry}} बोलो, मैं लिख देता हूँ।',
  'agent.live.say.setup_ready_entry.hi-Latn': '{{name}} taiyaar hai. Aaj ki pehli {{entry}} bolo, main likh deti hoon.',
  'agent.live.say.setup_ready_entry.hi-Latn_female': '{{name}} taiyaar hai. Aaj ki pehli {{entry}} bolo, main likh deti hoon.',
  'agent.live.say.setup_ready_entry.hi-Latn_male': '{{name}} taiyaar hai. Aaj ki pehli {{entry}} bolo, main likh deta hoon.',
  // Said once a session after two utterances in a row ended unsure (option B, step 5): how to be heard, in the person's language.
  'agent.live.say.say_name': 'Say “OS” first and I’ll answer.',
  'agent.live.say.say_name.hi': 'पहले “OS” बोलके पूछिए।',
  'agent.live.say.say_name.hi-Latn': 'Pehle “OS” bolke poochiye.',
  // The studio seat (onboarding harness 7.2): a spoken yes on the open Build sheet builds nothing, so the voice
  // says the click is the person's; and once a half-duplex call, why OS cannot hear them while it talks.
  'agent.live.say.create_click': 'Have a look, then click Build.',
  'agent.live.say.create_click.hi': 'एक बार देख लीजिए, फिर Build दबाइए।',
  'agent.live.say.create_click.hi-Latn': 'Ek baar dekh lijiye, phir Build dabaiye.',
  'agent.live.say.half_why': 'On this browser I can’t hear you while I talk. Click Interrupt to cut in.',
  'agent.live.say.half_why.hi': 'इस ब्राउज़र पर मैं बोलते समय आपको नहीं सुन सकती। बीच में बोलना हो तो Interrupt दबाइए।',
  'agent.live.say.half_why.hi_female': 'इस ब्राउज़र पर मैं बोलते समय आपको नहीं सुन सकती। बीच में बोलना हो तो Interrupt दबाइए।',
  'agent.live.say.half_why.hi_male': 'इस ब्राउज़र पर मैं बोलते समय आपको नहीं सुन सकता। बीच में बोलना हो तो Interrupt दबाइए।',
  'agent.live.say.half_why.hi-Latn': 'Is browser pe main bolte waqt aapko nahi sun sakti. Beech mein bolna ho toh Interrupt dabaiye.',
  'agent.live.say.half_why.hi-Latn_female': 'Is browser pe main bolte waqt aapko nahi sun sakti. Beech mein bolna ho toh Interrupt dabaiye.',
  'agent.live.say.half_why.hi-Latn_male': 'Is browser pe main bolte waqt aapko nahi sun sakta. Beech mein bolna ho toh Interrupt dabaiye.',
  // The studio seat while a line's draft runs (RF7, RF9 of docs/plans/receipts-first-words.md): the hold line said in place of
  // words that stated a change before it landed, and the one progress line of a draft past 8 s. Neither names a part or an outcome,
  // and neither claims a change: the progress line is said past 8 s of any line, a question's among them.
  'agent.live.say.studio_hold': 'One moment.',
  'agent.live.say.studio_hold.hi': 'एक सेकंड।',
  'agent.live.say.studio_hold.hi-Latn': 'Ek second.',
  'agent.live.say.studio_still': 'Still working on that.',
  'agent.live.say.studio_still.hi': 'अभी उस पर काम चल रहा है।',
  'agent.live.say.studio_still.hi-Latn': 'Abhi us par kaam chal raha hai.',
  // the fillers: short clips the voice plays while Coach works, and their words
  // as the voice's caption (C22). English is the bare key, every other language
  // sits under it by tag (`clipKeys`). Filler 1's first person is gendered in
  // hi, hi-Latn, mr, gu and pa: each has a `_female` and a `_male` form, which
  // `clipKeys` tries first for the chosen voice, and its base keeps the female
  // form, the default voice's, for a lookup that names no voice.
  'agent.filler.1': 'One second, looking…',
  'agent.filler.1.hi': 'एक सेकंड, देख रही हूँ…',
  'agent.filler.1.hi_female': 'एक सेकंड, देख रही हूँ…',
  'agent.filler.1.hi_male': 'एक सेकंड, देख रहा हूँ…',
  'agent.filler.1.hi-Latn': 'Ek second, dekh rahi hoon…',
  'agent.filler.1.hi-Latn_female': 'Ek second, dekh rahi hoon…',
  'agent.filler.1.hi-Latn_male': 'Ek second, dekh raha hoon…',
  'agent.filler.1.mr': 'एक सेकंद, पाहते आहे…',
  'agent.filler.1.mr_female': 'एक सेकंद, पाहते आहे…',
  'agent.filler.1.mr_male': 'एक सेकंद, पाहतो आहे…',
  'agent.filler.1.gu': 'એક સેકન્ડ, જોઈ રહી છું…',
  'agent.filler.1.gu_female': 'એક સેકન્ડ, જોઈ રહી છું…',
  'agent.filler.1.gu_male': 'એક સેકન્ડ, જોઈ રહ્યો છું…',
  'agent.filler.1.pa': 'ਇੱਕ ਸਕਿੰਟ, ਦੇਖ ਰਹੀ ਹਾਂ…',
  'agent.filler.1.pa_female': 'ਇੱਕ ਸਕਿੰਟ, ਦੇਖ ਰਹੀ ਹਾਂ…',
  'agent.filler.1.pa_male': 'ਇੱਕ ਸਕਿੰਟ, ਦੇਖ ਰਿਹਾ ਹਾਂ…',
  'agent.filler.1.bn': 'এক সেকেন্ড, দেখছি…',
  'agent.filler.1.ta': 'ஒரு நொடி, பார்க்கிறேன்…',
  'agent.filler.1.te': 'ఒక్క క్షణం, చూస్తున్నాను…',
  'agent.filler.1.kn': 'ಒಂದು ಕ್ಷಣ, ನೋಡುತ್ತಿದ್ದೇನೆ…',
  'agent.filler.1.ml': 'ഒരു നിമിഷം, നോക്കുകയാണ്…',
  'agent.filler.2': 'Just a little longer…',
  'agent.filler.2.hi': 'बस थोड़ी देर और…',
  'agent.filler.2.hi-Latn': 'Bas thodi der aur…',
  'agent.filler.2.mr': 'अजून थोडा वेळ…',
  'agent.filler.2.gu': 'બસ થોડી વાર વધુ…',
  'agent.filler.2.pa': 'ਬੱਸ ਥੋੜ੍ਹੀ ਦੇਰ ਹੋਰ…',
  'agent.filler.2.bn': 'আর একটু…',
  'agent.filler.2.ta': 'இன்னும் கொஞ்சம் நேரம்…',
  'agent.filler.2.te': 'ఇంకొంచెం సేపు…',
  'agent.filler.2.kn': 'ಇನ್ನೂ ಸ್ವಲ್ಪ ಹೊತ್ತು…',
  'agent.filler.2.ml': 'ഇനി കുറച്ചു കൂടി…',
  'agent.filler.3': 'Hold on a moment…',
  'agent.filler.3.hi': 'थोड़ा रुकिए…',
  'agent.filler.3.hi-Latn': 'Thoda rukiye…',
  'agent.filler.3.mr': 'थोडं थांबा…',
  'agent.filler.3.gu': 'થોડું રોકાઓ…',
  'agent.filler.3.pa': 'ਥੋੜ੍ਹਾ ਰੁਕੋ…',
  'agent.filler.3.bn': 'একটু অপেক্ষা করুন…',
  'agent.filler.3.ta': 'கொஞ்சம் பொறுங்கள்…',
  'agent.filler.3.te': 'కొంచెం ఆగండి…',
  'agent.filler.3.kn': 'ಸ್ವಲ್ಪ ತಡೆಯಿರಿ…',
  'agent.filler.3.ml': 'ഒന്ന് കാത്തിരിക്കൂ…',
  // dictation: the conversation's mic; the words show as they are heard and the check sends them
  'agent.dictation.start': 'Dictate',
  'agent.dictation.transcribing': 'Writing it down…',
  'agent.dictation.failed': 'The words did not come through. Try again.',
  'agent.dictation.mic_off': 'Turn on the microphone in Settings to dictate.',
  'agent.dictation.edit': 'Edit the words first',
  'agent.dictation.nothing_heard': 'Nothing was heard. Tap the mic and speak again.',
  // the history: docs/plans/agent-thread-history.md
  'agent.history.open': 'Past conversations',
  'agent.history.new': 'New conversation',
  'agent.history.back': 'Back to the conversation',
  'agent.history.title': 'Past conversations',
  'agent.history.section.today': 'Today',
  'agent.history.section.yesterday': 'Yesterday',
  'agent.history.section.week': 'This week',
  'agent.history.section.earlier': 'Earlier',
  'agent.history.current': 'Open',
  'agent.history.empty_title': 'No conversations yet',
  'agent.history.empty_body': 'Every conversation you have with me here is kept, so you can pick one up again.',
  'agent.history.more': 'Show more',
  'agent.history.failed': 'Your conversations could not be loaded.',
  'agent.history.open_failed': 'That conversation could not be opened.',
  'agent.history.loading': 'Opening…',
  // the cards: titles, rows, and the words for a change
  'agent.confirm_invite': 'Send this invitation',
  'agent.confirm_record': 'Create this',
  'agent.confirm_change': 'Change it',
  'agent.confirm_correction': 'Save the correction',
  'agent.invite_title': 'Invite {{person}}',
  'agent.link_title': '{{from}} → {{to}}',
  'agent.card_title': 'An $t(glossary.insight_one) on {{record}}',
  'agent.record_title': 'Create {{record}}',
  'agent.level': 'Level',
  'agent.job': 'Job',
  'agent.record_kind': 'Type',
  'agent.record_brings': 'Brings',
  'agent.record_kind_record': 'Record',
  'agent.record_kind_list': 'List',
  'agent.record_name': 'Name',
  'agent.record_counts': 'Counts as',
  'agent.entry_change_title': 'Correct an $t(glossary.entry_one) in {{record}}',
  'agent.entry_delete_title': 'Remove an $t(glossary.entry_one) from {{record}}',
  'agent.entry_delete_note': 'The $t(glossary.entry_one) is taken out of the $t(glossary.record_one). Its history stays.',
  'agent.empty_value': 'empty',
  // A table OS wrote, named for a screen reader while it scrolls sideways in its box: its columns.
  'agent.table_label': 'Table of {{columns}}',
  'agent.job_title': 'New $t(glossary.job_one): {{job}}',
  'agent.job_change_title': 'Change the {{job}} $t(glossary.job_one)',
  'agent.job_apply_title': 'Give the {{job}} $t(glossary.job_one)',
  'agent.job_reaches': 'Reaches {{count, number}} $t(glossary.person_other) who hold it',
  'agent.grant_title': 'Change a key to {{record}}',
  'agent.grant_revoke_title': 'Take back a key to {{record}}',
  'agent.business_title': 'Change the $t(glossary.business_one)',
  'agent.people': 'People',
  'agent.person': 'Person',
  'agent.confirm_remove': 'Remove it',
  'agent.confirm_revoke': 'Take the key back',
  'agent.confirm_job': 'Create this $t(glossary.job_one)',
  'agent.confirm_job_apply': 'Give the $t(glossary.job_one)',
  'agent.receipt.entry_change': 'The $t(glossary.entry_one) in {{record}} is corrected.',
  'agent.receipt.entry_delete': 'The $t(glossary.entry_one) is removed from {{record}}.',
  'agent.receipt.job': 'The {{job}} $t(glossary.job_one) exists.',
  'agent.receipt.job_change': 'The {{job}} $t(glossary.job_one) is changed.',
  'agent.receipt.job_apply': 'The {{job}} $t(glossary.job_one) is given.',
  'agent.receipt.grant': "{{person}}'s key to {{record}} is changed.",
  'agent.receipt.grant_revoke': '{{person}} can no longer open {{record}}.',
  'agent.receipt.business': 'The $t(glossary.business_one) is changed.',
  // a person's level changed, a person removed, a hand up for primary admin
  'agent.member_change_title': "Change {{person}}'s level",
  'agent.member_remove_title': 'Remove {{person}}',
  'agent.member_remove_note': 'Every key they hold is taken back. Every $t(glossary.entry_one) they wrote stays, with their name on it.',
  'agent.closes_ask': 'Answers their ask',
  'agent.primary_ask_title': 'Ask to become a primary admin',
  'agent.primary_dismiss_title': "Put away {{person}}'s ask",
  'agent.confirm_level': 'Change the $t(glossary.level_one)',
  'agent.confirm_remove_member': 'Remove them',
  'agent.confirm_ask': 'Ask',
  'agent.confirm_dismiss_ask': 'Put it away',
  'agent.receipt.member_change': "{{person}}'s level is now {{level}}.",
  'agent.receipt.member_remove': '{{person}} is no longer in the $t(glossary.business_one).',
  'agent.receipt.primary_ask': 'Asked. Every primary admin will see it.',
  'agent.receipt.primary_dismiss': "{{person}}'s ask is put away. They may ask again.",
  // roles, a person's role and their access, for a phone that draws roles
  'agent.role_title': 'New role: {{role}}',
  'agent.role_change_title': 'Change the {{role}} role',
  'agent.role_give_title': 'Give {{person}} the {{role}} role',
  'agent.access_title': 'Change what {{person}} can open',
  'agent.access_give_title': 'Give {{person}} access',
  'agent.access_take_title': "Take back {{person}}'s access",
  'agent.confirm_role': 'Make the role',
  'agent.confirm_role_give': 'Give the role',
  'agent.confirm_access_give': 'Give access',
  'agent.confirm_access_take': 'Take it back',
  'agent.role': 'Role',
  'agent.can_open': 'Can open',
  'agent.reports_to': 'Reports to',
  'agent.person_remove_note': 'Their access is taken away. Every $t(glossary.entry_one) they wrote stays, with their name on it.',
  'agent.team_moves_one': '{{count, number}} $t(glossary.person_one) who reports to them now reports to {{person}}.',
  'agent.team_moves_other': '{{count, number}} $t(glossary.person_other) who report to them now report to {{person}}.',
  'agent.team_moves_up_one': '{{count, number}} $t(glossary.person_one) who reports to them now reports to nobody.',
  'agent.team_moves_up_other': '{{count, number}} $t(glossary.person_other) who report to them now report to nobody.',
  'agent.role_not_made': 'The role this invitation names is not made yet.',
  'agent.role_gone': 'That role is not in the $t(glossary.business_one) any more.',
  'agent.see_invites': 'See the invitations',
  'agent.receipt.role': '{{role}} is made.',
  'agent.receipt.role_change': '{{role}} is changed.',
  'agent.receipt.role_give': '{{person}} now holds {{role}}.',
  'agent.receipt.access': "{{person}}'s access is changed.",
  // The caller leaving through OS: a removal aimed at themselves
  'agent.leave_title': 'Leave this $t(glossary.business_one)',
  'agent.confirm_leave': 'Leave',
  'agent.leave_note': 'Your access is taken away. Every $t(glossary.entry_one) you wrote stays, with your name on it.',
  'agent.receipt.leave': 'You are no longer in this $t(glossary.business_one).',
  // OS's phase-3 cards: a person changed, a role removed or copied, an ask
  // for a right and its answer, an invitation withdrawn or sent again, and
  // the workspace handed over (roles-parity 3.7)
  'agent.person_change_title': 'Change {{person}}',
  'agent.person_change_self_title': 'Change your details',
  'agent.person_name': 'Name',
  'agent.phone': 'Phone',
  'agent.address': 'Address',
  'agent.not_set': 'Not set',
  'agent.receipt.person_change': "{{person}}'s changes are saved.",
  'agent.receipt.person_change_self': 'Your details are changed.',
  'agent.role_remove_title': 'Remove the {{role}} role',
  'agent.moves_to': 'Moves to',
  'agent.holders_one': '{{count, number}} $t(glossary.person_one)',
  'agent.holders_other': '{{count, number}} $t(glossary.person_other)',
  'agent.confirm_role_remove': 'Remove the role',
  'agent.role_copy_title': 'Copy the {{role}} role',
  'agent.copy_name': 'The copy',
  'agent.confirm_role_copy': 'Make the copy',
  'agent.ask_title': 'Ask for a right',
  'agent.asked_for': 'Asked for',
  'agent.receipt.ask': 'Asked. Everyone who may change roles and access will see it.',
  'agent.ask_grant_title': 'Give {{person}} what they asked for',
  'agent.ask_dismiss_title': "Put away {{person}}'s ask",
  'agent.confirm_ask_grant': 'Give it',
  'agent.receipt.ask_grant': 'Given to {{person}}.',
  'agent.receipt.ask_dismiss': "{{person}}'s ask is put away. They may ask again.",
  'agent.invite_withdraw_title': "Withdraw {{person}}'s invitation",
  'agent.invite_resend_title': "Send {{person}}'s invitation again",
  'agent.sent_to': 'Sent to',
  'agent.confirm_invite_withdraw': 'Withdraw it',
  'agent.confirm_invite_resend': 'Send it again',
  'agent.receipt.invite_withdraw': "{{person}}'s invitation is withdrawn. Its link and code no longer work.",
  'agent.receipt.invite_resend': '{{person}} has a fresh code, good for seven more days.',
  'agent.handover_title': 'Hand the $t(glossary.business_one) to {{person}}',
  'agent.your_role_then': 'Your role, from then on',
  'agent.confirm_handover': 'Hand it over',
  'agent.receipt.handover': '{{person}} is asked to accept. Until they do, you are the primary admin.',
  // a hand-over waiting, answered on an OS card (roles-parity 8.1): accepted, turned down or taken back
  'agent.handover_accept_title': "Accept {{person}}'s hand-over",
  'agent.handover_decline_title': "Turn down {{person}}'s hand-over",
  'agent.handover_withdraw_title': 'Take back the hand-over to {{person}}',
  'agent.offered_by': 'Offered by',
  'agent.their_role_then': 'Their role, from then on',
  'agent.confirm_handover_accept': 'Accept',
  'agent.confirm_handover_decline': 'Turn it down',
  'agent.confirm_handover_withdraw': 'Take it back',
  'agent.receipt.handover_accept': 'You are the primary admin now.',
  'agent.receipt.handover_decline': '{{person}} stays the primary admin.',
  'agent.receipt.handover_withdraw': 'The hand-over is taken back. You stay the primary admin.',
  // the one primary admin, where the app draws roles: an ask to be it, and one put away
  'agent.roles_primary_ask_title': 'Ask to be the primary admin',
  'agent.roles_primary_dismiss_title': "Put away {{person}}'s ask to be the primary admin",
  'agent.confirm_roles_primary_ask': 'Ask to be it',
  'agent.receipt.roles_primary_ask': 'Asked. The primary admin will see it on Team.',
  'agent.change.add': 'New',
  'agent.change.update': 'Changed',
  'agent.change.archive': 'Removed',
  'agent.change.rename': 'Renamed',
  'agent.change.order': 'Reordered',
  // after the tap
  'agent.done': 'Done',
  'agent.undone': 'Undone.',
  'agent.left_alone': 'Left as it was.',
  'agent.stale': 'This $t(glossary.record_one) changed since I looked. I have reloaded it. Ask me again.',
  'agent.card_unreadable': 'This $t(glossary.insight_one) is not one this phone can draw.',
  'agent.link_gone': 'That $t(glossary.link_one) is not on this $t(glossary.record_one) any more.',
  'agent.link_retired': 'A $t(glossary.link_one) can no longer be made or changed. A money rule on the $t(glossary.record_one) moves its money now.',
  // A link bundled with the record it leans on (links-fixes L2): why it was not posted, and what to do next.
  'agent.link_record_not_made': 'The $t(glossary.link_one) was not made, because {{record}} was not made first. Make {{record}}, then ask for the $t(glossary.link_one) again.',
  'agent.link_record_lacks': '{{record}} is made, but the $t(glossary.link_one) was not: {{record}} has no {{fields}}. Add it to {{record}}, then ask for the $t(glossary.link_one) again.',
  // A structure card whose request changes nothing: never "Done", since nothing was saved.
  'agent.structure_nothing': 'Nothing on this card changes the $t(glossary.record_one). Ask me for the change again.',
  'agent.show_code': 'Show the code',
  'agent.show_codes': 'Show the codes',
  'agent.receipt.entry': 'Saved into {{record}}.',
  'agent.receipt.list': 'Added to {{record}}.',
  // several entries in one card: `{{count}}` is the count, then the record
  'agent.entries_title': '{{count, number}} $t(glossary.entry_other) in {{record}}',
  'agent.entries_list_title': '{{count, number}} names for {{record}}',
  'agent.confirm_entries': 'Save all {{count, number}}',
  'agent.receipt.entries': '{{count, number}} saved into {{record}}.',
  'agent.entry.rows_first': 'Confirm {{rows}} on the card above first: this $t(glossary.entry_one) picks it.',
  // A draft that waits for a row its writer may not add (L64.5).
  'agent.entry.confirm_draft': 'Save as a draft',
  'agent.entry.waits_row_saved': 'Saved as a draft: waiting for {{owner}} to add {{name}}',
  'agent.entry.waits_row_saved_owner': 'Saved as a draft: waiting for the owner to add {{name}}',
  'agent.entry.waits_row_line': 'Kept as a draft until {{owner}} adds {{name}} to {{list}}. It counts in nothing until then.',
  'agent.entry.waits_row_line_owner': 'Kept as a draft until the owner adds {{name}} to {{list}}. It counts in nothing until then.',
  'agent.entry.waits_row_value': 'Waits for {{name}}',
  'agent.ask_owner_draft_first': 'Save the draft on the card above first: this asks for the row it waits for.',
  'agent.receipt.entries_partial': '{{saved, number}} of {{total, number}} saved into {{record}}. The rest were refused.',
  'agent.entries_more': 'and {{count, number}} more',
  // a row the person takes off a batch card before the tap
  'agent.drop_row': 'Take this $t(glossary.entry_one) off',
  // several invitations in one card: `{{count}}` is the count, then the person
  'agent.invites_title': 'Invite {{count, number}} $t(glossary.person_other)',
  'agent.confirm_invites': 'Send all {{count, number}}',
  'agent.receipt.invites': '{{count, number}} invitations are ready.',
  'agent.receipt.invites_partial': '{{ready, number}} of {{total, number}} are ready. {{person}} was refused.',
  'agent.invites_more': 'and {{count, number}} more',
  // several proposals confirmed in one tap
  'agent.bundle_title': '{{count, number}} things in one go',
  'agent.confirm_bundle': 'Do all {{count, number}}',
  'agent.receipt.bundle': '{{count, number}} things are done.',
  'agent.kit_title': '{{kit}} on {{record}}',
  'agent.confirm_kit': 'Put it in',
  'agent.receipt.kit': '{{kit}} is on {{record}}.',
  'agent.kit.adds': 'What it adds',
  'agent.kit.field_lines': 'Lines: {{fields}}',
  'agent.kit.field_worked': 'Worked out',
  'agent.kit.field_typed': 'Typed in',
  'agent.kit.changes': 'What it changes',
  'agent.kit.field_to_worked': 'Worked out from {{from}}, each saved value copied into {{from}} first',
  'agent.kit.rules': 'Rules it keeps',
  'agent.kit.knobs': 'Set for you',
  'agent.kit.knob': '{{what}}: {{value}}',
  'agent.kit.old_entries': 'Entries already saved stay as they are.',
  'agent.kit.web_only': 'Open RecordOS on the web to put this kit in.',
  'agent.bundle_stopped': '{{done, number}} of {{total, number}} done. The rest were left as they were.',
  'agent.bundle_waiting': 'Waiting',
  'agent.receipt.structure': '{{record}} is changed.',
  'agent.receipt.link': 'The $t(glossary.link_one) into {{record}} is on.',
  'agent.receipt.link_change': 'The $t(glossary.link_one) into {{record}} is changed.',
  'agent.receipt.card': '{{insight}} is on top of {{record}}.',
  'agent.receipt.invite': 'The invitation for {{person}} is ready.',
  'agent.receipt.record': '{{record}} is created.',
  // the opening line, per place: the actions the person can take there, with
  // no fact and no number (FD2). packages/shared/src/agent-opening.ts reads it.
  'agent.opening.default': 'Ask anything, or say what to do',
  'agent.opening.dashboard': 'Ask how the business is doing, or what changed',
  'agent.opening.record': 'Ask about {{record}}, or say what to add',
  'agent.opening.new_entry': 'Say what happened, and it goes into {{record}}',
  'agent.opening.entry': 'Ask about this $t(glossary.entry_one), or say what to change',
  'agent.opening.customize': 'Say what to add, rename or take off in {{record}}',
  'agent.opening.links': 'Say what should fire from {{record}}, and when',
  'agent.opening.insights': 'Say what number to put on top of {{record}}',
  'agent.opening.team': 'Say who to invite, or ask who sees what',
  'agent.opening.business': 'Say who to invite, or what to change in the $t(glossary.business_one)',
  'agent.opening.knows': 'Say what to remember about the $t(glossary.business_one), or what to forget',
  'agent.opening.account': 'Ask how something works, or say what to do',
  'agent.opening.insight': 'Ask anything about this $t(glossary.insight_one)',
  'agent.opening.roles': 'Say what a role should let people do, or who should hold it',
  // The web's Routines page (docs/plans/routines.md R9): OS adds task rules and email rules.
  'agent.opening.routines': 'Say what should happen by itself, and when. You see the $t(glossary.routine_one) before it is saved',
  'agent.opening.documents': 'Say which document to make from {{record}}: an invoice, a receipt or a quotation',
  'agent.opening.document': 'Say what to change on this document',
  // the day's first greeting, by the part of the day
  'agent.greet.morning': 'Good morning, {{name}}.',
  'agent.greet.afternoon': 'Good afternoon, {{name}}.',
  'agent.greet.evening': 'Good evening, {{name}}.',
  'agent.greet.morning.no_name': 'Good morning.',
  'agent.greet.afternoon.no_name': 'Good afternoon.',
  'agent.greet.evening.no_name': 'Good evening.',
  // what the voice asks after the day's first greeting, and nothing else
  'agent.greet.ask': 'How can I help?',
  // the pills under the opening line
  'agent.suggest.default.1': 'What can you do here?',
  'agent.suggest.default.2': 'What did I record today?',
  'agent.suggest.home.1': 'What did I record today?',
  'agent.suggest.home.2': 'Which $t(glossary.record_one) does a new bill go in?',
  'agent.suggest.home.3': 'Who can see my $t(glossary.record_other)?',
  'agent.suggest.home.4': 'Add an $t(glossary.entry_one)',
  'agent.suggest.dashboard.1': 'How is the month going?',
  'agent.suggest.dashboard.2': 'What came in this week?',
  'agent.suggest.dashboard.3': 'Who wrote the most $t(glossary.entry_other)?',
  'agent.suggest.record.1': 'Add to {{record}}',
  'agent.suggest.record.2': 'How much this week in {{record}}?',
  'agent.suggest.record.3': 'Add a $t(glossary.field_one)',
  'agent.suggest.record.4': 'Put an $t(glossary.insight_one) on top',
  'agent.suggest.record.5': 'Who can see {{record}}?',
  'agent.suggest.new_entry.1': 'What did I record last time?',
  'agent.suggest.new_entry.2': 'What does each $t(glossary.field_one) mean?',
  'agent.suggest.new_entry.3': 'Fill this in for me',
  'agent.suggest.entry.1': 'Change this $t(glossary.entry_one)',
  'agent.suggest.customize.1': 'Add a $t(glossary.field_one)',
  'agent.suggest.customize.2': 'Rename a $t(glossary.field_one)',
  'agent.suggest.customize.3': 'Make a $t(glossary.field_one) required',
  'agent.suggest.links.1': 'Only fire this over an amount',
  'agent.suggest.links.2': 'Pause a $t(glossary.link_one)',
  'agent.suggest.links.3': 'Add a $t(glossary.link_one)',
  'agent.suggest.insights.1': 'Total this month, on top',
  'agent.suggest.insights.2': 'Bars by day, last two weeks',
  'agent.suggest.insight.1': 'Which days were below average?',
  'agent.suggest.insight.2': 'Compare with the days before',
  'agent.suggest.insight.3': 'Why was the best day so high?',
  'agent.suggest.team.1': 'Invite somebody as a member',
  'agent.suggest.team.2': 'What can each $t(glossary.person_one) see?',
  'agent.suggest.business.1': 'Change a $t(glossary.job_one)',
  'agent.suggest.business.2': 'What do you know about my $t(glossary.business_one)?',
  // A client that draws roles (roles 2.11); `business.1` stays for an old client's bundled table.
  'agent.suggest.business.3': 'Change a role',
  'agent.suggest.knows.1': 'Remember something',
  'agent.suggest.knows.2': 'Forget something',
  'agent.suggest.roles.1': 'Make a role for delivery',
  'agent.suggest.roles.2': 'What can each role do?',
  'agent.suggest.roles.3': 'Give somebody a role',
  'agent.suggest.routines.1': 'Remind me 3 days before a plan ends',
  'agent.suggest.routines.2': 'Email who owes money every Monday',
  'agent.suggest.routines.3': 'Make a task when an entry is added',
  'agent.suggest.documents.1': 'Make a GST tax invoice',
  'agent.suggest.documents.2': 'Make a payment receipt',
  'agent.suggest.documents.3': 'Make a quotation',
  'agent.suggest.document.1': 'Add my bank details and UPI QR',
  'agent.suggest.document.2': 'Show HSN on each item',
  'agent.suggest.document.3': 'Print it on A5',
  'agent.suggest.account.1': 'What can you do?',
  'agent.suggest.account.2': 'What did I record today?',
  // a proposal that carries a recipe: the row above the sentence, the way a
  // hop is said out loud, and where an insight that reads two records goes
  'agent.worked_out': 'Worked out',
  'agent.worked_out_as': 'Worked out as {{recipe}}',
  'agent.reads_through': 'The {{via}}’s {{field}}',
  'agent.card_counts_rows': 'How many $t(glossary.entry_other)',
  'agent.card_counts_expr': 'A worked-out value',
  'agent.card_combines': 'Puts {{parts}} together',
  'agent.card_on_dashboard': 'On the $t(glossary.dashboard)',
  'agent.confirm_card_dashboard': 'Put it on the $t(glossary.dashboard)',
  // one total, said out loud: "Total of Litres", "Total of the amount in
  // Deliveries". The aggregate's own word comes from insight.agg.*
  'agent.card_of': '{{agg}} of {{source}}',
  'agent.card_in': '{{total}} in {{record}}',
  'agent.card_amount': 'the amount',
  // and the receipt for one, which names no record: it went on neither
  'agent.receipt.card_dashboard': '{{insight}} is on the $t(glossary.dashboard).',
  // a pinned insight changed in its place, or taken down
  'agent.card_change_title': 'Change an $t(glossary.insight_one) on {{record}}',
  'agent.card_change_dashboard_title': 'Change a $t(glossary.dashboard) $t(glossary.insight_one)',
  'agent.card_replaces': 'Takes the place of {{insight}}',
  'agent.card_remove_title': 'Take an $t(glossary.insight_one) off {{record}}',
  'agent.card_remove_dashboard_title': 'Take an $t(glossary.insight_one) off the $t(glossary.dashboard)',
  'agent.card_remove_note': 'The $t(glossary.insight_one) comes off. Nothing it counted is lost.',
  // a record or list the assistant proposes to remove: the confirm sends the same delete the screen does
  'agent.record_remove_title': 'Remove the $t(glossary.record_one) {{record}}',
  'agent.record_remove_title_list': 'Remove the $t(glossary.list_one) {{record}}',
  'agent.record_remove_note': 'It leaves every page for everyone in this workspace. Nothing is removed until you confirm.',
  // a choice field the assistant proposes to turn into a list: the confirm sends the to-list route, and the list takes the choices as its rows
  'agent.field_convert_title': 'Turn the $t(glossary.field_one) “{{field}}” into a $t(glossary.list_one)',
  'agent.field_convert_field': 'Field',
  'agent.field_convert_list': 'List',
  'agent.field_convert_rows': 'Rows',
  'agent.field_convert_more': 'and {{count, number}} more',
  'agent.field_convert_note': 'Every $t(glossary.entry_one) keeps its choice, now as a $t(glossary.row_one) of the $t(glossary.list_one). It cannot be undone with one tap.',
  'agent.field_convert_not_here': 'Turning a $t(glossary.field_one) into a $t(glossary.list_one) is only on the web for now.',
  'agent.confirm_field_convert': 'Make the $t(glossary.list_one)',
  'agent.confirm_take_down': 'Take it down',
  'agent.receipt.card_change': '{{insight}} has changed.',
  'agent.receipt.card_remove': '{{insight}} is taken off.',
  'agent.receipt.record_remove': '{{record}} is removed.',
  'agent.receipt.field_convert': 'The $t(glossary.field_one) “{{field}}” now picks from the $t(glossary.list_one) “{{list}}”.',
  // a field the proposal gives a starting value to: said the same way the
  // customize screen says it, because it is the same sentence about the same tree
  'agent.starts_with_as': 'Starts with {{recipe}}',
  // a fact: one sentence the records cannot hold, remembered, corrected or
  // forgotten (docs/design/agent-business-memory, the Words board). A sentence
  // the person said is never a key: it is printed as they said it, in quotes.
  'agent.fact_title': 'Remember',
  'agent.fact_change_title': 'Replaces',
  'agent.fact_forget_title': 'Forget',
  'agent.fact_scope_business': 'For the $t(glossary.business_one)',
  'agent.fact_scope_person': 'For you',
  'agent.fact_not_saved': 'Not saved until you confirm.',
  'agent.confirm_fact': 'Remember this',
  'agent.confirm_fact_forget': 'Forget it',
  'agent.receipt.fact': 'Remembered.',
  'agent.receipt.fact_change': 'Changed. The old sentence is kept under Retired.',
  'agent.receipt.fact_forget': 'Forgotten. It is kept under Retired.',
  // the three refusals a fact's tap can meet: who may write for the business,
  // a sentence like one that stands (`{{fact}}` is that sentence), a line already changed
  'agent.fact_not_allowed': 'Only the owner, or someone who may set policies, can remember something for the whole $t(glossary.business_one). Ask them, or keep it as a fact for yourself.',
  'agent.fact_similar': 'A sentence like that already stands: “{{fact}}”.',
  'agent.fact_retired': 'That line was already changed.',

  // --------------------------------------------- what the assistant knows
  // The screen under the business's settings (decision 16 of
  // docs/plans/agent-business-memory.md), from the same Words board.
  'facts.title': 'What OS knows',
  'facts.help': 'What you have told it, in your words',
  'facts.section.business': 'About the $t(glossary.business_one)',
  'facts.section.you': 'About you',
  'facts.section.setup': 'From setup',
  'facts.retired': 'Retired',
  'facts.retired_on': 'Retired {{day}}',
  'facts.replaces_from': 'replaces one from {{day}}',
  'facts.edit': 'Edit',
  'facts.forget': 'Forget',
  'facts.edit.title': 'Edit',
  'facts.edit.note': 'The old sentence is kept under Retired.',
  'facts.edit.count': '{{count, number}}/200',
  'facts.empty': 'What you tell OS about how your business works is kept here.',

  // ------------------------------------------------- the trail of the steps
  // What the assistant did while it worked, one row per tool call, folded to
  // one line. The words are the design's own map (docs/design/agent-activity/
  // build.mjs, WORDS): every variable takes a record name, a field label or a
  // number, never a tool name and never an id.
  'agent.step.steps_one': '{{count, number}} step',
  'agent.step.steps_other': '{{count, number}} steps',
  'agent.step.list_records.run': 'Looking up your $t(glossary.record_other)',
  'agent.step.list_records.done': 'Looked up your $t(glossary.record_other)',
  'agent.step.list_records.sum_one': '{{count, number}} $t(glossary.record_one)',
  'agent.step.list_records.sum_other': '{{count, number}} $t(glossary.record_other)',
  'agent.step.read_schema.run': 'Reading the shape of {{record}}',
  'agent.step.read_schema.done': 'Read the shape of {{record}}',
  'agent.step.read_schema.sum_one': '{{count, number}} $t(glossary.field_one)',
  'agent.step.read_schema.sum_other': '{{count, number}} $t(glossary.field_other)',
  'agent.step.read_file.run': 'Reading the files',
  'agent.step.read_file.done': 'Read the files',
  'agent.step.list_entries.run': 'Reading {{record}}',
  'agent.step.list_entries.done': 'Read {{record}}',
  'agent.step.list_entries.sum_one': '{{count, number}} $t(glossary.entry_one)',
  'agent.step.list_entries.sum_other': '{{count, number}} $t(glossary.entry_other)',
  'agent.step.total.sum.run': 'Adding up {{field}} in {{record}}',
  'agent.step.total.sum.done': 'Added up {{field}} in {{record}}',
  'agent.step.total.count.run': 'Counting $t(glossary.entry_other) in {{record}}',
  'agent.step.total.count.done': 'Counted $t(glossary.entry_other) in {{record}}',
  'agent.step.total.avg.run': 'Averaging {{field}} in {{record}}',
  'agent.step.total.avg.done': 'Averaged {{field}} in {{record}}',
  'agent.step.total.sum': '{{total}} over {{count, number}} $t(glossary.entry_other)',
  'agent.step.list_links.run': 'Checking the $t(glossary.link_other) out of {{record}}',
  'agent.step.list_links.done': 'Checked the $t(glossary.link_other) out of {{record}}',
  'agent.step.list_links.sum_one': '{{count, number}} $t(glossary.link_one)',
  'agent.step.list_links.sum_other': '{{count, number}} $t(glossary.link_other)',
  'agent.step.list_members.run': 'Looking at the team',
  'agent.step.list_members.done': 'Looked at the team',
  'agent.step.list_members.sum_one': '{{count, number}} $t(glossary.person_one)',
  'agent.step.list_members.sum_other': '{{count, number}} $t(glossary.person_other)',
  'agent.step.list_jobs.run': 'Reading the $t(glossary.job_other)',
  'agent.step.list_jobs.done': 'Read the $t(glossary.job_other)',
  'agent.step.list_jobs.sum_one': '{{count, number}} $t(glossary.job_one)',
  'agent.step.list_jobs.sum_other': '{{count, number}} $t(glossary.job_other)',
  'agent.step.list_access.run': 'Checking who can open {{record}}',
  'agent.step.list_access.done': 'Checked who can open {{record}}',
  'agent.step.list_access.sum_one': '{{count, number}} $t(glossary.person_one)',
  'agent.step.list_access.sum_other': '{{count, number}} $t(glossary.person_other)',
  'agent.step.what_may_i_do.run': 'Checking what you may do here',
  'agent.step.what_may_i_do.done': 'Checked what you may do here',
  'agent.step.review_business.run': 'Reviewing the $t(glossary.business_one)',
  'agent.step.review_business.done': 'Reviewed the $t(glossary.business_one)',
  'agent.step.review_business.sum_one': '{{count, number}} finding',
  'agent.step.review_business.sum_other': '{{count, number}} findings',
  'agent.step.refused.not_allowed': 'not open to you',
  'agent.step.refused.field_unknown': 'no such $t(glossary.field_one)',
  'agent.step.refused.record_unknown': 'no such $t(glossary.record_one)',
  'agent.step.refused.no_measure': 'nothing to add up',
  'agent.step.refused.other': 'could not',
  'agent.step.failed': 'did not finish',
  'agent.step.chip.by': 'By {{who}}',
  'agent.step.chip.titled': 'Titled “{{words}}”',
  'agent.step.chip.newest': 'Newest {{count, number}}',
  'agent.step.chip.since': 'Since {{day}}',
  'agent.step.chip.until': 'Until {{day}}',
  // What a write tool is drafting, one sentence per thing it can draft, in
  // both phases: '.run' while the card streams in, '.done' once it has. The
  // words come from the card itself (a record's name, a person's, a count);
  // before the card lands they come from the tool's input, and a name not
  // known yet ends the sentence where it would have stood ("Drafting an
  // invitation for…"). 'other' answers a write tool this build has no words for.
  'agent.step.propose.fields_rename.run': 'Drafting a new name for {{record}}',
  'agent.step.propose.fields_rename.done': 'Drafted a new name for {{record}}',
  'agent.step.propose.fields_add.run_one': 'Drafting {{count, number}} new $t(glossary.field_one) for {{record}}',
  'agent.step.propose.fields_add.run_other': 'Drafting {{count, number}} new $t(glossary.field_other) for {{record}}',
  'agent.step.propose.fields_add.done_one': 'Drafted {{count, number}} new $t(glossary.field_one) for {{record}}',
  'agent.step.propose.fields_add.done_other': 'Drafted {{count, number}} new $t(glossary.field_other) for {{record}}',
  'agent.step.propose.fields_update.run_one': 'Drafting a change to {{count, number}} $t(glossary.field_one) in {{record}}',
  'agent.step.propose.fields_update.run_other': 'Drafting changes to {{count, number}} $t(glossary.field_other) in {{record}}',
  'agent.step.propose.fields_update.done_one': 'Drafted a change to {{count, number}} $t(glossary.field_one) in {{record}}',
  'agent.step.propose.fields_update.done_other': 'Drafted changes to {{count, number}} $t(glossary.field_other) in {{record}}',
  'agent.step.propose.fields_archive.run_one': 'Drafting the removal of {{count, number}} $t(glossary.field_one) from {{record}}',
  'agent.step.propose.fields_archive.run_other': 'Drafting the removal of {{count, number}} $t(glossary.field_other) from {{record}}',
  'agent.step.propose.fields_archive.done_one': 'Drafted the removal of {{count, number}} $t(glossary.field_one) from {{record}}',
  'agent.step.propose.fields_archive.done_other': 'Drafted the removal of {{count, number}} $t(glossary.field_other) from {{record}}',
  'agent.step.propose.fields_order.run': 'Drafting a new $t(glossary.field_one) order for {{record}}',
  'agent.step.propose.fields_order.done': 'Drafted a new $t(glossary.field_one) order for {{record}}',
  'agent.step.propose.fields_icon.run': 'Drafting a new icon for {{record}}',
  'agent.step.propose.fields_icon.done': 'Drafted a new icon for {{record}}',
  'agent.step.propose.fields_capture.run': 'Drafting a sheet for {{record}}',
  'agent.step.propose.fields_capture.done': 'Drafted a sheet for {{record}}',
  'agent.step.propose.fields_mixed.run': 'Drafting changes to {{record}}',
  'agent.step.propose.fields_mixed.done': 'Drafted changes to {{record}}',
  'agent.step.propose.layout.run': 'Drafting a form layout for {{record}}',
  'agent.step.propose.layout.done': 'Drafted a form layout for {{record}}',
  'agent.step.propose.record.run': 'Drafting the {{name}} $t(glossary.record_one)',
  'agent.step.propose.record.done': 'Drafted the {{name}} $t(glossary.record_one)',
  'agent.step.propose.list.run': 'Drafting the {{name}} $t(glossary.list_one)',
  'agent.step.propose.list.done': 'Drafted the {{name}} $t(glossary.list_one)',
  'agent.step.propose.entry.run': 'Drafting an $t(glossary.entry_one) in {{record}}',
  'agent.step.propose.entry.done': 'Drafted an $t(glossary.entry_one) in {{record}}',
  'agent.step.propose.entries.run_one': 'Drafting {{count, number}} $t(glossary.entry_one) in {{record}}',
  'agent.step.propose.entries.run_other': 'Drafting {{count, number}} $t(glossary.entry_other) in {{record}}',
  'agent.step.propose.entries.done_one': 'Drafted {{count, number}} $t(glossary.entry_one) in {{record}}',
  'agent.step.propose.entries.done_other': 'Drafted {{count, number}} $t(glossary.entry_other) in {{record}}',
  'agent.step.propose.entry_change.run': 'Drafting a correction in {{record}}',
  'agent.step.propose.entry_change.done': 'Drafted a correction in {{record}}',
  'agent.step.propose.entry_remove.run': 'Drafting the removal of an $t(glossary.entry_one) from {{record}}',
  'agent.step.propose.entry_remove.done': 'Drafted the removal of an $t(glossary.entry_one) from {{record}}',
  'agent.step.propose.link.run': 'Drafting a $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.link.done': 'Drafted a $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.link_change.run': 'Drafting a change to the $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.link_change.done': 'Drafted a change to the $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.link_remove.run': 'Drafting the removal of the $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.link_remove.done': 'Drafted the removal of the $t(glossary.link_one) from {{record}} to {{target}}',
  'agent.step.propose.card.run': 'Drafting an $t(glossary.insight_one) for {{record}}',
  'agent.step.propose.card.done': 'Drafted an $t(glossary.insight_one) for {{record}}',
  'agent.step.propose.card_dashboard.run': 'Drafting an $t(glossary.insight_one) for the $t(glossary.dashboard)',
  'agent.step.propose.card_dashboard.done': 'Drafted an $t(glossary.insight_one) for the $t(glossary.dashboard)',
  'agent.step.propose.card_change.run': 'Drafting a change to the $t(glossary.insight_one) “{{card}}”',
  'agent.step.propose.card_change.done': 'Drafted a change to the $t(glossary.insight_one) “{{card}}”',
  'agent.step.propose.card_remove.run': 'Drafting the removal of the $t(glossary.insight_one) “{{card}}”',
  'agent.step.propose.card_remove.done': 'Drafted the removal of the $t(glossary.insight_one) “{{card}}”',
  'agent.step.propose.record_remove.run': 'Drafting the removal of “{{record}}”',
  'agent.step.propose.record_remove.done': 'Drafted the removal of “{{record}}”',
  'agent.step.propose.field_convert.run': 'Drafting a $t(glossary.list_one) from “{{field}}”',
  'agent.step.propose.field_convert.done': 'Drafted a $t(glossary.list_one) from “{{field}}”',
  'agent.step.propose.invite.run': 'Drafting an invitation for {{person}}',
  'agent.step.propose.invite.done': 'Drafted an invitation for {{person}}',
  'agent.step.propose.invites.run_one': 'Drafting {{count, number}} invitation',
  'agent.step.propose.invites.run_other': 'Drafting {{count, number}} invitations',
  'agent.step.propose.invites.done_one': 'Drafted {{count, number}} invitation',
  'agent.step.propose.invites.done_other': 'Drafted {{count, number}} invitations',
  'agent.step.propose.invite_change.run': "Drafting a change to {{person}}'s invitation",
  'agent.step.propose.invite_change.done': "Drafted a change to {{person}}'s invitation",
  'agent.step.propose.job.run': 'Drafting the {{job}} $t(glossary.job_one)',
  'agent.step.propose.job.done': 'Drafted the {{job}} $t(glossary.job_one)',
  'agent.step.propose.job_change.run': 'Drafting a change to the {{job}} $t(glossary.job_one)',
  'agent.step.propose.job_change.done': 'Drafted a change to the {{job}} $t(glossary.job_one)',
  'agent.step.propose.job_apply.run': 'Drafting the {{job}} $t(glossary.job_one) for {{person}}',
  'agent.step.propose.job_apply.done': 'Drafted the {{job}} $t(glossary.job_one) for {{person}}',
  'agent.step.propose.job_apply_many.run_one': 'Drafting the {{job}} $t(glossary.job_one) for {{count, number}} $t(glossary.person_one)',
  'agent.step.propose.job_apply_many.run_other': 'Drafting the {{job}} $t(glossary.job_one) for {{count, number}} $t(glossary.person_other)',
  'agent.step.propose.job_apply_many.done_one': 'Drafted the {{job}} $t(glossary.job_one) for {{count, number}} $t(glossary.person_one)',
  'agent.step.propose.job_apply_many.done_other': 'Drafted the {{job}} $t(glossary.job_one) for {{count, number}} $t(glossary.person_other)',
  'agent.step.propose.grant.run': 'Drafting access to {{record}} for {{person}}',
  'agent.step.propose.grant.done': 'Drafted access to {{record}} for {{person}}',
  'agent.step.propose.grant_revoke.run': "Drafting the end of {{person}}'s access to {{record}}",
  'agent.step.propose.grant_revoke.done': "Drafted the end of {{person}}'s access to {{record}}",
  'agent.step.propose.access.run': 'Drafting what {{person}} can open',
  'agent.step.propose.access.done': 'Drafted what {{person}} can open',
  'agent.step.propose.role.run': 'Drafting the {{role}} role',
  'agent.step.propose.role.done': 'Drafted the {{role}} role',
  'agent.step.propose.role_change.run': 'Drafting a change to the {{role}} role',
  'agent.step.propose.role_change.done': 'Drafted a change to the {{role}} role',
  'agent.step.propose.role_remove.run': 'Drafting the removal of the {{role}} role',
  'agent.step.propose.role_remove.done': 'Drafted the removal of the {{role}} role',
  'agent.step.propose.role_copy.run': 'Drafting a copy of the {{role}} role',
  'agent.step.propose.role_copy.done': 'Drafted a copy of the {{role}} role',
  'agent.step.propose.role_give.run': 'Drafting the {{role}} role for {{person}}',
  'agent.step.propose.role_give.done': 'Drafted the {{role}} role for {{person}}',
  'agent.step.propose.person_change.run': 'Drafting a change for {{person}}',
  'agent.step.propose.person_change.done': 'Drafted a change for {{person}}',
  'agent.step.propose.member_remove.run': 'Drafting the removal of {{person}} from the $t(glossary.business_one)',
  'agent.step.propose.member_remove.done': 'Drafted the removal of {{person}} from the $t(glossary.business_one)',
  'agent.step.propose.primary_ask.run': 'Drafting an ask to be the primary admin',
  'agent.step.propose.primary_ask.done': 'Drafted an ask to be the primary admin',
  'agent.step.propose.ask.run': 'Drafting an ask for a right',
  'agent.step.propose.ask.done': 'Drafted an ask for a right',
  'agent.step.propose.ask_answer.run': "Drafting an answer to {{person}}'s ask",
  'agent.step.propose.ask_answer.done': "Drafted an answer to {{person}}'s ask",
  'agent.step.propose.handover.run': 'Drafting the hand-over of the $t(glossary.business_one) to {{person}}',
  'agent.step.propose.handover.done': 'Drafted the hand-over of the $t(glossary.business_one) to {{person}}',
  'agent.step.propose.handover_answer.run': "Drafting an answer to {{person}}'s hand-over",
  'agent.step.propose.handover_answer.done': "Drafted an answer to {{person}}'s hand-over",
  'agent.step.propose.business.run': 'Drafting a change to the $t(glossary.business_one) details',
  'agent.step.propose.business.done': 'Drafted a change to the $t(glossary.business_one) details',
  'agent.step.propose.fact.run': 'Drafting something to remember',
  'agent.step.propose.fact.done': 'Drafted something to remember',
  'agent.step.propose.fact_change.run': 'Drafting a change to what I know',
  'agent.step.propose.fact_change.done': 'Drafted a change to what I know',
  'agent.step.propose.bundle.run_one': 'Drafting {{count, number}} change',
  'agent.step.propose.bundle.run_other': 'Drafting {{count, number}} changes',
  'agent.step.propose.bundle.done_one': 'Drafted {{count, number}} change',
  'agent.step.propose.bundle.done_other': 'Drafted {{count, number}} changes',
  'agent.step.propose.import.run': 'Drafting a way to bring it in',
  'agent.step.propose.import.done': 'Drafted a way to bring it in',
  'agent.step.propose.other.run': 'Drafting a change',
  'agent.step.propose.other.done': 'Drafted a change',
  'agent.voice.collapse': 'Back to voice',
  'agent.answer_landed': 'The answer is in the conversation',
  'agent.live.error.dropped_body': 'The answer is in the chat. Talk live to carry on.',
  'agent.live.stopped': 'Stopped',
  'agent.queued': 'Next',
  'agent.queued_drop': 'Don’t ask this',
  'agent.queued_drop_hint': 'Offers to drop this question.',
  'agent.queued_edit_hint': 'Puts the words back in the box to change them.',
  'agent.live.couldnt_finish': 'I couldn’t finish that',
  'agent.live.couldnt_finish_female': 'I couldn’t finish that',
  'agent.live.couldnt_finish_male': 'I couldn’t finish that',
  'agent.feedback.marked_right': 'Marked right',
  'agent.feedback.reason.action': 'It did the wrong thing',
  'agent.feedback.reason.numbers': 'The numbers are wrong',
  'agent.feedback.reason.other': 'Something else',
  'agent.feedback.reason.understood': 'It didn’t understand me',
  'agent.feedback.report': 'Report a problem',
  'agent.feedback.right': 'This is right',
  'agent.feedback.sent': 'Sent · {{reason}}',
  'agent.feedback.wrong': 'This is wrong',
  'agent.feedback.attached': 'Your question and the answer go with it.',
  'agent.feedback.note': 'Add a line (optional)',
  'agent.feedback.title': 'What went wrong?',
  'agent.voice.touch_confirm': 'I never do this by voice. Check it, then tap to confirm.',
  'agent.voice.touch_confirm_female': 'I never do this by voice. Check it, then tap to confirm.',
  'agent.voice.touch_confirm_male': 'I never do this by voice. Check it, then tap to confirm.',
  'agent.touch_remove': 'Remove it',
  'agent.live.opening_form': 'Opening the form',
  'agent.live.opening_form_female': 'Opening the form',
  'agent.live.opening_form_male': 'Opening the form',
  'agent.minimized.label': 'OS, on a call',
  'agent.minimized.hint': 'Opens the conversation. Touch and hold for more.',
  'agent.minimized.open': 'Open',
  'agent.ask_why': 'Why I ask',
  'agent.ask_why_female': 'Why I ask',
  'agent.ask_why_male': 'Why I ask',
  'agent.undo_failed': 'That could not be undone. Try again in a moment.',

  // ----------------------------------------------------------- failures
  // What a person reads when a call failed, app-wide (`failure-words.ts`).
  // None names the server, a status code, a command or a host: those go to
  // the developer's console in development only.
  'failure.unreachable': 'Could not reach RecordOS. Check your internet connection, then try again.',
  'failure.signed_out': 'You were signed out. Sign in again to carry on.',
  'failure.not_allowed': 'Your role does not open this. Ask the person who runs this workspace.',
  'failure.out_of_date': 'RecordOS was just updated. Open it again, then try again.',
  'failure.too_often': 'Too many tries at once. Wait a minute, then try again.',
  'failure.our_side': 'Something went wrong in RecordOS. Nothing you typed is lost. Try again in a minute.',
  'failure.refused': 'That did not go through. Try again, or change what you asked.',
  'failure.auth.invalid_email_or_password':
    'That email and password do not match an account. Check both for a typo, or create an account.',
  'failure.auth.invalid_email': 'That email address does not look right. Check it for a typo.',
  'failure.auth.invalid_password': 'That password is wrong. Try again.',
  'failure.auth.user_not_found': 'No account uses that email address. Create one instead.',
  'failure.auth.user_already_exists': 'An account already uses that email address. Sign in instead.',
  'failure.auth.user_already_exists_use_another_email':
    'An account already uses that email address. Sign in instead, or use another address.',
  'failure.auth.credential_account_not_found': 'That account has no password set. It was created another way.',
  'failure.auth.email_not_verified': 'Verify your email address first. Look for the code in your inbox.',
  'failure.auth.password_too_short': 'That password is too short. Choose a longer one.',
  'failure.auth.password_too_long': 'That password is too long. Choose a shorter one.',
  'failure.auth.session_expired': 'Your session expired. Sign in again.',
  'failure.auth.not_now': 'Signing in did not work just now. Try again in a moment.',
  'failure.auth.validation_error': 'One of the fields is not right. Check them and try again.',
  'failure.auth.missing_field': 'One of the fields is empty. Fill it in and try again.',

  'common.done': 'Done',
  'common.remove': 'Remove',
  'common.save': 'Save',
  'common.try_again': 'Try again',
  'common.loading': 'Loading',
  // The boot splash, before anything knows who is looking. Its own sentence and
  // not `common.loading`, because what is being waited for is the one thing a
  // person cannot see: whether the session on this device is still good.
  'common.checking_session': 'Checking whether you are signed in',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.continue': 'Continue',
  // Back from a form with something not saved: Edit profile, Edit details.
  'common.unsaved.title': 'Discard your changes?',
  'common.unsaved.body': 'What you changed here is not saved yet',
  'common.keep_editing': 'Keep editing',
  'common.discard': 'Discard',
  // The photo library, refused, when a photo or a logo is chosen.
  'image_pick.refused': 'Your photos were not allowed. Allow them in Settings, then try again',

  /*
   * The design system's own chrome.
   *
   * These are the words `packages/ui` says for itself — a close cross, a clear
   * cross, a spinner's name, a password's eye. They were typed into components
   * as English literals because until `t()` moved into this package there was
   * no legal way for a design-system component to reach a key. *Writing in the
   * UI* rule 2 applies to a primitive exactly as it applies to a screen.
   */
  'common.close': 'Close',
  'common.clear': 'Clear',
  'common.dismiss': 'Dismiss',
  'common.show_password': 'Show password',
  'common.hide_password': 'Hide password',
  'common.undo': 'Undo',

  /* The currency sheet, which `packages/ui` draws for itself. */
  'currency.title': 'Currency',
  'currency.search': 'Search currencies',
  'currency.search_hint': 'Rupee, INR, ₹',

  /* The phone country sheet, which `packages/ui` draws for itself. */
  'phone.country': 'Country code',
  'phone.country_title': 'Country',
  'phone.search': 'Search countries',
  'phone.search_hint': 'India, +91',
  'phone.hint_all': 'Type a name or a code.',
  'phone.hint_none': 'No country matches',
  /*
   * The fields, which `packages/ui` draws for itself.
   *
   * Every word a field says: its validation messages, its empty states, the
   * labels a screen reader reads off its glyph buttons. They were English
   * literals in `fields/` for the same reason the chrome above was — the
   * package could not reach a key — and step 3.1 of the polish plan moved
   * them here.
   *
   * A `{{slot}}` is filled through `tWith`, which is `t` with its options
   * block spelled out. There is one placeholder spelling in this catalogue
   * and i18next fills it; `interpolation.enforcement.test.ts` is the guard.
   */
  'ui.field.required': 'This field is needed.',

  /* The currency sheet's hint line, which counts what it is showing. */
  'ui.currency.pick': 'Pick a currency',
  'ui.currency.hint_common': 'Common {{common}}. Type to search all {{all}}.',
  'ui.currency.hint_narrowed': '{{shown}} of {{found}}. Type another letter to narrow it.',
  'ui.currency.hint_all': '{{found}} match.',
  'ui.currency.no_match': 'No currency matches "{{query}}".',

  /* The reference picker: a field that points at a row in another record. */
  'ui.reference.choose_single': 'Choose one',
  'ui.reference.choose_from': 'Choose from {{target}}',
  'ui.reference.search': 'Search',
  'ui.reference.details': 'Details',
  'ui.reference.add_first': 'Add the first one',
  'ui.reference.add_named': 'Add "{{title}}"',
  'ui.reference.first_reason': 'This list is empty, so it can be started here',
  'ui.reference.first_reason_named': '{{target}} is empty, so it can be started here',
  'ui.reference.saved_here': 'Saved for next time',
  'ui.reference.saved_to': 'Saved to {{target}}, ready next time',
  'ui.reference.this_list': 'This list',
  'ui.reference.no_rows': '{{target}} has no $t(glossary.entry_other) yet.',
  'ui.reference.nothing_recent': 'Nothing used recently. Type to search.',
  'ui.reference.type_to_search': 'Type to search.',
  'ui.reference.no_match': 'Nothing matches that.',
  'ui.reference.filtered_by': 'Only the ones under {{parent}}',
  'ui.reference.filtered_by_named': 'Only {{target}} under {{parent}}',
  'ui.reference.parent_first': 'Pick the {{field}} first to add a new one',
  'ui.reference.no_parent': 'Not under any',
  'ui.multi.chosen_one': '{{count, number}} chosen',
  'ui.multi.chosen_other': '{{count, number}} chosen',
  'ui.multi.of_max': '{{chosen, number}} of {{most, number}}',
  'ui.multi.max_reached': 'You can pick {{most, number}}',
  'ui.multi.over_max': 'Pick no more than {{most, number}}',

  /* The chip picker: one or many from a closed list. */
  'ui.choice.choose_single': 'Choose one',
  'ui.choice.choose_any': 'Choose any',
  'ui.choice.filter': 'Filter',
  'ui.choice.no_match': 'Nothing matches that.',
  'ui.choice.empty': 'There are no options to choose from yet.',
  'ui.choice.empty_target': 'There is nothing in {{target}} to choose from yet.',
  'ui.choice.more': '{{rest}} more',

  /* The map picker, and the two sample coordinates it shows as placeholders. */
  'ui.map.latitude': 'Latitude',
  'ui.map.longitude': 'Longitude',
  'ui.map.place_name': 'Place name',
  'ui.map.place_hint': 'Shop front',
  'ui.map.latitude_hint': '12.971599',
  'ui.map.longitude_hint': '77.594566',
  'ui.map.latitude_range': 'Latitude runs from -{{max}} to {{max}}.',
  'ui.map.longitude_range': 'Longitude runs from -{{max}} to {{max}}.',
  'ui.map.needs_coordinates': 'A place needs its latitude and longitude, not just a name.',
  'ui.map.needs_both': 'A place needs both a latitude and a longitude.',
  'ui.map.use_button': 'Use the current location button to set this.',
  'ui.map.not_found': 'Could not find where you are. Try again in the open.',
  'ui.map.open_title': 'Open in Maps?',
  'ui.map.open_body': 'This opens Maps on this pin.',
  'ui.map.open_confirm': 'Open in Maps',
  'ui.map.not_now': 'Not now',
  'ui.map.open_action': 'Open',

  /* The number fields: the stepper with its two glyph buttons, and the plain one. */
  'ui.number.not_a_number': 'This needs to be a number.',
  'ui.number.too_big': 'That number is too big to store.',
  'ui.number.range': 'Enter a number between {{min}} and {{max}}.',
  'ui.number.min_only': 'Enter {{min}} or more.',
  'ui.number.max_only': 'Enter {{max}} or less.',
  'ui.stepper.range': 'Pick a number between {{min}} and {{max}}.',
  'ui.stepper.min_only': 'Pick {{min}} or more.',
  'ui.stepper.max_only': 'Pick {{max}} or less.',
  'ui.stepper.less': 'One less {{label}}',
  'ui.stepper.more': 'One more {{label}}',

  /* The text fields. */
  'ui.text.too_long': 'Keep this to {{limit}} characters.',
  'ui.text.call': 'Call',
  'ui.phone.digits_only': 'A phone number is only digits, and may start with a +.',
  'ui.phone.too_short': 'A phone number needs at least {{digits}} digits.',
  'ui.phone.too_long': 'A phone number holds at most {{digits}} digits.',
  'ui.unit.label': '{{label}} unit',

  /* The date picker. `MONTHS` is not here yet; step 3.6 moves it to `format.ts`. */
  'ui.date.unreadable': 'This needs to be a date.',
  'ui.date.range': 'Pick a date between {{from}} and {{to}}.',
  'ui.date.min_only': 'Pick {{from}} or later.',
  'ui.date.max_only': 'Pick {{to}} or earlier.',
  'ui.date.pick_datetime': 'Pick a date and time.',
  'ui.date.pick_date': 'Pick a date.',
  'ui.date.choose_datetime': 'Choose a date and time',
  'ui.date.choose_date': 'Choose a date',

  /* The file picker, whose counts each take a singular word of their own. */
  'ui.file.limit_one': 'Attach {{count, number}} file only.',
  'ui.file.limit_other': 'Attach at most {{count, number}} files.',
  'ui.file.photo': 'Take a photo',
  'ui.file.photo_more': 'Take another photo',
  'ui.file.library': 'Choose from library',
  'ui.file.library_more': 'Choose another',
  'ui.file.any': 'Add a file',
  'ui.file.any_more': 'Add another file',
  'ui.file.empty': 'Nothing attached yet.',
  'ui.file.empty_room_one': 'Nothing attached yet. Up to {{count, number}} file.',
  'ui.file.empty_room_other': 'Nothing attached yet. Up to {{count, number}} files.',
  'ui.file.room_one': 'Room for {{count, number}} more.',
  'ui.file.room_other': 'Room for {{count, number}} more.',
  'ui.file.wrong_kind': '{{rejected}} of those is the wrong kind of file.',
  'ui.file.wrong_kind_named': '{{rejected}} of those is not {{kinds}}.',
  'ui.file.overflow': '{{dropped}} did not fit.',
  'ui.file.only_fit': 'Only {{limit}} fit here.',
  'ui.file.remove': 'Remove {{name}}',


  /* ------------------------------------------------------------- signup
   * The doors, from design/explorations/signup-options. Every door the product can offer
   * has a word here even where its server side is not configured yet: a key
   * costs nothing, and the screen that draws the door is the same screen
   * whenever the secret lands.
   */
  'signup.title': 'Get started',
  'signup.lede': 'One account holds every record, on every device you sign in on.',
  'signup.continue_with_email': 'Continue with email',
  'signup.continue_with_phone': 'Continue with phone',
  'signup.continue_with_apple': 'Continue with Apple',
  'signup.continue_with_google': 'Continue with Google',
  'signup.continue_with_outlook': 'Continue with Outlook',
  'signup.continue_with_truecaller': 'Continue with Truecaller',
  'signup.or': 'or',
  'signup.legal': 'By continuing you agree to the Terms and the Privacy Policy.',
  'signup.terms': 'Terms',
  'signup.privacy': 'Privacy Policy',
  'signup.signing_in': 'Signing you in…',
  'signup.email_title': 'Your email address',
  'signup.email_lede': 'A six-digit code goes there. There is no password.',
  'signup.email_send': 'Send the code',
  'signup.email_not_sent': 'The code could not be sent to {{email}}. Try again, or use another way in.',
  'signup.code_title': 'Enter the code',
  'signup.code_sent_to': 'Sent to {{email}}.',
  'signup.code_label': 'Six-digit code',
  'signup.code_resend_in': 'Send a new code in {{time}}',
  'signup.code_checking': 'Checking the code…',
  'signup.name_title': 'What is your name?',
  'signup.name_lede': 'This is the name on the records you share. You can change it later.',
  'signup.name_from_invitation': 'From your invitation. Change it if you like.',
  'signup.no_door': 'No way in is set up on this server yet. Ask whoever runs it to turn one on.',
  /* One sentence, one door named in it. `{{door}}` is the button's own word. */
  'signup.provider_down': '{{door}} could not be reached. Try another way in.',

  /* The floor — the password form the email door falls back to. */

  /* The money calculator, whose errors are about the sum rather than the field. */
  'ui.money.unfinished': 'Finish the sum before saving it.',
  'ui.money.two_signs': 'Two signs in a row is not a sum.',
  'ui.money.divide_by_zero': 'Nothing divides by zero.',
  'ui.money.not_an_amount': 'This sum cannot be worked out.',
  /* Not the same refusal: the sum works, the field declines to take one. */
  'ui.money.plain_amount_only': 'This field takes a plain amount.',
  'ui.money.too_big': 'That amount is too big to store.',
  'ui.money.money_out': '{{label}} is money out',
  'ui.money.money_in': '{{label}} is money in',
  'ui.money.quick_amounts': 'Common {{label}} amounts',
  'ui.reference.all': 'All',
  'ui.reference.recent': 'Recent',

  /*
   * The web app (apps/web). The same words as the phone wherever the phone has
   * one; these are the few the desktop layout adds. One section per flow, so
   * each flow adds its keys under its own line.
   */
  // web: the app frame
  'app.name': 'RecordOS',
  'route.not_found.title': 'This page does not exist',
  'route.not_found.body': 'The link may be old, or the page may have moved.',
  'route.error.title': 'This page could not open',
  'route.error.body': 'Try again. If it keeps happening, reload the page.',
  'route.error.reload': 'Reload the page',
  'nav.main': 'Main',
  'nav.expand': 'Expand the sidebar',
  'nav.collapse': 'Collapse the sidebar',
  'nav.business_details': 'Workspace details',
  'nav.new': 'New',
  'nav.recent': 'Recent',
  'nav.recent.none': 'Nothing opened yet',
  'nav.search_or_ask': 'Search or ask OS',
  'nav.invite': 'Invite people',
  'nav.switch_business': 'Switch $t(glossary.business_one)',
  'nav.account_menu': 'Account menu',
  'nav.scheme': 'Appearance',
  'nav.scheme.system': 'Match the system',
  'nav.scheme.light': 'Light',
  'nav.scheme.dark': 'Dark',
  'nav.sync.synced': 'Synced {{when}}',
  'nav.sync.never': 'Not synced yet',
  'nav.sync.syncing': 'Syncing',
  'nav.sync.pending_one': '{{count, number}} change waiting to sync',
  'nav.sync.pending_other': '{{count, number}} changes waiting to sync',
  'record.panel.switch': 'Switch $t(glossary.record_one)',
  'record.panel.entries': 'Entries',
  'record.panel.insights': 'Insights',
  'record.panel.links': 'Links',
  'record.panel.access': 'Access',
  'record.panel.customize': 'Customize',
  'record.panel.sheet': 'Sheet',
  'record.panel.today': 'Today',
  'record.panel.entry_count_one': '{{count, number}} $t(glossary.entry_one)',
  'record.panel.entry_count_other': '{{count, number}} $t(glossary.entry_other)',
  'record.panel.balance': 'Balance',
  'business.panel.label': 'Settings for this $t(glossary.business_one)',
  'business.panel.details': 'Details',
  'account.panel.label': 'Account settings',
  'account.settings.title': 'Settings',
  // web: the shell and the kit
  /* What the top nav's sync mark opens (board 03-offline). */
  'nav.sync.status': 'Save and sync status',
  'nav.sync.last_synced': 'Last synced {{when}}',
  'nav.sync.waiting_one': '{{count, number}} syncing',
  'nav.sync.waiting_other': '{{count, number}} syncing',
  'nav.sync.retries': 'Tries again on its own',
  'nav.sync.now': 'Sync now',
  /* The sidebar (Stage 01 phase 3): its groups, its foot and the sync row. */
  'nav.all_records': 'All $t(glossary.record_other) · {{count, number}}',
  'nav.all_lists': 'All $t(glossary.list_other) · {{count, number}}',
  'nav.none_yet': 'None yet',
  'nav.keep_open': 'Keep the sidebar open',
  'nav.help': 'Help and shortcuts',
  'nav.help.shortcuts': 'Keyboard shortcuts',
  'nav.help.navigation': 'Collapse or expand the sidebar',
  'nav.help.close': 'Close a menu, a sheet or a dialog',
  'nav.help.ask': 'Ask OS from any page',
  'nav.help.os_layout': 'Move OS to its next layout',
  'nav.version': 'RecordOS {{version}}',
  'nav.sync.row.syncing': 'Syncing…',
  'nav.sync.row.not_syncing': 'Not syncing · last {{when}}',
  'nav.sync.offline.title': 'While you are offline',
  'nav.sync.offline.works': 'Works offline',
  'nav.sync.offline.waits': 'Needs the connection',
  'nav.sync.offline.entries': 'Add, change and delete $t(glossary.entry_other)',
  'nav.sync.offline.open': 'Open every $t(glossary.record_one) and $t(glossary.list_one), and search them',
  'nav.sync.offline.export': 'Export what is on this device',
  'nav.sync.offline.coach': 'OS, calls and dictation need the connection',
  'nav.sync.offline.later': 'Inviting people and changing $t(glossary.field_other) wait until you are back',
  'business.sheet.role_records_one': '{{role}} · {{count, number}} $t(glossary.record_one)',
  'business.sheet.role_records_other': '{{role}} · {{count, number}} $t(glossary.record_other)',
  'ui.breadcrumb.label': 'Breadcrumb',
  'ui.more': 'More',
  'ui.command.description': 'Type a word, then pick a result',
  'ui.calendar.nav': 'Change the month',
  'ui.calendar.previous_month': 'Previous month',
  'ui.calendar.next_month': 'Next month',
  'ui.calendar.month': 'Month',
  'ui.calendar.year': 'Year',
  'ui.calendar.today': 'Today, {{day}}',
  'ui.calendar.selected': '{{day}}, selected',
  'ui.toast.region': 'Notifications',
  // web: surfaces, states and the one-save head (stage 1, phase 5)
  'ui.panel.open_full': 'Open full page',
  'ui.serious.failed': 'That did not go through. Try again.',
  'ui.empty.tell_os': 'Tell OS',
  'ui.ask_admin.action': 'Ask an admin for the right',
  'ui.ask_admin.note': 'An admin of this $t(glossary.business_one) can give you this right. Ask one of them.',
  'ui.save_head.none': 'No changes yet',
  'ui.save_head.count_one': '{{count, number}} change not saved',
  'ui.save_head.count_other': '{{count, number}} changes not saved',
  'ui.save_head.discard': 'Discard',
  'ui.save_head.save': 'Save changes',
  'ui.save_head.saved': 'Saved',
  'ui.save_head.failed': 'The changes were not saved. Try again.',
  'ui.delta.up': 'Up',
  'ui.delta.down': 'Down',
  'ui.freshness.refresh': 'Refresh',
  'ui.condition.remove': 'Remove the condition on {{property}}',
  'ui.code.character': 'Character {{position, number}} of {{total, number}}',
  // web: the shared table (stage 1, phase 6)
  'ui.table.search': 'Search',
  'ui.table.search_clear': 'Clear the search',
  'ui.table.filter_set': '{{filter}} is {{value}}',
  'ui.table.filter_remove': 'Remove {{filter}}',
  'ui.table.sort': 'Sort',
  'ui.table.columns': 'Columns',
  'ui.table.export': 'Export',
  'ui.table.view': 'View',
  'ui.table.view_table': 'Table',
  'ui.table.view_tiles': 'Tiles',
  'ui.table.rows_per_page': 'Rows per page',
  'ui.table.per_page_one': '{{count, number}} row',
  'ui.table.per_page_other': '{{count, number}} rows',
  'ui.table.range': '{{from, number}}–{{to, number}} of {{total, number}}',
  'ui.table.previous': 'Previous page',
  'ui.table.next': 'Next page',
  'ui.table.columns_in_view': 'Columns {{from, number}}–{{to, number}} of {{total, number}}',
  'ui.table.scroll_left': 'One column to the left',
  'ui.table.scroll_right': 'One column to the right',
  'ui.table.scroll_track': 'Scroll the columns',
  'ui.table.select_shown': 'Select every one shown',
  'ui.table.selected_one': '{{count, number}} selected',
  'ui.table.selected_other': '{{count, number}} selected',
  'ui.table.select_all_count_one': 'Select all {{count, number}}',
  'ui.table.select_all_count_other': 'Select all {{count, number}}',
  'ui.table.clear_selection': 'Clear the selection',
  'ui.table.new': 'New',
  'ui.table.key_escape': 'Esc',
  'ui.table.key_search': '/',
  // web: graphics
  // web: auth, account and system states
  'auth.brand.lede_ready': 'The $t(glossary.record_other) shops like yours keep are set up ready.',
  'auth.field.name': 'Your name',
  'auth.field.name_placeholder': 'Priya Sharma',
  'auth.field.email': 'Email',
  'auth.field.email_placeholder': 'you@example.com',
  /* `{{terms}}` and `{{privacy}}` are `signup.terms` and `signup.privacy`, drawn as links. */
  'auth.legal': 'By continuing you agree to the {{terms}} and the {{privacy}}.',
  'auth.verify.expired': 'That code has expired. Send a new code and try again.',
  'auth.verify.resend': 'Send a new code',
  'auth.verify.resent': 'A new code is on its way to {{email}}.',
  'auth.verify.attempts_used': 'That code was tried too many times. Send a new code.',
  'auth.verify.wrong': 'That code is not right. Check the email and try again.',
  /* The sign-in split (web). The carousel's slides draw a sample shop: the names and goods are examples, not data. */
  'auth.top.signed_in_as': 'Signed in as {{email}}',
  'auth.top.not_you': 'Not you? Sign out',
  'auth.top.back_to': 'Back to {{workspace}}',
  'auth.legal_foot.label': 'Legal',
  'auth.legal_foot.privacy': 'Privacy policy',
  'auth.legal_foot.terms': 'Terms of use',
  'auth.brand.about': 'About RecordOS',
  'auth.brand.foot': 'For shops, clinics and small $t(glossary.business_other) that keep their $t(glossary.record_other) by hand today.',
  'auth.carousel.label': 'What RecordOS does',
  'auth.carousel.slide': '{{at, number}} of {{total, number}}',
  'auth.carousel.previous': 'Previous',
  'auth.carousel.next': 'Next',
  'auth.carousel.pause': 'Pause',
  'auth.carousel.play': 'Play',
  'auth.carousel.talk.title': 'Say it, and it is written down',
  'auth.carousel.talk.body': 'Tell OS what happened, in your own words. It becomes an $t(glossary.entry_one).',
  'auth.carousel.talk.ask': 'What did you sell?',
  'auth.carousel.talk.said': 'Two strips of Paracetamol to Venkat Rao, {{amount}}, UPI',
  'auth.carousel.talk.new': 'New $t(glossary.entry_one)',
  'auth.carousel.sample.paracetamol': 'Paracetamol 500 × {{count, number}}',
  'auth.carousel.sample.cough_syrup': 'Cough syrup 100 ml',
  'auth.carousel.sample.ors': 'ORS sachet × {{count, number}}',
  'auth.carousel.sample.venkat': 'Venkat Rao',
  'auth.carousel.sample.sita': 'Sita Devi',
  'auth.carousel.sample.walk_in': 'Walk-in',
  'auth.carousel.sample.kavya': 'Kavya Clinic',
  'auth.carousel.sample.ravi': 'Ravi Kumar',
  'auth.carousel.sample.suresh': 'Suresh',
  'auth.carousel.sample.meena': 'Meena',
  'auth.carousel.sample.daily_sales': 'Daily sales',
  'auth.carousel.sample.shop_expenses': 'Shop expenses',
  'auth.carousel.sample.party_dues': 'Party dues',
  'auth.carousel.sample.staff_attendance': 'Staff attendance',
  'auth.carousel.records.title': 'Every $t(glossary.record_one) your shop keeps',
  'auth.carousel.records.body': 'Sales, expenses, dues, stock and staff, in one place. Add your own.',
  'auth.carousel.records.today': '{{amount}} today',
  'auth.carousel.records.to_collect': '{{amount}} to collect',
  'auth.carousel.records.stock': 'Stock received',
  'auth.carousel.records.deliveries_one': '{{count, number}} delivery',
  'auth.carousel.records.deliveries_other': '{{count, number}} deliveries',
  'auth.carousel.records.present': '{{present, number}} of {{total, number}} present',
  'auth.carousel.records.own': 'Your own',
  'auth.carousel.records.own_body': 'Tell OS what you keep',
  'auth.carousel.glance.title': 'Know where you stand today',
  'auth.carousel.glance.body': 'Totals, dues and what sells best, worked out as you go.',
  'auth.carousel.glance.week': 'Sales this week',
  'auth.carousel.glance.up': 'Up {{percent, number}}% on last week',
  'auth.carousel.glance.dues': 'Dues to collect',
  'auth.carousel.glance.parties_one': 'From {{count, number}} party',
  'auth.carousel.glance.parties_other': 'From {{count, number}} parties',
  'auth.carousel.glance.top': 'Top item',
  'auth.carousel.glance.top_item': 'Paracetamol 500',
  'auth.carousel.glance.sold_one': '{{count, number}} sold this week',
  'auth.carousel.glance.sold_other': '{{count, number}} sold this week',
  'auth.carousel.links.title': 'One $t(glossary.entry_one), counted everywhere',
  'auth.carousel.links.body': 'A sale on credit adds to what the customer owes. Nothing is written twice.',
  'auth.carousel.links.sale': 'A credit sale in {{record}}',
  'auth.carousel.links.sale_line': '{{party}} · {{amount}} · {{mode}}',
  'auth.carousel.sample.credit': 'Credit',
  'auth.carousel.links.counts': 'counted at once',
  'auth.carousel.links.owes': '{{party}} owes {{amount}} more',
  'auth.carousel.team.title': 'Your team sees only their part',
  'auth.carousel.team.body': 'Invite staff with a code. Each one sees what their role lets them.',
  'auth.carousel.team.every': 'Every $t(glossary.record_one)',
  'auth.carousel.team.counter': 'Counter',
  'auth.carousel.team.counter_sees': 'Adds sales, sees today only',
  'auth.carousel.team.accounts': 'Accounts',
  'auth.carousel.team.accounts_sees': 'Expenses and dues',
  'auth.carousel.team.sync': 'The same $t(glossary.record_other) on every phone and computer, even offline',
  'invite.accept.help': 'A code works for seven days, and once only.',
  'invite.accept.help_from_link': 'Filled in from your link. A code works for seven days, and once only.',
  'invite.accept.no_code': 'No code? Ask the person who runs the $t(glossary.business_one) to invite you.',
  'invite.accept.can_view': 'Can view',
  'invite.accept.can_change': 'Can add and change',
  /* The app language, chosen once before setup (web). A place is a guess from the connection, never asked for. */
  'setup.app_language.title': 'Choose your app language',
  'setup.app_language.lede': 'The language RecordOS appears in for you. OS talks with you in it too; everyone on your team picks their own.',
  'setup.app_language.label': 'Language',
  'setup.app_language.showing_for': 'Showing languages for {{place}}',
  'setup.app_language.show_all': 'Show all languages',
  'setup.app_language.guess_note': 'A guess from where you are connecting. RecordOS does not ask for your location.',
  'setup.app_language.back_to_guess': 'Languages for {{place}}',
  'setup.app_language.every': 'Every language',
  'setup.app_language.no_guess': 'RecordOS could not guess where you are connecting from, so every language is here.',
  'setup.app_language.count_one': '{{count, number}} language',
  'setup.app_language.count_other': '{{count, number}} languages',
  'setup.app_language.search': 'Search in English or in the language itself',
  'setup.app_language.search_label': 'Search languages',
  'setup.app_language.no_match_title': 'No language called “{{query}}”',
  'setup.app_language.no_match_body': 'Try its English name, or the way it is written in its own script.',
  'setup.app_language.continue': 'Continue',
  'setup.app_language.saving': 'Saving…',
  'setup.app_language.save_failed': 'Your language could not be saved. Check your connection and try again.',
  'setup.app_language.place.IN': 'India',
  'setup.app_language.place.US': 'the USA',
  /* Setup's frame on the web (web redesign stage 7, phase 4; web-setup-canvas.md): the top bar, the conversation on the left, the sketch on the right, and the setup card. */
  'setup.frame.title': 'Setting up your business',
  'setup.frame.title_named': 'Setting up {{name}}',
  'setup.frame.steps': 'Setup steps',
  'setup.frame.step_work': 'Your work',
  'setup.frame.step_talk': 'Talk',
  'setup.frame.step_build': 'Build',
  'setup.ways.title': 'Let’s set up your business',
  'setup.ways.title_beside': 'Let’s set up your new business',
  'setup.ways.lede': 'Share what you do and what you keep track of. Your $t(glossary.record_other), $t(glossary.list_other) and dashboard are ready in a few minutes. Pick a way; you can switch at any time.',
  'setup.ways.lede_beside': 'A separate $t(glossary.business_one) with its own $t(glossary.record_other), $t(glossary.list_other) and team; nothing from {{name}} is copied. Pick a way; you can switch at any time.',
  'setup.ways.talk.title': 'Talk it through',
  'setup.ways.talk.desc': 'A voice call, like telling a new helper at the shop. About 4 minutes. Needs your microphone.',
  'setup.ways.type.title': 'Type it out',
  'setup.ways.type.desc': 'Type in your own words. Works anywhere, even somewhere noisy. About 5 minutes.',
  'setup.ways.files.desc': 'Keep a register, Excel sheets, bills or a website? Have them ready: sharing them first makes setup faster. Used only to set up your $t(glossary.business_one).',
  'setup.ways.back': 'App language: {{language}}',
  'setup.files.step_files': 'Your files',
  'setup.files.step_data': 'Bring in data',
  'setup.files.title': 'Start with your files',
  'setup.files.sub': 'OS · optional, and it makes setup faster',
  'setup.files.ask': 'Do you keep records already? Share them, and I will set up most of your workspace from them.',
  'setup.files.ask_body': 'A sales register, Excel sheets, bills, a Tally export, or your website. I read them, then ask only what they do not say. Nothing is created until you click Build.',
  'setup.files.skip_chat': 'No files? Skip this and type',
  'setup.files.skip_voice': 'No files? Skip this and talk',
  'setup.files.placeholder_first': 'Or just start typing about your work',
  'setup.files.drop': 'Drop your files here',
  'setup.files.limits': 'Up to 20 files, 50 MB each. A photo of a paper register works too.',
  'setup.files.choose': 'Choose files',
  'setup.files.drop_now_one': 'Let go to add {{count, number}} file',
  'setup.files.drop_now_other': 'Let go to add these {{count, number}} files',
  'setup.files.drop_now_desc': 'OS starts reading as soon as they land. Up to 20 files, 50 MB each.',
  'setup.files.list': 'Your files',
  'setup.files.count_one': '{{count, number}} file',
  'setup.files.count_other': '{{count, number}} files',
  'setup.files.reading_n': 'Reading {{done, number}} of {{total, number}}',
  'setup.files.learned': 'What I learned from your files',
  'setup.files.two_things_one': '{{count, number}} thing your files do not say:',
  'setup.files.two_things_other': '{{count, number}} things your files do not say:',
  'setup.files.numbered': '{{number, number}}. {{question}}',
  'setup.files.nothing_learned': 'These files do not say how you keep your records. Share a register or a sheet if you have one, or just tell me how a normal day goes.',
  'setup.files.go_talk': 'Go on without files',
  'setup.files.data_waiting_one': 'Your files also hold {{count, number}} $t(glossary.entry_one). Once it is built, I can bring it in, and you choose how.',
  'setup.files.data_waiting_other': 'Your files also hold {{count, number}} $t(glossary.entry_other). Once it is built, I can bring them in, and you choose how.',
  'import.title': 'Bring in your data',
  'import.status.ready': 'Nothing saved yet',
  'import.later': 'You can bring it in later, from OS or from any record.',
  'import.sub': 'OS · from the files you shared',
  'import.sub_check': 'Check each step · OS waits for you at each step',
  'import.sub_auto': 'Do it all for me · OS keeps going on its own',
  'import.status.done': 'Brought in',
  'import.loading': 'Looking at your import…',
  'import.offer.making': 'Reading your files to plan what to bring in…',
  'import.offer.nothing': 'Nothing in your files fits the $t(glossary.record_other) you made. Your setup is ready as it is.',
  // The same, when a workbook's sheets past its first ones were never read: listed under it.
  'import.offer.nothing_unread':
    'Nothing in the sheets read fits the $t(glossary.record_other) you made. Only the first {{read, number}} sheets of a workbook are read: to bring in one past them, move it to the front and add the file again.',
  'import.offer.line_one': '{{name}} is created. Your files also hold your data: {{count, number}} $t(glossary.entry_one).',
  'import.offer.line_other': '{{name}} is created. Your files also hold your data: {{count, number}} $t(glossary.entry_other).',
  'import.offer.ask': 'How do you want to bring it in? Nothing is saved until you choose.',
  'import.offer.plan_cut': 'The import plan ended before it was made.',
  'import.plan.title': 'What I would bring in, in this order',
  'import.plan.rows_one': '{{count, number}} $t(glossary.entry_one)',
  'import.plan.rows_other': '{{count, number}} $t(glossary.entry_other)',
  'import.plan.kind': 'Rows with {{kind}}',
  'import.empty.title': 'Rows that hold no entry',
  'import.empty.left_one': '{{count, number}} row of {{sheet}} holds none of its kinds of $t(glossary.entry_other): left out, not set aside.',
  'import.empty.left_other': '{{count, number}} rows of {{sheet}} hold none of its kinds of $t(glossary.entry_other): left out, not set aside.',
  'import.plan.names_one': '{{count, number}} name',
  'import.plan.names_other': '{{count, number}} names',
  'import.plan.saved_one': '{{count, number}} saved',
  'import.plan.saved_other': '{{count, number}} saved',
  'import.plan.of_one': '{{done, number}} of {{count, number}}',
  'import.plan.of_other': '{{done, number}} of {{count, number}}',
  'import.mark.saved': 'Saved',
  'import.mark.saving': 'Saving…',
  'import.mark.skipped': 'Skipped',
  'import.mark.undone': 'Undone',
  'import.mark.shape': 'Set aside',
  'import.mark.now': 'Now',
  'import.mode.label': 'How to bring it in',
  'import.mode.check.title': 'Check each step',
  'import.mode.check.p1': 'I show you each step before I save it',
  'import.mode.check.p2': 'You see how your columns match your fields',
  'import.mode.check.p3': 'Approve, change or skip any step',
  'import.mode.auto.title': 'Do it all for me',
  'import.mode.auto.p1': 'I save everything I am sure of, step after step',
  'import.mode.auto.p2': 'Anything unclear waits for you to check later',
  'import.mode.auto.p3': 'Pause at any time, and undo it all in one go',
  // OS's import card, for a file too big for one card in a conversation (board os-page-big-file).
  'os.files.mode.ask': 'That is a lot to bring in. How do you want to do it?',
  'os.files.mode.step.1': 'I show each part before I save it: lists first, then each record.',
  'os.files.mode.step.2': 'Change, skip or fix any of it as you go.',
  'os.files.mode.step.3': 'Best the first time, or when the data is messy.',
  'os.files.mode.step.go': 'Check each step',
  'os.files.mode.auto.1': 'I bring in everything I am sure of, in the background.',
  'os.files.mode.auto.2': 'Anything unclear waits in a list for you, unsaved.',
  'os.files.mode.auto.3': 'One click undoes the whole import.',
  'os.files.mode.auto.go': 'Do it all',
  'os.files.mode.note': 'Either way, every $t(glossary.entry_one) I bring in is marked as from this file, so you can find it, or undo it.',
  'os.files.import.part': '{{sheet}} · {{record}}',
  'os.files.import.where_fits': 'Where it fits',
  'import.start_auto': 'Bring it all in',
  'import.not_now': 'Not now',
  'import.auto_note': 'You can pause, and undo it all.',
  'import.status.stopped': 'Stopped',
  'import.status.undone': 'Undone',
  'import.step_of': 'Step {{step, number}} of {{total, number}}',
  'import.percent': '{{percent, number}}%',
  'import.skip': 'Skip this step',
  'import.back': 'Back',
  'import.undo': 'Undo',
  'import.working.reading': 'Reading step {{step, number}}: {{record}}…',
  'import.working.saving': 'Saving into {{record}}…',
  'import.switch.auto': 'Auto-approve all',
  'import.switch.auto_tip': 'Stop checking; OS saves the rest and sets aside anything unclear',
  'import.switch.check': 'Check the rest myself',
  'import.switch.ask_title': 'Auto-approve all the rest?',
  'import.switch.ask_desc': 'I stop asking at each step and save the rest on my own. Anything I am unsure of is set aside for you to check at the end.',
  'import.switch.ask_note': 'You can pause, switch back to checking each step, or undo it all.',
  'import.switch.keep': 'Keep checking',
  'import.switch.this_step': 'This step',
  'import.switch.this_step_value_one': '{{record}}: {{count, number}} $t(glossary.entry_one); the {{aside, number}} unclear set aside',
  'import.switch.this_step_value_other': '{{record}}: {{count, number}} $t(glossary.entry_other); the {{aside, number}} unclear set aside',
  'import.switch.this_step_clean_one': '{{record}}: {{count, number}} $t(glossary.entry_one)',
  'import.switch.this_step_clean_other': '{{record}}: {{count, number}} $t(glossary.entry_other)',
  'import.switch.then': 'Then',
  'import.switch.kept': 'Kept',
  'import.switch.kept_value_one': 'The {{count, number}} step you already approved',
  'import.switch.kept_value_other': 'The {{count, number}} steps you already approved',
  'import.switch.sub': 'Do it all for me · switched at step {{step, number}}',
  'import.switch.by_you': 'Checked by you',
  'import.switch.by_os': 'By OS',
  'import.lists.title_one': '{{count, number}} name for {{list}}',
  'import.lists.title_other': '{{count, number}} names for {{list}}',
  'import.lists.intro': 'Lists first, because every $t(glossary.record_one) picks from them. These names are in your files.',
  'import.lists.sample_one': '{{names}} +{{count, number}}',
  'import.lists.sample_other': '{{names}} +{{count, number}}',
  'import.lists.twin': '“{{name}}” and “{{match}}” look like one. Are they the same?',
  'import.lists.same': 'The same',
  'import.lists.new': 'Add it as new',
  'import.lists.go_one': 'Add {{count, number}} to {{list}}',
  'import.lists.go_other': 'Add {{count, number}} to {{list}}',
  'import.lists.none_new_one': 'The {{count, number}} name is already in {{list}}. Nothing to add.',
  'import.lists.none_new_other': 'All {{count, number}} names are already in {{list}}. Nothing to add.',
  'import.lists.go_none': 'Continue',
  'import.lists.unfilled': 'No file fills {{fields}}, so each new name comes in without it, to fill in {{list}} later.',
  'import.plan.waiting': 'Waiting its turn',
  'import.plan.reading': 'Reading it against your records…',
  'import.plan.goes_to': 'Goes into {{record}}',
  'import.plan.program': 'Reading it with a program',
  // A record fits the sheet, but no mapping or program reads its layout.
  'import.plan.set_aside': 'Fits {{record}}, but its layout cannot be read yet',
  'import.plan.none': 'No record fits this sheet',
  'import.plan.not_read': 'Read in the chat only',
  'import.plan.no_table': 'No table in this file',
  // A workbook whose first sheets hold no table and whose sheets past them were never read, by the chat or the import.
  'import.plan.sheets_not_read': 'No table in the sheets read',
  // A workbook whose sheets read no record fits and whose sheets past them were never read, under a plan with no step.
  'import.plan.sheets_none': 'No $t(glossary.record_one) fits the sheets read',
  // A sheet or a file the plan does not bring in, under the steps, with why.
  'import.outcome.not_brought_in': 'Not brought in',
  'import.outcome.set_aside_why': 'No $t(glossary.record_one) fits this sheet.',
  'import.outcome.shape_why': 'This sheet fits {{record}}, but its layout cannot be read yet.',
  'import.outcome.not_read_why': 'This file holds no table to bring in. It was read in the chat only.',
  'import.map.title': 'How your sheet fits {{record}}',
  'import.map.in_file': 'In your file',
  'import.map.goes_to': 'Goes into',
  'import.map.goes_to_for': 'Where {{column}} goes',
  'import.map.matched': 'Matched',
  'import.map.check': 'Check this',
  'import.map.leave_out': 'Leave it out',
  'import.map.not_kept': 'Not kept',
  // Under a column not kept, while the person's pick for it is OS's: why OS left it out. {{field}} and {{record}} are labels.
  'import.map.reason.computed': 'OS works out {{field}} itself',
  'import.map.reason.other_record': 'Belongs in {{record}} › {{field}}. Not brought in here',
  'import.map.reason.private': 'Left out as private',
  // Under the line's field a column fills, in a sheet whose rows each make one line of the entry: the record's lines field.
  'import.map.in_lines': 'In {{lines}}',
  'import.map.group_column': 'Rows with the same {{column}} make one $t(glossary.entry_one), each row a line of it.',
  'import.map.group_sheet': 'Every row is a line of one $t(glossary.entry_one).',
  'import.map.unfilled': 'No column fills {{fields}}, so every row would wait for you. Pick the column that holds it.',
  'import.map.more_columns_one': 'Show {{count, number}} more column, not kept',
  'import.map.more_columns_other': 'Show {{count, number}} more columns, not kept',
  'import.map.go': 'Looks right, check the rows',
  'import.rows.title_one': '{{count, number}} row, checked',
  'import.rows.title_other': '{{count, number}} rows, checked',
  'import.rows.in_sheet': 'rows in your sheet',
  'import.rows.ready': 'ready to save',
  'import.rows.look': 'to look at',
  'import.rows.more_one': 'And {{count, number}} more like these',
  'import.rows.more_other': 'And {{count, number}} more like these',
  'import.rows.aside_one': 'Set {{count, number}} aside',
  'import.rows.aside_other': 'Set {{count, number}} aside',
  'import.rows.go_one': 'Save {{count, number}} $t(glossary.entry_one)',
  'import.rows.go_other': 'Save {{count, number}} $t(glossary.entry_other)',
  'import.accept_9_one': 'Accept {{count, number}} suggestion',
  'import.accept_9_other': 'Accept {{count, number}} suggestions',
  'import.accept': 'Accept',
  'import.fix': 'Fix',
  'import.fix.title': 'Fix row {{row, number}}',
  'import.fix.title_rows': 'Fix rows {{from, number}} to {{to, number}}',
  'import.fix.title_entry': 'Fix row {{row, number}}, $t(glossary.entry_one) {{entry, number}}',
  'import.fix.add_waiting': '{{name}} is still on its way to the list. Save again in a moment.',
  'import.fix.add_refused': '{{name}} could not be added to the list. Pick another, or add it in the list itself.',
  'import.leave_out_row': 'Leave this row out',
  'import.flag.row': 'Row',
  'import.flag.row_n': 'Row {{row, number}}',
  'import.flag.row_entry': 'Row {{row, number}}, $t(glossary.entry_one) {{entry, number}}',
  'import.flag.row_cell': '{{row, number}}',
  'import.flag.row_cell_entry': '{{row, number}}, $t(glossary.entry_one) {{entry, number}}',
  'import.flag.sheet': 'The whole sheet',
  'import.flag.in_file': 'In your file',
  'import.flag.why': 'Why',
  'import.flag.suggest': 'My suggestion',
  'import.flag.rules': 'The $t(glossary.record_one)’s rules',
  'import.flag.item': 'Name not in the list',
  'import.flag.more_rows_one': 'And {{count, number}} more row of the file',
  'import.flag.more_rows_other': 'And {{count, number}} more rows of the file',
  'import.flag.name_missing': 'The file names {{name}} here, and it is not in the list. Fix the row to pick it or add it.',
  'import.flag.amount': 'Amount unclear',
  'import.flag.date': 'No date I can read',
  'import.flag.unsure': 'Not sure of it',
  'import.flag.shape': 'An unusual layout',
  'import.flag.type': 'Type it in',
  'import.flag.as_read': 'Save it as read',
  'import.aside.title': 'Set aside for you',
  'import.aside.record': 'Record',
  'import.aside.actions': 'What to do with it',
  'import.dup.skipped_one': '{{count, number}} row is already in {{record}}: skipped, so nothing is counted twice.',
  'import.dup.skipped_other': '{{count, number}} rows are already in {{record}}: skipped, so nothing is counted twice.',
  'import.totals.title': 'The totals do not agree',
  'import.totals.badge': 'Nothing saved yet',
  'import.totals.file': 'total in your file',
  'import.totals.rows': 'the rows I read',
  'import.totals.gap': 'missing',
  'import.totals.why': 'Some rows may be missing or unreadable. Nothing from this file is saved until you choose.',
  'import.totals.anyway': 'Bring it in anyway, marked',
  'import.totals.anyway_desc': 'Marked: the import notes that this file’s total did not agree, and the report lists it.',
  'import.totals.leave': 'Leave this file out',
  'import.saved.list_title': 'Names for {{list}}, checked',
  'import.saved.into': 'saved into {{record}}',
  'import.saved.already': 'already there, skipped',
  'import.saved.aside': 'set aside for you',
  'import.saved.done_one': 'Done · {{count, number}} $t(glossary.entry_one) saved into {{record}}.',
  'import.saved.done_other': 'Done · {{count, number}} $t(glossary.entry_other) saved into {{record}}.',
  'import.saved.next': 'Coming next: step {{step, number}} · {{record}}',
  'import.next_step': 'Show me step {{step, number}}',
  'import.shape.aside': '{{record}}: the sheet “{{sheet}}” is laid out in a way I cannot read row by row yet. It is set aside for you, and nothing from it is saved.',
  'import.shape.title': 'Your sheet, turned into entries',
  'import.shape.in_file': 'In your file',
  'import.shape.as_entries': 'As entries in {{record}}',
  'import.shape.go_one': 'Looks right, bring in {{count, number}}',
  'import.shape.go_other': 'Looks right, bring in {{count, number}}',
  'import.shape.more': 'Show me more rows',
  'import.shape.count_one': 'Your sheet becomes {{count, number}} $t(glossary.entry_one).',
  'import.shape.count_other': 'Your sheet becomes {{count, number}} $t(glossary.entry_other).',
  'import.shape.count_aside_one': 'Your sheet becomes {{count, number}} $t(glossary.entry_one); {{aside, number}} of them wait for you.',
  'import.shape.count_aside_other': 'Your sheet becomes {{count, number}} $t(glossary.entry_other); {{aside, number}} of them wait for you.',
  'import.progress.title': 'Bringing in your data',
  'import.progress.left_one': '{{done, number}} of {{count, number}} $t(glossary.entry_one) · about {{minutes, number}} min left',
  'import.progress.left_other': '{{done, number}} of {{count, number}} $t(glossary.entry_other) · about {{minutes, number}} min left',
  'import.progress.timing_one': '{{done, number}} of {{count, number}} $t(glossary.entry_one) · working out the time left',
  'import.progress.timing_other': '{{done, number}} of {{count, number}} $t(glossary.entry_other) · working out the time left',
  'import.paused.title': 'Paused',
  'import.paused.desc': 'What is saved stays saved. Nothing else is added until you resume.',
  'import.aside_so_far_one': '{{count, number}} set aside for you so far',
  'import.aside_so_far_other': '{{count, number}} set aside for you so far',
  'import.aside_when': 'You check them at the end',
  'import.pause': 'Pause',
  'import.resume': 'Resume',
  'import.stop': 'Stop here',
  'import.undo_note': 'You can undo the whole import afterwards',
  'import.look_around': 'Look around, or close this tab. I keep going, and OS in the sidebar shows where I am.',
  'import.look_around_paused': 'Your saved data is already in your records. Resume from here or from OS.',
  'import.done.title': 'Your data is in',
  'import.done.none_title': 'Nothing came in',
  'import.done.desc_one': '{{count, number}} $t(glossary.entry_one) came in from your files',
  'import.done.desc_other': '{{count, number}} $t(glossary.entry_other) came in from your files',
  'import.done.list': 'What came in',
  'import.done.line': 'Every $t(glossary.entry_one) remembers the file and row it came from: open one and look under Source. The rows set aside are not saved; they wait for you.',
  'import.done.aside_one': '{{count, number}} set aside',
  'import.done.aside_other': '{{count, number}} set aside',
  'import.done.not_started': 'Not started',
  'import.done.unfilled': '{{fields}} to fill',
  'import.done.what_both': '{{entries}} and {{names}}',
  'import.done.files_one': '{{count, number}} file',
  'import.done.files_other': '{{count, number}} files',
  'import.done.summary': '{{what}}, from {{files}}, in {{time}}',
  'import.done.summary_untimed': '{{what}}, from {{files}}',
  'import.time.seconds': '{{seconds, number}} s',
  'import.time.minutes': '{{minutes, number}} min',
  'import.time.minutes_seconds': '{{minutes, number}} min {{seconds, number}} s',
  'import.check_14_one': 'Check the {{count, number}}',
  'import.check_14_other': 'Check the {{count, number}}',
  'import.open': 'Open',
  'import.open_sales': 'Open {{record}}',
  'import.undo_all': 'Undo the whole import',
  'import.undo.title': 'Undo the whole import?',
  'import.undo.desc': 'This takes out everything that came from your files. Your records, lists and dashboard stay, and so does anything you added or changed yourself.',
  'import.undo.entries_one': '{{count, number}} $t(glossary.entry_one)',
  'import.undo.entries_other': '{{count, number}} $t(glossary.entry_other)',
  'import.undo.in_records_one': 'in {{count, number}} $t(glossary.record_one)',
  'import.undo.in_records_other': 'in {{count, number}} $t(glossary.record_other)',
  'import.undo.rows_one': '{{count, number}} row',
  'import.undo.rows_other': '{{count, number}} rows',
  'import.undo.still_aside': 'still set aside',
  'import.undo.keep': 'Keep it',
  'import.undo.go': 'Undo the import',
  'import.undone.line': 'The import is undone. What came from your files is taken out; your $t(glossary.record_other) stay.',
  'import.stopped.title': 'Stopped at step {{step, number}}: {{record}}',
  'import.stopped.kept': 'What is saved stays saved. Nothing else is added.',
  'import.aside.head_one': '{{count, number}} row to check',
  'import.aside.head_other': '{{count, number}} rows to check',
  'import.aside.meta': 'Not saved. Accept a suggestion, fix it, or leave it out.',
  'import.aside.leave_all': 'Leave all out',
  'import.aside.all': 'All',
  'import.aside.none': 'Nothing waits for you here. Every row is saved or left out.',
  'import.aside.note': 'A suggestion comes from the rows around it, or from your $t(glossary.list_other). Fix a row to put in what the file did not say.',
  'import.aside.refused_one': '{{count, number}} row could not be saved as suggested. Fix it instead.',
  'import.aside.refused_other': '{{count, number}} rows could not be saved as suggested. Fix them instead.',
  // An import seen from the rest of the workspace: OS in the sidebar, the band on return, OS's float (Decided 17).
  'import.back.running': 'Still bringing in your data',
  'import.back.running_desc_one': '{{percent, number}}%, about {{count, number}} minute left. It keeps going while this tab is closed.',
  'import.back.running_desc_other': '{{percent, number}}%, about {{count, number}} minutes left. It keeps going while this tab is closed.',
  'import.back.running_soon': '{{percent, number}}%. It keeps going while this tab is closed.',
  'import.back.show': 'Show progress',
  'import.back.dismiss': 'Dismiss',
  'import.back.done': 'Your data is in',
  'import.back.none': 'Nothing came in',
  'import.back.stopped': 'The import stopped',
  'import.back.ended': 'The import ended',
  'import.back.brought_one': '{{count, number}} row was brought in.',
  'import.back.brought_other': '{{count, number}} rows were brought in.',
  'import.back.done_desc_one': '{{count, number}} $t(glossary.entry_one) came in while you were away.',
  'import.back.done_desc_other': '{{count, number}} $t(glossary.entry_other) came in while you were away.',
  'import.back.done_waits_one': '{{count, number}} row waits for you.',
  'import.back.done_waits_other': '{{count, number}} rows wait for you.',
  'import.back.report': 'See the report',
  'import.back.waiting': 'Step {{step, number}} is waiting for you',
  'import.back.waiting_desc': '{{record}} is ready for you to check. What is saved stays saved.',
  'import.back.waiting_saved': 'What is saved stays saved.',
  'import.back.open_step': 'Open step {{step, number}}',
  'import.back.fab': 'Bringing in your data · {{percent, number}}%',
  'import.check_one': 'Check the {{count, number}}',
  'import.check_other': 'Check the {{count, number}}',
  'import.float_sub': 'Bringing in your data · step {{step, number}} of {{steps, number}}',
  'import.tables_fill': 'Your records fill as each step is saved',
  // L38 of docs/plans/ledger.md: the money the import counted, as one line of the totals check.
  'import.check.money_balance': 'Money in and out balance',
  'import.check.money_off_one': '{{count, number}} $t(glossary.entry_one) does not balance money in and out',
  'import.check.money_off_other': '{{count, number}} $t(glossary.entry_other) do not balance money in and out',
  'import.check.title': 'The file’s own totals, checked against what was saved',
  'import.check.cell': '{{sheet}}, {{cell}}',
  'import.check.file': 'File {{figure, number}}',
  'import.check.saved': 'Saved {{figure, number}}',
  'import.check.mark.same': 'Same',
  'import.check.mark.differs': 'Different',
  'import.check.mark.unchecked': 'Not checked',
  'import.check.explained': 'These rows make the whole difference:',
  'import.check.part': 'These rows make part of the difference:',
  'import.check.no_rows': 'No row of the file makes the difference: the file’s total may count rows no longer in it.',
  'import.check.more_one': '{{count, number}} more row',
  'import.check.more_other': '{{count, number}} more rows',
  'import.check.why.aside': 'Row {{row, number}}: set aside, so not saved yet ({{figure, number}})',
  'import.check.why.empty': 'Row {{row, number}}: holds no $t(glossary.entry_one), so not saved ({{figure, number}})',
  'import.check.why.spelling': 'Row {{row, number}}: “{{value}}” has a space the file’s formula misses, so the file leaves out {{figure, number}}',
  'import.check.why.figure': 'Row {{row, number}}: the file says {{file, number}}, worked out as {{saved, number}}',
  'import.check.why.not_saved': 'Row {{row, number}}: already in the $t(glossary.record_one), not saved again ({{figure, number}})',
  'setup.files.go_on_one': 'Go on with the {{count, number}} file read',
  'setup.files.go_on_other': 'Go on with the {{count, number}} files read',
  'setup.files.sent': 'Files sent',
  'setup.files.nothing': 'Nothing read yet',
  'setup.files.rec_hint': 'Each $t(glossary.record_one) your files keep',
  'setup.files.rec_hint2': 'Its $t(glossary.field_other), from your columns',
  'setup.files.list_hint': 'Items, customers, parties: from your files',
  'setup.files.outline_hint': 'Share your files. I sketch each $t(glossary.record_one) and $t(glossary.list_one) here as I read them, and say which file it came from.',
  'setup.files.outline_sub': 'A sketch, not your $t(glossary.record_other). It fills in as I read, and nothing is created until you build it.',
  'setup.files.private': 'Your files stay in your workspace, under What OS knows. Nothing is shared.',
  'setup.files.reading_lead': 'Reading your files. Here is what I have done so far; the sketch fills in as each one is read.',
  'setup.files.read_lead_one': 'I read {{count, number}} file. Here is what I have done; the sketch holds what it gave.',
  'setup.files.read_lead_other': 'I read {{count, number}} files. Here is what I have done; the sketch holds what they gave.',
  'setup.files.all_read_one': 'Read {{count, number}} file · {{steps}}',
  'setup.files.all_read_other': 'Read {{count, number}} files · {{steps}}',
  'setup.files.steps_one': '{{count, number}} step',
  'setup.files.steps_other': '{{count, number}} steps',
  'setup.files.took_one': 'I read {{count, number}} file. Here is what I took from it, and where.',
  'setup.files.took_other': 'I read all {{count, number}} files. Here is what I took from them, and where.',
  'setup.files.check_guess_one': 'Check the one marked as a guess.',
  'setup.files.check_guess_other': 'Check the ones marked as a guess.',
  'setup.files.steps_label': 'What OS did with your files',
  'setup.files.record.sales': 'Sales',
  'setup.files.record.purchases': 'Purchases',
  'setup.files.record.expenses': 'Expenses',
  'setup.files.record.stock': 'Stock received',
  'setup.files.record.staff': 'Staff attendance',
  'setup.files.record.statement': 'Bank',
  'setup.files.list.customers': 'Customers',
  'setup.files.list.items': 'Items',
  'setup.files.list.suppliers': 'Suppliers',
  'setup.files.list.modes': 'Payment modes',
  'setup.files.list.staff': 'Staff',
  'setup.files.list.categories': 'Categories',
  'files.from': 'From {{name}}',
  'files.about.sales': 'Sales or invoices I sent',
  'files.about.purchases': 'Bills I paid',
  'files.about.expenses': 'Expenses',
  'files.about.stock': 'Stock',
  'files.about.staff': 'Staff or attendance',
  'files.about.contacts': 'Customers or suppliers',
  'files.about.statement': 'A bank statement',
  'files.about.accounts': 'An accounts export',
  'files.about.paper': 'A business paper',
  'files.about.other': 'Something else',
  'files.add_more': 'Add more files',
  'files.drop_title': 'Drop to give these to OS',
  'files.drop_desc': 'Photos, PDF, Excel, CSV, Markdown or a Tally export. Up to 20 files, 50 MB each.',
  'files.call_hint': 'Drop a file here, or say “I sent you a file”',
  'os.files.call.sent': 'Sent on the call · read',
  'os.files.attach': 'Attach a file or photo',
  'os.files.attach_busy': 'Files can be added once OS is done',
  'os.files.offline': 'Adding files needs the connection. Your files on this computer are not touched.',
  'os.files.list': 'Files to send',
  'os.files.more': '+{{more, number}}',
  'os.files.show_all': 'Show every file',
  'os.files.too_big': '{{name}} is {{size}}. The limit is 50 MB.',
  'os.files.too_big_tile': '{{size}} · too big',
  'os.files.cannot_open_tile': 'Cannot open this',
  'os.files.wrong_kind': 'OS cannot open .zip files. Unzip it and add the files inside.',
  'os.files.drop_count_one': '{{count, number}} file',
  'os.files.drop_count_other': '{{count, number}} files',

  // ------------------------------------------------ an empty record or list: the two ways in (docs/plans/empty-pages.md)
  /** Import from a file, the secondary button, and its line under the buttons: picking a file, or dropping one on the page, starts the import. */
  'empty_start.import.title': 'Import from a file',
  'empty_start.import.body': 'An Excel sheet, a CSV, or photos of your register. You can also drop it here.',
  /** For an admin on a server without OS: why Import cannot run, said instead of a card that does nothing. */
  'empty_start.import.no_os': 'Importing a file needs OS, which is not on here.',
  /** An empty record's one line on what it holds, over the two ways in. */
  'empty_start.record.body': 'Every $t(glossary.entry_one) in {{record}} shows up here.',
  /** An empty list's one line on what it holds, over the two ways in. */
  'empty_start.list.body': 'The names in {{list}}, ready to pick from in a form.',
  'files.waiting': 'Waiting',
  'files.uploading': 'Uploading {{percent, number}}%',
  'files.reading': 'Reading…',
  'files.remove': 'Remove this file',
  'files.try_again': 'Try again',
  'files.found.pages_one': '{{count, number}} page read',
  'files.found.pages_other': '{{count, number}} pages read',
  'files.found.pages_of': '{{read, number}} of {{total, number}} pages read now, the rest when you bring in your data',
  'files.found.over_days': '{{what}} over {{days}}, {{columns}}',
  'files.found.marked': '{{what}}, {{days}} marked',
  'files.found.each_with': '{{what}}, each with {{columns}}',
  'files.found.plain': '{{what}}, {{columns}}',
  'files.found.names_and': '{{names}} and {{last}}',
  'files.found.days_one': '{{count, number}} day',
  'files.found.days_other': '{{count, number}} days',
  'files.found.columns_one': '{{count, number}} column',
  'files.found.columns_other': '{{count, number}} columns',
  'files.found.thing.sales_one': '{{count, number}} sale',
  'files.found.thing.sales_other': '{{count, number}} sales',
  'files.found.thing.purchases_one': '{{count, number}} bill',
  'files.found.thing.purchases_other': '{{count, number}} bills',
  'files.found.thing.expenses_one': '{{count, number}} expense',
  'files.found.thing.expenses_other': '{{count, number}} expenses',
  'files.found.thing.stock_one': '{{count, number}} stock line',
  'files.found.thing.stock_other': '{{count, number}} stock lines',
  'files.found.thing.staff_one': '{{count, number}} person',
  'files.found.thing.staff_other': '{{count, number}} people',
  'files.found.thing.contacts_one': '{{count, number}} contact',
  'files.found.thing.contacts_other': '{{count, number}} contacts',
  'files.found.thing.statement_one': '{{count, number}} transaction',
  'files.found.thing.statement_other': '{{count, number}} transactions',
  'files.found.thing.accounts_one': '{{count, number}} voucher',
  'files.found.thing.accounts_other': '{{count, number}} vouchers',
  'files.found.thing.rows_one': '{{count, number}} line',
  'files.found.thing.rows_other': '{{count, number}} lines',
  // What reading a file left out, one line under what it found (files-paging-and-fixes, Decided 7).
  'files.left_out.pages_scan': '{{read, number}} of {{total, number}} pages read, the rest at import',
  'files.left_out.pages': '{{read, number}} of {{total, number}} pages read',
  'files.left_out.sheets': '{{read, number}} of {{total, number}} sheets read',
  'files.left_out.sheets_more': '{{read, number}} of {{total, number}} or more sheets read',
  'files.left_out.columns_one': '{{count, number}} column left out: {{names}}',
  'files.left_out.columns_other': '{{count, number}} columns left out: {{names}}',
  'files.left_out.unsure_one': 'Not sure what {{names}} holds',
  'files.left_out.unsure_other': 'Not sure what {{names}} hold',
  'files.left_out.join': '{{first}} · {{rest}}',
  'knows.file.skipped': 'Sheets not read',
  'files.step.opened': 'Opened {{name}}: the column names on row {{row, number}}',
  'files.step.read_rows': 'Read {{found}}',
  'files.step.read_file': 'Read {{name}}: {{found}}',
  'files.step.reading': 'Reading {{name}}',
  'files.step.sending': 'Sending {{name}}',
  'files.step.failed': 'Could not read {{name}}: {{why}}',
  'files.what.desc.sales': 'Sales to customers. Goes into a $t(glossary.record_one) of money in.',
  'files.what.desc.purchases': 'Stock or goods bought from suppliers. Goes into a $t(glossary.record_one) of money out.',
  'files.what.desc.expenses': 'Rent, bills and other spending. Goes into a $t(glossary.record_one) of money out.',
  'files.what.desc.stock': 'What comes in and goes out. Goes into a stock $t(glossary.record_one).',
  'files.what.desc.staff': 'Who works and when. Goes into an attendance $t(glossary.record_one).',
  'files.what.desc.contacts': 'People you deal with. Goes into a $t(glossary.list_one) of names.',
  'files.what.desc.statement': 'Money in and out of the bank. Goes into a bank $t(glossary.record_one).',
  'files.what.desc.accounts': 'Your books. Each part goes into the $t(glossary.record_one) it fits.',
  'files.what.desc.paper': 'Details of your business. Fills in your workspace, not a $t(glossary.record_one).',
  'files.what.desc.other': 'Something else. Say what it holds as you talk.',
  'files.err.too_big': 'Over 50 MB. Split it, or send the months you need.',
  'files.err.wrong_type': 'This kind of file cannot be read. Save it as CSV, Excel, PDF or a photo.',
  'files.err.zip': 'I cannot open a .zip. Unzip it and add the files inside.',
  'files.err.password': 'This PDF has a password.',
  'files.err.blurred': 'Too blurred to read. Take it again in good light.',
  'files.err.nothing': 'Nothing about your work in this photo. Left out.',
  'files.err.nothing_file': 'Nothing about your work in this file. Left out.',
  'files.err.empty': 'This file is empty.',
  'files.err.unreadable': 'This file could not be read. Add another copy of it.',
  'files.err.over_limit': 'Up to 20 files at a time. Add this one once the others are in.',
  'files.err.stopped': 'Stopped at {{percent, number}}%: no connection',
  'files.err.not_sent': 'Not sent yet: no connection',
  'files.err.failed': 'Not sent. Try again.',
  'files.err.remove': 'Remove',
  'files.offline.title': 'You are offline',
  'files.offline.desc_one': '{{count, number}} file did not finish uploading. It starts again on its own when you are back.',
  'files.offline.desc_other': '{{count, number}} files did not finish uploading. They start again on their own when you are back.',
  'files.err.too_dark': 'Too dark to read. Take it again in good light.',
  'setup.frame.leave': 'Leave setup',
  'setup.frame.leave_title': 'Leave setup?',
  'setup.frame.leave_body': 'Nothing is created yet, and what you told me so far is not kept.',
  'setup.frame.leave_confirm': 'Leave',
  'setup.frame.keep_going': 'Keep going',
  'setup.frame.convo_sub': 'OS · talk or type, in your own words',
  'setup.frame.starting': 'Starting setup',
  'setup.frame.outline_sub': 'A sketch, not your $t(glossary.record_other). It changes as you talk, and nothing is created until you build it.',
  'setup.frame.created_label': 'Here is what I created',
  'setup.frame.created_sub': 'Every $t(glossary.record_one) here is created and empty, ready for its first $t(glossary.entry_one).',
  'setup.frame.your_workspace': 'Your $t(glossary.business_one)',
  'setup.frame.nothing_known': 'Nothing known yet',
  'setup.frame.records': 'Records',
  'setup.frame.lists': 'Lists',
  'setup.frame.links': 'Links',
  'setup.frame.on_top': 'On top',
  'setup.frame.so_far': '{{count, number}} so far',
  'setup.frame.records_empty': 'Each $t(glossary.record_one) you keep, as I hear it',
  'setup.frame.records_empty_fields': 'Its $t(glossary.field_other) and the $t(glossary.list_other) it picks from',
  'setup.frame.lists_empty': 'Lists you pick from',
  'setup.frame.lists_empty_filled': 'Filled in where I can',
  'setup.frame.empty_hint': 'Tell me about your work. I sketch each $t(glossary.record_one) and $t(glossary.list_one) here as you talk.',
  'setup.frame.filled_in': '{{count, number}} filled in',
  'setup.frame.posts_into': '{{from}} posts into {{to}}',
  'setup.frame.mark.changed': 'Changed',
  'setup.frame.mark.left_out': 'Left out',
  'setup.frame.mark.fix': 'To fix',
  'setup.frame.mark.created': 'Created',
  'setup.frame.mark.creating': 'Creating',
  'setup.frame.mark.waiting': 'Waiting',
  'setup.frame.status.ready': 'Ready to build',
  'setup.frame.status.building': 'Building',
  'setup.frame.status.created': 'Created',
  'setup.frame.status.to_fix_one': '{{count, number}} thing to fix',
  'setup.frame.status.to_fix_other': '{{count, number}} things to fix',
  'setup.frame.card_label': 'Your setup',
  'setup.frame.seeded_lists': '{{names}} (filled in for you)',
  'setup.frame.build': 'Build',
  'setup.frame.change_placeholder': 'Tell me what to change',
  // Over the typed box: the hand-off before OS has asked everything, and once it has, with the line beside it.
  'setup.frame.enough_build': 'That is enough, build it',
  'setup.frame.keep_typing': 'Or keep typing to change something',
  // Beside Build my setup, greyed, while the workspace has no name (r3 Setup, item 15).
  'setup.frame.still_name': 'Still to ask: what your $t(glossary.business_one) is called',
  'setup.frame.changed_line': '{{names}} changed.',
  'setup.frame.left_out_line': '{{names}} left out.',
  'setup.frame.added_line': '{{names}} added.',
  'setup.frame.earlier': 'Earlier version · {{counts}}',
  'setup.frame.earlier_use': 'Go back to this version',
  'setup.frame.building_line': 'Building it now. If any part cannot be built, nothing is created.',
  'setup.frame.building_desc_one': 'Creating… {{done, number}} of {{count, number}} $t(glossary.record_one)',
  'setup.frame.building_desc_other': 'Creating… {{done, number}} of {{count, number}} $t(glossary.record_other)',
  'setup.frame.refused_title': 'I could not create it yet',
  'setup.frame.failed_title': 'I could not create it',
  'setup.frame.built_title': 'Your setup is created',
  'setup.frame.ready_title': '{{name}} is ready.',
  'setup.frame.ready_body': 'Everything on the sketch is created, and empty. Your team sees only what their role allows.',
  'setup.frame.built_foot': 'OS stays one click away, for anything else.',
  'setup.frame.open': 'Open {{name}}',
  'setup.frame.first_record': 'Your first $t(glossary.record_one)',
  // web: Edit the setup (stage 7, phase 5)
  'setup.edit.title': 'Editing the setup of {{name}}',
  'setup.edit.step_change': 'Change',
  'setup.edit.step_build': 'Build again',
  'setup.edit.pane_title': 'Edit the setup',
  'setup.edit.sub_voice': 'OS · on a call; say what should change',
  'setup.edit.sub_typed': 'OS · typed; say what should change',
  'setup.edit.opening':
    'What should change? I can change a $t(glossary.record_one), add one or leave one out. The $t(glossary.business_one) keeps working, and nothing changes until you build again.',
  'setup.edit.chip_change': 'Change a $t(glossary.record_one)',
  'setup.edit.chip_add': 'Add a $t(glossary.record_one)',
  'setup.edit.chip_leave': 'Leave one out',
  'setup.edit.placeholder': 'Say what should change',
  'setup.edit.outline_sub': 'Your setup as it is. Nothing changes in the $t(glossary.business_one) until you build again.',
  'setup.edit.outline_sub_changes':
    'Your setup as it is, each change marked. Nothing changes in the $t(glossary.business_one) until you build again.',
  'setup.edit.built_sub': 'Every change is built.',
  'setup.edit.built_sub_left_out': 'Every change is built. {{names}} left out.',
  'setup.edit.mark.new': 'New',
  'setup.edit.changes_one': '{{count, number}} change to build',
  'setup.edit.changes_other': '{{count, number}} changes to build',
  'setup.edit.build_again': 'Build again',
  'setup.edit.build_again_count': 'Build again · {{changes, number}}',
  'setup.edit.confirm_title_one': 'Build this {{count, number}} change?',
  'setup.edit.confirm_title_other': 'Build these {{count, number}} changes?',
  'setup.edit.confirm_body': '{{name}} keeps working while I build. No $t(glossary.entry_one) is lost.',
  'setup.edit.not_yet': 'Not yet',
  'setup.edit.line.added': '{{names}} added',
  'setup.edit.line.removed': '{{names}} taken off',
  'setup.edit.line.renamed': '{{from}} renamed {{to}}',
  'setup.edit.line.field_from': '{{field}} from {{list}}',
  'setup.edit.line.new': 'New',
  'setup.edit.line.new_with_list': 'New, with a new $t(glossary.list_one), {{names}}',
  'setup.edit.line.new_list': 'A new $t(glossary.list_one)',
  'setup.edit.line.left_out_empty': 'Left out; it has no $t(glossary.entry_other)',
  'setup.edit.line.left_out_kept_one': 'Left out; its {{count, number}} $t(glossary.entry_one) is kept',
  'setup.edit.line.left_out_kept_other': 'Left out; its {{count, number}} $t(glossary.entry_other) are kept',
  'setup.edit.building_line': 'Building your changes. The $t(glossary.business_one) stays open while I work.',
  'setup.edit.building_title': 'Building your changes',
  'setup.edit.building_desc': 'Changing… {{done, number}} of {{total, number}}',
  'setup.edit.built_title': 'Your changes are built',
  'setup.edit.built_foot': 'Edit the setup again any time from Workspace settings.',
  'setup.edit.go': 'Go to your $t(glossary.business_one)',
  // Built from the Business map (opened with ?from=map), the foot goes back to it.
  'setup.edit.back_to_map': 'Back to the map',
  'setup.edit.again': 'Edit the setup',
  'setup.edit.again_hint': 'Change it again any time.',
  'setup.edit.refused_title': 'I could not build the changes yet',
  'setup.edit.others_wait_one': 'The other {{count, number}} change waits with it.',
  'setup.edit.others_wait_other': 'The other {{count, number}} changes wait with it.',
  'setup.edit.repair_request': 'The changes could not be built: {{problems}} Fix each one and hold the changes again.',
  'setup.edit.status.not_built': 'Not built',
  'setup.edit.close_title': 'Leave without building?',
  'setup.edit.close_body_one': 'The {{count, number}} change you asked for is not built, and it is not kept.',
  'setup.edit.close_body_other': 'The {{count, number}} changes you asked for are not built, and they are not kept.',
  'setup.edit.close_confirm': 'Leave',
  'setup.edit.keep_editing': 'Keep editing',
  'setup.edit.not_allowed_title': 'Only the primary admin can edit the setup',
  'setup.edit.not_allowed_body': 'Ask an admin to change what this $t(glossary.business_one) keeps.',
  'setup.edit.load_failed_title': 'The setup could not be read',

  // A new workspace beside one already held (stage 7, Decided 7): the same
  // setup, with its own title, the way back to the one it came from, and a
  // first line saying nothing is copied.
  'setup.another.title': 'Setting up a new $t(glossary.business_one)',
  'setup.another.back': 'Back to {{name}}',
  'setup.another.back_title': 'Go back to {{name}}?',
  'setup.another.back_body': 'The new $t(glossary.business_one) is not created, and what you told me so far is not kept.',
  'setup.another.question': 'A new $t(glossary.business_one). Tell me about this one: what happens there, and what do you need to keep track of?',
  'setup.another.not_copied':
    'It starts empty, with you as its primary admin, and nothing from {{name}} is copied. I ask the way I did before, sketch it as you talk, and build it when you say so.',
  'setup.another.separate': 'A separate $t(glossary.business_one): its own $t(glossary.record_other), $t(glossary.list_other) and team.',
  // Your settings, one page per row of the sub-nav (stage 7, Decided 8; contract section 9).
  'settings.profile.card': 'You',
  'settings.profile.card_lede': 'Who is signed in, and how the people in your $t(glossary.business_other) see you.',
  'settings.profile.photo_hint': 'Shown beside what you write',
  'settings.profile.change_photo': 'Change photo',
  'settings.profile.remove_photo': 'Remove photo',
  'settings.profile.photo_unreadable': 'That picture could not be read. Choose a JPEG, PNG or WebP image.',
  'settings.profile.name_required': 'Enter your name',
  'settings.profile.email_hint': 'The email you sign in with',
  'settings.profile.verified': 'Verified',
  'settings.profile.not_verified': 'Not verified',
  'settings.profile.photo_too_big': 'That picture is too large. Choose one of {{size, number}} MB or less',
  'settings.profile.member_since': 'Member since {{date}}',
  'settings.danger.title': 'Sign out or delete',
  'settings.workspaces.count_one': '{{count, number}} $t(glossary.business_one)',
  'settings.workspaces.count_other': '{{count, number}} $t(glossary.business_other)',
  'settings.workspaces.you_run': 'You run {{count, number}}',
  'settings.workspaces.card': 'Your $t(glossary.business_other)',
  'settings.workspaces.joined': 'Joined {{date}}',
  'settings.workspaces.open_now': 'Open now',
  'settings.workspaces.open': 'Open',
  'settings.workspaces.open_named': 'Open {{name}}',
  'settings.workspaces.code_hint':
    'Whoever runs the $t(glossary.business_one) gives you a code, or a link with the code in it. A code works for seven days, and once only.',
  'settings.appearance.saved_here': 'Saved for this browser',
  'settings.appearance.card': 'Theme',
  'settings.appearance.card_lede': 'How RecordOS looks in this browser. The phone app keeps its own setting.',
  'settings.appearance.row': 'Light or dark',
  'settings.appearance.row_hint': 'Follow system changes on its own, as your computer does',
  'settings.appearance.light_lede': 'White pages, ink text.',
  'settings.appearance.dark_lede': 'Easier at night and in a dim shop.',
  'settings.appearance.system': 'Follow system',
  'settings.appearance.system_lede': 'Light or dark, as your computer is set.',
  'settings.language.in_use_meta': 'In use: {{language}}',
  'settings.language.row': 'App language',
  'settings.language.row_hint': 'OS speaks with you in this language. Everyone on your team picks their own.',
  'settings.language.in_use': 'In use',
  'settings.language.os_note': 'OS speaks and answers in the app’s language.',
  'settings.language.web_note': 'This browser shows RecordOS in English for now. Anything not translated yet stays in English.',
  'settings.language.saved': 'OS now speaks {{language}}.',
  'settings.voice.read_on_meta': 'Read replies aloud: on',
  'settings.voice.read_off_meta': 'Read replies aloud: off',
  'settings.voice.card': 'Voice',
  'settings.voice.card_lede': 'Used from your next call. OS speaks, and writes about itself, as this voice.',
  'settings.voice.row_hint': 'Samples play in the app’s language: {{language}}. Change it in Language.',
  'settings.voice.stop': 'Stop',
  'settings.voice.read_aloud': 'Read replies aloud',
  'settings.voice.read_on': 'On: OS reads each reply aloud when it arrives, in this browser.',
  'settings.voice.read_off': 'Off: outside a call, OS reads a reply aloud only when you click Read it aloud under it.',
  'settings.voice.mic_card': 'Microphone',
  'settings.voice.mic_row': 'This site',
  'settings.voice.mic_hint': 'Your browser decides. To stop OS hearing you, block the microphone for this site in your browser.',
  'settings.voice.mic_granted': 'Allowed for this site',
  'settings.voice.mic_prompt': 'Your browser asks the first time OS listens',
  'settings.voice.mic_denied': 'Blocked for this site',
  'settings.voice.mic_unknown': 'This browser does not say',
  'settings.voice.mic_default': 'Default microphone',
  'settings.about.open_new_tab': 'Open in a new tab',
  'settings.delete.what_goes': 'What goes',
  'settings.delete.goes_sign_in': 'Your sign-in, your name and your photo',
  'settings.delete.goes_records':
    'Every $t(glossary.record_one), $t(glossary.list_one) and $t(glossary.entry_one) in the $t(glossary.business_other) you own',
  'settings.delete.goes_os': 'OS’s chats, and what OS knows about them',
  'settings.delete.export_first': 'Export what you want to keep first.',
  'account.page.lede': 'Who is signed in, the $t(glossary.business_other) you can open, how RecordOS looks, and the way out.',
  'account.businesses.lede': 'Every $t(glossary.business_one) this account can open.',
  'account.businesses.access': 'Your access',
  'account.sign_out.lede': 'You are signed in as {{email}}.',
  'account.delete.lede': 'This permanently deletes your account and every $t(glossary.business_one) you own. There is no undo.',
  'account.delete.dialog_body':
    'This permanently deletes your account and every $t(glossary.business_one) you own — on the server and on this device.',
  'account.delete.owned': 'Workspaces you own',
  'account.delete.warning': 'You are signed out straight away. There is no undo.',
  /* The phone's sync sentences, for a browser: it keeps its copy on this device, not on a phone. */
  'sync.banner.body.web':
    'Everything you see is saved on this device, and new $t(glossary.entry_other) will sync when the server recovers.',
  'sync.download.title.web': 'Getting your $t(glossary.business_one) ready on this device',
  'sync.download.saved.web_one': '{{saved, number}} of {{count, number}} $t(glossary.entry_one) saved on this device',
  'sync.download.saved.web_other': '{{saved, number}} of {{count, number}} $t(glossary.entry_other) saved on this device',
  'sync.offline_empty.title.web': 'Nothing here yet, and this device is offline',
  'sync.offline_empty.body.web':
    'Your $t(glossary.record_other) live on this device once they have synced. Connect once, and everything after that works offline.',
  /* The band above the page while the browser has no connection (board 03-offline). */
  'sync.offline.title.web': 'This device is offline',
  'sync.offline.body.web':
    'Everything you see is saved on this device, and new $t(glossary.entry_other) will sync when you are back online.',
  'view_as.banner.body.web': 'Read only. Nothing you click will change anything.',
  // web: onboarding
  'onboarding.step': 'Step {{step, number}} of {{total, number}}',
  'onboarding.brand.title': 'Every $t(glossary.record_one) your business keeps, in one place',
  'onboarding.brand.body': 'Cash in, cash out, dues, stock and staff. Your team sees only what their job needs',
  'onboarding.too_long': 'Use at most {{limit, number}} characters',
  'onboarding.name.name_required': 'Enter a $t(glossary.business_one) name',
  'onboarding.name.currency_required': 'Pick the currency this $t(glossary.business_one) keeps its $t(glossary.record_other) in',
  'onboarding.blank.name_required': 'Enter a name for this $t(glossary.record_one)',
  'setup.language.your_browser': 'Your browser',
  'setup.talk.talk_instead': 'Talk instead',
  // web: home, lists, new record and search
  'search.web.key_escape': 'Esc',
  'home.web.offline_title': 'Nothing here yet, and this browser is offline',
  'home.web.offline_body':
    'Your $t(glossary.record_other) arrive in this browser once it reaches the server. Connect, then try again.',
  'home.web.lists_count_one': '{{count, number}} $t(glossary.list_one)',
  'home.web.lists_count_other': '{{count, number}} $t(glossary.list_other)',
  'home.web.rows_count_one': '{{count, number}} $t(glossary.entry_one)',
  'home.web.rows_count_other': '{{count, number}} $t(glossary.entry_other)',
  'home.web.tip_dismiss': 'Dismiss the tip',
  'home.web.column.entries': 'Entries',
  'home.web.column.last_entry': 'Last entry',
  'home.web.column.total': 'Total',
  'home.web.column.counts_as': 'Counts as',
  'home.web.column.rows': 'Entries',
  'home.web.column.contents': 'What is in it',
  'home.web.column.last_changed': 'Last changed',
  'home.web.sort_by': 'Sort by {{column}}',
  'home.web.sorted.last_newest': 'Newest $t(glossary.entry_one) first',
  'home.web.sorted.last_oldest': 'Oldest $t(glossary.entry_one) first',
  'home.web.sorted.name_asc': 'By name, A to Z',
  'home.web.sorted.name_desc': 'By name, Z to A',
  'home.web.sorted.count_desc': 'Largest first',
  'home.web.sorted.count_asc': 'Smallest first',
  'home.web.sorted.total_desc': 'Largest total first',
  'home.web.sorted.total_asc': 'Smallest total first',
  'home.web.row_more': 'More for {{name}}',
  'home.web.lists_desc':
    'Shared across $t(glossary.record_other). Name it once, and every $t(glossary.record_one) picks from the same $t(glossary.list_one).',
  // web: the Records and Lists pages with nothing of their own kind yet
  // (Decided D8 of canvas-layout-and-app-polish): each says which is missing,
  // even when the other kind exists. The phone keeps `home.empty.*`.
  'home.web.records_empty.title': 'No $t(glossary.record_other) yet',
  'home.web.records_empty.body':
    'A $t(glossary.record_one) keeps one kind of $t(glossary.entry_one) as it happens, like each sale, each expense or each day’s attendance.',
  'home.web.lists_empty.title': 'No $t(glossary.list_other) yet',
  'home.web.lists_empty.body':
    'A $t(glossary.list_one) holds the names your $t(glossary.record_other) pick from, like customers, items or payment modes, so each is typed once.',
  'home.web.always_on': 'Always on',
  // web: Records home (stage 5 phase 2)
  'home.web.search': 'Search $t(glossary.record_other)',
  'home.web.people': 'People',
  'home.web.column.in_month': 'In · this month',
  'home.web.column.out_month': 'Out · this month',
  'home.web.shown_of_one': '{{shown, number}} of {{count, number}} $t(glossary.record_one)',
  'home.web.shown_of_other': '{{shown, number}} of {{count, number}} $t(glossary.record_other)',
  'home.web.names_and': '{{names}} and {{last}}',
  'home.web.names_or': '{{names}} or {{last}}',
  'home.web.people_open_to': 'Open to {{names}}',
  'home.web.people_none_open': 'None open to {{names}}',
  'home.web.people_filter': 'Filter by people',
  'home.web.people_find': 'Find someone',
  'home.web.people_group': 'Records they can use',
  'home.web.people_every': 'Every $t(glossary.record_one)',
  'home.web.people_none_yet': 'None yet',
  'home.web.people_you': 'You · {{role}}',
  'home.web.people_no_match': 'No one by that name',
  'home.web.people_loading': 'Getting your team',
  'home.web.people_show_one': 'Show {{count, number}} $t(glossary.record_one)',
  'home.web.people_show_other': 'Show {{count, number}} $t(glossary.record_other)',
  'home.web.people_empty_person': '{{name}} can use no $t(glossary.record_one) yet',
  'home.web.people_empty_several': 'None of them can use a $t(glossary.record_one) yet',
  'home.web.people_empty_body':
    'They see only the $t(glossary.record_other) someone gives them. Give access from a $t(glossary.record_one)’s Access tab.',
  'home.web.people_open_person': 'Open {{name}}’s page',
  'home.web.people_open_team': 'Open the team',
  'home.web.people_plus': '+{{extra, number}}',
  'home.web.people_unnamed': 'Someone',
  'home.web.search_empty': 'No $t(glossary.record_one) matches “{{typed}}”',
  'home.web.new_record_hint': 'Tell OS what you track, or set it up yourself',
  'home.web.loading': 'Getting your $t(glossary.record_other)',
  'home.web.facts_failed': 'Who can use each $t(glossary.record_one), and this month’s figures, could not be loaded.',
  'home.web.sort_group.last': 'Sort by last $t(glossary.entry_one)',
  'home.web.sort_group.name': 'Sort by name',
  'home.web.sort_group.count': 'Sort by $t(glossary.entry_other)',
  'home.web.sort_group.total': 'Sort by total',
  'home.web.tile_last_today': 'Last $t(glossary.entry_one) today',
  'home.web.tile_last_yesterday': 'Last $t(glossary.entry_one) yesterday',
  'home.web.tile_last_on': 'Last $t(glossary.entry_one) {{day}}',
  'home.web.tile_none': 'No $t(glossary.entry_other) yet',
  'home.web.export_csv': 'Export CSV',
  // web: the Entries tab (stage 5 phase 3)
  'entry.web.column.date': 'Date',
  'entry.web.opening': 'Opening {{record}}',
  'entry.web.opening_meta': 'Opening',
  'entry.web.foot_total': 'Total',
  'entry.web.foot_total_in': 'Total in',
  'entry.web.foot_total_billed': 'Total billed',
  'entry.web.foot_total_out': 'Total out',
  'entry.web.foot_match_one': '{{shown, number}} of {{count, number}} $t(glossary.entry_one) matches',
  'entry.web.foot_match_other': '{{shown, number}} of {{count, number}} $t(glossary.entry_other) match',
  'entry.web.foot_search_one': '{{count, number}} $t(glossary.entry_one) in {{record}} matches “{{typed}}”',
  'entry.web.foot_search_other': '{{count, number}} $t(glossary.entry_other) in {{record}} match “{{typed}}”',
  'entry.web.foot_unsynced_one': '{{count, number}} not synced yet',
  'entry.web.foot_unsynced_other': '{{count, number}} not synced yet',
  'entry.web.search_everywhere': 'Search every $t(glossary.record_one)',
  'entry.web.older_failed': 'Older $t(glossary.entry_other) could not be loaded',
  'entry.web.columns_title': 'Columns in {{record}}',
  'entry.web.columns_always': 'Always shown',
  'entry.web.columns_under': 'Shown under {{field}}',
  'entry.web.columns_caption': 'Drag to change the order. Only your view changes; nothing about {{record}} does.',
  'entry.web.columns_reset': 'Reset to the usual columns',
  'entry.web.columns_changed': 'Columns changed for you',
  'entry.web.column_shown': '{{column}} is shown',
  'entry.web.column_hidden': '{{column}} is hidden',
  'entry.web.columns_moved': 'The order changed',
  'entry.web.remove_many_title_one': 'Remove {{count, number}} $t(glossary.entry_one)?',
  'entry.web.remove_many_title_other': 'Remove {{count, number}} $t(glossary.entry_other)?',
  'entry.web.remove_many_body': 'They are taken out of {{record}}. Their history stays, and an admin can see who removed them.',
  'entry.web.remove_many_confirm_one': 'Remove {{count, number}} $t(glossary.entry_one)',
  'entry.web.remove_many_confirm_other': 'Remove {{count, number}} $t(glossary.entry_other)',
  'entry.web.remove_many_more_one': 'and {{count, number}} more',
  'entry.web.remove_many_more_other': 'and {{count, number}} more',
  'entry.web.remove_many_kept_one': '{{count, number}} selected $t(glossary.entry_one) stays',
  'entry.web.remove_many_kept_other': '{{count, number}} selected $t(glossary.entry_other) stay',
  'entry.web.remove_many_kept_body': 'A link wrote them, or someone else added them, so they are not yours to remove here.',
  'entry.web.removed_one': '{{count, number}} $t(glossary.entry_one) removed',
  'entry.web.removed_other': '{{count, number}} $t(glossary.entry_other) removed',
  'entry.web.remove_partly_one': '{{count, number}} $t(glossary.entry_one) removed. The rest were not: {{reason}}',
  'entry.web.remove_partly_other': '{{count, number}} $t(glossary.entry_other) removed. The rest were not: {{reason}}',
  'entry.web.selecting_all': 'Selecting every $t(glossary.entry_one)',
  'record.web.who_adds': '{{names}} add here',
  'record.web.you_add': 'You add here',
  'record.web.names_you': 'you',
  'record.web.menu.rename': 'Rename',
  'record.web.key_rename': 'F2',
  'record.web.menu.icon': 'Change icon',
  'record.web.menu.duplicate': 'Duplicate',
  'record.web.menu.duplicate_hint': 'Only the $t(glossary.field_other)',
  'record.web.menu.customize': 'Customize the $t(glossary.entry_one) form',
  'record.web.menu.access': 'Who can use this',
  'record.web.menu.people_one': '{{count, number}} $t(glossary.person_one)',
  'record.web.menu.people_other': '{{count, number}} $t(glossary.person_other)',
  'record.web.menu.delete': 'Delete $t(glossary.record_one)',
  'record.web.rename_title': 'Rename {{record}}',
  'record.web.rename_label': 'Name',
  'record.web.rename_empty': 'Give it a name.',
  'record.web.renamed': 'Renamed to {{name}}',
  'record.web.icon_title': 'The icon of {{record}}',
  'record.web.icon_saved': 'Icon changed',
  'record.web.duplicated': '{{name}} made, with the same $t(glossary.field_other) and no $t(glossary.entry_other)',
  'record.web.delete_title': 'Delete {{record}}?',
  'record.web.delete_body': 'It leaves every page for everyone in this workspace.',
  'record.web.delete_counting': 'Counting what goes with it',
  'record.web.delete_goes_links_one': '{{count, number}} $t(glossary.link_one) stops',
  'record.web.delete_goes_links_other': '{{count, number}} $t(glossary.link_other) stop',
  'record.web.delete_goes_picked_one': '{{field}} in {{record}}, picked by {{count, number}} $t(glossary.entry_one)',
  'record.web.delete_goes_picked_other': '{{field}} in {{record}}, picked by {{count, number}} $t(glossary.entry_other)',
  'record.web.delete_stays_title': 'What is already written elsewhere stays',
  'record.web.delete_stays_body': 'The $t(glossary.entry_other) in other $t(glossary.record_other) keep the names they picked.',
  'record.web.delete_acknowledge': 'Delete {{record}} and its {{entries}} for good',
  'record.web.delete_acknowledge_plain': 'Delete {{record}} and its $t(glossary.entry_other) for good',
  'record.web.delete_confirm': 'Delete $t(glossary.record_one)',
  'record.web.deleted': '{{name}} deleted',
  // web: the entry panel and Export (stage 5 phase 4)
  'entry.web.panel_title': '{{heading}}, {{time}}',
  'entry.web.step_hint': 'Next $t(glossary.entry_one)',
  'entry.web.attachments_count_one': 'Attachments · {{count, number}}',
  'entry.web.attachments_count_other': 'Attachments · {{count, number}}',
  'entry.web.location': 'Location',
  'entry.web.location_saved': 'Saved with the $t(glossary.entry_one)',
  // An imported entry's file and row (setup-from-files.md, Decided 13).
  'entry.web.source': 'Source',
  'entry.web.source_file': 'An imported file',
  'entry.web.source_row': 'Row {{row, number}}',
  'entry.web.map': 'Map',
  'entry.web.map_open': 'Open {{place}} on a map',
  'entry.web.editing_in': 'In {{record}}',
  'entry.web.key_mod_enter': '⌘↵',
  'entry.web.export': 'Export',
  'entry.web.export_title': 'Export {{record}}',
  'entry.web.export_desc': 'A file of $t(glossary.entry_other) to open in a spreadsheet or send on.',
  'entry.web.export_which': 'Which $t(glossary.entry_other)',
  'entry.web.export_view': 'This view · {{total, number}}',
  'entry.web.export_view_desc': '{{days}}, as the table shows them now',
  'entry.web.export_view_desc_cut': '{{days}}, with your filters and search, as the table shows them now',
  'entry.web.export_all_days': 'Every day',
  'entry.web.export_every': 'Every $t(glossary.entry_one) · {{total, number}}',
  'entry.web.export_every_desc': 'Everything in {{record}}, whatever the table shows',
  'entry.web.export_selected': 'The {{total, number}} you selected',
  'entry.web.export_selected_none': 'The ones you select',
  'entry.web.export_selected_hint': 'Tick $t(glossary.entry_other) in the table first',
  'entry.web.export_selected_over': 'At most {{max, number}} at a time. Choose This view instead.',
  'entry.web.export_columns': 'Which columns',
  'entry.web.export_columns_shown': 'The columns you see · {{total, number}}',
  'entry.web.export_columns_shown_desc': 'In the order the table shows them',
  'entry.web.export_columns_every': 'Every $t(glossary.field_one) · {{total, number}}',
  'entry.web.export_columns_every_desc': 'Hidden columns come too, in the order of the form',
  'entry.web.export_file': 'File',
  'entry.web.export_format_csv': 'CSV',
  'entry.web.export_format_csv_desc': 'Opens in any spreadsheet.',
  'entry.web.export_access_note': 'Only the $t(glossary.field_other) your access shows are ever in the file.',
  'entry.web.export_go_one': 'Export {{count, number}} $t(glossary.entry_one)',
  'entry.web.export_go_other': 'Export {{count, number}} $t(glossary.entry_other)',
  'entry.web.exporting': 'Exporting',
  'entry.web.exported': '{{record}} exported',
  'entry.web.exported_desc': '{{file}}, in Downloads',
  // web: the new-entry page (stage 5 phase 5)
  'entry.web.page.picked_by_one': '{{names}} picks from it',
  'entry.web.page.picked_by_other': '{{names}} pick from it',
  'entry.web.page.save_another': 'Save and add another',
  'entry.web.page.send_another': 'Send and add another',
  'entry.web.page.key_shift_mod_enter': '⇧⌘↵',
  'entry.web.page.needs_one': '{{count, number}} $t(glossary.field_one) needs a value',
  'entry.web.page.needs_other': '{{count, number}} $t(glossary.field_other) need a value',
  'entry.web.page.fix_one': '{{count, number}} thing to fix before this $t(glossary.entry_one) is saved',
  'entry.web.page.fix_other': '{{count, number}} things to fix before this $t(glossary.entry_one) is saved',
  'entry.web.page.fix_desc': 'Nothing is saved yet. What you typed stays here.',
  'entry.web.page.writing': 'Writing it to this browser',
  'entry.web.page.offline': 'Saves on this browser, syncs when online',
  'entry.web.page.approval': 'An admin approves it before it counts',
  'entry.web.page.saved_next_named': '{{entry}}. Ready for the next one.',
  'entry.web.page.sent': 'Sent for approval',
  'entry.web.page.sent_desc': 'An admin sees it next',
  // A save the server refused (B10 of the ledger live test): the form keeps it, with the server's word under each field.
  'entry.web.page.saved_waiting': 'Saved on this browser. It syncs once the server answers.',
  'entry.web.refused.title': 'This $t(glossary.entry_one) did not save',
  'entry.web.refused.body': 'Put right what is marked below, then save it again. Or discard it.',
  'entry.web.page.no_add_record': 'You cannot add to this $t(glossary.record_one).',
  'entry.web.page.no_add_list': 'You cannot add to this $t(glossary.list_one).',
  'entry.web.page.no_add_desc': 'An admin can give you the right. Everything already here you can still read.',
  'entry.web.page.back_to': 'Back to {{record}}',
  'entry.web.page.admin_only': 'Only an admin can change what you may do',
  // web: OS fills the new-entry page (web-redesign-10 phase 7)
  'entry.web.page.os_filled': 'Filled in by OS',
  'entry.web.page.os_filled_call': 'Filled in by OS from the call',
  'entry.web.page.on_call': 'On a call with OS',
  'blank.web.desc': 'A name, and what each $t(glossary.entry_one) counts. You pick the $t(glossary.field_other) on the next screen.',
  'search.web.title': 'Search results for "{{typed}}"',
  'search.web.entries_one': '{{count, number}} $t(glossary.entry_one)',
  'search.web.entries_other': '{{count, number}} $t(glossary.entry_other)',
  'search.web.sources_one': '{{count, number}} $t(glossary.record_one) or $t(glossary.list_one)',
  'search.web.sources_other': '{{count, number}} $t(glossary.record_other) and $t(glossary.list_other)',
  'search.web.results_one': '{{count, number}} result',
  'search.web.results_other': '{{count, number}} results',
  'search.web.best_first': 'Best match first',
  'search.web.open_record': 'Open $t(glossary.record_one)',
  'search.web.open_list': 'Open $t(glossary.list_one)',
  'search.web.ask_coach': 'Ask OS',
  'search.web.ask_coach_for': 'Ask OS: {{typed}}',
  'search.web.everything_for': 'Search everything: {{typed}}',
  'search.web.open_results': 'Open results page',
  'search.web.filter_label': 'Filter the results',
  'search.web.found_in': 'Found in',
  'search.web.scope_title': 'What is searched',
  'search.web.scope_body':
    'Every word in every $t(glossary.record_one) and $t(glossary.list_one), and the names your $t(glossary.list_other) draw. Dates and yes-or-no answers are not searched here. Use the filter inside a $t(glossary.record_one) for those.',
  'search.web.promise':
    'Searches every $t(glossary.record_one) and $t(glossary.list_one) in this $t(glossary.business_one). It reads the words your $t(glossary.list_other) draw, never the ids underneath them.',
  'search.web.none_body_indexing': 'This browser is still indexing, so more may match soon.',
  'search.web.waiting_download': 'Your data is still arriving. Search finds everything once this browser has indexed it.',
  'search.web.kind_record': 'Record',
  'search.web.kind_list': 'List',
  'search.web.see_all_one': 'See all {{count, number}} result',
  'search.web.see_all_other': 'See all {{count, number}} results',
  'search.web.actions': 'Actions',
  'search.web.navigate': 'Navigate',
  'search.web.open': 'Open',
  // web: ⌘K and the search page, stage 7
  'search.web.jump_to': 'Jump to',
  'search.web.new_entry_in': 'New entry in {{record}}',
  'search.web.ask_os_desc': 'Ask, or tell OS what to do',
  'search.web.ask_os_answers': 'OS reads your $t(glossary.record_other) and answers, or does it for you',
  'search.web.every_record_desc': 'Opens the results page, with filters',
  'search.web.move': 'Move',
  'search.web.on_device': 'Searches every $t(glossary.record_one) and $t(glossary.list_one) on this device',
  'search.web.results_for': 'Results for “{{typed}}”',
  'search.web.searching': 'Searching…',
  'search.web.more_for': 'More for this search',
  'search.web.copy_link': 'Copy a link to these results',
  'search.web.link_copied': 'Link copied',
  'search.web.clear_recent': 'Clear recent searches',
  'search.web.table_label': 'Search results',
  'search.web.sort.best': 'Best match',
  'search.web.sort.newest': 'Newest first',
  'search.web.sort.oldest': 'Oldest first',
  'search.web.column.name': 'Name',
  'search.web.column.matched': 'What matched',
  'search.web.column.date': 'Date',
  'search.web.column.amount': 'Amount',
  'search.web.facet.kind': 'Kind',
  'search.web.facet.entries': 'Entries',
  'search.web.facet.list_entries': 'List entries',
  'search.web.facet.names': 'Records and lists by name',
  'search.web.facet.record': 'Record',
  'search.web.facet.date': 'Date',
  'search.web.date.any': 'Any time',
  'search.web.date.pick': 'Pick dates…',
  'search.web.date.range': '{{from}} to {{to}}',
  'search.web.chip.none': 'None',
  'search.web.chip.more_one': '{{first}} and {{count, number}} more',
  'search.web.chip.more_other': '{{first}} and {{count, number}} more',
  'search.web.none_filtered_one': '{{count, number}} filter is on. Without it there may be more.',
  'search.web.none_filtered_other': '{{count, number}} filters are on. Without them there may be more.',
  'search.web.reset_filters': 'Reset filters',
  'search.web.total_in': 'Total cash in:',
  'search.web.total_out': 'Total cash out:',
  // web: the Links tab and the manual link editor (stage 5 phase 6)
  'link.web.search': 'Search $t(glossary.link_other)',
  'link.web.col_on': 'On',
  'link.web.col_link': 'The $t(glossary.link_one)',
  'link.web.col_matched': 'Matched by',
  'link.web.flow_puts': 'Puts',
  'link.web.flow_takes': 'Takes',
  'link.web.flow_writes': 'Writes',
  'link.web.flow_into': 'Into',
  'link.web.flow_out_of': 'Out of',
  'link.web.flow_every': 'Every {{noun}}',
  'link.web.flow_an_entry': 'an $t(glossary.entry_one)',
  'link.web.flow_posts': 'Posts',
  'link.web.flow_to': 'To',
  'link.web.select_link': 'Select the $t(glossary.link_one) into {{record}}',
  'link.web.edit': 'Edit',
  'link.web.runs_into': 'The $t(glossary.link_one) into {{record}} runs on its own',
  'link.web.pause_short': 'Pause',
  'link.web.paused_toast': 'The $t(glossary.link_one) into {{record}} is paused',
  'link.web.paused_toast_desc': 'Every $t(glossary.entry_one) it already wrote stays.',
  'link.web.on_toast': 'The $t(glossary.link_one) into {{record}} is on',
  'link.web.paused_many_one': '{{count, number}} $t(glossary.link_one) paused',
  'link.web.paused_many_other': '{{count, number}} $t(glossary.link_other) paused',
  'link.web.removed_one': '{{count, number}} $t(glossary.link_one) removed',
  'link.web.removed_other': '{{count, number}} $t(glossary.link_other) removed',
  'link.web.remove_one_body': 'It stops firing from the next {{noun}}. Every $t(glossary.entry_one) it already wrote stays in {{record}}.',
  'link.web.remove_many_title_one': 'Remove {{count, number}} $t(glossary.link_one)?',
  'link.web.remove_many_title_other': 'Remove {{count, number}} $t(glossary.link_other)?',
  'link.web.remove_many_body': 'They stop firing from the next {{noun}}. Every $t(glossary.entry_one) they already wrote stays where it landed.',
  'link.web.remove_count_one': 'Remove {{count, number}} $t(glossary.link_one)',
  'link.web.remove_count_other': 'Remove {{count, number}} $t(glossary.link_other)',
  'link.web.they_wrote': 'What they wrote',
  'link.web.stays_in_one': '{{count, number}} $t(glossary.entry_one) stays in {{places}}',
  'link.web.stays_in_other': '{{count, number}} $t(glossary.entry_other) stay in {{places}}',
  'link.web.pause_instead': 'Pause instead',
  'link.web.fired_added': '{{fired}} · added by {{person}}',
  'link.web.opening': 'Opening $t(glossary.link_other)',
  'link.web.none_match': 'No $t(glossary.link_one) matches these filters.',
  'link.web.change': 'Change',
  'link.web.nothing_saved': 'Nothing saved yet',
  'link.web.example_from': 'Worked out from the last {{noun}} that fired it',
  'link.web.change_named': 'Change: {{what}}',
  'link.web.where_in': 'Every {{noun}} in {{record}}, where',
  'link.web.for_example': 'For example',
  'link.web.controls': 'The $t(glossary.link_one)’s settings',
  // web: Customize and New record, set it up yourself (stage 5 phase 7)
  'customize.web.fields_in_form': 'Fields in the $t(glossary.entry_one) form',
  'customize.web.fields_list': 'The $t(glossary.field_other), in form order',
  'customize.web.fields_caption': 'Drag to set the order. Click one to change it. * Must be filled.',
  'customize.web.fields_caption_read': 'The order they come in. * Must be filled.',
  'customize.web.preview_form': 'Preview the form',
  'customize.web.form_of': 'The $t(glossary.entry_one) form of {{record}}',
  'customize.web.back_to': 'Back to {{record}}',
  'customize.web.record_row_hint': 'Name, icon, totals',
  'customize.web.a_list': 'A $t(glossary.list_one)',
  'customize.web.one_entry_is': 'one $t(glossary.entry_one) is called {{noun}}',
  'customize.web.name_this': 'Name this $t(glossary.record_one)',
  'customize.web.name_this_line': 'What it counts, and its icon',
  'customize.web.not_in_form': 'Not in the form',
  'customize.web.from_list': 'From {{list}}',
  'customize.web.one_of_one': 'One of {{count, number}} choice',
  'customize.web.one_of_other': 'One of {{count, number}} choices',
  'customize.web.meta_opens_first': 'opens first',
  'customize.web.meta_keeps_last': 'keeps the last',
  'customize.web.keys_reorder': 'Or with the keyboard: Space lifts a $t(glossary.field_one), the arrow keys move it, Esc puts it back.',
  'customize.web.look_only': 'Look only',
  'customize.web.ask_note': 'An admin can let you change the $t(glossary.entry_one) form.',
  'customize.web.read_only_title': 'Your role lets you look at this, not change it.',
  'customize.web.read_only_body': 'An admin can change your role, or your access to {{record}}, in Team.',
  'customize.web.foot_pending': 'Not saved yet. Save changes to keep it.',
  'customize.web.foot_read_only': 'Your role cannot change this.',
  'customize.web.yes': 'Yes',
  'customize.web.no': 'No',
  'customize.web.hint_short': 'Hint',
  'customize.web.show_short': 'Show in the form',
  'customize.web.more_icons': 'More icons',
  'customize.web.noun_caption': 'Buttons read “New {{one}}”, the $t(glossary.list_one) reads “{{many}}”.',
  'customize.web.noun_caption_new': 'Buttons read “New {{one}}” until you name it.',
  'customize.web.totals_both': '{{field}} · in or out, by {{sign}}',
  'customize.web.totals_count': '{{field}} · a count, not money',
  'customize.web.totals_note': 'Changing how totals work moves every past $t(glossary.entry_one) with it, so the numbers on the Dashboard change too. You see the change before it saves.',
  'customize.web.counts_note': 'This decides the totals only.',
  'customize.web.change_noun': 'One $t(glossary.entry_one) is called {{word}} from now on.',
  'customize.web.change_noun_back': 'One $t(glossary.entry_one) goes back to the word it had.',
  'customize.web.change_totals': 'Totals work a different way. Every past $t(glossary.entry_one) is counted the new way.',
  'customize.web.kind': 'Kind',
  'customize.web.change': 'Change',
  'customize.web.search_kinds': 'Search kinds: amount, date, photo',
  'customize.web.no_kind': 'No kind of $t(glossary.field_one) matches that.',
  'customize.web.add_the_field': 'Add the $t(glossary.field_one)',
  'customize.web.joins_on_save': 'Joins the form when you save',
  'customize.web.total_next_line': 'Nobody fills it in',
  'customize.web.list_entries_one': '{{count, number}} $t(glossary.entry_one)',
  'customize.web.list_entries_other': '{{count, number}} $t(glossary.entry_other)',
  'customize.web.new_list_for_field': 'Make one for this $t(glossary.field_one)',
  'customize.web.back_to_new_field': 'Back to the new $t(glossary.field_one)',
  'customize.web.list_for_new': 'For the new $t(glossary.field_one)',
  'customize.web.back_to_field': 'Back to the {{field}} $t(glossary.field_one)',
  'customize.web.list_for': 'For the {{field}} $t(glossary.field_one)',
  'customize.web.always_on': 'Always on',
  'customize.web.columns_caption': 'Every $t(glossary.entry_one) has a name. Choose what else you keep.',
  'customize.web.new_list_note': 'A new $t(glossary.list_one) is created straight away, shows on home under Lists, and becomes what {{field}} reads.',
  'customize.web.list_made': '{{list}} made',
  'customize.web.list_made_desc': '{{field}} reads {{list}}.',
  'customize.web.new_record': 'New $t(glossary.record_one)',
  'customize.web.new_record_desc': 'Set it up yourself',
  'customize.web.new_record_meta': 'Nothing is made until you click Make the $t(glossary.record_one).',
  'customize.web.make_record': 'Make the $t(glossary.record_one)',
  'customize.web.foot_new_record': 'Name it and pick what it totals, then add its $t(glossary.field_other).',
  'customize.web.record_made': '{{record}} is made',
  'customize.web.made_fields_failed': '{{record}} was made, and its new $t(glossary.field_other) were not saved: {{why}}',
  // web: Lists, every list and one list (stage 5 phase 8)
  'list.web.entries_count_one': '{{count, number}} $t(glossary.entry_one)',
  'list.web.entries_count_other': '{{count, number}} $t(glossary.entry_other)',
  'list.web.no_entries': 'No $t(glossary.entry_other) yet',
  'list.web.column.used_by': 'Used by',
  'list.web.column.changed_by': 'Changed by',
  'list.web.column.sample': 'What is in it',
  'list.web.column.used_in': 'Used in',
  'list.web.used_by_none': 'Not used yet',
  'list.web.changed_by': '{{when}} · {{who}}',
  'list.web.search_lists': 'Search $t(glossary.list_other)',
  'list.web.find': 'Find',
  'list.web.show_lists_one': 'Show {{count, number}} $t(glossary.list_one)',
  'list.web.show_lists_other': 'Show {{count, number}} $t(glossary.list_other)',
  'list.web.filter_empty': 'No $t(glossary.list_one) matches these filters.',
  'list.web.facts_failed': 'Who uses each $t(glossary.list_one) could not be loaded.',
  'list.web.sort_by': 'Sort by',
  'list.web.sort.name': 'Name, A to Z',
  'list.web.sort.entries': 'Most $t(glossary.entry_other) first',
  'list.web.sort.last_newest': 'Last changed, newest first',
  'list.web.sort.last_oldest': 'Last changed, oldest first',
  'list.web.sort.name_asc': 'Name, A to Z',
  'list.web.sort.name_desc': 'Name, Z to A',
  'list.web.sort.used': 'Most used first',
  'list.web.sort.newest': 'Newest first',
  'list.web.view': 'View',
  'list.web.view.all': 'All $t(glossary.row_other)',
  'list.web.view.filtered': 'Filtered',
  'list.web.sort.figure_high': '{{field}}, largest first',
  'list.web.sort.figure_low': '{{field}}, smallest first',
  'list.web.menu_delete': 'Delete $t(glossary.list_one)',
  'list.web.change_holds': 'Change what {{list}} holds',
  'list.web.add_entry': 'Add $t(glossary.entry_one)',
  'list.web.not_used': 'Not used by a $t(glossary.record_one) yet',
  'list.web.used_by_records_one': 'Used by {{count, number}} $t(glossary.record_one)',
  'list.web.used_by_records_other': 'Used by {{count, number}} $t(glossary.record_other)',
  'list.web.changed_when': 'Changed {{when}}',
  'list.web.read_only': 'You can look at {{list}} and pick from it in a form. Changing {{list}} is not open to you.',
  'list.web.not_used_yet': 'Not used yet',
  'list.web.every_day': 'Every $t(glossary.entry_one)',
  'list.web.used_in_none': 'Not used in a $t(glossary.record_one) yet',
  'list.web.used_in_records_one': 'Used in {{count, number}} $t(glossary.record_one)',
  'list.web.used_in_records_other': 'Used in {{count, number}} $t(glossary.record_other)',
  'list.web.picked_it_one': '{{count, number}} $t(glossary.entry_one) picked it',
  'list.web.picked_it_other': '{{count, number}} $t(glossary.entry_other) picked it',
  'list.web.remove_line': '{{name}} is taken out of {{list}}. Its history stays.',
  'list.web.remove_line_plain': 'The $t(glossary.entry_one) is taken out of {{list}}. Its history stays.',
  'list.web.picks_through': 'Picks it in {{fields}}',
  'list.web.remove_keeps': 'They keep the name {{name}}. New $t(glossary.entry_other) cannot pick it.',
  'list.web.remove_keeps_in': 'They keep the name {{name}}. New $t(glossary.entry_other) in {{records}} cannot pick it.',
  'list.web.empty_body_read': 'Nothing has been added here yet.',
  'list.web.tell_os': 'Tell OS the names',
  'list.web.tell_os_words': 'Add these names to {{list}}:',
  'list.web.use_it_hint': 'To pick from {{list}} in a form, add a field to a $t(glossary.record_one) that picks from this $t(glossary.list_one).',
  'list.web.use_it': 'Use it in a $t(glossary.record_one)',
  'list.web.add_row.choose_from': 'Choose from {{list}}',
  'list.web.add_row.search': 'Search {{list}}',
  'list.web.add_row.add_to': 'Add {{name}} to {{list}}',
  'list.web.add_row.move': 'move',
  'list.web.add_row.choose': 'choose',
  'list.web.add_row.taken': '{{list}} already has {{name}}.',
  'list.web.delete_body': 'Nothing picks from it. It leaves every page for everyone in this workspace.',
  'list.web.delete_body_picked_one': '{{count, number}} $t(glossary.record_one) picks from {{list}}. Deleting it takes that field out of it.',
  'list.web.delete_body_picked_other': '{{count, number}} $t(glossary.record_other) pick from {{list}}. Deleting it takes that field out of each of them.',
  'list.web.delete_picked_line_one': '{{field}} field · {{count, number}} $t(glossary.entry_one)',
  'list.web.delete_picked_line_other': '{{field}} field · {{count, number}} $t(glossary.entry_other)',
  'list.web.delete_stays_title': 'Entries already written keep their names',
  'list.web.delete_stays_body': 'The names already on them stay. New $t(glossary.entry_other) cannot pick from {{list}}.',
  'list.web.delete_keep': 'Keep it, take it off a $t(glossary.record_one)',
  'list.web.delete_confirm': 'Delete $t(glossary.list_one)',
  // web: a record and its entries
  'record.web.no_entries': 'No $t(glossary.entry_other) yet',
  'record.web.last_entry': 'Last entry {{when}}',
  'record.web.insights_one': 'Insights · {{count, number}}',
  'record.web.insights_other': 'Insights · {{count, number}}',
  'entry.web.column.time': 'Time',
  'entry.web.column.added_by': 'Added by',
  'entry.web.author_you': 'You',
  'entry.web.author_someone': 'Someone',
  'entry.web.select_all': 'Select every $t(glossary.entry_one) shown',
  'entry.web.select': 'Select {{entry}}',
  'entry.web.actions': 'Actions for {{entry}}',
  'entry.web.open': 'Open',
  'entry.web.edit': 'Edit',
  'entry.web.shown_one': '{{shown, number}} of {{count, number}} $t(glossary.entry_one)',
  'entry.web.shown_other': '{{shown, number}} of {{count, number}} $t(glossary.entry_other)',
  'entry.web.loading_older': 'Loading older $t(glossary.entry_other)',
  'entry.web.show_older': 'Show older $t(glossary.entry_other)',
  'entry.web.selected_one': '{{count, number}} selected',
  'entry.web.selected_other': '{{count, number}} selected',
  'entry.web.clear_selection': 'Clear the selection',
  'entry.web.sum_in': '{{amount}} in',
  'entry.web.sum_out': '{{amount}} out',
  'entry.web.export_csv': 'Export CSV',
  'entry.web.previous': 'Previous $t(glossary.entry_one)',
  'entry.web.next': 'Next $t(glossary.entry_one)',
  'entry.web.step_keys': 'Next or previous $t(glossary.entry_one)',
  'entry.web.details': 'Details',
  // A field of the entry that holds nothing, in its Details row, quieter than a filled one.
  'entry.web.not_filled': 'Not filled',
  'entry.web.history': 'History',
  'entry.web.added': 'Added {{when}}',
  'entry.web.changed': 'Changed {{when}}',
  // A Room bill an order moved: the field names itself, so nobody reads it as their own edit.
  'entry.web.worked_out': '{{fields}} updated automatically {{when}}',
  'entry.web.worked_out_any': 'Updated automatically {{when}}',
  // Under a rollup on an entry's panel: the entries it adds up, each one a door.
  'entry.web.rollup_parts': 'In {{field}}',
  'entry.web.rollup_parts_short': 'The total also counts {{record}} entries this browser does not show.',
  'entry.web.attachments': 'Attachments',
  'entry.web.attachments_failed': 'The files could not be listed. Try again in a moment.',
  'entry.web.attachment_open': 'Open {{name}}',
  'entry.web.remove': 'Remove',
  'entry.web.remove_title': 'Remove this $t(glossary.entry_one)',
  'filter.web.find_field': 'Find a $t(glossary.field_one)',
  'filter.web.no_field_match': 'No $t(glossary.field_one) matches that',
  'filter.web.no_fields': 'Nothing in this $t(glossary.record_one) can be filtered by value',
  'filter.web.find_value': 'Find a value',
  // The one check over every value row; while a search is typed it covers the rows that match.
  'filter.web.all_values': 'All values',
  'filter.web.all_matches': 'All matches',
  // The values column's foot, beside its Clear: "2 selected · Clear".
  'filter.web.selected_one': '{{count, number}} selected',
  'filter.web.selected_other': '{{count, number}} selected',
  'filter.web.back': 'Back to $t(glossary.field_other)',
  'filter.web.step_less': '{{side}}, less by {{step}}',
  'filter.web.step_more': '{{side}}, more by {{step}}',
  'filter.web.pick_days': 'Pick days',
  // The verb of a multi select's pill: "Services · has all of · Cut, Colour".
  'filter.web.has_all': 'has all of',
  'filter.web.has_any': 'has any of',
  // Names the any-of / all-of switch for a screen reader.
  'filter.web.match_label': 'Which entries match',
  /* Key caps, drawn as they are printed on a keyboard. */
  'entry.web.key_escape': 'Esc',
  'entry.web.key_slash': '/',
  'entry.web.key_up': '↑',
  'entry.web.key_down': '↓',
  // The lines of an entry on the web form (docs/plans/entry-lines-and-layout.md).
  // `nouns` is what the lines record calls its entries, already in the count's
  // form and mid-sentence: "item", "rooms".
  // Under a count that could not go lower: the lines over it hold something (Decision 9).
  'entry.web.lines.count_note_one': '{{count, number}} {{nouns}} has details. Remove it by hand to go lower.',
  'entry.web.lines.count_note_other': '{{count, number}} {{nouns}} have details. Remove one by hand to go lower.',
  // Save stopped before the server would refuse it for good. `field` is the lines field's name.
  'entry.web.lines.over_max_one': '{{field}} holds at most {{count, number}} line. Remove one to save.',
  'entry.web.lines.over_max_other': '{{field}} holds at most {{count, number}} lines. Remove some to save.',
  'entry.web.lines.over_total_one': 'This $t(glossary.entry_one) holds at most {{count, number}} line in all. Remove one to save.',
  'entry.web.lines.over_total_other': 'This $t(glossary.entry_one) holds at most {{count, number}} lines in all. Remove some to save.',
  // Over Save: how many lines each table holds, "2 items · 1 room".
  'entry.web.lines.summary_count_one': '{{count, number}} {{nouns}}',
  'entry.web.lines.summary_count_other': '{{count, number}} {{nouns}}',
  // web: the entry form
  'entry_form.counter': '{{typed, number}} / {{limit, number}}',
  'entry_form.equals': '= {{value}}',
  'entry_form.time': '{{label}} time',
  'entry_form.phone.country': 'Country code {{country}}, +{{code}}',
  'entry_form.phone.dial': '+{{code}}',
  'entry_form.phone.search': 'Search countries',
  'entry_form.phone.search_hint': 'Type a name or a code',
  'entry_form.choice.filter_label': 'Filter {{label}}',
  'entry_form.reference.search_label': 'Search {{label}}',
  'entry_form.reference.searching': 'Searching {{label}}',
  'entry_form.reference.move': 'move',
  'entry_form.reference.choose': 'choose',
  'entry_form.keypad': 'Calculator keypad',
  'entry_form.keypad_hint': 'Type the sum on your keyboard too. Enter works it out.',
  'entry_form.key.ac': 'AC',
  'entry_form.key.c': 'C',
  'entry_form.key.clear_all': 'Clear the whole sum',
  'entry_form.key.delete': 'Delete the last character',
  'entry_form.key.percent': 'Percent',
  'entry_form.key.divide': 'Divide',
  'entry_form.key.times': 'Times',
  'entry_form.key.minus': 'Minus',
  'entry_form.key.plus': 'Plus',
  'entry_form.key.point': 'Decimal point',
  'entry_form.key.equals': 'Work out the sum',
  'entry_form.file.drop': 'Drop a file here',
  'entry_form.file.uploading': 'Uploading',
  'entry_form.file.size_bytes': '{{size, number}} B',
  'entry_form.file.size_kb': '{{size, number}} KB',
  'entry_form.file.size_mb': '{{size, number}} MB',
  'entry_form.file.too_big': 'That file is {{size}}. The limit is {{limit}}.',
  'entry_form.map.choose': 'Choose a place',
  'entry_form.map.change': 'Change the place',
  'entry_form.map.use_current': 'Use my current location',
  'entry_form.map.attribution': '© OpenStreetMap',
  'entry_form.map.refused': 'Location was refused. Allow it for this site in the browser, then try again.',
  'entry_form.map.unsupported': 'This browser cannot tell where it is. Type the place instead.',
  'entry_form.new': 'New $t(glossary.entry_one)',
  'entry_form.edit': 'Change this $t(glossary.entry_one)',
  'entry_form.no_business': 'No $t(glossary.business_one) is open',
  'entry_form.no_title_field': '{{record}} has no title $t(glossary.field_one) to fill',
  'sheet_page.days': 'Days',
  'sheet_page.day_named': '{{name}}, {{weekday}} {{day}}',
  'sheet_page.on_list_one': '{{count, number}} on {{list}}',
  'sheet_page.on_list_other': '{{count, number}} on {{list}}',
  'sheet_page.one_mark_each': 'One mark each',
  'sheet_page.cell': '{{person}}: {{option}}',
  // web: customize a record
  // The recipe builder opened for a link's answer: under its title, and in its note. {{noun}} is the source's entry noun, lower case ("sale"); {{field}} and {{record}} are the target's.
  'customize.web.recipe_link_caption': 'Worked out on every {{noun}} this $t(glossary.link_one) fires on, and written into {{field}} in {{record}}.',
  'customize.web.recipe_link_pending': 'Not saved yet. It is kept when the $t(glossary.link_one) is saved.',
  // The recipe builder opened for an insight's measure: its note.
  'customize.web.recipe_insight_pending': 'Not saved yet. It is kept when the $t(glossary.insight_one) is added or saved.',
  // Beside "Add a piece" in the recipe picker: where the piece goes. {{op}} is the operator's sign and word, "× times".
  'customize.web.picker_after': 'After {{op}}',
  'customize.web.not_saved': 'Not saved yet',
  'customize.web.col_records': 'What it records',
  'customize.web.col_rules': 'Rules',
  'customize.web.drag': 'Drag {{field}} to reorder',
  'customize.web.drag_instructions': 'To move a $t(glossary.field_one), press Space on its handle, move it with the arrow keys, and press Space again. Escape puts it back.',
  'customize.web.drag_picked': 'Picked up {{field}}',
  'customize.web.drag_moved': '{{field}} is at {{position, number}} of {{total, number}}',
  'customize.web.drag_dropped': '{{field}} is now at {{position, number}} of {{total, number}}',
  'customize.web.drag_cancelled': '{{field}} is back where it was',
  'customize.web.drop_here': 'Drop here',
  'customize.web.drag_hint': 'Drag to set the order',
  'customize.web.preview_caption': 'The form as this draft makes it. A $t(glossary.field_one) that is off is gone, a new one is in, and the order is the one on the left.',
  'customize.web.preview_badge': 'Preview',
  'customize.web.preview_of': '{{record}} · as this draft makes it',
  'customize.web.panel_label': 'Customize {{record}}',
  'customize.web.keys_move': 'to move',
  'customize.web.keys_pick': 'to pick',
  'customize.web.keys_close': 'to close',
  'customize.web.step_rules': 'Name and rules',
  'customize.web.choices_one': '{{count, number}} choice',
  'customize.web.choices_other': '{{count, number}} choices',
  'customize.web.list_rows_one': '{{count, number}} $t(glossary.entry_one)',
  'customize.web.list_rows_other': '{{count, number}} $t(glossary.entry_other)',
  'customize.web.field_gone': 'This $t(glossary.field_one) is not in the draft any more. Open the form again and pick it from the list.',
  'customize.web.formula_gone': 'No $t(glossary.recipe_one) is open. Open a $t(glossary.field_one) and start its $t(glossary.recipe_one) again.',
  'customize.web.back_to_form': 'Back to the form',
  'customize.web.last_entry': 'The last $t(glossary.entry_one)',
  'customize.web.last_entry_note': 'The newest $t(glossary.entry_one) on this $t(glossary.record_one) is the one the $t(glossary.recipe_one) is tried on.',
  'customize.web.looks': 'Looks',
  'customize.web.on_home': 'On home',
  'customize.web.load_failed': 'The form could not be opened',
  'customize.web.save_failed': 'The changes were not saved',
  'customize.web.key_up': '↑',
  'customize.web.key_down': '↓',
  'customize.web.key_left': '←',
  'customize.web.key_right': '→',
  'customize.web.key_enter': '↵',
  'customize.web.key_escape': 'Esc',
  'customize.web.change_count_one': '{{count, number}} change to the $t(glossary.entry_one) form',
  'customize.web.change_count_other': '{{count, number}} changes to the $t(glossary.entry_one) form',
  // web: insights and the dashboard
  // A meter's share at the end of an insight's head: "92%". The true ratio, never clamped.
  'insight.web.percent': '{{value, number}}%',
  // The buttons on an insight's tile, each naming the insight it belongs to.
  'insight.web.more_for': 'More for {{name}}',
  'insight.web.move': 'Move {{name}}',
  'insight.web.resize': 'Change the size of {{name}}',
  // The keyboard shortcuts beside Move earlier and Move later in edit mode's menu.
  'insight.web.key_earlier': '⌥←',
  'insight.web.key_later': '⌥→',
  // A record's Insights page: how many there are, or none.
  'insight.web.count_one': '{{count, number}} $t(glossary.insight_one)',
  'insight.web.count_other': '{{count, number}} $t(glossary.insight_other)',
  'insight.web.none_yet': 'No $t(glossary.insight_other) yet',
  'insight.web.hidden': 'The totals of this $t(glossary.record_one) are not shown to you',
  // The offer's instruction: the phone's `insight.suggest_hint`, clicked rather than tapped.
  'insight.web.suggest_hint': 'Click one to keep it on top of {{record}}.',
  // The galleries' lines: the phone's, said of a browser, and clicked rather than tapped.
  'insight.web.gallery_sizes_body':
    'Every number below is yours, worked out from the $t(glossary.entry_other) of {{record}}. Click one to see its sizes.',
  'insight.web.biz_gallery_body':
    'Every number below is yours, worked out from all your $t(glossary.record_other). Click one to set it up and keep it on top.',
  // The business gallery's list of its sections, down the left. Spoken, not printed.
  'insight.web.sections': 'Sections',
  // The editor's badge before its name: not yet kept. "Added" is the phone's word for one that is.
  'insight.web.draft': 'Draft',
  // Under the editor's name while the name is still the one the editor wrote.
  'insight.web.name_hint': 'Written for you. Type a name of your own to replace it.',
  // The editor's freshness line: the preview is worked out again on every change.
  'insight.web.worked_out_now': 'Worked out just now',
  // The builder panel's section holding row A, the record, and its conditions.
  'insight.web.counts': 'Counts',
  // Spoken on the sign between two combined totals: one click moves it to the next.
  'insight.web.change_op': 'Change {{op}}',
  // The condition picker's way back from a field's values to the list of fields.
  'insight.web.back_to_fields': 'Back to the $t(glossary.field_other)',
  // The business builder's switch beside "Only count": only the entries this person wrote.
  'insight.web.written_by_you': 'Written by you',
  // The table under the editor's line or bars: the numbers the drawing is made of.
  'insight.web.by_day': 'By day',
  'insight.web.by_day_note': 'the numbers under the line',
  'insight.web.series': 'Series',
  'insight.web.today_so_far': 'Today, so far',
  'insight.web.average_days': '{{days, number}}-day average',
  // The table under the editor's breakdown: each group ranked against the first.
  'insight.web.rank': 'Rank',
  'insight.web.group': 'Group',
  'insight.web.against_top': 'Against the top one',
  'insight.web.value': 'Value',
  'insight.web.total': 'Total',
  // The one insight editor (stage 6): the column's back arrow, where the preview sits, and its notes.
  'insight.web.back_to_dashboard': 'Back to the $t(glossary.dashboard)',
  'insight.web.back_to_insights': 'Back to the $t(glossary.insight_other) of {{record}}',
  'insight.web.preview': 'Preview',
  'insight.web.as_on_dashboard': '{{size}}, as it sits on the $t(glossary.dashboard)',
  'insight.web.as_on_record': '{{size}}, as it sits on {{record}}',
  // Twelve insights already on the page the editor adds to: its primary is off.
  'insight.web.full': '{{count, number}} of {{count, number}} on {{place}}. Take one off in Arrange $t(glossary.insight_other) to add another.',
  'insight.web.list_money': '{{field}} cannot be added up here. An $t(glossary.insight_one) adds up only the money a $t(glossary.list_one) works out from $t(glossary.record_other), and only over the $t(glossary.list_one)’s own $t(glossary.row_other).',
  // The line under a card over a list's stored balance (owner, 29 Oct 2026): the entries its totals leave out for picking no row of the list, so the figure is never quietly short. {{field}} is the pick's own label.
  'insight.unpicked_one': '{{count, number}} $t(glossary.entry_one) picks no {{field}}, not counted',
  'insight.unpicked_other': '{{count, number}} $t(glossary.entry_other) pick no {{field}}, not counted',
  // Row A of a workspace insight, and what it counts when nothing narrows it.
  'insight.web.every_record': 'Every $t(glossary.record_one)',
  'insight.web.every_entry_counts': 'Every $t(glossary.entry_one) in every $t(glossary.record_one) counts',
  'insight.web.last_days_head': 'Last {{days, number}} days',
  // The head's meta line of a combined insight.
  'insight.web.from_totals_one': 'Worked out from {{count, number}} total',
  'insight.web.from_totals_other': 'Worked out from {{count, number}} totals',
  // The table of combined totals under the preview: "A − B, this month".
  'insight.web.combine_of': '{{formula}}, {{period}}',
  'insight.web.total_letter': 'Which total',
  // Add a condition with several values: the ticks, how many are ticked, and Clear.
  'insight.web.which_values': 'Which values',
  'insight.web.values_chosen_one': '{{count, number}} chosen',
  'insight.web.values_chosen_other': '{{count, number}} chosen',
  'insight.web.clear_values': 'Clear',
  // A condition's chip once it holds several values; hovering the count names them all.
  'insight.web.values_noun': '{{count, number}} {{noun}}',
  'insight.web.values_count_one': '{{count, number}} value',
  'insight.web.values_count_other': '{{count, number}} values',
  // What a workspace condition may ask about: what every entry of every record has in common.
  'insight.web.every_entry': 'Every $t(glossary.entry_one)',
  'insight.web.cond_record': 'Record',
  'insight.web.cond_creator': 'Written by',
  'insight.web.cond_channel': 'Arrived from',
  // web: Arrange insights, its own page (stage 6)
  'insight.arrange.edit_layout': 'Edit layout',
  // Who sees the Dashboard's insights follows the role's See insights, not a named rank.
  'insight.arrange.who_dashboard': 'Shown to everyone who may see $t(glossary.insight_other)',
  'insight.arrange.on_record': 'The $t(glossary.insight_other) on {{record}}’s Insights tab',
  'insight.arrange.discard_title': 'Discard your changes?',
  'insight.arrange.keep_editing': 'Keep editing',
  'insight.arrange.back_dashboard': 'The $t(glossary.dashboard) goes back to how it was.',
  'insight.arrange.back_record': '{{record}} goes back to how it was.',
  'insight.arrange.you_did': 'You {{changes}}.',
  'insight.arrange.moved_one': 'moved {{count, number}} $t(glossary.insight_one)',
  'insight.arrange.moved_other': 'moved {{count, number}} $t(glossary.insight_other)',
  'insight.arrange.moved_bare_one': 'moved {{count, number}}',
  'insight.arrange.moved_bare_other': 'moved {{count, number}}',
  'insight.arrange.resized_one': 'resized {{count, number}} $t(glossary.insight_one)',
  'insight.arrange.resized_other': 'resized {{count, number}} $t(glossary.insight_other)',
  'insight.arrange.resized_bare_one': 'resized {{count, number}}',
  'insight.arrange.resized_bare_other': 'resized {{count, number}}',
  'insight.arrange.took_off_one': 'took off {{count, number}} $t(glossary.insight_one)',
  'insight.arrange.took_off_other': 'took off {{count, number}} $t(glossary.insight_other)',
  'insight.arrange.took_off_bare_one': 'took off {{count, number}}',
  'insight.arrange.took_off_bare_other': 'took off {{count, number}}',
  'insight.arrange.added_one': 'added {{count, number}} $t(glossary.insight_one)',
  'insight.arrange.added_other': 'added {{count, number}} $t(glossary.insight_other)',
  'insight.arrange.added_bare_one': 'added {{count, number}}',
  'insight.arrange.added_bare_other': 'added {{count, number}}',
  'insight.arrange.reset_part': 'put back the usual $t(glossary.insight_other)',
  // A card's More on a record's Insights tab and the Dashboard: about that card only.
  'insight.web.open': 'Open',
  'insight.web.put_on_dashboard': 'Put on the $t(glossary.dashboard)',
  'insight.web.put_done': '{{name}} is on the $t(glossary.dashboard).',
  'insight.web.put_there': '{{name}} is already on the $t(glossary.dashboard).',
  'insight.web.download_csv': 'Download as CSV',
  'insight.web.csv_day': 'Day',
  'insight.web.csv_group': 'Group',
  'insight.web.csv_change': 'Change',
  // A record's Insights tab with none yet.
  'insight.web.none_body':
    'An $t(glossary.insight_one) is a number or a chart worked out from these $t(glossary.entry_other): today’s sales, the top item, how people pay.',
  'insight.web.admin_adds': 'An admin adds them.',
  // An insight's own page (stage 6): the badge saying where it sits, what it counts, Change it, and the stat strip.
  'insight.page.on_dashboard': 'On the $t(glossary.dashboard)',
  'insight.page.on_record': 'On {{record}}',
  'insight.page.every_record_counts': 'Every $t(glossary.record_one) counts',
  'insight.page.change_it': 'Change it',
  'insight.page.average_day': 'Average a day',
  'insight.page.highest': 'Highest',
  'insight.page.lowest': 'Lowest',
  'insight.page.days_one': '{{count, number}} day',
  'insight.page.days_other': '{{count, number}} days',
  // An insight with OS first (web-redesign-10 phase 5): Add an insight and Change an
  // insight open with OS asking, the insight drawn beside the talk; an opened insight has OS beside it.
  'insight.os.this_insight': 'This $t(glossary.insight_one)',
  'insight.os.opening': 'Ask me anything about {{insight}}.',
  'insight.os.save_as_new': 'Save as a new $t(glossary.insight_one)',
  'insight.os.put_on': 'Put it on {{record}}',
  'insight.os.add_title': 'Add an $t(glossary.insight_one)',
  'insight.os.change_title': 'Change {{insight}}',
  'insight.os.from_all': 'Worked out from all your $t(glossary.record_other)',
  'insight.os.from_record': 'Worked out from the $t(glossary.entry_other) of {{record}}',
  'insight.os.nothing_added': 'Nothing is added until you say so',
  'insight.os.nothing_changes': 'Nothing changes until you save',
  'insight.os.question_dashboard':
    'What should the $t(glossary.dashboard) show? Say it in your own words: a number, a trend, or who comes top. It is built in the preview as you talk.',
  'insight.os.question_record':
    'What should {{record}} show on top? Say it in your own words: a total, a trend, or what comes top. It is built in the preview as you talk.',
  'insight.os.question_change': 'What should change about {{insight}}? It changes in the preview first, and nothing is saved until you say so.',
  'insight.os.chip.money_in': 'Money in this month',
  'insight.os.chip.went_out': 'What went out this month',
  'insight.os.chip.owes': 'Who owes me the most',
  'insight.os.chip.split': 'Split it by {{field}}',
  'insight.os.chip.top': 'Top {{field}} this month',
  'insight.os.chip.as_bars': 'Show it as bars',
  'insight.os.chip.as_trend': 'Show it as a trend line',
  'insight.os.chip.this_month': 'Only this month',
  'insight.os.note.nothing': 'Nothing built yet',
  'insight.os.note.built': 'Built from what you said',
  'insight.os.note.as_is': 'As it is now',
  'insight.os.note.changed': 'Changed, not saved yet',
  'insight.os.note.by_hand': 'As you set it up yourself. Nothing is added yet.',
  'insight.os.note.added': 'Added just now',
  'insight.os.note.saved': 'Saved just now',
  'insight.os.was': 'Was: {{insight}}',
  'insight.os.ask.title': 'Anything the editor sets, you can ask for',
  'insight.os.ask.body': 'Say it, and it shows here. Or set it up yourself, and every setting is there.',
  'insight.os.ask.show_as': 'Show as',
  'insight.os.ask.show_as_say': '“Show it as a trend line”',
  'insight.os.ask.measure': 'Measure',
  'insight.os.ask.measure_say': '“Count the sales, not the money”',
  'insight.os.ask.break_down': 'Break down by',
  'insight.os.ask.break_down_say': '“Split it by payment mode”',
  'insight.os.ask.only': 'Only count',
  'insight.os.ask.only_say': '“Only UPI”',
  'insight.os.ask.period': 'Over what period',
  'insight.os.ask.period_say': '“Only this month”',
  'insight.os.ask.name': 'Name',
  'insight.os.ask.name_say': '“Call it money by mode”',
  'insight.os.open_it': 'Open it',
  'insight.os.add_another': 'Add another',
  // Under a card over a list's stored balance, when a total it stands on has a condition the server could not judge.
  'insight.unpicked_more_one': '{{count, number}} $t(glossary.entry_one) picks no {{field}}, not counted, and may leave out more',
  'insight.unpicked_more_other': '{{count, number}} $t(glossary.entry_other) pick no {{field}}, not counted, and may leave out more',
  'insight.unpicked_unjudged': 'May leave out $t(glossary.entry_other) that pick no {{field}}',
  // The Dashboard (stage 6, phase 6): OS's greeting and the one line about today, worked out in the browser.
  'dashboard.web.hello.morning': 'Good morning, {{name}}',
  'dashboard.web.hello.afternoon': 'Good afternoon, {{name}}',
  'dashboard.web.hello.evening': 'Good evening, {{name}}',
  'dashboard.web.hello.morning.no_name': 'Good morning',
  'dashboard.web.hello.afternoon.no_name': 'Good afternoon',
  'dashboard.web.hello.evening.no_name': 'Good evening',
  'dashboard.web.today.in_out': 'You have {{in}} in today and {{out}} out.',
  'dashboard.web.today.in': 'You have {{in}} in today.',
  'dashboard.web.today.out': '{{out}} went out today.',
  'dashboard.web.today.quiet': 'Nothing added today yet.',
  'dashboard.web.today.no_money': 'Your numbers, worked out from every $t(glossary.record_one) on this device.',
  'dashboard.web.today.no_records': 'Nothing kept yet. Tell OS what you keep, and it sets up the $t(glossary.record_other) for you.',
  'dashboard.web.today.no_entries': 'Your $t(glossary.record_other) are set up. Add the first $t(glossary.entry_one), and your numbers start.',
  'dashboard.web.today.offline_tail': 'OS needs the connection to answer.',
  'dashboard.web.today.offline_empty': 'This device is offline. OS needs the connection; your $t(glossary.entry_other) still save here.',
  'dashboard.web.today.not_read': 'Your numbers could not be read just now.',
  'dashboard.web.today.not_worked_out': 'Your numbers could not be worked out just now. Your $t(glossary.entry_other) are safe.',
  // Where OS's composer sits on the Dashboard until stage 8 draws it: a door to OS.
  'dashboard.web.ask': 'Ask OS, or say what to do',
  'dashboard.web.ask_off_view_as': 'OS stays off while you view as someone else',
  // The Dashboard's Quick actions.
  'dashboard.web.quick': 'Quick actions',
  'dashboard.web.quick.add_entry': 'Add an $t(glossary.entry_one)',
  'dashboard.web.quick.add_entry_line': 'Pick a $t(glossary.record_one)',
  'dashboard.web.quick.add_entry_none': 'Needs a $t(glossary.record_one) first',
  'dashboard.web.quick.new_record_line': 'From a start, or by asking OS',
  'dashboard.web.quick.new_list': 'New $t(glossary.list_one)',
  'dashboard.web.quick.new_list_line': 'Customers, items, anything',
  'dashboard.web.quick.invite': 'Invite someone',
  'dashboard.web.quick.invite_line_one': '{{count, number}} $t(glossary.person_one) here now',
  'dashboard.web.quick.invite_line_other': '{{count, number}} $t(glossary.person_other) here now',
  'dashboard.web.quick.invite_line_none': 'Add someone to this $t(glossary.business_one)',
  'dashboard.web.quick.invites_out_one': '{{count, number}} invitation made',
  'dashboard.web.quick.invites_out_other': '{{count, number}} invitations made',
  // The Dashboard's Insights with none on it, and when the numbers could not be worked out.
  'dashboard.web.none_body': 'An $t(glossary.insight_one) is a number or a chart you want on this page: today’s sales, dues to collect, your top item.',
  'dashboard.web.failed_title': 'Your numbers could not be worked out',
  'dashboard.web.failed_body': 'Try again in a moment. You can still add an $t(glossary.entry_one).',
  // web: links, provenance and access
  'link.web.pause': 'Pause this $t(glossary.link_one)',
  'link.web.more': 'More for this $t(glossary.link_one)',
  'link.web.remove_title': 'Remove this $t(glossary.link_one)?',
  'link.web.remove_body': 'It stops firing. Every $t(glossary.entry_one) it already wrote stays.',
  'link.web.fires_explain': 'Every $t(glossary.entry_one), or only the ones that meet a condition.',
  'link.web.carries_explain': 'A $t(glossary.field_one) on this $t(glossary.entry_one), the same value every time, or leave it empty.',
  'link.web.count_one': '{{count, number}} $t(glossary.link_one)',
  'link.web.count_other': '{{count, number}} $t(glossary.link_other)',
  'link.web.load_failed': 'The $t(glossary.link_other) could not be read',
  'link.web.back': 'Back to $t(glossary.link_other)',
  'link.web.when': 'When',
  'link.web.then': 'Then',
  'link.web.every_in': 'Every {{noun}} in {{record}}',
  'link.web.would_fire': 'Would fire it',
  'link.web.of_last_one': 'of the last {{count, number}} $t(glossary.entry_one)',
  'link.web.of_last_other': 'of the last {{count, number}} $t(glossary.entry_other)',
  'link.web.condition_n': 'Condition {{position, number}}',
  'link.web.remove_value': 'Take {{value}} off',
  'link.web.field_in': 'Field in {{record}}',
  'link.web.holds': 'Holds',
  'link.web.fills_it': 'What fills it',
  'link.web.in_preview': 'In the preview',
  'link.web.fields_needed_one': '{{count, number}} $t(glossary.field_one) · {{needed, number}} needed',
  'link.web.fields_needed_other': '{{count, number}} $t(glossary.field_other) · {{needed, number}} needed',
  'link.web.field_count_one': '{{count, number}} $t(glossary.field_one)',
  'link.web.field_count_other': '{{count, number}} $t(glossary.field_other)',
  'link.web.in_record': 'In {{record}}',
  'link.detail.title': '{{from}} → {{to}}',
  'link.detail.sentence': 'In a sentence',
  'link.detail.so_far': 'So far',
  'link.detail.on_line': 'It runs on its own, on every new $t(glossary.entry_one) in {{record}} it fits.',
  'link.detail.read_only': 'Only someone who may change the $t(glossary.link_other) of {{record}} can switch, edit or remove this one.',
  'link.detail.edit': 'Edit this $t(glossary.link_one)',
  'link.detail.example_real': 'Worked out on the newest {{record}} $t(glossary.entry_one) that fires it, from {{when}}.',
  'link.detail.example_made_up': 'Made up from the $t(glossary.field_other) of {{record}}: no $t(glossary.entry_one) here fires it yet.',
  'link.detail.example_a': 'A {{record}} $t(glossary.entry_one)',
  'link.detail.example_an': 'An {{record}} $t(glossary.entry_one)',
  'link.detail.example_a_of': 'A {{record}} $t(glossary.entry_one) of {{values}}',
  'link.detail.example_an_of': 'An {{record}} $t(glossary.entry_one) of {{values}}',
  'link.detail.example_adds': '{{entry}} adds {{figure}} to {{record}}.',
  'link.detail.example_adds_as': '{{entry}} adds {{figure}} to {{record}} as {{choices}}.',
  'link.detail.example_takes': '{{entry}} takes {{figure}} out of {{record}}.',
  'link.detail.example_takes_as': '{{entry}} takes {{figure}} out of {{record}} as {{choices}}.',
  'link.detail.example_posts': '{{entry}} posts {{figure}} to {{record}}.',
  'link.detail.example_posts_as': '{{entry}} posts {{figure}} to {{record}} as {{choices}}.',
  'link.detail.example_writes': '{{entry}} adds an $t(glossary.entry_one) to {{record}}.',
  'link.detail.example_writes_as': '{{entry}} adds an $t(glossary.entry_one) to {{record}} as {{choices}}.',
  'record_access.web.person': 'Person',
  'record_access.web.level_job': 'Level and job',
  'record_access.web.fields': 'Fields',
  'record_access.web.every_field': 'Every $t(glossary.field_one)',
  'record_access.web.set_by_hand_count_one': '{{count, number}} set by hand',
  'record_access.web.set_by_hand_count_other': '{{count, number}} set by hand',
  'record_access.web.load_failed': 'Who can use this could not be read',
  'record_access.web.nobody_else': 'Nobody but an owner can open this $t(glossary.record_one) yet.',
  'record_access.web.field': 'Field',
  'record_access.web.state': 'State',
  'record_access.web.does': 'What it does to the $t(glossary.field_one)',
  'record_access.web.they_write': 'They write',

  // The Money section of Workspace details (L42, L43 and L53 of
  // docs/plans/ledger.md): the financial year and tax. Never "book".
  // The Tax payment record (step 10.7 of docs/plans/ledger.md).
  'tax_payment.name': 'Tax payment',
  'tax_payment.description': 'What you paid the tax office, and the credit you set off.',
  'tax_payment.field.period': 'For the month',
  'tax_payment.field.collected_cgst': 'CGST you collected',
  'tax_payment.field.collected_sgst': 'SGST you collected',
  'tax_payment.field.collected_utgst': 'UTGST you collected',
  'tax_payment.field.collected_igst': 'IGST you collected',
  'tax_payment.field.collected_single': 'Tax you collected',
  'tax_payment.field.credit_cgst': 'CGST credit set off',
  'tax_payment.field.credit_sgst': 'SGST credit set off',
  'tax_payment.field.credit_utgst': 'UTGST credit set off',
  'tax_payment.field.credit_igst': 'IGST credit set off',
  'tax_payment.field.credit_single': 'Tax you claimed back',
  'tax_payment.field.paid_from': 'Paid from',
  'tax_payment.option.bank': 'Bank',
  'tax_payment.option.cash': 'Cash',
  'tax_payment.field.challan': 'Challan number',
  'tax_payment.field.reference': 'Payment reference',
  'money_settings.title': 'Money',
  'money_settings.line': 'How money is counted in this workspace. Only the owner and admins change these.',
  'money_settings.line_read': 'How money is counted in this workspace.',
  'money_settings.year': 'Financial year',
  'money_settings.year.help': 'Set by OS when the workspace was set up. You can change it until the first month is closed.',
  'money_settings.year.help_closed': 'A month is closed, so a change holds from the start of the next year.',
  'money_settings.year.unset': 'Not set yet',
  'money_settings.year.pick': 'Pick the month it starts',
  'money_settings.year.span': '{{from}} to {{to}}',
  'money_settings.year.runs': 'Your financial year runs {{span}}.',
  'money_settings.year.pending': 'From {{day}} it runs {{span}}.',
  'money_settings.tax': 'Tax',
  'money_settings.tax.pick': 'Pick how tax is kept',
  'money_settings.tax.unset': 'Not set yet',
  'money_settings.tax.none': 'Not registered',
  'money_settings.tax.in_gst': 'GST, registered',
  'money_settings.tax.single': 'VAT or GST, one tax',
  'money_settings.tax.sales_tax': 'Sales tax',
  'money_settings.tax.help.none': 'No tax is added to bills.',
  'money_settings.tax.help.in_gst': 'CGST and SGST inside your state. A customer in another state is charged IGST.',
  'money_settings.tax.help.single': 'One tax at each line’s rate, which you can claim back on what you buy.',
  'money_settings.tax.help.sales_tax': 'Tax collected on sales, with nothing claimed back.',
  'money_settings.tax.help.unset': 'Say whether the business is registered for tax.',
  'money_settings.month.1': 'January',
  'money_settings.month.2': 'February',
  'money_settings.month.3': 'March',
  'money_settings.month.4': 'April',
  'money_settings.month.5': 'May',
  'money_settings.month.6': 'June',
  'money_settings.month.7': 'July',
  'money_settings.month.8': 'August',
  'money_settings.month.9': 'September',
  'money_settings.month.10': 'October',
  'money_settings.month.11': 'November',
  'money_settings.month.12': 'December',

  // Phase 6 of docs/plans/ledger.md (L12, L17, L19, L53; boards C3, C4, C5):
  // the accountant view switch and Close a month in the Money section, an
  // entry in a closed month and its Correct, and the accountant view's cards
  // and columns. Debit and credit are said only in the accountant view.
  'money_settings.accountant': 'Accountant view',
  'money_settings.accountant.help': 'Shows debit, credit, the trial balance and the balance sheet.',
  'money_settings.accountant.on': 'On',
  'money_settings.accountant.off': 'Off',
  'money_settings.close': 'Close a month',
  'money_settings.close.help': 'Nobody can change an entry on or before this date. Closed through {{day}}.',
  'money_settings.close.help_none': 'Nobody can change an entry on or before this date. Nothing is closed yet.',
  'money_settings.close.pick': 'Pick a day',
  'money_settings.close.button': 'Close {{month}}',
  'money_settings.close.button_day': 'Close through {{day}}',
  'money_settings.close.to_sort_one': '{{count, number}} entry in To sort holds {{amount}}. Sort it before you close {{month}}.',
  'money_settings.close.to_sort_other': '{{count, number}} entries in To sort hold {{amount}}. Sort them before you close {{month}}.',
  'money_settings.close.waiting_one': '{{count, number}} entry in {{month}} is a draft or waits for approval. It counts in nothing until it is approved.',
  'money_settings.close.waiting_other': '{{count, number}} entries in {{month}} are drafts or wait for approval. They count in nothing until they are approved.',
  'money_settings.close.open_to_sort': 'Sort them now',
  'money_settings.close.confirm_title': 'Close {{month}}?',
  'money_settings.close.confirm_body': 'Nobody can change an entry dated on or before {{day}} after this. A mistake there is put right with a correction dated the day it is made.',
  'money_settings.close.done': '{{month}} is closed',
  'money_settings.close.done_day': 'Closed through {{day}}',
  'money_settings.close.nothing': 'Every month that has ended is closed.',
  'closed.title': '{{month}} is closed',
  'closed.title_day': 'Closed through {{day}}',
  'closed.desc': 'Entries on or before {{day}} cannot change. Correct it instead: a correction dated today puts it right and keeps the old entry visible.',
  'closed.off': 'An entry in a closed month cannot be edited or removed',
  'closed.correct': 'Correct',
  'closed.correcting_in': 'Correcting, dated today, in {{record}}',
  'closed.corrected': 'Corrected. The correction is dated today.',
  'accountant.section': 'Accountant view',
  'accountant.line': 'Accountant view is on · {{span}}',
  'accountant.tb.title': 'Trial balance, {{day}}',
  'accountant.tb.account': 'Account',
  'accountant.tb.earlier_years': 'Profit of earlier years',
  'accountant.tb.total': 'Total',
  'accountant.debit': 'Debit',
  'accountant.credit': 'Credit',
  'accountant.pl.title': 'Profit and loss',
  'accountant.pl.profit': 'Profit',
  'accountant.pl.to_sort': 'To sort, not counted yet',
  'accountant.bs.title': 'Balance sheet',
  'accountant.bs.have': 'What you have',
  'accountant.bs.owe': 'What you owe',
  'accountant.bs.owners': 'Owner’s money and profit',
  'accountant.bs.export': 'Export for your accountant: trial balance, profit and loss, tax summary.',

  // The category picker (step 3.2 and L16 of docs/plans/ledger.md; boards E1
  // and E2): a reference to the Accounts list, grouped by kind. Never
  // "ledger", "posting", "debit" or "credit".
  'money.group.income': 'Income',
  'money.group.shop': 'Shop expenses',
  'money.group.home': 'Not the shop’s',
  'money.group.debt': 'Loans',
  'money.group.move': 'Moved between your own money',
  'money.not_sure': 'Not sure',
  'money.not_sure_desc': 'It waits in To sort until someone picks',
  'money.explain.title': 'How money works here',
  'money.explain.what_for': 'Every entry in {{records}} says what the money was for.',
  'money.explain.what_for_none': 'Every money entry says what the money was for.',
  'money.explain.both_sides': 'RecordOS counts both sides of each entry, so Cash, what {{who}} owe and profit are always worked out, never typed.',
  'money.explain.customers': 'customers',
  'money.explain.basis.cash': 'Income counts when the money comes in.',
  'money.explain.basis.earned': 'Income counts when a sale or a charge is made, and a payment clears what is owed.',
  'money.explain.basis.both': 'A sale or a charge counts when it is made, and a plain Money in counts when the money comes in.',
  'money.explain.to_sort': 'Not sure what an entry was for? Pick Not sure, and it waits in To sort until someone picks.',
  'money.explain.year_unset': 'The financial year is not set yet.',
  'money.explain.tax.none': 'No tax is added to bills, as the business is not registered.',
  'money.explain.tax.in_gst': 'GST is worked out on each bill: CGST and SGST inside your state, IGST for another state.',
  'money.explain.tax.single': 'Tax is worked out on each line, and what you can claim back is kept apart.',
  'money.explain.tax.sales_tax': 'Sales tax is worked out on each sale.',
  'money.explain.tax.unset': 'Tax is not set yet: say whether the business is registered.',
  'money.explain.approvals.none': 'Every entry counts as soon as it is saved.',
  'money.explain.approvals.some': '{{list}} wait for approval, and count only once approved.',
  'money.explain.approvals.every': '{{record}} entries',
  'money.explain.approvals.above': '{{record}} entries above {{amount}}',
  'money.explain.list': '{{first}} and {{last}}',
  'money.explain.accounts': 'See the Accounts list',
  'money.explain.dismiss': 'Got it',
  'money.for_home_desc': 'Money you took for yourself. It is not a shop expense, so profit does not change',
  'money.add_category': 'Add “{{name}}” as a shop expense',
  'money.add_income': 'Add “{{name}}” as income',
  'money.near_duplicate': 'Looks like {{name}}. Use that instead?',
  'money.use_it': 'Use {{name}}',
  'money.find_category': 'Find a category',
  'money.find_or_add': 'Find or add a category',
  'money.choose_category': 'Choose a category',

  // An Accounts row's page (step 3.4, L15 and L32 of docs/plans/ledger.md;
  // boards S2 and S3): the balance and the entries that moved it.
  'money.kind.asset': 'Money you have',
  'money.kind.liability': 'Money you owe',
  'money.kind.equity': 'Owner’s money',
  'money.kind.income': 'Income',
  'money.kind.expense': 'Expense',
  'money.kind.to_sort': 'Waiting to be sorted',
  'money.accounts.balance': 'Balance',
  'money.accounts.balance_desc': 'Counted from every entry, so it is never typed',
  'money.accounts.in_today': 'In today',
  'money.accounts.out_today': 'Out today',
  'money.accounts.moved_by': 'Entries that moved it',
  'money.accounts.when': 'When',
  'money.accounts.entry': 'Entry',
  'money.accounts.in': 'In',
  'money.accounts.out': 'Out',
  'money.accounts.after': 'Balance after',
  'money.accounts.none': 'No entry has moved it yet.',
  'money.accounts.more': 'Older entries are not shown here. Each one is in its own record.',
  'money.accounts.hidden_entry': 'An entry you cannot open',
  'money.accounts.foot': 'Opening an entry opens it in its own record. To change this balance, change the entry, or add an adjustment with a reason.',

  // Money effect on an entry (step 3.1, L19 and L26 of docs/plans/ledger.md;
  // board S1): what one entry did to the money, never debit or credit.
  'money.effect.title': 'Money effect',
  'money.effect.owes_more': '{{party}} owes {{amount}} more.',
  'money.effect.owes_more_now': '{{party}} owes {{amount}} more. Now {{total}}.',
  'money.effect.dues_down': '{{party}}’s dues went down by {{amount}}.',
  'money.effect.dues_down_now': '{{party}}’s dues went down by {{amount}}. Now {{total}}.',
  'money.effect.you_owe_more': 'You owe {{party}} {{amount}} more.',
  'money.effect.you_owe_less': 'What you owe {{party}} went down by {{amount}}.',
  'money.effect.went_up': '{{what}} went up by {{amount}}.',
  'money.effect.went_down': '{{what}} went down by {{amount}}.',
  'money.effect.an_account': 'An account you cannot open',
  'money.effect.to_sort': '{{amount}} waits in To sort until someone picks what it was for.',
  'money.effect.gst_line': 'GST you collected went up by {{amount}}: CGST {{cgst}}, SGST {{sgst}}.',
  'money.effect.not_paid': 'Not paid yet. It will be matched to the next payment from {{party}}.',
  'money.effect.waiting': 'Nothing counts until it is approved.',
  'money.effect.corrected_by': 'Corrected by {{name}} on {{day}}. Open the correction',
  'money.effect.corrected_on': 'Corrected on {{day}}. Open the correction',
  'money.effect.corrected': 'Corrected. Open the correction',
  'money.effect.corrects': 'Corrects the entry of {{day}}. Open it',
  'money.effect.corrects_closed': 'Corrects an entry in a closed month. Open it',
  'money.effect.if.went_up': 'If approved, {{what}} goes up by {{amount}}.',
  'money.effect.if.went_down': 'If approved, {{what}} goes down by {{amount}}.',
  'money.effect.if.owes_more': 'If approved, {{party}} owes {{amount}} more.',
  'money.effect.if.dues_down': 'If approved, {{party}}’s dues go down by {{amount}}.',
  'money.effect.if.you_owe_more': 'If approved, you owe {{party}} {{amount}} more.',
  'money.effect.if.you_owe_less': 'If approved, what you owe {{party}} goes down by {{amount}}.',
  'money.effect.if.to_sort': 'If approved, {{amount}} waits in To sort until someone picks what it was for.',
  'money.effect.if.gst_line': 'If approved, GST you collected goes up by {{amount}}: CGST {{cgst}}, SGST {{sgst}}.',

  // The Dashboard's money cards (step 3.5, L25, L39 and L51 of
  // docs/plans/ledger.md; board S5): ledger figures worked out on the server.
  'money.cards.section': 'Money',
  'money.cards.profit': 'Profit, this financial year',
  'money.pinned.profit_month': 'Profit, this month',
  'money.cards.profit_desc': 'Income less expenses',
  'money.cards.to_sort_note_one': '{{count, number}} entry to sort, {{amount}}. Profit may change.',
  'money.cards.to_sort_note_other': '{{count, number}} entries to sort, {{amount}}. Profit may change.',
  'money.cards.cash_bank': 'Cash and bank',
  'money.cards.tax_to_pay': 'Tax to pay, this month',
  'money.cards.tax_desc': 'Collected less what you can claim back',
  'money.cards.owed_to_you': 'Owed to you',
  'money.cards.paid_in_advance': 'Paid in advance',
  'money.cards.never_netted': 'Counted apart, never netted',
  'money.cards.you_owe': 'You owe',
  'money.cards.to_sort': 'To sort',
  'money.cards.sort_now': 'Sort now',
  'money.cards.tax_due_open': 'Due {{day}} · open tax payment',
  'money.cards.tax_open': 'Open tax payment',
  'money.cards.customers_one': '{{count, number}} customer',
  'money.cards.customers_other': '{{count, number}} customers',
  'money.cards.suppliers_one': '{{count, number}} supplier',
  'money.cards.suppliers_other': '{{count, number}} suppliers',
  'money.cards.entries_one': '{{count, number}} entry',
  'money.cards.entries_other': '{{count, number}} entries',
  'money.cards.part': '{{name}}: {{amount}}',

  // A charge run on the Charges record (L23 of docs/plans/ledger.md, steps
  // 5.3 and 5.4): the controls and earlier runs on the run's own routine
  // page, the Auto badge on what a run wrote, and the Dashboard's reminder
  // the day before. Never "posting", "ledger" or
  // "Jobs": Jobs are roles.
  'charge_run.now': 'Run now',
  'charge_run.now_period': 'Bill {{period}} now',
  'charge_run.skip': 'Skip {{period}}',
  'charge_run.skip_period': 'Skip this period',
  'charge_run.skip_title': 'Skip {{period}}?',
  'charge_run.skip_body': 'The run charges nobody for {{period}}. A charge you add by hand still counts.',
  'charge_run.skipped': '{{period}} skipped',
  'charge_run.ran_one': '{{count, number}} charge written',
  'charge_run.ran_other': '{{count, number}} charges written',
  'charge_run.failed_toast': 'The run for {{period}} could not write its charges. It tries again within the hour.',
  'charge_run.history': 'Earlier runs',
  'charge_run.col.run': 'Run',
  'charge_run.col.parties': 'Charged',
  'charge_run.col.total': 'Total',
  'charge_run.col.status': 'Status',
  'charge_run.col.note': 'Note',
  'charge_run.status.pending': 'Running',
  'charge_run.status.done': 'Done',
  'charge_run.status.failed': 'Failed',
  'charge_run.status.skipped': 'Skipped',
  'charge_run.changed_one': '{{count, number}} charge changed by hand',
  'charge_run.changed_other': '{{count, number}} charges changed by hand',
  'charge_run.badge_tip': 'Written by the run',
  'charge_run.changed_by_hand': 'Changed by hand',
  'charge_run.reminder_one': 'Tomorrow, {{count, number}} {{noun}} is charged {{amount}} in {{record}}.',
  'charge_run.reminder_other': 'Tomorrow, {{count, number}} {{noun}} are charged {{amount}} in {{record}}.',
  'charge_run.reminder_open': 'Open {{record}}',
  'charge_run.reminder_label': 'Runs due tomorrow',

  // Workspace details (web-redesign-03 phase 7.1): the Details tab, one save
  // in the head, the plan for the primary admin, and the danger card.
  'workspace_page.tab.details': 'Details',
  'workspace_page.the_workspace.line': 'What everyone in it sees, and what is printed on bills.',
  'workspace_page.the_workspace.line_read':
    'What everyone in it sees. An admin or the primary admin changes these.',
  'workspace_page.mark': 'Mark',
  'workspace_page.mark.help': 'On the sidebar, receipts and shared pages',
  'workspace_page.mark.upload': 'Upload a logo',
  'workspace_page.name.empty': 'A $t(glossary.business_one) needs a name. Escape puts the old one back.',
  // The phone's Edit details: no Escape key there to put the old name back.
  'workspace_page.name.required': 'A $t(glossary.business_one) needs a name',
  'workspace_page.currency.help':'Every amount is kept in this currency.',
  'workspace_page.address': 'Address',
  'workspace_page.address.help': 'Printed on bills and shared pages',
  'workspace_page.branches.empty': 'No branches yet',
  'workspace_page.branches.empty_line': 'One shop is enough. Add a branch when a second site, till or van starts.',
  'workspace_page.branches.add': 'Add a branch',
  'workspace_page.branches.later': 'Branches cannot be added yet.',
  'workspace_page.setup': 'Setup',
  'workspace_page.setup.help': 'What OS set up from your answers',
  'workspace_page.setup.edit': 'Edit the setup',
  'workspace_page.setup.later': 'Editing the setup is not open yet.',
  'workspace_page.plan.line': 'Only a primary admin can see the plan, change it, or pay for it.',
  'workspace_page.plan.days_left_one': '{{count, number}} day left',
  'workspace_page.plan.days_left_other': '{{count, number}} days left',
  'workspace_page.plan.ends': 'Ends {{day}}. Nothing is lost when it ends; you choose a plan to keep adding.',
  'workspace_page.danger.leave_or_delete': 'Leave or delete',
  'workspace_page.danger.leave': 'Leave',
  'workspace_page.leave.line': 'Every $t(glossary.entry_one) you wrote stays where it is, with your name on it.',
  'workspace_page.leave.primary': 'A $t(glossary.business_one) must always have one primary admin.',
  'workspace_page.leave.primary_fix': 'Make somebody else the primary admin first.',
  'workspace_page.leave.choose': 'Choose a new primary admin',
  'workspace_page.leave.armed':
    'Every $t(glossary.entry_one) you wrote stays where it is, with your name on it. You lose the key, not the work.',
  'workspace_page.left': 'You left {{workspace}}.',
  // The Settings tab read the workspace again on return and the read did not
  // land: the page keeps what it showed, with this line over it.
  'workspace_page.stale.offline': 'No connection, so this may be out of date',
  'workspace_page.stale.failed': 'Could not check for changes, so this may be out of date',
  'workspace_page.delete': 'Delete this $t(glossary.business_one)',
  'workspace_page.delete.button': 'Delete $t(glossary.business_one)',
  'workspace_page.delete.line_one':
    'Every $t(glossary.record_one), $t(glossary.list_one) and $t(glossary.entry_one) goes, for the {{count, number}} $t(glossary.person_one) in it. Only a primary admin can do this.',
  'workspace_page.delete.line_other':
    'Every $t(glossary.record_one), $t(glossary.list_one) and $t(glossary.entry_one) goes, for all {{count, number}} $t(glossary.person_other). Only a primary admin can do this.',
  'workspace_page.delete.title': 'Delete {{workspace}}?',
  'workspace_page.delete.description': 'This cannot be undone.',
  'workspace_page.delete.entries_one': '{{count, number}} $t(glossary.entry_one)',
  'workspace_page.delete.entries_other': '{{count, number}} $t(glossary.entry_other)',
  'workspace_page.delete.facts': 'Everything OS knows about it',
  'workspace_page.delete.stays': 'Nothing in it is kept',
  'workspace_page.delete.stays_body':
    'Download what you need first. Your account stays, and so do your other $t(glossary.business_other).',
  'workspace_page.delete.others_one':
    '{{count, number}} other $t(glossary.person_one) is still in it. Remove them from Team first.',
  'workspace_page.delete.others_other':
    '{{count, number}} other $t(glossary.person_other) are still in it. Remove them from Team first.',
  'workspace_page.delete.acknowledge': 'Delete {{workspace}} and everything in it for good',
  'workspace_page.deleted': '{{workspace}} is deleted.',
  'workspace_page.delete.issued_link_one': 'Open the {{count, number}} issued in {{record}}',
  'workspace_page.delete.issued_link_other': 'Open the {{count, number}} issued in {{record}}',
  'workspace_page.delete.issued_one':
    'You have {{count, number}} issued invoice. Download it before you delete; it is deleted with the $t(glossary.business_one).',
  'workspace_page.delete.issued_other':
    'You have {{count, number}} issued invoices. Download them before you delete; they are deleted with the $t(glossary.business_one).',

  // What OS knows, the second tab of Workspace details (web-redesign-03 phase 7.2).
  'os_knows.search': 'Search what OS knows',
  'os_knows.about': 'About',
  'os_knows.source': 'Source',
  'os_knows.standing': 'Standing',
  'os_knows.column.fact': 'Fact',
  'os_knows.column.added': 'Added',
  'os_knows.source.chat': 'Told in chat',
  'os_knows.source.screen': 'Added here',
  'os_knows.source.setup': 'From setup',
  'os_knows.standing_count_one': '{{count, number}} standing fact',
  'os_knows.standing_count_other': '{{count, number}} standing facts',
  'os_knows.retired_count_one': '{{count, number}} retired',
  'os_knows.retired_count_other': '{{count, number}} retired',
  'os_knows.retired_hint': 'A retired sentence is kept to read. OS no longer uses it, and it cannot be changed.',
  'os_knows.no_match': 'Nothing kept matches that.',
  'os_knows.no_retired': 'Nothing is retired yet.',
  'os_knows.empty.title': 'Nothing kept yet',
  'os_knows.empty.line': 'What you tell OS about how your $t(glossary.business_one) works is kept here.',
  'os_knows.empty.placeholder': 'Rent of ₹18,000 is paid on the 5th',
  'os_knows.empty.hint': 'Or just tell OS in a chat or a call. It asks before it keeps anything.',
  'os_knows.edit_row': 'Edit: {{fact}}',
  'os_knows.forget_row': 'Forget: {{fact}}',
  'os_knows.forgotten': 'Forgotten. It is kept under Retired.',
  'os_knows.edit.line': '{{about}} · {{source}} · {{day}}',
  'os_knows.offline': 'What OS knows is never kept in this browser. It is read fresh every time.',
  'os_knows.source.file': 'From a file',

  // What OS knows › Facts and Files (setup-from-files Decided 20; boards knows-files, knows-file-open, knows-file-remove).
  'knows.half.label': 'What OS knows',
  'knows.half.facts': 'Facts',
  'knows.half.files': 'Files',
  'knows.files.search': 'Search your files',
  'knows.files.only_admins': 'Only admins see these files',
  'knows.files.why':
    'OS keeps every file you give it, to read again when you ask about your work. Each file says what OS took from it and what came into your records.',
  'knows.files.col.file': 'File',
  'knows.files.col.took': 'What OS took from it',
  'knows.files.col.came': 'What came in',
  'knows.files.col.added': 'Added in',
  'knows.files.from_setup': 'From setup',
  'knows.files.later': 'Added later',
  'knows.files.foot_one': '{{count, number}} file · {{size}} · kept in this $t(glossary.business_one)',
  'knows.files.foot_other': '{{count, number}} files · {{size}} · kept in this $t(glossary.business_one)',
  'knows.files.open': 'Open',
  'knows.files.open_row': 'Open {{name}}',
  'knows.files.more': 'More for {{name}}',
  'knows.files.took_none': 'Nothing new',
  'knows.files.took_more_one': '{{first}}, and {{count, number}} more',
  'knows.files.took_more_other': '{{first}}, and {{count, number}} more',
  'knows.files.came_none': 'Nothing brought in',
  'knows.files.came_in_one': '{{count, number}} $t(glossary.entry_one) in {{record}}',
  'knows.files.came_in_other': '{{count, number}} $t(glossary.entry_other) in {{record}}',
  'knows.files.came_across_one': '{{count, number}} $t(glossary.entry_one) across {{records, number}} $t(glossary.record_other)',
  'knows.files.came_across_other': '{{count, number}} $t(glossary.entry_other) across {{records, number}} $t(glossary.record_other)',
  'knows.files.aside_one': '{{count, number}} set aside',
  'knows.files.aside_other': '{{count, number}} set aside',
  'knows.files.origin.setup': 'Setup',
  'knows.files.origin.chat': 'A chat with OS',
  'knows.files.origin.call': 'A call with OS',
  'knows.files.origin.record': 'A $t(glossary.record_one)',
  'knows.files.origin.list': 'A $t(glossary.list_one)',
  'knows.files.added_at': '{{place}} · {{day}}',
  'knows.files.no_match': 'No file matches that.',
  'knows.files.offline': 'Your files are never kept in this browser. They are read fresh every time.',
  'knows.files.empty.title': 'No files yet',
  'knows.files.empty.line': 'Files you give OS, in setup or in a chat, are kept here with what came from each.',
  'knows.file.kind.sheet': 'Excel',
  'knows.file.kind.csv': 'CSV',
  'knows.file.kind.tally': 'Tally export',
  'knows.file.kind.pdf': 'PDF',
  'knows.file.kind.image': 'Photo',
  'knows.file.kind.website': 'Website',
  'knows.file.meta': '{{kind}} · {{size}}',
  'knows.file.added': 'added in {{place}}, {{day}}',
  'knows.file.took': 'What OS took from it',
  'knows.file.see_facts': 'See these in Facts',
  'knows.file.came': 'What came in from it',
  'knows.file.came_entries_one': '{{count, number}} $t(glossary.entry_one)',
  'knows.file.came_entries_other': '{{count, number}} $t(glossary.entry_other)',
  'knows.file.open_record': 'Open {{record}}',
  'knows.file.aside': 'Set aside for you',
  'knows.file.aside_rows_one': '{{count, number}} row',
  'knows.file.aside_rows_other': '{{count, number}} rows',
  'knows.file.inside': 'Inside the file',
  'knows.file.sheets': 'Sheets',
  'knows.file.rows': 'Rows',
  'knows.file.cols': 'Columns',
  'knows.file.left_out': 'Left out',
  'knows.file.pages': 'Pages',
  'knows.file.remove': 'Remove',
  'knows.file.download': 'Download',
  'knows.remove.title': 'Remove {{name}}?',
  'knows.remove.desc': 'OS deletes the file and forgets what it read only in it. Choose what happens to what came into your records from it.',
  'knows.remove.desc_none': 'OS deletes the file and forgets what it read only in it. Nothing came into your $t(glossary.record_other) from it.',
  'knows.remove.keep_one': 'Keep the {{count, number}} $t(glossary.entry_one) that came in',
  'knows.remove.keep_other': 'Keep the {{count, number}} $t(glossary.entry_other) that came in',
  'knows.remove.keep_desc': 'They stay in your records. Each one still says which file it came from.',
  'knows.remove.take': 'Take them out too',
  'knows.remove.take_desc': 'The same as Undo for this file. Entries someone changed since are kept.',
  'knows.remove.cancel': 'Keep the file',
  'knows.remove.go': 'Remove the file',
  'knows.remove.done': '{{name}} is removed.',

  // Your website, beside your files (setup-from-files Phase 9, Decided 23 to 31; boards site-*).
  'site.skip_note': 'Files and a website can both be added later.',
  'site.err.not_site': 'That does not look like a website address.',
  'site.kept_fact': 'Kept as a fact',
  'site.kept.phone': 'Shop phone',
  'site.kept.handle': 'Social page',
  'site.kept.lead': 'I do not look up phone numbers or read social pages. I kept these as facts about your shop.',
  'site.kept.ask': 'Tell me what you sell most, or share a price list, and I set it up from that.',
  'site.kept.said_phone': 'The shop’s phone is {{text}}.',
  'site.kept.said_handle': 'The shop is on social media as {{text}}.',
  'site.tag': 'Website',
  'site.tag.found': 'Found in your files',
  'site.tag.market': 'Marketplace page',
  'site.from': 'From your website: {{name}}',
  'site.checking': 'Checking the address…',
  'site.reading_one': 'Reading {{done, number}} of {{count, number}} page',
  'site.reading_other': 'Reading {{done, number}} of {{count, number}} pages',
  'site.stop': 'Stop reading the website',
  'site.remove': 'Remove the website',
  'site.read_again': 'Read again',
  'site.badge': '{{files}} · 1 website',
  'site.badge_only': '1 website',
  'site.pages_one': '{{count, number}} page',
  'site.pages_other': '{{count, number}} pages',
  'site.read.found': '{{pages}}: {{what}}',
  'site.read.lead_one': 'I read {{count, number}} page of your website. Here is what it gave me.',
  'site.read.lead_other': 'I read {{count, number}} pages of your website. Here is what the main ones gave me.',
  'site.reading_lead': 'Reading your website, the same way as your files. Here is what I have opened so far.',
  'site.found.items_priced_one': '{{count, number}} item with a price',
  'site.found.items_priced_other': '{{count, number}} items with prices',
  'site.found.items_one': '{{count, number}} item',
  'site.found.items_other': '{{count, number}} items',
  'site.found.address': 'address',
  'site.found.hours': 'hours',
  'site.found.phone': 'phone',
  'site.found.name': 'your name',
  'site.found.nothing': 'nothing to use',
  'site.found.line': 'On {{file}} · checked · {{pages}} read',
  'site.thin.found': '{{pages}}: only {{what}}',
  'site.thin.none': 'Almost no text on it',
  'site.err.none': 'No site answers at this address',
  'site.err.robots': 'Your site asks readers not to read it, so I did not',
  'site.err.not_html': 'That address is a file, not a web page',
  'site.err.too_big': 'Its pages are too big to read',
  'site.err.failed': 'Not read. Try again.',
  'site.market.found': '1 public page: {{what}}',
  'site.market.use.items_prices': 'The items and the prices',
  'site.market.use.items': 'The items, not the prices',
  'site.market.use.leave': 'Leave it out',
  'site.market.said': 'From {{address}}: {{choice}}',
  'site.step.checking': 'Checking {{host}}',
  'site.step.opened': 'Opened {{host}}',
  'site.step.picked_one': 'Picked {{count, number}} page on your own site that says what you do',
  'site.step.picked_other': 'Picked {{count, number}} pages on your own site that say what you do',
  'site.step.read_page': 'Read {{page}}: {{gave}}',
  'site.step.reading_page': 'Reading {{page}}',
  'site.step.reading': 'Reading {{host}}',
  'site.step.read': 'Read {{host}}: {{found}}',
  'site.step.failed': 'Could not read {{host}}: {{why}}',
  'site.step.thin': 'Found {{what}}: no items, prices or hours',
  'site.conflict.file.sales': 'your sales register',
  'site.conflict.file.purchases': 'your purchase bills',
  'site.conflict.file.expenses': 'your expenses',
  'site.conflict.file.stock': 'your stock sheet',
  'site.conflict.file.staff': 'your staff sheet',
  'site.conflict.file.contacts': 'your customers and suppliers',
  'site.conflict.file.statement': 'your bank statement',
  'site.conflict.file.accounts': 'your accounts export',
  'site.conflict.file.paper': 'your business papers',
  'site.err.gone': 'This link no longer opens',
  'site.err.not_business': 'This page is not about a business',
  'site.err.parked': 'An empty page, for sale',
  'site.err.private': 'Only its owner can open it. Share it, then read it again.',
  'site.err.sign_in': 'It opens a sign-in page, so nothing on it was read.',
  'knows.site.group': 'Websites',
  'knows.site.kind': 'Website · {{pages}}, read {{day}}',
  'knows.site.kind_unread': 'Website · not read yet',
  'knows.site.files_one': '{{count, number}} file',
  'knows.site.files_other': '{{count, number}} files',
  'knows.site.sites_one': '{{count, number}} website',
  'knows.site.sites_other': '{{count, number}} websites',
  'knows.site.foot': '{{files}} and {{sites}} · kept in this $t(glossary.business_one)',
  'knows.site.added': 'Website · {{place}}, read {{day}}',
  'knows.site.read_now': 'Website · read again just now',
  'knows.site.took.address': 'Address: {{text}}',
  'knows.site.took.phone': 'Phone: {{text}}',
  'knows.site.took.gstin': 'GSTIN: {{text}}',
  'knows.site.pages': 'Pages read',
  'knows.site.read': 'Read',
  'knows.site.left': 'Left out',
  'knows.site.came': 'What came in',
  'knows.site.read_list': '{{read, number}} of {{seen, number}}: {{titles}}',
  'knows.site.left_none': 'Nothing',
  'knows.site.left_line': '{{page}}, {{why}}',
  'knows.site.left_more_one': '{{pages}}; and {{count, number}} more',
  'knows.site.left_more_other': '{{pages}}; and {{count, number}} more',
  'knows.site.why.robots': 'the site asks not to be read',
  'knows.site.why.other_site': 'on another site',
  'knows.site.why.form': 'a form',
  'knows.site.why.limit': 'past the 30 pages read',
  'knows.site.again_note': 'I read your website again only when you ask. What I read is text on public pages: never a form or a login.',
  'knows.site.remove': 'Remove',
  'knows.site.open_site': 'Open the site',
  'knows.site.read_again': 'Read again',
  'knows.site.changed_one': '{{count, number}} thing changed since {{day}}',
  'knows.site.changed_other': '{{count, number}} things changed since {{day}}',
  'knows.site.changed_none': 'Nothing changed since {{day}}',
  'knows.site.changed_desc': 'Nothing changes in your records until you choose.',
  'knows.site.change_about': 'What changed',
  'knows.site.was': 'Was',
  'knows.site.now': 'Now',
  'knows.site.none': 'None',
  'knows.site.use': 'Use these changes',
  'knows.site.keep': 'Keep what I have',
  'knows.site.used': 'The changes from {{name}} are in use.',
  'knows.site.kept': 'Kept what you have. Nothing changed.',
  'knows.file.kind.docx': 'Word document',
  'knows.file.kind.json': 'JSON export',
  'knows.file.kind.page': 'Web page',
  'knows.file.kind.place': 'Map place',
  'knows.file.kind.pptx': 'PowerPoint',
  'knows.file.kind.text': 'Text',
  'knows.file.kind.xls': 'Excel 97–2003',
  'knows.file.kind.xml': 'XML export',
  'os.site.card': 'Read {{address}}',
  'os.site.read': 'Read {{address}} · {{pages}}',
  'os.site.changes_one': '{{count, number}} thing changed since it was last read. Nothing changes until you choose.',
  'os.site.changes_other': '{{count, number}} things changed since it was last read. Nothing changes until you choose.',
  'os.site.no_changes': 'Nothing changed since it was last read.',

  // Asks, and handing over the primary admin (web-redesign-03 phase 7.3).
  'primary_admin.asked_banner': '{{person}} asked to become the primary admin.',
  'primary_admin.hand': 'Hand the $t(glossary.business_one) to {{person}}',
  'primary_admin.hand.line':
    'They become the owner: the plan and billing, deleting the $t(glossary.business_one), and handing it over. There is one primary admin at a time, so you pick a role for yourself first.',
  'primary_admin.ask': 'Ask to be the primary admin',
  'primary_admin.ask.line':
    'The primary admin owns the $t(glossary.business_one): the plan and billing, deleting it, and handing it over. Asking puts your name in front of {{primary}}, who can hand it to you.',
  'primary_admin.asked': 'Asked. {{primary}} will see it on Team.',
  'primary_admin.asked_any': 'Asked. The primary admin will see it on Team.',
  'primary_admin.ask.line_any':
    'The primary admin owns the $t(glossary.business_one): the plan and billing, deleting it, and handing it over. Asking puts your name in front of them, and they can hand it to you.',
  'primary_admin.waiting': 'Offered to {{person}}. It moves to them once they accept.',
  'primary_admin.take_back': 'Take the offer back',
  'primary_admin.taken_back': 'The offer to {{person}} is taken back.',
  'primary_admin.offer_banner': '{{person}} offered you the $t(glossary.business_one): you would become its primary admin.',
  'primary_admin.accept': 'Become the primary admin',
  'primary_admin.decline': 'Put it away',
  'primary_admin.accepted': 'You are the primary admin now. {{person}} has the {{role}} role.',
  'primary_admin.offered': 'Sent to {{person}}. The $t(glossary.business_one) moves to them once they accept.',
  'primary_admin.dialog.title': 'Hand {{workspace}} to {{person}}?',
  'primary_admin.dialog.line':
    'There is one primary admin at a time. Once they accept, the $t(glossary.business_one) is theirs.',
  'primary_admin.dialog.gets': '{{person}} gets',
  'primary_admin.dialog.gets_line':
    'The plan and billing, deleting the $t(glossary.business_one), and handing it over again',
  'primary_admin.dialog.keep': 'You keep',
  'primary_admin.dialog.role': 'Your role, from then on',
  'primary_admin.dialog.role_help.admin':
    'Admin lets you do everything else, as now. Your $t(glossary.entry_other) keep your name.',
  'primary_admin.dialog.role_help.any': 'You can do what {{role}} allows. Your $t(glossary.entry_other) keep your name.',
  'primary_admin.dialog.confirm': 'Type the $t(glossary.business_one) name to confirm',
  'primary_admin.dialog.submit': 'Hand it over',
  'team_asks.right_permission': '{{person}} asked for: {{right}}.',
  'team_asks.right_record.see': '{{person}} asked to see {{record}}.',
  'team_asks.right_record.change': '{{person}} asked to change {{record}}.',
  'team_asks.give': 'Give it',
  'team_asks.given': 'Given to {{person}}.',
  'team_asks.put_away': 'Put it away',
  'team_asks.put_away_done': 'The ask is put away.',
  'team_asks.label': 'Open asks',
  /* An entry the server saved over a rule that holds only on the form (F14 of docs/plans/field-relations.md). */
  'team_asks.held': '{{person}} saved an $t(glossary.entry_one) in {{record}} that breaks a rule: {{message}}',
  'team_asks.open_entry': 'Open the $t(glossary.entry_one)',
  // web: the business and the team
  /* The rule's builder panel (canvas 10-rule): the rows the phone draws under its sentence. */
  'rule.web.panel': 'Rule settings',
  'rule.web.position_one': 'Rule {{position, number}} of {{count, number}}',
  'rule.web.position_other': 'Rule {{position, number}} of {{count, number}}',
  'rule.web.held_one': '{{count, number}} of {{max, number}}',
  'rule.web.held_other': '{{count, number}} of {{max, number}}',
  'rule.web.change_where': 'Change which $t(glossary.record_other) and $t(glossary.list_other)',
  /* The management pages are read fresh every time, in a browser as on the phone. */
  'team.offline.body.web':
    'Who is in the $t(glossary.business_one), and what each $t(glossary.person_one) may do, is never kept in this browser. It is read fresh every time, so nobody is shown an old answer about who can open what.',
  'business.web.level_and_job': 'Level and job',
  'business.logo.change': 'Change logo',
  'business.logo.formats': 'Use a JPEG, PNG or WebP image of up to {{size, number}} MB.',
  'business.logo.remove': 'Remove logo',
  'business.logo.too_big': 'That image is too large. Choose one of {{size, number}} MB or less.',
  'business.logo.unreadable': 'That image could not be read. Choose a JPEG, PNG or WebP image.',
  'business.logo.wrong_type': 'That file is not an image the logo can use. Choose a JPEG, PNG or WebP image.',
  'team.web.open_invites_one': '{{count, number}} open invitation',
  'team.web.open_invites_other': '{{count, number}} open invitations',
  'team.web.export': 'Export CSV',
  'team.web.search': 'Search $t(glossary.person_other)',
  'team.web.every_level': 'Every $t(glossary.level_one)',
  'team.web.every_job': 'Every $t(glossary.job_one)',
  'team.web.updated': 'Updated {{clock}}',
  'team.web.can_open.everything': 'Everything',
  'team.web.column.person': 'Person',
  'team.web.column.can_open': 'Can open',
  'team.web.column.joined': 'Joined',
  'team.web.column.actions': 'Actions',
  'team.web.column.note': 'Note',
  'team.web.select_all': 'Select every $t(glossary.person_one)',
  'team.web.select_self': 'You cannot select yourself',
  'team.web.select': 'Select {{person}}',
  'team.web.invites_band_one': 'Open invitation · {{count, number}}',
  'team.web.invites_band_other': 'Open invitations · {{count, number}}',
  'team.web.invite_open': 'Open the invitation {{code}}',
  'team.web.expires': 'Expires {{day}}',
  'team.web.joined_on': 'Joined {{day}}',
  'team.web.bulk.label': 'Act on the chosen $t(glossary.person_other)',
  'team.web.bulk.count_one': '{{count, number}} selected',
  'team.web.bulk.count_other': '{{count, number}} selected',
  'team.web.bulk.make_viewers': 'Make them a Viewer',
  'team.web.bulk.clear': 'Clear the selection',
  'team.web.bulk.refused': 'Not changed for {{person}}: {{reason}}',
  'team.web.bulk.removed_one': '{{count, number}} $t(glossary.person_one) removed',
  'team.web.bulk.removed_other': '{{count, number}} $t(glossary.person_other) removed',
  'team.web.bulk.viewers_one': '{{count, number}} $t(glossary.person_one) is a Viewer now',
  'team.web.bulk.viewers_other': '{{count, number}} $t(glossary.person_other) are Viewers now',
  'team.web.bulk.remove_title_one': 'Remove {{count, number}} $t(glossary.person_one)?',
  'team.web.bulk.remove_title_other': 'Remove {{count, number}} $t(glossary.person_other)?',
  'invite.web.lede': 'Pick what they will be. You get a code to hand them yourself.',
  /* The browser can hand over a link as well as the code, and it opens the join page with the code in it. */
  'invite.code_body.web':
    'They enter it once, on their phone or at the link below, and they are in. It works for seven days and once only.',
  'invite.web.link': 'Or send this link',
  'invite.web.copy_link': 'Copy the link',
  'invite.web.works_until': 'Works until',
  'invite.web.works_until_value': '{{day}}, once only',
  'invite.web.opened_to_you_one': '{{count, number}} $t(glossary.record_one) open to you',
  'invite.web.opened_to_you_other': '{{count, number}} $t(glossary.record_other) open to you',
  // An invited person's first arrival: the invitation before they sign in, the
  // join, the welcome, and the ways a link stops working. invite-onboarding.
  'invite.landing.title': 'Join {{workspace}}',
  'invite.landing.lede': '{{inviter}} invited you. Make your account and you are in.',
  'invite.landing.lede_signed_in': '{{inviter}} invited you. You are in as soon as you join.',
  'invite.landing.card_line': '{{inviter}} invited you as {{role}}',
  'invite.landing.opens_one': 'Opens {{count, number}} $t(glossary.record_one) to you',
  'invite.landing.opens_other': 'Opens {{count, number}} $t(glossary.record_other) to you',
  'invite.landing.for': 'For {{email}} · works until {{day}}',
  'invite.create.email_fixed': 'The invitation is for this address.',
  'invite.joining': 'Joining {{workspace}}…',
  'invite.joined.title': 'You are in {{workspace}}',
  'invite.joined.lede': '{{inviter}} opened these $t(glossary.record_other) to you, as {{role}}.',
  'invite.joined.lede_none': '{{inviter}} added you as {{role}}.',
  'invite.joined.got_it': 'Got it',
  'invite.joined.not_open': 'You joined {{workspace}}, but it did not open here. Try again.',
  'invite.wrong_email.title': 'This invitation is for {{email}}',
  'invite.wrong_email.body': 'You are signed in as {{current}}. Sign in with the invited address to join, or ask {{inviter}} to invite this one.',
  'invite.wrong_email.switch': 'Use {{email}} instead',
  'invite.wrong_email.stay': 'Stay signed in as {{current}}',
  'invite.unusable.title': 'This link no longer works',
  'invite.unusable.own': 'Make my own $t(glossary.business_one)',
  'invite.unusable.code': 'I have a code',
  'invite.app.title': 'Open it in the app',
  'invite.app.lede': 'RecordOS works best on your phone. Get the app, then tap the link in your email again.',
  'invite.app.store_ios': 'Get it on the App Store',
  'invite.app.store_android': 'Get it on Google Play',
  'invite.app.browser': 'Continue in the browser',
  'invite.first_launch.row': 'Invited to a $t(glossary.business_one)?',
  'invite.first_launch.enter_code': 'Enter the code',
  'job.web.covers': 'Its rules cover',
  'job.web.covers_nothing': 'Nothing yet',
  'job.web.nobody_holds': 'Nobody holds this $t(glossary.job_one) yet',
  'job.web.open': 'Open {{job}}',
  'job.web.no_rules': 'No rules yet. Add one to open a $t(glossary.record_one) to this $t(glossary.job_one).',
  'rule.web.back_to': 'Back to {{job}}',
  'rule.web.lede_one': '{{job}} carries {{count, number}} rule, said here as one sentence. Every piece in a box is a choice.',
  'rule.web.lede_other':
    'One of the {{count, number}} rules {{job}} carries, said as one sentence. Every piece in a box is a choice.',
  'rule.web.nothing_saves': 'Nothing here saves on its own. Save {{job}} to write it, and you see who it reaches first.',
  'field_rule.web.edit': 'Edit field rules',
  'field_rule.web.field': 'Field',
  'field_rule.web.type': 'Type',
  'field_rule.web.field_in': 'Field in {{record}}',
  'field_rule.web.for_job': 'For the {{job}}',
  'field_rule.web.what_it_does': 'What it does',
  'field_rule.web.what_it_does_to_them': 'What it does to them',
  'field_rule.web.open_to_them': 'Open to them',
  'field_rule.web.form_needs_it': 'the form needs it',
  'field_rule.web.state_of': 'The rule for {{field}}',
  'field_rule.web.for_everyone': 'for everyone who holds {{job}}',
  'field_rule.web.rest_open': 'Every other $t(glossary.field_one) in {{record}} is open to them.',
  'facts.web.add': 'Add a fact',
  'facts.web.add_label': 'What OS should know',
  'facts.web.add_submit': 'Add',
  'facts.web.about': 'What it is about',
  'facts.web.similar': 'OS already knows something nearly the same',
  'facts.web.similar_body': 'Edit that one instead, or add this one anyway.',
  'facts.web.add_anyway': 'Add it anyway',
  'facts.web.menu': 'Edit or forget: {{fact}}',
  'facts.web.none_active': 'Nothing stands right now. Every sentence here was retired.',
  // A business turn the server tries again, said on the turn's working row,
  // and the line when it gives up (Decided 8 of docs/plans/files-paging-and-fixes.md).
  'agent.retry.attempt': 'Trying again, attempt {{attempt, number}} of {{of, number}}',
  'agent.retry.gave_up': 'OS could not answer. Try again',
  // web: coach
  // The Coach page's threads panel, and the dock beside a record's pages.
  'agent.web.threads_label': 'OS conversations',
  'agent.web.start_from_record': 'Or start from one of your $t(glossary.record_other)',
  'agent.web.open_full': 'Open OS full page',
  'agent.web.close': 'Close OS',
  // The floating button at the foot of the page's box: its name is agent.open.
  'agent.web.fab.offline': 'OS needs the connection',
  'agent.web.fab.working_label': 'Working. Open the conversation',
  'agent.web.fab.call': 'On a call',
  'agent.web.fab.call_label': 'On a call, {{time}}. Open the call',
  // The page when the door is not drawn: a server with no model, or View as.
  'agent.web.off_title': 'I’m off here',
  'agent.web.off_body': 'This server has no model for me, so there is nothing to ask yet.',
  'agent.web.off_view_as': 'While you view the app as somebody else, I stay off.',
  'agent.web.off_no_role': 'Your role does not let you ask me. Ask an admin of this $t(glossary.business_one).',
  // web: voice
  // the browser's microphone: the phone's "in Settings" is the site's setting in
  // the browser here, and a desktop can have no microphone at all
  'agent.web.voice_mic_off_body': 'Allow the microphone for this site in your browser to talk to me, or type instead.',
  'agent.web.dictation_mic_off': 'Allow the microphone for this site in your browser to dictate.',
  'agent.web.no_microphone_title': 'No microphone found',
  'agent.web.no_microphone_body': 'Connect a microphone to talk to me, or type instead.',
  'agent.web.dictation_no_microphone': 'Connect a microphone to dictate.',
  'agent.web.microphone_failed': 'The microphone could not start. Try again, or type instead.',
  // the first-run tip over the live pill: the phone's "Tap to mute" names a tap, which the web never says
  'agent.web.mute_hint': 'Mute pauses the microphone',
  // a page that cannot reach a microphone at all (no getUserMedia: an insecure page, an old browser)
  'agent.web.mic_unavailable_title': 'Microphone not available',
  'agent.web.mic_unsupported_body': 'This browser can’t use a microphone on this page. Type instead, and I answer here.',
  // the browser's own way to allow the microphone again, the first of the blocked note's steps
  'agent.web.mic_allow.chromium': 'Click the icon left of the address and turn Microphone on.',
  'agent.web.mic_allow.safari': 'In the Safari menu, open the settings for this website and allow the microphone.',
  'agent.web.mic_allow.firefox': 'Click the crossed-out microphone in the address bar and clear the block.',
  // web: the OS composer (contract section 7): the box, its chips, dictation written live in it
  'agent.web.composer.follow_up': 'Ask a follow-up',
  'agent.web.composer.about': 'Asking about',
  'agent.web.composer.this_record': 'This record',
  'agent.web.composer.remove_about': 'Remove {{name}}',
  'agent.web.composer.dictate': 'Dictate: say it, then check the words',
  'agent.web.composer.mic_off': 'The microphone is off',
  'agent.web.composer.cancel_dictation': 'Stop dictating, keep nothing',
  'agent.web.composer.finish_dictation': 'Finish recording',
  'agent.web.composer.nothing_heard': 'Nothing was heard.',
  'agent.web.composer.nothing_heard_body': 'Click the mic and speak again, or type it.',
  'agent.web.composer.dictate_again': 'Dictate again',
  // after agent.web.fab.offline, under the box while there is no connection
  'agent.web.composer.offline_saves': 'Your entries still save on this device.',
  // web: where OS sits (web-redesign-08-os.md, Decided 4 to 6): the Layout button and its
  // menu, the floating panel's head and its folded pill, and Past conversations inside them
  'agent.web.about': 'About {{place}}',
  'agent.web.layout.label': 'Layout',
  // OS changed the list in front of the person (os-live-mode.md 3.1): the note over it, and the way back.
  'agent.view.set': 'OS set this view: {{what}}',
  'agent.view.back': 'Back to your view',
  'agent.web.layout.float': 'Floating',
  'agent.web.layout.side': 'Side sheet',
  'agent.web.layout.page': 'Full page',
  'agent.web.layout.side_none': 'Only beside a record, a list, your team or an insight',
  'agent.web.float.collapse': 'Collapse',
  'agent.web.float.expand': 'Expand',
  'agent.web.float.move': 'Move to another corner: drag, or use the arrow keys',
  'agent.web.float.answered': 'The answer is ready',
  'agent.web.float.answered_open': 'Open',
  'agent.web.history.search': 'Search conversations',
  'agent.web.history.count_one': '{{count, number}} conversation',
  'agent.web.history.count_other': '{{count, number}} conversations',
  'agent.web.history.no_match': 'No conversation title has “{{words}}” in it.',
  // the OS page's list: one line per conversation, and its More (web-redesign-08-os.md, Decided 7)
  'agent.web.threads.level.record': 'Record',
  'agent.web.threads.level.list': 'List',
  'agent.web.threads.level.settings': 'Workspace settings',
  'agent.web.threads.level.workspace': 'Workspace',
  'agent.web.threads.where': '{{level}} · {{place}}',
  'agent.web.threads.row_label': '{{title}}, in {{place}}, {{time}}',
  'agent.web.threads.more': 'More for {{title}}',
  'agent.web.threads.rename': 'Rename',
  'agent.web.threads.rename_label': 'Rename the conversation',
  'agent.web.threads.go_to': 'Go to {{place}}',
  'agent.web.threads.delete': 'Delete',
  'agent.web.threads.delete_title': 'Delete this conversation?',
  'agent.web.threads.delete_body': '“{{title}}” and everything said in it are deleted for good. What it saved in your $t(glossary.record_other) stays.',
  'agent.web.threads.rename_failed': 'The conversation could not be renamed. Try again.',
  'agent.web.threads.delete_failed': 'The conversation could not be deleted. Try again.',
  // the row under a finished reply on the web: rate, copy, read aloud, report
  'agent.web.reply.actions': 'Rate or share this answer',
  'agent.web.reply.copy': 'Copy the answer',
  'agent.web.reply.copied': 'Copied',
  'agent.web.reply.read_aloud': 'Read it aloud',
  'agent.web.reply.stop_reading': 'Stop reading',
  'agent.web.reply.read_failed': 'The answer could not be read aloud. You can still read it.',
  'agent.web.reply.take_back': 'Take back your rating',
  'agent.web.feedback.understood': 'It did not understand me',
  'agent.web.feedback.about': 'About “{{question}}”',
  'agent.web.touch.body': 'I never do this by voice. Check it, then click to confirm.',
  'agent.web.touch.not_now': 'Not now',
  'agent.web.live.not_reached': 'I did not get to that',
  'agent.web.card.edit': 'Edit',
  'agent.web.card.never_alone': 'Nothing is saved until you say yes or click.',
  'agent.web.card.entry_refused': 'Not saved into {{record}}. {{problem}}',
  'agent.web.card.entry_refused_edit': 'Edit opens it with what to put right.',
  // Stage 8 phase 8: OS off in its own box, the Dashboard's door, and asking an admin.
  'agent.web.off.title': 'I am off here',
  'agent.web.off.view_as_box': 'I am off while you view as somebody else',
  'agent.web.off.no_model_box': 'This server has no model for me',
  'agent.web.off.no_role_box': 'Your role does not let you ask me',
  'agent.web.ask_admin': 'Ask an admin',
  // Stage 10 phase 1: the AI-first frame, its divider, and the changes marked in its live preview.
  'ai_first.set_up_yourself': 'Set it up yourself',
  'ai_first.preview': 'Live preview',
  'ai_first.resize': 'Resize the live preview',
  'ai_first.preview_size': 'Preview · {{width}} px',
  'ai_first.mark.new': 'New',
  'ai_first.mark.needed': 'Now needed',
  'ai_first.mark.changed': 'Changed',
  'ai_first.mark.removed': 'Taken out',
  // Stage 10 phase 2: New record with OS first, the one record beside the talk, and built.
  'new_record.os.meta': 'OS sets it up while you talk',
  'new_record.os.meta_kept': 'Nothing is made until you say so.',
  'new_record.os.made_meta': 'Made just now by you',
  'new_record.os.question': 'What do you want to keep track of? Say it in your own words, and I draw the $t(glossary.record_one) beside this conversation as you talk.',
  'new_record.os.chip.deliveries': 'Home deliveries',
  'new_record.os.chip.lending': 'Money I lend to staff',
  'new_record.os.chip.bills': 'Bills from suppliers',
  'new_record.os.chip.returns': 'Things customers return',
  'new_record.os.empty_title': 'Your $t(glossary.record_one) shows here',
  'new_record.os.empty_body':
    'As you talk, OS draws the $t(glossary.record_one): its name, what each $t(glossary.entry_one) holds, and how a few $t(glossary.entry_other) would read. Nothing is kept until you say so.',
  'new_record.os.note.empty': 'Nothing yet. It fills in as you talk.',
  'new_record.os.note.drafted': 'Here is what I would create. Nothing is made yet.',
  'new_record.os.note.by_hand': 'As you set it up yourself. Nothing is made yet.',
  'new_record.os.note.made': 'Made. It is in your $t(glossary.record_other) now.',
  'new_record.os.note.unsaved_one': '{{count}} change, not saved yet.',
  'new_record.os.note.unsaved_other': '{{count}} changes, not saved yet.',
  'new_record.os.note.saved': 'Saved.',
  'new_record.os.status.not_made': 'Not made yet',
  'new_record.os.status.made': 'Made just now',
  'new_record.os.status.unsaved': 'Not saved yet',
  'new_record.os.status.saved': 'Saved',
  'new_record.os.fields_one': '{{count}} $t(glossary.field_one)',
  'new_record.os.fields_other': '{{count}} $t(glossary.field_other)',
  'new_record.os.rows': 'What each $t(glossary.entry_one) holds',
  'new_record.os.date_note': 'Every $t(glossary.record_one) gets a date',
  'new_record.os.detail.always': 'Always on',
  'new_record.os.detail.from': 'From {{list}}',
  'new_record.os.detail.words': 'Words',
  'new_record.os.detail.number': 'A number',
  'new_record.os.detail.count': 'A count',
  'new_record.os.detail.money': 'Money',
  'new_record.os.detail.date': 'A date',
  'new_record.os.detail.yes_no': 'Yes or no',
  'new_record.os.detail.choice': 'A choice',
  'new_record.os.detail.worked_out': 'Worked out',
  'new_record.os.detail.in_or_out': 'In or out',
  'new_record.os.detail.other': 'Kept as it is',
  'new_record.os.sample_label': 'How a few $t(glossary.entry_other) would read',
  'new_record.os.sample_note': 'How a few $t(glossary.entry_other) would read. These $t(glossary.entry_other) are only a picture.',
  'new_record.os.sample.today': 'Today',
  'new_record.os.sample.yesterday': 'Yesterday',
  'new_record.os.sample.yes': 'Yes',
  'new_record.os.sample.no': 'No',
  'new_record.os.built': '{{record}} is ready. Add the first $t(glossary.entry_one), or tell me what to change.',
  'new_record.os.first_entry': 'Add the first $t(glossary.entry_one)',
  'new_record.os.open': 'Open {{record}}',
  'new_record.os.back': 'Back to OS',
  'new_record.os.home_title': 'Tell OS what you track',
  'new_record.os.home_body': 'Say it in your own words. OS sets up the $t(glossary.field_other), and nothing is made until you look it over.',
  'new_record.os.home_action': 'Set up with OS',
  // web: a link set up with OS first (web-redesign-10 phase 3)
  'link.os.new_title': 'New $t(glossary.link_one)',
  'link.os.meta_on': 'On {{record}}',
  'link.os.meta_nothing_runs': 'Nothing runs until you turn it on',
  'link.os.meta_just_now': 'Added by you just now',
  'link.os.title': '{{what}} into {{record}}',
  'link.os.title_what': '{{value}} {{noun}}',
  'link.os.question': 'What should happen in another $t(glossary.record_one) when you save to {{record}}? Say it in your own words, and I draw the $t(glossary.link_one) beside this conversation.',
  'link.os.chip_into': 'Put each one into {{record}}',
  'link.os.edit_question': 'What should change about this $t(glossary.link_one)? {{rule}} Say it plainly, and I change it beside this conversation. Nothing is saved until you say so.',
  'link.os.chip_over': 'Only when the amount is over {{amount}}',
  'link.os.chip_pause': 'Pause it',
  'link.os.empty_title': 'The $t(glossary.link_one) shows here',
  'link.os.empty_body': 'What fires it, where it lands, and the $t(glossary.entry_one) it would write, worked out from one of your real $t(glossary.entry_other). Nothing runs until you turn it on.',
  'link.os.note.empty': 'Nothing yet. It fills in as you talk.',
  'link.os.note.proposed': 'Worked out from your newest $t(glossary.entry_one). Nothing runs yet.',
  'link.os.note.by_hand': 'As you set it up yourself. Nothing runs yet.',
  'link.os.note.running': 'The $t(glossary.link_one) as it runs now. Nothing changes until you save.',
  'link.os.note.on': 'On. It is in the $t(glossary.link_other) of {{record}}.',
  'link.os.note.paused': 'Paused. It is in the $t(glossary.link_other) of {{record}}.',
  'link.os.note.changed': 'Changed beside you, not saved yet.',
  'link.os.note.saved': 'Saved.',
  'link.os.status.not_on': 'Not on yet',
  'link.os.status.changed': 'Changed, not saved',
  'link.os.matched_by': 'Matched by {{field}}.',
  'link.os.would_write': 'The $t(glossary.entry_one) it would write',
  'link.os.same_save': 'in the same save',
  'link.os.sample_fires': 'It fires on this one.',
  'link.os.sample_skips': 'It would not fire on this one.',
  'link.os.no_sample': 'There is no $t(glossary.entry_one) here yet to work it out from.',
  'link.os.on_line': 'It is on. From the next one it fires on, it lands in {{record}} on its own. What is already saved stays as it is.',
  'link.os.paused_line': 'Paused. Nothing lands in {{record}} until you turn it on again.',
  'link.os.turn_on_again': 'Turn it on',
  'link.os.back_to_links': 'Back to {{links}}',
  // web: Customize with OS first (web-redesign-10 phase 4)
  'customize.os.title': 'Customize {{record}}',
  'customize.os.question': 'What do you want to change about {{record}}? I change the form beside this conversation, and nothing is saved until you say so.',
  'customize.os.chip.add': 'Add a $t(glossary.field_one)',
  'customize.os.chip.needed': 'Make {{field}} needed',
  'customize.os.chip.take_out': 'Take {{field}} out of the form',
  'customize.os.note.as_is': 'The form as it opens today.',
  'customize.os.note.proposed_one': '{{count}} change, not saved yet.',
  'customize.os.note.proposed_other': '{{count}} changes, not saved yet.',
  'customize.os.note.by_hand': 'As you set it up yourself. Nothing is saved yet.',
  'customize.os.note.saved': 'Saved. The form opens like this from now on.',
  'customize.os.line.new': '{{field}} is new',
  'customize.os.line.needed': '{{field}} is now needed',
  'customize.os.line.changed': '{{field}} is changed',
  'customize.os.line.removed': '{{field}} is taken out',
  'customize.amount_moved': 'The amount is no longer at the top. It shows as a normal amount box, and the keypad does not open by itself.',
  'customize.move_to_top': 'Move it back to the top',
  'customize.layout.inner_section': 'Section inside it',
  'customize.layout.no_section': 'Not in a section',
  'customize.layout.no_section_hint': 'The field shows at the end of the form.',
  'customize.layout.rest': 'At the end of the form',
  'customize.layout.section': 'Section',
  'customize.layout.section_empty': 'No fields here. The form does not show an empty section.',
  'customize.layout.shows_in': 'Shows in',
  'customize.layout.simple': 'Use the simple form',
  'customize.layout.simple_note': 'The sections come off, and every field shows in this order.',
  'customize.layout.untitled': 'No title',
  'customize.layout.fields_caption': 'The form is in sections. Tap a title to rename it, and open a field to move it to another section.',
  'customize.layout.where': 'Where it shows',
  'customize.web.layout_caption': 'The form is in sections. Rename a section where it stands, and open a field to move it to another section.',
  'customize.web.layout_moves': 'The field shows at the end of this section.',
  // Stage 10 phase 6: New role with OS first, the one role beside the talk, and its card naming the role it makes.
  'agent.web.confirm_role_named': 'Make the {{role}} role',
  'agent.card.edit': 'Edit',
  'agent.card.under': '{{value}}, under {{parent}}',
  // The lines an entry card carries (Phase G of the lines plan): "Rooms · 2", the
  // field's name and a bare count, as the entry panel heads its lines. The name
  // is the noun already: "Items · 3 items" says it twice (12 Oct, Devendra).
  'agent.lines.run_one': '{{label}} · {{count, number}}',
  'agent.lines.run_other': '{{label}} · {{count, number}}',
  'agent.lines.untitled': 'Line {{number, number}}',
  // Where a line an entry change touches sits: "Added to Rooms › Room 101 › Guests".
  'agent.lines.change_add': 'Added to {{run}}',
  'agent.lines.change_change': 'Changed in {{run}}',
  'agent.lines.change_remove': 'Removed from {{run}}',
  // What a fields card says of a new field of lines, one step in, and where a
  // change to the fields of each line sits: "Guests, inside each room".
  'agent.lines.each_line_of': 'Each line of {{field}} holds',
  'agent.lines.inside_each': '{{field}}, inside each {{noun}}',
  'agent.lines.inside_each_line_of': '{{field}}, inside each line of {{parent}}',
  'agent.lines.field_needed': '{{type}}, needed',
  'agent.lines.counted_by': '{{field}} sets how many',
  'agent.lines.at_most_one': 'At most {{count, number}} line',
  'agent.lines.at_most_other': 'At most {{count, number}} lines',
  'agent.shows_when_as': 'Shows only when {{recipe}}',
  // A layout card: each section's look, and a section that holds no field yet.
  'agent.layout.style_plain': 'Open',
  'agent.layout.style_card': 'In a card',
  'agent.layout.style_folded': 'Folded',
  'agent.layout.no_fields': 'No fields yet',
  'agent.live.minutes_one': '{{count, number}} min',
  'agent.live.minutes_other': '{{count, number}} min',
  'agent.business_detail_title': 'Save to business details',
  'agent.entry_approve_title': 'Approve · {{title}}',
  'agent.entry_send_back_title': 'Send back · {{title}}',
  'agent.doc_template_title': 'A report template for {{record}}',
  'agent.receipt.business_detail': 'Saved to business details.',
  'agent.receipt.entry_approved': 'Approved.',
  // OS's money cards (Phase 12 of docs/plans/ledger.md, L59): business words only (L19).
  'agent.month_close_title': 'Close {{label}}',
  'agent.charge_run_title': 'Run the charges · {{record}}',
  'agent.charge_skip_title': 'Skip a charge run · {{record}}',
  'agent.entry_correct_title': 'Correct · {{title}}',
  'agent.match_move_title': 'Pay another charge',
  'agent.approval_chain_title': 'Approvals · {{record}}',
  'agent.confirm_month_close': 'Close the month',
  'agent.confirm_charge_run': 'Run now',
  'agent.confirm_charge_skip': 'Skip this period',
  'agent.confirm_entry_correct': 'Correct',
  'agent.confirm_match_move': 'Move the payment',
  'agent.confirm_approval_chain': 'Save approvals',
  'agent.receipt.month_closed': '{{label}} is closed.',
  'agent.receipt.charge_run': 'The charges are written.',
  'agent.receipt.charge_skipped': 'That period is skipped.',
  'agent.receipt.entry_corrected': 'Corrected.',
  'agent.receipt.match_moved': 'The payment pays the other charge now.',
  'agent.receipt.approval_chain': 'Approvals saved.',
  'agent.money_card.through': 'Entries on or before {{day}}',
  'agent.money_card.period': '{{from}} to {{to}}',
  'agent.money_card.billed_one': '{{count, number}} on {{list}} billed · {{total}}',
  'agent.money_card.billed_other': '{{count, number}} on {{list}} billed · {{total}}',
  'agent.money_card.skip': 'Nobody is billed for this period.',
  'agent.money_card.dated': 'Dated {{day}}',
  'agent.money_card.was': 'Was {{value}}',
  'agent.money_card.empty': 'Empty',
  'agent.money_card.paid': '{{party}} paid {{amount}}',
  'agent.money_card.paid_unnamed': 'Paid {{amount}}',
  'agent.money_card.from': 'From {{charge}}',
  'agent.money_card.to': 'To {{charge}}',
  'agent.money_card.replaces': 'This replaces the approvals the record has now.',
  'agent.receipt.entry_sent_back': 'Sent back.',
  'agent.receipt.doc_template': 'The report template for {{record}} is saved.',
  'agent.task_rule_title': 'Task rule · {{record}}',
  'agent.confirm_task_rule': 'Save task rule',
  'agent.receipt.task_rule': 'Task rule saved on {{record}}.',
  // OS's repair cards (L64 of docs/plans/ledger.md): a fix to the setup, and Ask the owner.
  'agent.setup_fix_title': 'Fix the setup',
  'agent.confirm_setup_fix': 'Apply fix',
  'agent.receipt.setup_fix': 'Fix applied.',
  'agent.setup_fix_waiting_one': '{{count, number}} $t(glossary.entry_one) waiting is saved again once it is applied.',
  'agent.setup_fix_waiting_other': '{{count, number}} $t(glossary.entry_other) waiting are saved again once it is applied.',
  'agent.ask_owner_title': 'Ask the owner',
  'agent.confirm_ask_owner': 'Ask the owner',
  'agent.receipt.ask_owner': 'The owner is asked. It is in their tasks.',
  'task.card.title': 'Tasks for you',
  'task.card.line': 'Raised from $t(glossary.entry_other) by the task rules of your $t(glossary.record_other)',
  'task.card.more_one': '{{count, number}} more task',
  'task.card.more_other': '{{count, number}} more tasks',
  'task.where': '{{record}} · {{due}}',
  'task.due_today': 'Due today',
  'task.due_on': 'Due {{day}}',
  'task.was_due': 'Was due {{day}}',
  'task.done': 'Done',
  'task.dismiss': 'Dismiss',
  'task.done_saved': 'Task done',
  'task.dismissed': 'Task dismissed',
  'task.ask.by': 'Asked by {{name}}',
  'task.ask.fix': 'The fix: {{fix}}',
  'task.ask.row_apply': 'Add {{name}} ({{choice}}) and save the $t(glossary.entry_one)',
  'task.ask.row_apply_plain': 'Add {{name}} to {{list}} and save the $t(glossary.entry_one)',
  'task.ask.row_name': 'Name',
  'task.ask.row_saved': '{{name}} added; the $t(glossary.entry_one) is saved',
  'task.ask.row_sent': '{{name}} added; the $t(glossary.entry_one) is sent for approval',
  'repair.apply': 'Apply',
  'repair.not_applied': 'This fix could not be applied.',
  'repair.health.title': 'Things to fix',
  'repair.health.line': 'What stops $t(glossary.entry_other) from saving or counting the right way, and the fix for each',
  'repair.health.failed': 'Things to fix could not be read',
  'repair.health.waiting_one': '{{count, number}} draft waits',
  'repair.health.waiting_other': '{{count, number}} drafts wait',
  'repair.health.about': '{{record}} · {{waiting}}',
  'repair.health.need_owner_one': '{{count, number}} more needs the owner or an admin',
  'repair.health.need_owner_other': '{{count, number}} more need the owner or an admin',
  'repair.health.apply_all_one': 'Apply {{count, number}} safe fix',
  'repair.health.apply_all_other': 'Apply all {{count, number}} safe fixes',
  'repair.health.fixed_one': '{{count, number}} fix applied',
  'repair.health.fixed_other': '{{count, number}} fixes applied',
  'repair.health.fixed_saved_one': '{{fixed}}; {{count, number}} draft saved',
  'repair.health.fixed_saved_other': '{{fixed}}; {{count, number}} drafts saved',
  'repair.health.fixed_but': '{{fixed}}. {{reason}}',
  'task_rule.when_saved': 'When an $t(glossary.entry_one) is saved',
  'task_rule.when_saved_if': 'When an $t(glossary.entry_one) is saved and {{conditions}}',
  'task_rule.when_approved': 'When an $t(glossary.entry_one) is approved',
  'task_rule.when_approved_if': 'When an $t(glossary.entry_one) is approved and {{conditions}}',
  'task_rule.condition': '{{field}} {{op}}',
  'task_rule.condition_value': '{{field}} {{op}} {{value}}',
  'task_rule.and': '{{left}} and {{right}}',
  'task_rule.or': '{{left}} or {{right}}',
  'task_rule.op.is': 'is',
  'task_rule.op.is_not': 'is not',
  'task_rule.op.filled': 'is filled',
  'task_rule.op.empty': 'is empty',
  'task_rule.op.at_least': 'is at least',
  'task_rule.op.at_most': 'is at most',
  'task_rule.op.exactly': 'is exactly',
  'task_rule.op.contains': 'contains',
  'task_rule.op.not_contains': 'does not contain',
  'task_rule.op.is_today': 'is today',
  'task_rule.op.before_today': 'is before today',
  'task_rule.op.after_today': 'is after today',
  'task_rule.who.owner': 'Goes to the owner',
  'task_rule.who.writer': 'Goes to whoever saved the $t(glossary.entry_one)',
  'task_rule.who.role': 'Goes to everyone in {{role}}',
  'task_rule.who.field': 'Goes to the $t(glossary.person_one) in {{field}}',
  'task_rule.who_due': '{{who}} · {{due}}',
  'task_rule.due.raised': 'due the day it is raised',
  'task_rule.due.after_raised_one': 'due {{count, number}} day after it is raised',
  'task_rule.due.after_raised_other': 'due {{count, number}} days after it is raised',
  'task_rule.due.before_raised_one': 'due {{count, number}} day before it is raised',
  'task_rule.due.before_raised_other': 'due {{count, number}} days before it is raised',
  'task_rule.due.on': 'due on {{field}}',
  'task_rule.due.after_one': 'due {{count, number}} day after {{field}}',
  'task_rule.due.after_other': 'due {{count, number}} days after {{field}}',
  'task_rule.due.before_one': 'due {{count, number}} day before {{field}}',
  'task_rule.due.before_other': 'due {{count, number}} days before {{field}}',
  'task_rule.remove': 'Take off',
  'task_rule.removed': 'Task rule taken off',
  'role_new.os.meta': 'OS picks the permissions while you talk',
  'role_new.os.meta_kept': 'Nothing is kept until you say so',
  'role_new.os.question': 'What do the people in this role do? Say it in your own words, and I pick the permissions as you talk. Nothing is kept until you say so.',
  'role_new.os.chip.adds': 'They add what they drop off, and fix their own',
  'role_new.os.chip.looks': 'They only look at the $t(glossary.record_other) they are given',
  'role_new.os.chip.runs': 'They run the shop when I am away',
  'role_new.os.empty_title': 'Your role shows here',
  'role_new.os.empty_body': 'Say what the people in it do, and the permissions it holds are drawn here as you talk.',
  'role_new.os.note.empty': 'Nothing yet. It fills in as you talk.',
  'role_new.os.note.added': 'Changes as you talk. {{permission}} was just added.',
  'role_new.os.note.added_many_one': 'Changes as you talk. {{count}} permission was just added.',
  'role_new.os.note.added_many_other': 'Changes as you talk. {{count}} permissions were just added.',
  'role_new.os.note.ready': 'Ready. Nothing is kept until you make it.',
  'role_new.os.note.by_hand': 'As you set it up yourself. Nothing is kept until you make it.',
  'role_new.os.count': 'A new role · {{on}} of {{total}} permissions',
  'role_new.os.group': '{{group}} · {{on}} of {{total}}',
  'role_new.os.nothing': 'Nothing',
  'role_new.os.just_added': 'Just added',
  'role_new.os.foot': 'Which $t(glossary.record_other) they open is set on each $t(glossary.person_one), when you give them the role.',
  // the phone says these to a screen reader in its own words; here they are catalogue words
  'setup.web.talk_instead': 'Talk instead',
  'setup.web.close_voice': 'Close voice input',
  'setup.web.finish_recording': 'Finish recording',
  // Setup's call as live mode in the box, on the web (web-redesign-07b phase C): the
  // greeting's line and its hint, the line under the live button, and the call's two
  // troubles: a microphone that did not start, and a call that dropped.
  'setup.web.call_lede':
    'Talk the way you would to a new helper at the shop, or type. I sketch each thing beside this as I hear it, and nothing is created until you click Build.',
  'setup.web.call_start_hint': 'Talk, type, or pick one to start',
  'setup.web.call_hint': 'Say it the way you would tell a person, or type',
  'setup.web.call_still_add': 'You can still add something',
  'setup.web.call_speaking_hint': 'Speaking… Click the microphone to cut in',
  'setup.web.call_paused_hint': 'Paused. Click the microphone to talk',
  'setup.web.mic_failed_title': 'The microphone could not start',
  'setup.web.mic_failed_body': 'Try again, or type instead.',
  'setup.web.call_dropped_body': 'Type to carry on, or click Back to voice to call again.',
  'setup.sketch.added': 'Added for you',
  'setup.sketch.kind.list': 'A list of names',
  'setup.sketch.kind.list_built': 'List',
  'setup.sketch.kind.record': 'Something you write in',
  'setup.sketch.kind.record_built': 'Record · {{direction}}',
  'setup.sketch.more_names_one': '+{{count, number}} more',
  'setup.sketch.more_names_other': '+{{count, number}} more',
  'setup.sketch.you_said': 'You said “{{words}}”',
  'setup.sketch.key': 'Key',
  'setup.sketch.key.link': 'What one changes in another',
  'setup.sketch.key.link_built': 'Link',
  'setup.sketch.label': 'Sketch of your setup',
  'setup.sketch.on_top': 'On top',
  'setup.sketch.sub_building': 'Creating it now. Each part turns solid as it is made.',
  'setup.sketch.sub_proposed': 'Your setup, drawn up. Nothing is created until you build it.',
  'setup.sketch.receipt.added': 'Added {{names}}',
  'setup.sketch.receipt.by_build': 'Added for you: {{names}}',
  'setup.sketch.receipt.changed': 'Changed {{names}}',
  'setup.sketch.receipt.drew': 'Your setup is drawn up',
  'setup.sketch.receipt.fields': '{{thing}} keeps {{fields}}',
  'setup.sketch.receipt.label': '{{words}}. Show it on the sketch',
  'setup.sketch.receipt.left_out': 'Left out {{names}}',
  'setup.sketch.receipt.unsure': 'Heard {{names}}, not placed yet',
  'setup.sketch.about': 'About {{name}}:',
  'setup.sketch.card_line': 'It is drawn on the sketch. Click anything there to ask about it.',
  'setup.sketch.link_always': 'Every time',
  'setup.sketch.link_when': 'When {{when}}',
  'setup.sketch.questions_left_one': 'About {{count, number}} more question',
  'setup.sketch.questions_left_other': 'About {{count, number}} more questions',
  'setup.sketch.see': 'See the sketch',
  'setup.team.line.checks': 'Sees everything and the totals · settles {{settles}}',
  'setup.team.line.checks_all': 'Sees everything and the totals',
  'setup.team.line.counter': 'Adds {{adds}} · no {{hidden}}, no totals',
  'setup.team.line.counter_all': 'Adds {{adds}} · no totals',
  'setup.team.line.field': 'Adds {{adds}} · nothing else, no totals',
  'setup.team.line.field_own': 'Adds their own {{adds}} · sees only those',
  'setup.team.names_and': '{{names}} and {{last}}',
  'setup.team.role.clinic.checks': 'Accountant',
  'setup.team.role.clinic.counter': 'Receptionist',
  'setup.team.role.clinic.field': 'Dentist',
  'setup.team.role.clinic.field_doctor': 'Doctor',
  'setup.team.role.contractor.checks': 'Accountant',
  'setup.team.role.contractor.counter': 'Site supervisor',
  'setup.team.role.contractor.field': 'Labour lead',
  'setup.team.role.food.checks': 'Manager',
  'setup.team.role.food.counter': 'Cashier',
  'setup.team.role.food.field': 'Kitchen in-charge',
  'setup.team.role.food_round.checks': 'Partner',
  'setup.team.role.food_round.counter': 'Collector',
  'setup.team.role.food_round.field': 'Delivery person',
  'setup.team.role.other.checks': 'Partner',
  'setup.team.role.other.counter': 'Helper',
  'setup.team.role.other.field': 'Staff member',
  'setup.team.role.services.checks': 'Accountant',
  'setup.team.role.services.counter': 'Assistant',
  'setup.team.role.services.field': 'Freelancer',
  'setup.team.role.shop.checks': 'Partner',
  'setup.team.role.shop.counter': 'Cashier',
  'setup.team.role.shop.field': 'Stock keeper',
  'setup.team.role.transport.checks': 'Accountant',
  'setup.team.role.transport.counter': 'Dispatcher',
  'setup.team.role.transport.field': 'Driver',
  'setup.team.role.wholesale.checks': 'Partner',
  'setup.team.role.wholesale.counter': 'Sales person',
  'setup.team.role.wholesale.field': 'Godown keeper',
  'setup.team.say.checks': 'The {{role}} sees every $t(glossary.record_one) and the totals on top, and settles {{settles}}. They add nothing.',
  'setup.team.say.checks_all': 'The {{role}} sees every $t(glossary.record_one) and the totals on top. They add nothing.',
  'setup.team.say.counter': 'The {{role}} adds {{adds}}, and sees everyone’s.',
  'setup.team.say.field': 'The {{role}} adds {{adds}}, and opens nothing else.',
  'setup.team.say.field_own': 'The {{role}} adds their own {{adds}}, and only ever sees those.',
  'setup.team.say.hidden': '{{things}} stay hidden from them.',
  'setup.team.say.the_totals': 'the totals',
  'setup.team.say.totals_hidden': 'The totals stay hidden from them.',
  'setup.team.card_label': 'How a team would see {{name}}',
  'setup.team.later': 'Nobody is added and nothing is set up. Add your team later from Team, in the sidebar.',
  'setup.team.role_label': '{{role}}: {{line}}',
  'setup.team.say': 'One more thing before you open it. When you take on staff, each person sees only what their role allows. Here is how that could look for {{name}}. Click a role to see it on the sketch.',
  'setup.team.title': 'How a team would see it',
  'setup.team.hint': 'Click a role on the left to see the sketch through their eyes.',
  'setup.team.hint_as': 'Three roles, three views of one drawing. Make them for real later, from Team or Roles.',
  'setup.team.sub': 'As the {{role}} would see it. An example: nobody has this role yet.',
  'setup.team.reach.adds_names': 'Adds new names',
  'setup.team.reach.chooses': 'Chooses from it',
  'setup.team.reach.everyone': 'Sees: everyone’s',
  'setup.team.reach.own': 'Sees: their own',
  // The four lessons while OS builds the setup (business-map-onboarding-web.md
  // part 2, boards B2 to B6): each filled from what OS heard, the general case
  // when a part was not heard.
  'setup.lesson.intro': 'That is enough to build it. It takes about a minute: here is what each part is, while I make it.',
  'setup.lesson.kicker': 'While I build · {{n, number}} of {{total, number}}',
  'setup.lesson.names_and': '{{names}} and {{last}}',
  'setup.lesson.records.title': 'Records are what you fill in.',
  'setup.lesson.records.body_pair': '{{record}} and {{other}} are $t(glossary.record_other). Each time something happens, you add one $t(glossary.entry_one) to {{record}}.',
  'setup.lesson.records.body_single': '{{record}} is a $t(glossary.record_one). Each time something happens, you add one $t(glossary.entry_one) to it.',
  'setup.lesson.records.body_none': 'A $t(glossary.record_one) is where you write down what happens: one $t(glossary.entry_one) each time.',
  'setup.lesson.records.fields': 'On each one: {{fields}}.',
  'setup.lesson.records.see_pair': 'On the right: the two $t(glossary.record_other), ringed.',
  'setup.lesson.records.see_single': 'On the right: the $t(glossary.record_one), ringed.',
  'setup.lesson.records.see_none': 'On the right: what you said, as it is drawn up.',
  'setup.lesson.lists.title': 'Lists are the names you choose from.',
  'setup.lesson.lists.body_several': '{{lists}} are $t(glossary.list_other). On {{record}} you choose from {{list}} instead of typing, so each name is spelled the same every time.',
  'setup.lesson.lists.body_single': '{{list}} is a $t(glossary.list_one). On {{record}} you choose from it instead of typing, so each name is spelled the same every time.',
  'setup.lesson.lists.body_none': 'A $t(glossary.list_one) holds the names you choose from, so each one is spelled the same every time.',
  'setup.lesson.lists.see_lines': 'Each thin line: a box on a $t(glossary.record_one) choosing from a $t(glossary.list_one).',
  'setup.lesson.lists.see_ringed': 'On the right: the $t(glossary.list_other), ringed.',
  'setup.lesson.lists.see_none': 'On the right: what you said, as it is drawn up.',
  'setup.lesson.links.title': 'Links do the next step for you.',
  'setup.lesson.links.body_part': 'When an $t(glossary.entry_one) in {{from}} is paid only in part, the rest goes into {{to}} by itself. You never write it by hand.',
  'setup.lesson.links.body_link': 'An $t(glossary.entry_one) in {{from}} adds one to {{to}} by itself, so you never write it twice.',
  'setup.lesson.links.body_none': 'Ask OS for one any time.',
  'setup.lesson.links.tiles_watch': 'And the $t(glossary.insight_other) you asked to watch add themselves up.',
  'setup.lesson.links.tiles': 'And the $t(glossary.insight_other) add themselves up.',
  'setup.lesson.links.example': 'An $t(glossary.entry_one) of {{amount}} in {{from}}, {{paid}} paid → {{rest}} added to {{to}}.',
  'setup.lesson.links.see_link': 'On the right: the $t(glossary.link_one) and the two $t(glossary.record_other) it joins.',
  'setup.lesson.links.see_tiles': 'On the right: the $t(glossary.insight_other), which add themselves up.',
  'setup.lesson.check.title': 'Nothing is real until you say Build.',
  'setup.lesson.check.body':
    'I am checking it against what you told me. Then you can try one example day through it, change anything by saying so, and only then create it. After that, I will show how your team would see it.',
  'setup.lesson.check.see': 'Dashed on the sketch means not created yet.',
  // A short label beside what a lesson rings on the sketch.
  'setup.lesson.label.record': 'Record · one entry each time',
  'setup.lesson.label.owed': 'Record · what is owed',
  'setup.lesson.label.list': 'List · chosen on each entry',
  'setup.lesson.label.link': 'Filled by the link',
  'setup.lesson.label.tiles': 'Add themselves up',
  // Under a lesson whose step has run past 25 seconds; the step is one of the five below.
  'setup.lesson.still': 'Still working on {{step}}…',
  'setup.lesson.step.records': 'the names',
  'setup.lesson.step.fields': 'the fields',
  'setup.lesson.step.links': 'the links',
  'setup.lesson.step.cards': 'the $t(glossary.insight_other)',
  'setup.lesson.step.check': 'the check',
  // Below the desktop's width, in place of the line that points at the sketch.
  'setup.lesson.see': 'See it on the sketch',
  'setup.lesson.sketch_building': 'Building your setup from what you said. Nothing is created until you build it.',
  'setup.lesson.sketch_checking': 'Checking it against what you said. Nothing is created until you build it.',
  // Once the card is in: OS's line, and the four lessons one click away.
  'setup.lesson.done': 'It is drawn up on the right: {{parts}}. You have seen what each part does. Try one day to watch an entry go through it.',
  'setup.lesson.count.records_one': '{{count, number}} $t(glossary.record_one)',
  'setup.lesson.count.records_other': '{{count, number}} $t(glossary.record_other)',
  'setup.lesson.count.lists_one': '{{count, number}} $t(glossary.list_one)',
  'setup.lesson.count.lists_other': '{{count, number}} $t(glossary.list_other)',
  'setup.lesson.count.links_one': '{{count, number}} $t(glossary.link_one)',
  'setup.lesson.count.links_other': '{{count, number}} $t(glossary.link_other)',
  'setup.lesson.again': 'See the four parts again',
  // The studio's words (docs/plans/onboarding-harness, Appendix C). The
  // language page asks only which language OS uses; the screens stay English.
  'setup.studio.canvas.ask': 'Ask about this',
  'setup.studio.canvas.rename': 'Rename',
  'setup.studio.lang.lede': 'OS talks and writes with you in it, in setup and after. Everyone on your team picks their own.',
  'setup.studio.lang.preview': 'What you will see',
  'setup.studio.lang.preview_os': 'OS, in {{language}}',
  'setup.studio.lang.preview_screens': 'Screens, in English',
  'setup.studio.lang.speaks': 'OS talks and writes in each of these, typed or live.',
  'setup.studio.lang.title': 'Which language should OS use?',
  'setup.studio.lang.truth': 'RecordOS’s screens are in English for now. Buttons and the names of your records stay in English; OS’s words change.',
  'setup.studio.files.more': '+ {{n, number}} more',
  'setup.studio.files.needs_one': '{{count, number}} needs you',
  'setup.studio.files.needs_other': '{{count, number}} need you',
  'setup.studio.source.state.failed_short': 'Couldn’t read',
  'setup.studio.when.days_one': '{{count, number}} day ago',
  'setup.studio.when.days_other': '{{count, number}} days ago',
  'setup.studio.when.hours_one': '{{count, number}} hour ago',
  'setup.studio.when.hours_other': '{{count, number}} hours ago',
  'setup.studio.when.minutes_one': '{{count, number}} min ago',
  'setup.studio.when.minutes_other': '{{count, number}} min ago',
  'setup.studio.when.now': 'just now',
  'setup.studio.empty.sub': 'Your setup appears here as you talk.',
  'setup.studio.offline.title': 'No connection',
  'setup.studio.start.down.title': 'OS is not answering right now',
  'setup.studio.start.change': 'Change',
  'setup.studio.start.os_speaks': 'OS speaks {{language}}',
  'setup.studio.cards.app_sub': 'OS looks it up',
  'setup.studio.cards.files': 'Files',
  'setup.studio.cards.files_sub': 'Excel, PDF, Word',
  'setup.studio.cards.label': 'Or bring what you keep',
  'setup.studio.cards.link': 'Website or link',
  'setup.studio.cards.link_sub': 'Site, sheet or page',
  'setup.studio.cards.maps': 'Google Maps place',
  'setup.studio.cards.maps_sub': 'Your place on the map',
  'setup.studio.cards.more': 'More ways',
  'setup.studio.cards.more_sub': 'Talk live, a photo from your phone',
  'setup.studio.cards.photo': 'Photo',
  'setup.studio.cards.photo_sub': 'Register or bill',
  'setup.studio.tray.app': 'The app you already use',
  'setup.studio.composer.placeholder': 'Tell OS about your work, or drop a file',
  'setup.studio.drop.count': '{{ok, number}} of {{total, number}} can be read',
  // The tray, the link box and files over the page (step 5.13).
  'setup.studio.composer.add': 'Add',
  'setup.studio.tray.files': 'Files',
  'setup.studio.tray.files_sub': 'Excel sheets, PDFs, Word files, exports from your app, photos of bills',
  'setup.studio.tray.photo': 'Photo',
  'setup.studio.tray.photo_sub': 'Take it with your phone',
  'setup.studio.tray.link': 'Paste a link',
  'setup.studio.tray.link_sub': 'Your website, Google Maps, a Google Sheet',
  'setup.studio.tray.app_sub': 'Name it; OS looks it up and sets up the same',
  'setup.studio.tray.live_sub': 'Say it instead of typing',
  'setup.studio.tray.web': 'Look things up on the web',
  'setup.studio.tray.drop': 'Or drop files anywhere on this page',
  'setup.studio.link2.title': 'Paste links, or name the app you use',
  'setup.studio.link2.sub': 'One on each line. Each is read on its own; one that fails never holds back the others.',
  'setup.studio.link2.role': 'Whose is it?',
  'setup.studio.link2.app_what': 'you typed its name',
  'setup.studio.link.read': 'Read it',
  'setup.studio.link.look_up': 'Look it up',
  'setup.studio.link.placeholder': 'Paste a link, or type an app’s name',
  'setup.studio.link.kind.website': 'Website',
  'setup.studio.link.kind.maps': 'Google Maps place',
  'setup.studio.link.kind.sheet': 'Google Sheet',
  'setup.studio.link.kind.app': 'An app',
  // Every kind of link, several at once, and a link heard on a call (step 5.13c).
  'setup.studio.link.kind.drive': 'A shared file',
  'setup.studio.link.kind.table': 'A shared table',
  'setup.studio.link.kind.social': 'A business page',
  'setup.studio.link.kind.listing': 'A business listing',
  'setup.studio.link.kind.store': 'An online store',
  'setup.studio.link.kind.video': 'A video or blog',
  'setup.studio.link.kind.help': 'An app’s help pages',
  'setup.studio.link.kind.catalogue': 'A catalogue',
  'setup.studio.link.kind.pdf': 'A price list',
  'setup.studio.link.kept_fact': 'Kept as a fact about your business, not read',
  'setup.studio.link.read_these': 'Read these {{n, number}}',
  'setup.studio.link.many_one': '{{count, number}} link in one message',
  'setup.studio.link.many_other': '{{count, number}} links in one message: each is read on its own',
  'setup.studio.app.i_use': 'I use {{name}} now.',
  'setup.studio.source.role.mine': 'Mine',
  'setup.studio.source.role.like': 'Like this',
  'setup.studio.source.role.reference': 'Reference app',
  'setup.studio.source.state.waiting': 'Waiting',
  'setup.studio.source.state.uploading': 'Uploading · {{pct, number}}%',
  'setup.studio.source.state.reading': 'Reading',
  'setup.studio.source.state.needs': 'Needs you',
  'setup.studio.source.state.read': 'Read',
  'setup.studio.source.state.partly': 'Partly read',
  'setup.studio.source.state.failed': 'Could not read it',
  'setup.studio.source.state.left': 'Left out',
  'setup.studio.source.retry': 'Retry',
  'setup.studio.several.reading': '{{done, number}} of {{total, number}} read · send now, I keep reading',
  'setup.studio.several.password': '{{name}} asks for a password.',
  'setup.studio.several.type_password': 'Type the password',
  'setup.studio.several.leave_out': 'Leave it out',
  'setup.studio.start.site.based': 'Written from your website',
  'setup.studio.start.site.os': 'I read your website and wrote down what I found. Change anything, add what you keep track of, then send.',
  'setup.studio.drop.title': 'Drop to add them',
  'setup.studio.drop.ok': 'Can be read',
  'setup.studio.drop.no_exe': 'Can’t be read: it is a program',
  // What you shared: the column of sources, its rows and their ways on (step 5.13a).
  'setup.studio.sources.title': 'What you shared',
  'setup.studio.sources.add': 'Add',
  'setup.studio.sources.fold': 'Fold to one line',
  'setup.studio.sources.unfold': 'Show what you shared',
  'setup.studio.sources.taught_title': 'What it taught',
  'setup.studio.sources.see_read': 'See what OS read',
  'setup.studio.sources.role_change': 'Change',
  'setup.studio.sources.with_build': '{{what}} come in with Build',
  'setup.studio.sources.after_build': '{{what}} come in after Build',
  'setup.studio.sources.import_started': '{{what}} started coming in after Build',
  'setup.studio.sources.import_review': '{{what}} wait for your review in Bring in your data',
  'setup.studio.sources.not_in_one': '{{count, number}} row in {{name}} is not brought in yet',
  'setup.studio.sources.not_in_other': '{{count, number}} rows in {{name}} are not brought in yet',
  'setup.studio.sources.not_data_document': 'Not brought in: its tables explain the business',
  'setup.studio.sources.not_data_no_header': 'Not brought in: it has no header row',
  'setup.studio.sources.not_data_person': 'Not brought in: you said it is not data',
  'setup.studio.sources.not_data_model': 'Not brought in: {{why}}',
  'setup.studio.sources.not_data_model_plain': 'Not brought in: it reads as notes, not data',
  'setup.studio.sources.not_data_some_one': '{{count, number}} sheet is not brought in: it is not data',
  'setup.studio.sources.not_data_some_other': '{{count, number}} sheets are not brought in: they are not data',
  'setup.studio.sources.bring_in': 'Bring this in',
  'setup.studio.sources.not_data': 'Not data',
  'setup.studio.sources.context': 'Context, not data',
  'setup.studio.sources.undo_7': 'You can undo it for 7 days',
  'setup.studio.sources.lit': 'Lit on the canvas: what {{name}} shaped',
  'setup.studio.sources.retake': 'Take it again',
  'setup.studio.sources.others_fine': 'Everything else is read and on the canvas.',
  'setup.studio.sources.about_shop': 'About your work',
  'setup.studio.sources.too_dark': 'Too dark to read',
  'setup.studio.sources.too_big': 'Too big ({{size}}): send its sheets',
  'setup.studio.sources.too_big_plain': 'Too big: send its sheets',
  'setup.studio.sources.pages_read': 'Read {{n, number}} of {{total, number}} pages. The site asks us to skip {{skipped, number}}.',
  'setup.studio.sources.pages_later': 'Read {{n, number}} of {{total, number}} pages for now; the rest come in after Build',
  'setup.studio.sources.left': 'Left out: {{what}}',
  // Under a workbook's "20 of 23 sheets read" (files.left_out.sheets): why, in the words of import.offer.nothing_unread.
  'setup.studio.sources.sheets_why': 'Only the first {{read, number}} sheets of a workbook are read: to bring in one past them, move it to the front and add the file again.',
  'setup.studio.source.go_on': 'Go on without it',
  'setup.studio.source.step.left': 'You left it out. Nothing from it is used.',
  'setup.studio.link.private_short': 'Private',
  'setup.studio.link.private': 'This sheet is private.',
  'setup.studio.link.private_step': 'Only its owner can open it. Share it, then read it again.',
  'setup.studio.link.share_any': 'Share it with anyone who has the link',
  'setup.studio.link.upload': 'Upload the file instead',
  'setup.studio.link.read_again': 'Read it again',
  'setup.studio.link.sign_in_short': 'Opens a sign-in page',
  'setup.studio.link.sign_in': 'It opens a sign-in page, so nothing on it was read.',
  'setup.studio.link.open_any': 'Make the link open for anyone',
  'setup.studio.link.blocked_short': 'Keeps readers out',
  'setup.studio.link.blocked': 'This site does not let readers in, so nothing on it was read.',
  'setup.studio.link.down': 'Did not answer',
  'setup.studio.link.down_step': 'Nothing from it is lost: OS tries again twice, and the rest go on.',
  'setup.studio.link.try_again': 'Try again',
  'setup.studio.link.pin_only': 'A pin with no business on it',
  'setup.studio.link.paste_place': 'Paste your own place instead',
  'setup.studio.link.not_business_short': 'Not about a business',
  'setup.studio.link.not_business': 'This page is not about a business.',
  'setup.studio.link.shape_only': 'Use it for shape only',
  'setup.studio.link.preview_only': 'Public preview only',
  'setup.studio.link.send_shots': 'Send screenshots instead',
  'setup.studio.link.go_on_read': 'Go on with what is read',
  'setup.studio.link.one_view': 'One view shared: {{n, number}} of {{total, number}} columns',
  'setup.studio.link.share_whole': 'Share the whole table',
  'setup.studio.link.part_shown': 'Shows readers {{n, number}} of {{total, number}} items',
  'setup.studio.link.send_product_file': 'Send your product file',
  'setup.studio.link.no_words': 'No captions, little to read',
  'setup.studio.link.tell_liked': 'Tell OS what you liked',
  'setup.studio.link.gone': 'No longer opens',
  'setup.studio.link.fresh': 'Paste a fresh link',
  'setup.studio.link.parked': 'An empty page, for sale',
  'setup.studio.link.not_yours': 'Another business’s listing',
  'setup.studio.link.is_mine': 'It is mine',
  'setup.studio.link.one_i_like': 'One I like',
  'setup.studio.link.password': 'Asks for a password',
  'setup.studio.link.heard': 'Heard a website',
  'setup.studio.link.heard_wait': 'Heard on the call. Nothing is read until you say yes.',
  'setup.studio.link.read_q': 'Read {{name}}?',
  'setup.studio.link.not_this': 'Not this',
  'setup.studio.privacy.phones_one': '{{count, number}} phone number',
  'setup.studio.privacy.phones_other': '{{count, number}} phone numbers',
  'setup.studio.privacy.aadhaar_one': '{{count, number}} Aadhaar number',
  'setup.studio.privacy.aadhaar_other': '{{count, number}} Aadhaar numbers',
  'setup.studio.privacy.pan_one': '{{count, number}} PAN',
  'setup.studio.privacy.pan_other': '{{count, number}} PANs',
  'setup.studio.privacy.card_one': '{{count, number}} card number',
  'setup.studio.privacy.card_other': '{{count, number}} card numbers',
  'setup.studio.privacy.bank_one': '{{count, number}} bank account number',
  'setup.studio.privacy.bank_other': '{{count, number}} bank account numbers',
  'setup.studio.privacy.otp_one': '{{count, number}} one-time code',
  'setup.studio.privacy.otp_other': '{{count, number}} one-time codes',
  'setup.studio.privacy.password_one': '{{count, number}} password',
  'setup.studio.privacy.password_other': '{{count, number}} passwords',
  'setup.studio.privacy.column_one': '{{count, number}} column of ID numbers',
  'setup.studio.privacy.column_other': '{{count, number}} columns of ID numbers',
  // What OS read, Left out, the role menu, a password, and sources that disagree (step 5.13b).
  'setup.studio.read.title': 'What OS read',
  'setup.studio.read.title_of': 'What OS read · {{name}}',
  'setup.studio.read.photo_of': 'Your photo',
  'setup.studio.read.columns': 'Columns it found',
  'setup.studio.read.unsure': 'Not sure',
  'setup.studio.read.unsure_n': 'Not sure: {{count, number}}',
  'setup.studio.read.unsure_hint': 'Check these before they come in',
  'setup.studio.read.fix': 'Fix a word',
  'setup.studio.read.rows_seen_one': '{{count, number}} line on this page',
  'setup.studio.read.rows_seen_other': '{{count, number}} lines on this page',
  'setup.studio.read.screen_of': 'Your screenshot',
  'setup.studio.read.shape_only': 'Used for shape only. Nothing on it is kept.',
  'setup.studio.read.someone_else': 'The names and amounts on it are someone else’s.',
  'setup.studio.read.not_yet': 'Not in RecordOS yet',
  'setup.studio.read.lines': 'What it read',
  'setup.studio.privacy.title': 'Left out of {{name}}',
  'setup.studio.privacy.never': 'Never kept, never sent on',
  'setup.studio.privacy.phones_why': 'Written in the margin, not part of any entry.',
  'setup.studio.privacy.id_why': 'An ID number is never kept, from any source.',
  'setup.studio.privacy.keep_phones': 'Keep the phone numbers',
  'setup.studio.privacy.kept': 'What was kept',
  'setup.studio.role.title': 'What is this to you?',
  'setup.studio.role.mine_sub': 'Mine. Use its facts and data.',
  'setup.studio.role.like_sub': 'One I like. Take the shape only, none of its facts.',
  'setup.studio.role.reference_sub': 'The app you already use. OS looks it up and sets up the same parts.',
  'setup.studio.role.unsure_q': 'Is {{name}} yours, or one you like?',
  'setup.studio.role.not_kept': 'Not kept: its names, prices and phone numbers',
  'setup.studio.link.password_label': 'Password',
  'setup.studio.link.password_once': 'Used once to open it, never stored.',
  'setup.studio.link.unlock': 'Open it',
  'setup.studio.link.password_wrong_one': 'That password did not open it. {{count, number}} try left.',
  'setup.studio.link.password_wrong_other': 'That password did not open it. {{count, number}} tries left.',
  'setup.studio.link.password_save': 'Open it, save it without a password, and add it again.',
  'setup.studio.conflict.title': 'Two sources disagree',
  'setup.studio.conflict.later': 'Decide later',
  'setup.studio.conflict.on_card': 'Sources disagree',
  'setup.studio.conflict.you_said': 'You said',
  'setup.studio.conflict.likely': 'Likely',
  'setup.studio.start.a.lede': 'Say what you sell and what you keep track of, in your own words. I draw your workspace as you talk. Nothing is made until you press Build.',
  'setup.studio.start.down.body': 'What you wrote is kept, and goes out as soon as OS answers. Or set up your workspace by hand.',
  'setup.studio.start.down.by_hand': 'Set it up by hand',
  'setup.studio.start.down.not_sent': 'Not sent',
  'setup.studio.start.down.retrying': 'Trying again in {{s, number}} s',
  'setup.studio.start.invite_code': 'Joining someone else’s workspace? Use your invite code',
  // The studio's head (5.7): what is saved, the readiness signal and the one Build.
  // "Not saved yet" says what the head keeps doing without a first person (writing.md).
  'setup.studio.bar.saved': 'Saved · version {{n, number}}',
  'setup.studio.bar.saving': 'Saving…',
  'setup.studio.bar.offline': 'Not saved yet · trying again',
  'setup.studio.bar.as_you_talk': 'Saved as you talk',
  'setup.studio.bar.versions': 'Earlier versions',
  'setup.studio.ready.not': 'Not ready',
  'setup.studio.ready.needs_name': 'needs a name',
  'setup.studio.ready.needs_record': 'needs something to write in',
  'setup.studio.ready.needs_fix': 'needs a fix',
  'setup.studio.ready.name_taken': 'the name is taken',
  'setup.studio.ready.check_one': '{{count, number}} thing to check',
  'setup.studio.ready.check_other': '{{count, number}} things to check',
  'setup.studio.ready.building': 'Building · {{done, number}} of {{total, number}}',
  'setup.studio.build.why_off': 'Build turns on once your business has a name.',
  'setup.studio.build.why_off_record': 'Build turns on once there is something to write in.',
  'setup.studio.build.why_off_fix': 'Build turns on once the marked part is fixed.',
  'setup.studio.build.building': 'Building…',
  'setup.studio.composer.placeholder_scoped': 'Ask about {{name}}, or tell OS what to change',
  // The conversation's pieces (5.8): the person's bubble, the working row, receipts, lookups, push-backs, not yet.
  'setup.studio.q.about': 'About {{name}}',
  'setup.studio.working.stop': 'Stop',
  // A turn's steps folded into one row, closed by default (Devendra, 16 Oct), and the line that closes the newest reply.
  'setup.studio.steps.count_one': '{{count, number}} step',
  'setup.studio.steps.count_other': '{{count, number}} steps',
  'setup.studio.steps.at': 'Step {{n, number}}',
  'setup.studio.steps.show': 'Show the steps',
  'setup.studio.steps.hide': 'Hide the steps',
  // The working line names what OS is doing at that moment (Devendra, 16 Oct): drawing only while new parts are drawn.
  'setup.studio.working.thinking': 'Thinking…',
  'setup.studio.working.looking': 'Looking up {{topic}}…',
  'setup.studio.working.looking_web': 'Looking it up…',
  'setup.studio.working.looking_app': 'Looking up the app you use…',
  'setup.studio.working.reading': 'Reading {{source}}…',
  'setup.studio.working.adding': 'Drawing {{name}}…',
  'setup.studio.working.on': 'Changing {{name}}…',
  'setup.studio.working.checking': 'Checking your setup…',
  'setup.studio.receipt.added': 'Added {{what}}',
  'setup.studio.receipt.changed': 'Changed {{what}}',
  'setup.studio.receipt.left': 'Left out {{what}}',
  'setup.studio.receipt.restored': 'Back to version {{n, number}}',
  'setup.studio.receipt.restored_start': 'Back to the start',
  'setup.studio.receipt.start': 'The start',
  'setup.studio.receipt.version': 'Version {{n, number}}',
  'setup.studio.receipt.show': 'Show on the canvas',
  'setup.studio.receipt.decided': 'You let OS decide.',
  'setup.studio.receipt.more_one': 'and {{count, number}} more',
  'setup.studio.receipt.more_other': 'and {{count, number}} more',
  'setup.studio.receipt.fields_one': '{{count, number}} field',
  'setup.studio.receipt.fields_other': '{{count, number}} fields',
  'setup.studio.looked.title': 'Looked up: {{topic}}',
  'setup.studio.looked.looking': 'Looking up: {{topic}}',
  'setup.studio.looked.sources_one': '{{count, number}} source',
  'setup.studio.looked.sources_other': '{{count, number}} sources',
  'setup.studio.push.to_check': 'To check',
  'setup.studio.push.on': 'On {{name}}',
  'setup.studio.push.picked': 'You picked “{{fix}}”',
  'setup.studio.push.checked': 'Checked',
  'setup.studio.check.checked_off': 'Checked off',
  'setup.studio.check.still': 'Still to check',
  'setup.studio.check.reply': 'Reply in chat',
  'setup.studio.check.chip': 'Answering “{{title}}”',
  'setup.studio.check.placeholder': 'Answer it in your own words',
  'setup.studio.notyet.title': 'Not in RecordOS yet',
  'setup.studio.notyet.fit': 'Closest fit: {{fit}}',
  'setup.studio.could_not.title': 'Could not: {{change}}',
  'setup.studio.could_not.bare': 'Could not make this',
  'setup.studio.fail.lost_thread': 'I lost my train of thought. Your setup is as it was.',
  // The question card, alone and in a set (5.9).
  'setup.studio.q.decide': 'Decide for me',
  'setup.studio.q.other_placeholder': 'Say it your way',
  'setup.studio.q.send_n_one': 'Send {{count, number}}',
  'setup.studio.q.send_n_other': 'Send {{count, number}}',
  // Under a question: whether it takes one answer or several; Send stays greyed until one is ticked.
  'setup.studio.q.pick_single': 'Pick one',
  'setup.studio.q.pick_any': 'Pick any',
  'setup.studio.q.send': 'Send',
  'setup.studio.q.of': '{{at, number}} of {{total, number}}',
  'setup.studio.q.prev_q': 'Previous question',
  'setup.studio.q.next_q': 'Next question',
  'setup.studio.q.skip': 'Skip for now',
  'setup.studio.q.done': 'Done',
  'setup.studio.q.yours': 'Your answer. Pick another to change it.',
  'setup.studio.q.skipped': 'Skipped',
  'setup.studio.q.skipped_note_one': '{{count, number}} skipped · OS asks again before Build',
  'setup.studio.q.skipped_note_other': '{{count, number}} skipped · OS asks again before Build',
  'setup.studio.q.skipped_line': 'Skipped: {{q}}',
  'setup.studio.q.back_to_it': 'Back to it',
  // "N things to check", the side sheet (5.10). Its line does not say "OS found": OS is never a second party (one-coach).
  'setup.studio.checks.desc': 'Found while you talked. Each has a fix ready; leave any you do not need.',
  'setup.studio.checks.foot': 'Build works with these open. It lists them first.',
  // Earlier versions (5.11): the list, looking at one, going back.
  'setup.studio.versions.now': 'Now',
  'setup.studio.versions.looking': 'Looking',
  'setup.studio.versions.look': 'Look at this version',
  'setup.studio.versions.kept': 'Every version is kept. Going back makes a new one, so you can undo it.',
  'setup.studio.versions.previewing': 'Looking at version {{n, number}} · {{when}}',
  'setup.studio.versions.previewing_start': 'Looking at the start · {{when}}',
  'setup.studio.versions.back_now': 'Back to now',
  // Narrow windows (5.12): two tabs under 1024, the same words at every narrow width.
  'setup.studio.narrow.talk': 'Talk',
  'setup.studio.narrow.canvas': 'Your workspace',
  'setup.studio.narrow.menu': 'More: earlier versions, leave setup',
  'setup.studio.narrow.selected': 'Selected on your workspace',
  'setup.studio.narrow.check_short_one': '{{count, number}} to check',
  'setup.studio.narrow.check_short_other': '{{count, number}} to check',
  'setup.studio.another.fresh': 'Start fresh',
  'setup.studio.another.fresh_sub': 'An empty draft. Tell OS about this one from the start.',
  'setup.studio.another.lede': 'Start from what you made for {{name}}, or start fresh.',
  'setup.studio.another.like': 'Start like {{name}}',
  'setup.studio.another.like_sub': 'Its records, lists, links, cards and roles, as a draft you can change.',
  'setup.studio.another.separate': 'A separate workspace: its own entries and its own team. No entries and no people come over.',
  'setup.studio.another.title': 'Set up your next workspace',
  'setup.studio.another.placeholder': 'What is different about this one?',
  // A refused Start like (Q8 of docs/plans/qa-fixes-1.md): the start page says why, and Start fresh sends the line kept.
  'setup.studio.another.refused_not_owner': 'Only the person who set up {{name}} can start one like it, so nothing was made. Start fresh instead.',
  'setup.studio.another.refused_not_in': 'You are not in {{name}}, so a new workspace cannot start like it. Nothing was made. Start fresh instead.',
  'setup.studio.another.refused_not_copied': 'The setup of {{name}} could not be copied, so nothing was made. Start fresh instead.',
  'setup.studio.files.photo_n': 'Photo {{n, number}}',
  'setup.studio.files.photos_one': '{{count, number}} photo',
  'setup.studio.files.photos_other': '{{count, number}} photos',
  'setup.studio.files.read_n': '{{n, number}} read',
  'setup.studio.phone.connected': 'Connected · {{name}}',
  'setup.studio.phone.copy': 'Copy link',
  'setup.studio.phone.desc': 'Scan the code with your phone’s camera. Each photo you take arrives here.',
  'setup.studio.phone.device_android': 'Android phone',
  'setup.studio.phone.device_iphone': 'iPhone',
  'setup.studio.phone.device_any': 'your phone',
  'setup.studio.phone.done_n_one': 'Done · {{count, number}} photo',
  'setup.studio.phone.done_n_other': 'Done · {{count, number}} photos',
  'setup.studio.phone.empty': 'Photos you take appear here, one by one.',
  'setup.studio.phone.failed_n': '{{n, number}} couldn’t read',
  'setup.studio.phone.more': '+{{n, number}}',
  'setup.studio.phone.no_install': 'Any phone with a camera. Nothing to install.',
  'setup.studio.phone.reading_n': '{{n, number}} reading',
  'setup.studio.phone.title': 'Take photos with your phone',
  'setup.studio.phone.waiting': 'Waiting for your phone',
  'setup.studio.phone_page.ended': 'This link has ended. Make a new one on your computer.',
  'setup.studio.phone_page.lede': 'Each photo goes to your computer as you take it.',
  'setup.studio.phone_page.sent_n_one': '{{count, number}} sent to your computer',
  'setup.studio.phone_page.sent_n_other': '{{count, number}} sent to your computer',
  'setup.studio.phone_page.take': 'Take a photo',
  'setup.studio.phone_page.title': 'Take a photo of each page',
  'setup.studio.count.cards_one': '{{count, number}} $t(glossary.insight_one)',
  'setup.studio.count.cards_other': '{{count, number}} $t(glossary.insight_other)',
  'setup.studio.count.roles_one': '{{count, number}} role',
  'setup.studio.count.roles_other': '{{count, number}} roles',
  'setup.studio.count.links_one': '{{count, number}} link',
  'setup.studio.count.links_other': '{{count, number}} links',
  'setup.studio.count.lists_one': '{{count, number}} list',
  'setup.studio.count.lists_other': '{{count, number}} lists',
  'setup.studio.count.records_one': '{{count, number}} record',
  'setup.studio.count.records_other': '{{count, number}} records',
  'setup.studio.device.computer': 'computer',
  'setup.studio.device.phone': 'phone',
  'setup.studio.resume.expired_canvas': 'Nothing drawn yet',
  'setup.studio.resume.expired_canvas_sub': 'Start from what you shared and OS draws it again here, then asks only what it cannot read.',
  'setup.studio.resume.expired_desc': 'The conversation and the drawing were cleared on {{day}}. The files and links you shared are still here, so OS can start again from them.',
  'setup.studio.resume.expired_fresh': 'Start with nothing',
  'setup.studio.resume.expired_from': 'Start from these',
  'setup.studio.resume.expired_title': 'Your last setup was kept for 7 days, and has gone',
  'setup.studio.resume.over_body': 'The conversation and the drawing are cleared. The files and links you shared stay for the new start.',
  'setup.studio.resume.over_confirm': 'Start over',
  'setup.studio.resume.over_title': 'Start over?',
  'setup.studio.resume.picked_up': 'Picked up where you left off · {{when}}',
  'setup.studio.start.resume.carry_on': 'Carry on',
  'setup.studio.start.resume.keep': 'Kept until {{date}}',
  'setup.studio.start.resume.over': 'Start over',
  'setup.studio.start.resume.over_note': 'Start over asks first, and keeps your files for the new start.',
  'setup.studio.start.resume.placeholder': 'Answer here, or tell OS what changed',
  'setup.studio.start.resume.when': 'Version {{n, number}} · {{when}}, on your {{device}}',
  'setup.studio.start.resume.where': 'Left off at: {{topic}}',
  'setup.studio.invite.lede': '{{inviter}} set it up already, so there is nothing for you to set up.',
  'setup.studio.invite.os_lang': 'OS talks with you in {{language}}',
  'setup.studio.invite.own': 'Set up a workspace of my own',
  'setup.studio.invite.title': 'You are in {{workspace}}',
  'setup.studio.invite.what': 'What you can do, as {{role}}',
  'setup.studio.inspect.adds': 'Writes in it',
  'setup.studio.inspect.sees': 'Sees it',
  'setup.studio.versions.failed': 'The versions could not be read',
  'setup.studio.versions.older': 'Show older versions',
  // ------------------------------------------------------- the setup canvas (Phase 6)
  // The draft drawn as a map: each part's card, its lines, where it came from.
  'setup.studio.anatomy.only_when': 'Only when {{cond}}',
  'setup.studio.canvas.from': 'From {{name}}',
  'setup.studio.canvas.from_records': 'From {{a}} and {{b}}',
  'setup.studio.canvas.rows_from_one': '{{count, number}} row from {{file}}',
  'setup.studio.canvas.rows_from_other': '{{count, number}} rows from {{file}}',
  'setup.studio.canvas.rows_from_some': 'Rows from {{file}}',
  'setup.studio.canvas.files_one': '{{count, number}} file',
  'setup.studio.canvas.files_other': '{{count, number}} files',
  'setup.studio.canvas.app_file': 'the app you use',
  'setup.studio.canvas.your_file': 'your file',
  'setup.studio.canvas.sheet': 'One tap per person, each day',
  'setup.studio.canvas.ghost_sub': 'Not in RecordOS yet',
  'setup.studio.canvas.drawing': 'Drawing {{name}}…',
  'setup.studio.canvas.suggested': 'Suggested',
  'setup.studio.canvas.add_it': 'Add it',
  'setup.studio.canvas.owner': 'Owner',
  'setup.studio.canvas.reach_all': 'Everything',
  'setup.studio.canvas.shows_when': '{{what}}, {{when}}',
  'setup.studio.links.label_in': 'Adds to {{record}}',
  'setup.studio.links.label_out': 'Takes from {{record}}',
  'setup.studio.links.picks_each': 'Each {{noun}} picks one {{what}}',
  'setup.studio.links.reads_rate': 'reads its {{what}}',
  'setup.studio.links.insp.sentence': 'In a sentence',
  'setup.studio.links.insp.parts': 'Part by part',
  'setup.studio.links.insp.when': 'When',
  'setup.studio.links.insp.only_if': 'Only if',
  'setup.studio.links.insp.then': 'Then',
  'setup.studio.links.insp.is_saved': 'is saved',
  'setup.studio.links.insp.add_row': 'add a row to',
  'setup.studio.links.insp.writes': 'What it writes in {{name}}',
  'setup.studio.links.insp.example': 'An example run',
  'setup.studio.links.insp.not_this_time': 'Not this time: {{why}}',
  'setup.studio.links.insp.starts': 'When it starts',
  'setup.studio.links.insp.runs': 'Runs on its own',
  'setup.studio.links.insp.runs_sub': 'From the first entry after Build',
  'setup.studio.links.insp.paused': 'Paused until you check it',
  'setup.studio.links.insp.paused_sub': 'Nothing is written while it is paused',
  'setup.studio.links.insp.turn_on': 'Turn it on',
  'setup.studio.links.insp.would': 'Would have written',
  'setup.studio.links.insp.of_picked': 'Asked of the {{what}} it picks',
  'setup.studio.links.insp.add_cond': 'Add a condition',
  'setup.studio.links.insp.add_or': 'Or…',
  'setup.studio.links.insp.and': 'and',
  'setup.studio.links.insp.or': 'or',
  'setup.studio.links.insp.cards': 'Cards it changes',
  'setup.studio.links.insp.from': 'Where it came from',
  'setup.studio.links.fill.field': 'From a field',
  'setup.studio.links.fill.fixed': 'Always the same',
  'setup.studio.links.fill.through': 'Read through a pick',
  'setup.studio.links.fill.worked': 'Worked out',
  'setup.studio.links.paused_tag': 'Paused',
  'setup.studio.links.bar.on': 'On',
  'setup.studio.links.bar.or_hint': 'Tick two: it runs on either',
  'setup.studio.links.by_you': 'Done by you, straight away. No OS needed.',
  'setup.studio.links.lifted_fill': 'Every {{from}} $t(glossary.entry_one) now lands in {{to}} with {{field}} {{value}}, also one with {{field}} {{others}}. To keep each one’s own {{field}}, copy {{field}} from {{from}} instead, or add the condition back.',
  'setup.studio.links.add_cond_lead': 'Add a condition to {{name}}:',
  'setup.studio.inspector.always': 'Always “{{value}}”',
  'setup.studio.links.asking_tag': 'OS is asking',
  'setup.studio.links.ghost.link': 'Not a link RecordOS can make yet',
  'setup.studio.prov.app': 'From the app you use · {{part}}',
  'setup.studio.prov.photo': 'From your {{what}} photo',
  'setup.studio.prov.looked': 'Looked up · {{site}}',
  'setup.studio.prov.maps': 'From Google Maps',
  'setup.studio.prov.said': 'From what you said',
  'setup.studio.prov.informed': 'Informed by {{name}}',
  'setup.studio.prov.shared': 'From what you shared',
  // A field's type in shape words, one per AUTHOR_TYPES.
  'setup.studio.type.text': 'Words',
  'setup.studio.type.long_text': 'A few lines of words',
  'setup.studio.type.number': 'A number',
  'setup.studio.type.money': 'An amount',
  'setup.studio.type.quantity': 'A count',
  'setup.studio.type.date': 'A date',
  'setup.studio.type.datetime': 'A date and time',
  'setup.studio.type.boolean': 'Yes or no',
  'setup.studio.type.select': 'One of: {{options}}',
  'setup.studio.type.multi_select': 'Some of: {{options}}',
  'setup.studio.type.reference': 'Picks from {{list}}',
  'setup.studio.type.attachment': 'A photo',
  'setup.studio.type.phone': 'A phone number',
  'setup.studio.type.location': 'A place',
  'setup.studio.type.formula': 'Worked out: {{how}}',
  'setup.studio.type.lines': 'A few lines: {{fields}}',
  'setup.studio.type.rollup': 'A total of other $t(glossary.entry_other)',
  'setup.studio.type.series': 'A number given in turn',
  'setup.studio.type.how_total': '{{what}}, added up',
  'setup.studio.canvas.fit': 'Fit',
  'setup.studio.canvas.zoom': 'Zoom',
  'setup.studio.canvas.zoom_in': 'Zoom in',
  'setup.studio.canvas.zoom_out': 'Zoom out',
  'setup.studio.canvas.map': 'Map of the canvas',
  'setup.studio.zoom.fit_all': 'Fit everything',
  'setup.studio.zoom.fit_selected': 'Fit to what is selected',
  'setup.studio.zoom.hint': 'Drag the dotted ground to move around · Ctrl and scroll to zoom',
  'setup.studio.zoom.show_ghosts': 'Show what RecordOS cannot do yet',
  'setup.studio.zoom.show_map': 'Show the map',
  // Cards moved by hand (canvas-layout-and-app-polish 3.1, 3.2): the setup canvas and the Business map say the same.
  'card_layout.menu': 'Layout',
  'card_layout.tidy': 'Tidy up',
  'card_layout.reset': 'Reset layout',
  'card_layout.reset_ask': 'Reset layout…',
  'card_layout.reset_title': 'Reset the layout?',
  'card_layout.reset_body': 'Every card you moved goes back to the place it is given for you.',
  'card_layout.not_saved': 'Where you moved the cards is not saved yet.',
  'setup.studio.canvas.more_one': '+ {{count, number}} more',
  'setup.studio.canvas.more_other': '+ {{count, number}} more',
  'setup.studio.canvas.who': 'Who uses it',
  // The canvas head (6.6): folded to one line, two summary chips that open their strips.
  'setup.studio.canvas.head.less': 'Fold them into one line',
  'setup.studio.canvas.head.more': 'Show $t(glossary.insight_other) and people',
  'setup.studio.built.sub_made': 'Made.',
  'setup.studio.built.sub_lists': 'Your lists are filled in.',
  'setup.studio.built.sub_coming': 'Rows are coming in.',
  'setup.studio.built.sub_waiting': 'Some files’ rows wait for your review.',
  'setup.studio.canvas.head.new_one': '{{count, number}} new',
  'setup.studio.canvas.head.new_other': '{{count, number}} new',
  'setup.studio.canvas.head.people_one': '{{count, number}} person',
  'setup.studio.canvas.head.people_other': '{{count, number}} people',
  'setup.studio.canvas.head.tops_one': '{{count, number}} $t(glossary.insight_one)',
  'setup.studio.canvas.head.tops_other': '{{count, number}} $t(glossary.insight_other)',
  'setup.studio.inspect.adds_entry': 'Adds an entry there',
  'setup.studio.inspect.columns': 'What each name holds',
  'setup.studio.inspect.comes_from': 'Comes in from {{name}}',
  'setup.studio.inspect.does': 'What it does',
  'setup.studio.inspect.fill': 'What you fill in',
  'setup.studio.inspect.from': 'Where it came from',
  'setup.studio.inspect.goes_to': 'Goes into {{name}}',
  'setup.studio.inspect.lines': 'Lines inside',
  'setup.studio.inspect.links_short': 'Links in and out',
  'setup.studio.inspect.names': 'Names in it',
  'setup.studio.inspect.on_off': 'On or paused',
  'setup.studio.inspect.only_if': 'Only if',
  'setup.studio.inspect.picked_in': 'Picked in',
  'setup.studio.inspect.picks': 'Picks from {{name}}',
  'setup.studio.inspect.reads_from': 'Reads from',
  'setup.studio.inspect.shows': 'What it shows',
  'setup.studio.inspect.tops': 'Insights',
  'setup.studio.inspect.what': 'What it is',
  'setup.studio.inspect.what_list': 'One row for each {{noun}}',
  'setup.studio.inspect.what_record': '{{kind}}: one {{noun}} on each line',
  'setup.studio.inspect.when': 'When {{name}}',
  'setup.studio.inspect.when_saved': 'When an entry is saved',
  'setup.studio.inspect.who': 'Who sees it',
  'setup.studio.inspector.biggest_first': 'Biggest first',
  'setup.studio.inspector.can_do': 'What {{name}} can do',
  'setup.studio.inspector.comes_in_one': '{{count, number}} name comes in with the workspace at Build',
  'setup.studio.inspector.comes_in_other': '{{count, number}} names come in with the workspace at Build',
  'setup.studio.inspector.filled_by': 'What fills it',
  'setup.studio.inspector.for_each': 'For each',
  'setup.studio.inspector.pause': 'Pause this link',
  'setup.studio.inspector.reads': 'Reads',
  'setup.studio.inspector.worked_out': 'How it is worked out',
  'setup.studio.inspect.state.draft': 'Draft',
  'setup.studio.inspector.see_as': 'See it as {{name}}',
  'setup.studio.inspect.hidden': 'Does not see it',
  'setup.studio.inspector.back_all': 'Back to everyone',
  'setup.studio.inspector.seen_as': 'Seen as {{name}}',
  'setup.studio.select.leave_out': 'Leave it out',
  'setup.studio.narrow.details': 'Details',
  'setup.studio.narrow.selected_names': 'Selected on your workspace: {{names}}',
  'setup.studio.direct.by_you': 'Done by you, straight away',
  'setup.studio.direct.failed': 'Not saved. Your change is undone; try again.',
  'setup.studio.direct.moved': 'OS changed this a moment ago. Look again and try once more.',
  // A check's fix pressed after the draft moved, when the server finds the check no longer open (R6 of docs/plans/qa-fixes-2.md).
  'setup.studio.direct.already': 'That is already done, so it is checked off.',
  // A Build has the setup (409 SESSION_NOT_DRAFTING): never "try again", since it cannot work.
  'setup.studio.direct.not_drafting': 'This setup is being built, or was built already, so it cannot change now.',
  'setup.studio.direct.offline': 'Not saved: no connection. Your change is undone.',
  'setup.studio.direct.goes': 'Goes with it',
  'setup.studio.direct.leave_title': 'Leave out {{name}}?',
  'setup.studio.direct.stays': 'Stays',
  'setup.studio.direct.still_picks': '{{name}} still picks from it',
  'setup.studio.direct.undo_hint': 'You can undo it from the conversation.',
  'setup.studio.direct.rename_keys': 'Enter to keep · Esc to cancel',
  'setup.studio.direct.rename_label': 'New name for {{name}}',
  'setup.studio.direct.taken': '{{name}} is already a {{kind}}',
  'setup.studio.direct.kind.card': 'card',
  'setup.studio.direct.kind.field': 'field',
  'setup.studio.direct.kind.link': 'link',
  'setup.studio.direct.kind.list': 'list',
  'setup.studio.direct.kind.record': 'record',
  'setup.studio.direct.kind.role': 'role',
  'setup.studio.versions.by_os': 'OS',
  'setup.studio.versions.by_you_check': 'You, from a check',
  'setup.studio.versions.short': 'v{{n}}',
  'setup.studio.versions.start': 'Start',
  'setup.studio.app.add_it': 'Add {{theirs}}, as in the app I use',
  'setup.studio.app.differs': 'Works differently',
  'setup.studio.app.how_sure_one': 'From {{count, number}} page · not checked by us',
  'setup.studio.app.how_sure_other': 'From {{count, number}} pages · not checked by us',
  'setup.studio.app.keep': 'Keep',
  'setup.studio.app.like_it': 'You want something like it, so I took its shape for your sites, not its words.',
  'setup.studio.app.like_short': 'You want something like it',
  'setup.studio.app.map_ours': 'In your setup',
  'setup.studio.app.map_theirs': 'In the app you use',
  'setup.studio.app.map_title': 'From the app you use',
  'setup.studio.app.map_title_named': 'From the app you named',
  'setup.studio.app.more_parts_one': '{{count, number}} more part you did not pick: {{names}}',
  'setup.studio.app.more_parts_other': '{{count, number}} more parts you did not pick: {{names}}',
  'setup.studio.app.same': 'Same',
  'setup.studio.app.what_differs': 'What differs',
  'setup.studio.app.closest_fit': 'Closest fit',
  'setup.studio.app.ask_instead': 'Ask me about it instead',
  'setup.studio.app.just_ask': 'Just ask me',
  'setup.studio.app.send_export': 'Send an export',
  'setup.studio.app.send_shot': 'Send a screenshot',
  'setup.studio.app.see_map': 'See the mapping',
  'setup.studio.app.export_read': 'An export from the app you use',
  'setup.studio.app.screen_read': 'A screen from the app you use',
  'setup.studio.app.door': 'Which app do you use now?',
  'setup.studio.app.door_hint': 'OS looks it up on the web and sets up the same parts here.',
  'setup.studio.build.title': 'Build {{name}}',
  'setup.studio.sheet.into': '{{what}} into {{record}}',
  'setup.studio.sheet.name_from': 'From {{source}}',
  'setup.studio.sheet.rows_one': '{{list}} · {{count, number}} row',
  'setup.studio.sheet.rows_other': '{{list}} · {{count, number}} rows',
  'setup.studio.sheet.record_rows_one': '{{record}} · {{count, number}} row',
  'setup.studio.sheet.record_rows_other': '{{record}} · {{count, number}} rows',
  'setup.studio.sheet.title_unnamed': 'Build your workspace',
  'setup.studio.build.after': 'Waits for your review after Build',
  'setup.studio.build.after_review': 'Once built, you see how each file’s columns match, then bring its rows in, fix a match or skip it. You can undo an import for 7 days.',
  'setup.studio.build.anyway': 'Build anyway',
  'setup.studio.build.checks_one': '{{count, number}} thing to check first',
  'setup.studio.build.checks_other': '{{count, number}} things to check first',
  'setup.studio.build.desc': 'Everything on the canvas is made in one go. You can change any of it later.',
  'setup.studio.build.for': 'Who it is for',
  'setup.studio.build.invite_after': 'You invite them after Build',
  'setup.studio.build.leave_it': 'Leave it',
  'setup.studio.build.makes': 'What Build makes',
  'setup.studio.build.rule': 'Nothing is created until you click Build.',
  'setup.studio.build.with': 'Comes in with Build',
  'setup.studio.sheet.ask_again': 'Ask again',
  'setup.studio.sheet.if_left': 'If you leave it: {{what}}',
  'setup.studio.sheet.kept_note': 'Kept as a note, not built',
  'setup.studio.sheet.left_count_one': '{{count, number}} thing left as it is',
  'setup.studio.sheet.left_count_other': '{{count, number}} things left as they are',
  'setup.studio.sheet.left_title': 'Left as it is',
  'setup.studio.sheet.more': 'Show everything',
  'setup.studio.sheet.name_label': 'What is your business called?',
  'setup.studio.sheet.nothing_line': 'Every open thing is answered or left on purpose.',
  'setup.studio.sheet.nothing_to_check': 'Nothing left to check',
  'setup.studio.sheet.voice_hint': 'Have a look, then click Build',
  'setup.studio.refused.divider': 'Not built · nothing was made',
  'setup.studio.refused.fix_title': 'My fix',
  'setup.studio.refused.make': 'Make this change',
  'setup.studio.refused.nothing_until': 'Nothing changes until you say so.',
  'setup.studio.refused.or_name': 'Or give this one another name',
  'setup.studio.build.step.records': 'Records',
  'setup.studio.build.step.lists': 'Lists, with their names',
  'setup.studio.build.step.cards': 'Insights',
  'setup.studio.build.step.links': 'Links',
  'setup.studio.build.step.roles': 'Roles',
  'setup.studio.refused.why_off': 'Fix the marked link first.',
  'setup.studio.build.step.data': 'Then your entries, as an import',
  'setup.studio.refused.name_taken': 'You made a workspace called {{name}} a few minutes ago, on another device.',
  'setup.studio.build.steps': 'Making your workspace',
  'setup.studio.building.leave_safe': 'You can leave this page. Building carries on, and {{name}} will be here when you come back, on any device.',
  'setup.studio.building.of': '{{done, number}} of {{total, number}}',
  'setup.studio.building.promise': 'If any part cannot be built, nothing is made and your setup stays as it is.',
  'setup.studio.failed.body': 'The connection dropped before anything was made. Nothing is half-made, and your setup is saved.',
  'setup.studio.failed.build_anyway': 'Build anyway',
  'setup.studio.failed.layout': 'Where you moved the cards did not save, so nothing was built yet. Your setup is saved.',
  'setup.studio.failed.layout_safe': 'Try again saves the cards first. Build anyway uses them as last saved.',
  'setup.studio.failed.safe': 'Safe to try again: it can never make two.',
  'setup.studio.failed.title': 'Not built yet',
  'setup.studio.failed.waiting': 'Waiting for the connection to come back',
  'setup.studio.refused.open_that': 'Open that one',
  'setup.studio.building.away': 'Built at {{time}}, while you were away',
  'setup.studio.built.made_all': 'Everything on the canvas is made.',
  'setup.studio.built.lists_in': '{{lists}} came in with it.',
  'setup.studio.built.lists_empty_one': '{{lists}} is still empty.',
  'setup.studio.built.lists_empty_other': '{{lists}} are still empty.',
  'setup.studio.built.coming_line': 'Coming in now: {{what}}.',
  'setup.studio.built.rows_into_one': '{{count, number}} row into {{record}}',
  'setup.studio.built.rows_into_other': '{{count, number}} rows into {{record}}',
  'setup.studio.built.not_in': 'Not brought in yet: {{files}}. Bring in your data shows where their rows can go.',
  'setup.studio.built.review_line': 'Waiting for your review in Bring in your data: {{what}}.',
  'setup.studio.building.leave': 'Leave',
  'setup.studio.building.leave_body': 'Nothing stops if you go. Building carries on without this page, and {{name}} will be waiting for you on any device. If any part cannot be built, nothing is made and your setup stays as it is.',
  'setup.studio.building.leave_title': 'Leave while it builds?',
  'setup.studio.building.stay': 'Stay here',
  'setup.studio.first.bill': 'Write today’s first {{noun}}',
  // Two of the setup guide's first-entry rows whose records say the same noun (two records of plain entries) name their record, so no two rows read alike.
  'setup.studio.first.bill_in': 'Write today’s first {{noun}} in {{record}}',
  'setup.studio.first.bill_line': '{{record}} · rates fill in',
  'setup.studio.first.see_card': 'See {{card}}',
  'setup.studio.build.made': 'What was made',
  'setup.studio.build.next.invite_btn': 'Invite',
  'setup.studio.built.coming': 'Coming in now',
  'setup.studio.built.review': 'Waits for your review',
  'setup.studio.built.review_note': 'See how each file’s columns match and which rows will not fit, then bring it in, fix a match or skip it.',
  'setup.studio.built.convo': 'Show the conversation',
  'setup.studio.built.first': 'Your first day',
  'setup.studio.built.from': 'From {{source}} · {{done, number}} of {{total, number}}',
  'setup.studio.built.in_one': 'In · {{count, number}}',
  'setup.studio.built.in_other': 'In · {{count, number}}',
  'setup.studio.built.later': 'Change anything later from Workspace details.',
  'setup.studio.built.show_made': 'Show what was made',
  'setup.studio.built.team': 'Invite your team',
  'setup.studio.invite.add': 'Add someone else',
  'setup.studio.invite.copy': 'Copy invite link',
  'setup.studio.invite.desc': 'Each person sees only what their role allows. They get a link to join {{name}}.',
  'setup.studio.invite.later': 'Not now',
  'setup.studio.invite.name': 'Name',
  'setup.studio.invite.phone': 'Phone number',
  'setup.studio.invite.role': 'Role',
  'setup.studio.invite.send_one': 'Send {{count, number}} invite',
  'setup.studio.invite.send_other': 'Send {{count, number}} invites',
  'setup.studio.day.coming': 'Your entries are coming in',
  'setup.studio.day.coming_line': '{{done, number}} of {{total, number}} · undo any time',
  'setup.studio.day.in': 'Your entries are in',
  'setup.studio.day.in_line': '{{saved, number}} saved, {{aside, number}} to check',
  'setup.studio.day.invite': 'Invite {{names}}',
  'setup.studio.day.invite_line': '{{roles}}',
  'setup.studio.day.map': 'Meet your Business map',
  'setup.studio.day.map_line': 'The canvas you built, with live numbers',
  'setup.studio.day.data': 'Bring in your data',
  'setup.studio.day.data_line_one': '{{count, number}} file waits for you to bring it in or skip it',
  'setup.studio.day.data_line_other': '{{count, number}} files wait for you to bring them in or skip them',
  'setup.studio.day.data_btn': 'Review them',
  // Bring in your data (step 3.2 of docs/plans/setup-file-data.md): the review step after Build.
  'setup.review.meta': 'The rows in the files you set this workspace up with. Bring each in, fix a match first, or skip it. You can undo an import for 7 days.',
  'setup.review.rows_into_one': '{{count, number}} row into {{record}}',
  'setup.review.rows_into_other': '{{count, number}} rows into {{record}}',
  'setup.review.no_target_one': '{{count, number}} row. Nothing in the setup matched it: pick where it goes.',
  'setup.review.no_target_other': '{{count, number}} rows. Nothing in the setup matched them: pick where they go.',
  'setup.review.goes_to': 'Goes to',
  'setup.review.goes_to_for': 'Where the rows of {{file}} go',
  'setup.review.pick': 'Pick a record or list',
  'setup.review.list': 'List',
  'setup.review.columns': 'How the columns match',
  'setup.review.not_kept': 'Not kept',
  'setup.review.sample': 'First rows of {{file}}',
  'setup.review.checking': 'Checking every row…',
  'setup.review.fit_all_one': '{{count, number}} row fits as it stands.',
  'setup.review.fit_all_other': 'All {{count, number}} rows fit as they stand.',
  'setup.review.fit_some_one': '{{fits, number}} of {{count, number}} row fits as it stands.',
  'setup.review.fit_some_other': '{{fits, number}} of {{count, number}} rows fit as they stand.',
  'setup.review.unfit_one': '{{count, number}} row: {{why}}',
  'setup.review.unfit_other': '{{count, number}} rows: {{why}}',
  // A name not on its list yet: the import adds it to the list as the rows come in (R9 of qa-fixes-2).
  'setup.review.unfit_name_one': '{{count, number}} row: {{why}}. That name is added to its list as the row comes in.',
  'setup.review.unfit_name_other': '{{count, number}} rows: {{why}}. Those names are added to its list as the rows come in.',
  // The same, naming the list the names go to and, when it has any, the required fields each new name is left to fill (R9 of docs/plans/qa-fixes-2.md).
  'setup.review.unfit_name_to_one': '{{count, number}} row: {{why}}. That name is added to {{list}} as the row comes in.',
  'setup.review.unfit_name_to_other': '{{count, number}} rows: {{why}}. Those names are added to {{list}} as the rows come in.',
  'setup.review.unfit_name_fill_one': '{{count, number}} row: {{why}}. That name is added to {{list}} as the row comes in; {{fields}} to fill.',
  'setup.review.unfit_name_fill_other': '{{count, number}} rows: {{why}}. Those names are added to {{list}} as the rows come in; {{fields}} to fill.',
  'setup.review.unfit_note': 'A row that does not fit waits on Set aside for you, to fix or leave out. A name the import adds to its list first then fits.',
  'setup.review.unfilled': 'Every row needs {{fields}}, and no column fills it.',
  'setup.review.fit_failed': 'Couldn’t check the rows. They are checked again as they come in.',
  'setup.review.lists_first': 'Bring lists in first: the rows that name their items then fit.',
  'setup.review.bring': 'Bring it in',
  'setup.review.fix': 'Fix a match',
  'setup.review.skip': 'Skip',
  'setup.review.started': 'Coming in. You can undo it for 7 days.',
  'setup.review.see_import': 'See the import',
  'setup.review.skipped': 'Skipped. Its rows are not brought in.',
  'setup.review.bring_after_all': 'Bring it in after all',
  'setup.review.empty': 'Your setup files hold no rows to bring in.',
  'setup.review.waits_one': '{{count, number}} file waits for you',
  'setup.review.waits_other': '{{count, number}} files wait for you',
  'setup.review.all_decided': 'Every file is decided.',
  'setup.review.offer': 'The files you set this workspace up with hold rows that never came in: {{files}}. Bring them in? You see where each goes before anything is saved.',
  'setup.review.offer_more_one': '{{files}} and {{count, number}} more',
  'setup.review.offer_more_other': '{{files}} and {{count, number}} more',
  'setup.review.offer_yes': 'Review them',
  'setup.review.offer_no': 'No, thanks',
  'setup.studio.day.names': '{{rest}} and {{last}}',
  'setup.studio.day.set_up': 'Workspace set up',
  'setup.studio.day.guide': 'Setup guide',
  'setup.studio.day.invite_btn': 'Invite them',
  'setup.studio.day.meet': 'Meet it',
  'setup.studio.day.skip': 'Skip',
  'setup.studio.day.title': 'Setup guide · {{done, number}} of {{total, number}}',
  'setup.studio.entry.new': 'New {{noun}}',
  // Live mode in the studio (onboarding harness 7.2, Phase 7): the pill's detail while the drafter reads, the
  // working row, the mic and in-app-browser words, the canvas tag on a card OS points at, and a dropped call's reason.
  'setup.studio.voice.reading': 'reading {{what}}',
  'setup.studio.voice.reading_full': 'Reading {{what}}',
  'setup.studio.voice.pages': '{{n, number}} of {{total, number}} pages',
  'setup.studio.voice.waiting_type': 'waiting for you to type',
  'setup.studio.voice.why_mic': 'Your browser will ask for the microphone next. Allow it so I can hear you.',
  'setup.studio.voice.break': 'Taking a break? Your setup is kept.',
  'setup.studio.voice.inapp.title': 'This browser inside the app can’t use your mic',
  'setup.studio.voice.inapp.body': 'Open this page in your phone’s own browser to talk to OS. Typing works here, and your setup carries over.',
  'setup.studio.voice.inapp.open': 'Open in your browser',
  'setup.studio.voice.point.tag': 'OS is talking about this',
  'setup.studio.voice.reason.dropped': 'the connection dropped',
  // The studio's refusals in words (9.2, `studio-failure.ts`).
  'setup.studio.fail.part': 'That change to {{name}} could not be made. It is marked on the canvas.',
  'setup.studio.fail.parts_one': '{{count, number}} change could not be made. It is marked on the canvas.',
  'setup.studio.fail.parts_other': '{{count, number}} changes could not be made. They are marked on the canvas.',
  'setup.studio.fail.stale': 'Your setup changed in another place a moment ago. Here is the newest; say it again if it still applies.',
  // Why a change was refused, said under the button that made it (16 Oct): by the refusal's code, else the fallback; never a silent "Not saved yet".
  'setup.studio.refused.list_money': 'A $t(glossary.list_one) holds no money. A rate and a total go on the $t(glossary.entry_other) of a $t(glossary.record_one), not on the $t(glossary.row_other) of a $t(glossary.list_one).',
  'setup.studio.refused.lines_full': 'Each line already holds as many $t(glossary.field_other) as it can.',
  'setup.studio.refused.no_room': 'There is no room for more of that in this setup.',
  'setup.studio.refused.no_measure': 'That $t(glossary.record_one) has no money to add up.',
  'setup.studio.refused.other': 'OS could not make that change here.',
  'setup.studio.refused.hint': 'Tell OS in the chat what you want, and it finds a way that fits.',
  'setup.studio.refused.ask_os': 'Ask OS instead',
  // A Build refused with no fix, and its way on (16 Oct, Sharma Dairy): what could not be made, Leave it out and build, Ask OS.
  'setup.studio.refused.sheet_title': 'Not everything could be made',
  'setup.studio.refused.way_on': 'What to do now',
  'setup.studio.refused.leave_out_one': 'Leave it out and build',
  'setup.studio.refused.leave_out_other': 'Leave them out and build',
  'setup.studio.refused.ask_change': 'Ask OS to change it',
  'setup.studio.refused.ask_line': 'Build could not make this. Change it so it can be made: {{what}}',
  'setup.studio.refused.only_os': 'This can’t simply be left out, so OS changes it to fit.',
  'setup.studio.refused.folded_one': 'Build was refused: {{count, number}} thing could not be made',
  'setup.studio.refused.folded_other': 'Build was refused: {{count, number}} things could not be made',
  'setup.studio.start.resume.when_plain': 'Version {{n, number}} · {{when}}',
  'setup.studio.place.desc': 'A call with OS is running there. What changes there shows here as it happens.',
  'setup.studio.place.desc_typed': 'What changes there shows here as it happens.',
  'setup.studio.place.divider': 'On {{device}} · {{time}}',
  'setup.studio.place.divider_tab': 'In another tab · {{time}}',
  'setup.studio.place.move': 'Use it here',
  'setup.studio.place.move_note': 'The call on your phone ends. Nothing is lost.',
  'setup.studio.place.move_note_typed': 'The other place stops taking changes. Nothing is lost.',
  'setup.studio.place.now': 'now',
  'setup.studio.place.other_tab': 'This setup is open in another tab',
  'setup.studio.place.title': 'This setup is open on {{device}}',
  'setup.studio.place.your_phone': 'your phone',
  'setup.studio.place.edits_off': 'Changes happen in one place at a time.',
  'setup.studio.leave.delete': 'Delete this setup',
  'setup.studio.leave.delete_confirm': 'Delete it',
  'setup.studio.leave.delete_desc': 'The conversation, the drawing and the files you shared for it are removed. This cannot be undone.',
  'setup.studio.leave.delete_title': 'Delete this setup?',
  'setup.studio.leave.desc': 'Your setup is saved until {{day}}. Come back on this computer or your phone and carry on where you left off.',
  'setup.studio.leave.foot': 'Nothing is created until you click Build. You stay signed in.',
  'setup.studio.leave.kept_sources': 'What you shared',
  'setup.studio.leave.kept_sources_n_one': '{{count, number}} file or link',
  'setup.studio.leave.kept_sources_n_other': '{{count, number}} files and links',
  'setup.studio.leave.kept_talk': 'The conversation',
  'setup.studio.leave.kept_talk_n_one': '{{count, number}} message',
  'setup.studio.leave.kept_talk_n_other': '{{count, number}} messages',
  'setup.studio.leave.title': 'Leave setup for now?',
  'setup.studio.resume.door_drawn': 'Drawn so far',
  'setup.studio.waiting.carry_on': 'Carry on with {{name}}',
  'setup.studio.waiting.carry_on_sub': 'Opens it where you left off: the conversation, the drawing and what you shared.',
  'setup.studio.waiting.carry_on_unnamed': 'Carry on with it',
  'setup.studio.waiting.changed': 'Last changed {{when}}',
  'setup.studio.waiting.discard': 'Discard it and start new',
  'setup.studio.waiting.discard_sub': 'Removes it and the files shared for it, then starts from nothing.',
  'setup.studio.waiting.title': 'You have a setup in progress',
  'setup.studio.waiting.unnamed': 'An unnamed setup',
  'setup.studio.slow.queued': 'Waiting · goes next',
  'setup.studio.offline.cut': 'The connection dropped before OS finished. Nothing on the canvas changed.',
  'setup.studio.offline.desc': 'Everything up to version {{n, number}} is saved. What you type waits here and goes when you are back.',
  'setup.studio.offline.trying': 'Trying to reconnect…',
  'setup.studio.slow.elapsed': '{{seconds, number}} s',
  'setup.studio.slow.still': 'Still working. Keep talking if you like: your next message goes as soon as this is done.',
  'setup.studio.offline.bar': 'Saved · version {{n, number}} · offline',
  'setup.studio.offline.build_why': 'Build needs a connection',
  'setup.studio.quota.in_hours_one': '{{count, number}} hour',
  'setup.studio.quota.in_hours_other': '{{count, number}} hours',
  'setup.studio.quota.in_minutes_one': '{{count, number}} minute',
  'setup.studio.quota.in_minutes_other': '{{count, number}} minutes',
  'setup.studio.quota.midnight': '12 midnight',
  'setup.studio.quota.warn_one': '{{count, number}} message left with OS today',
  'setup.studio.quota.warn_other': '{{count, number}} messages left with OS today',
  'setup.studio.down.title': 'OS is not answering right now',
  'setup.studio.down.desc': 'Your setup is saved. You can still change the canvas yourself, and Build works: its check does not need OS.',
  'setup.studio.down.retrying': 'Trying again by itself in {{s, number}} s',
  'setup.studio.down.placeholder': 'Type here; it goes to OS as soon as OS answers',
  'setup.studio.down.live_off': 'OS is not answering right now',
  'setup.studio.down.because': 'You clicked “{{fix}}” on the canvas. No OS needed.',
  'setup.studio.quota.title': 'That’s today’s talk with OS',
  'setup.studio.quota.desc': 'It opens again at {{time}}, in {{in}}. Your setup is saved. You can still change the canvas, and Build works.',
  'setup.studio.quota.show_checks': 'Show what to check',
  'setup.studio.quota.unsent': 'Your last message is kept and goes first when talk opens.',
  'setup.studio.quota.placeholder': 'Talk with OS opens again at {{time}}',
  'setup.studio.large.links_one': '{{count, number}} $t(glossary.link_one)',
  'setup.studio.large.links_other': '{{count, number}} $t(glossary.link_other)',
  'setup.studio.large.note_one': '{{count, number}} $t(glossary.record_one) · all of it builds in one go',
  'setup.studio.large.note_other': '{{count, number}} $t(glossary.record_other) · all of it builds in one go',
  'setup.studio.large.over': 'This is more than one workspace can hold: {{what}}. Split it into two, or leave some out.',
  'setup.studio.notyet.fold_one': 'Not yet · {{count, number}}',
  'setup.studio.notyet.fold_other': 'Not yet · {{count, number}}',
  'setup.studio.conflict.how_many_items': 'How many items',
  'setup.studio.conflict.many_one': '{{count, number}} rate differs',
  'setup.studio.conflict.many_other': '{{count, number}} rates differ',
  'setup.studio.conflict.badge': 'Two sources disagree',
  'setup.studio.conflict.later_note': 'Decide later keeps it marked. The Build sheet asks once more.',
  'setup.studio.conflict.use': 'Use {{name}}',
  'setup.studio.conflict.use_all': 'Use {{name}}’s for all',
  'setup.studio.name.label': 'Call this one',
  'setup.studio.name.open': 'Open the one you have',
  'setup.studio.name.title': 'You already have a workspace called {{name}}',
  'setup.studio.name.use': 'Use this name',
  'setup.studio.name.build_why': 'Pick another name to build',
  'setup.studio.lang.chosen': 'Your language · {{language}}',
  'setup.studio.lang.not_spoken': 'I can’t talk in {{language}} yet. Shall I talk in English, Hindi or Hinglish? Names stay exactly as you write them, in any script.',
  'setup.studio.lang.why': 'OS talks in {{total, number}} languages; {{language}} is not one of them yet.',
  'setup.studio.lang.empty': 'Your workspace is drawn here as you talk',
  'setup.studio.second.from': 'from {{name}}',
  'setup.studio.second.started': 'Started like {{name}}',
  'setup.studio.second.safe': '{{name}} keeps working. Nothing in it changes.',
  // Edit the setup in the studio (9.17): the head, the Build again sheet, after it, and the doors.
  'setup.studio.edit.seeded': '{{name}} as it stands',
  'setup.studio.edit.bar': 'Changing the setup · nothing changes until you build',
  'setup.studio.edit.nothing_yet': 'Change something first.',
  'setup.studio.edit.keeps_added_one': '{{count, number}} $t(glossary.entry_one) keeps everything. {{field}} starts empty.',
  'setup.studio.edit.keeps_added_other': '{{count, number}} $t(glossary.entry_other) keep everything. {{field}} starts empty.',
  'setup.studio.edit.keeps_new_list_one': 'Starts with the {{count, number}} name you gave.',
  'setup.studio.edit.keeps_new_list_other': 'Starts with the {{count, number}} names you gave.',
  'setup.studio.edit.keeps_optional_one': '{{count, number}} $t(glossary.entry_one) keeps everything. {{field}} is not required.',
  'setup.studio.edit.keeps_optional_other': '{{count, number}} $t(glossary.entry_other) keep everything. {{field}} is not required.',
  'setup.studio.edit.keeps_hidden_one': '{{count, number}} $t(glossary.entry_one) keeps everything. {{field}} is hidden, and its values are kept.',
  'setup.studio.edit.keeps_hidden_other': '{{count, number}} $t(glossary.entry_other) keep everything. {{field}} is hidden, and its values are kept.',
  'setup.studio.edit.keeps_required_one': '{{count, number}} $t(glossary.entry_one) keeps everything. {{field}} must be filled from now on.',
  'setup.studio.edit.keeps_required_other': '{{count, number}} $t(glossary.entry_other) keep everything. {{field}} must be filled from now on.',
  'setup.studio.edit.keeps_renamed': 'Everything in it stays; only the name changes.',
  'setup.studio.edit.keeps_nothing': 'Nothing already kept changes.',
  'setup.studio.edit.line.adds': 'adds {{names}}',
  'setup.studio.edit.line.picks': 'picks a {{name}}',
  'setup.studio.edit.line.takes_off': 'takes off {{names}}',
  'setup.studio.edit.line.renames': 'renames {{from}} to {{to}}',
  'setup.studio.edit.line.optional': '{{names}} not required',
  'setup.studio.edit.line.required': '{{names}} must be filled',
  'setup.studio.edit.line.choices': 'new choices on {{names}}',
  'setup.studio.edit.line.new_list': 'a new $t(glossary.list_one)',
  'setup.studio.edit.line.new_record': 'a new $t(glossary.record_one)',
  'setup.studio.edit.line.renamed': 'renamed from {{from}}',
  'setup.studio.edit.line.left_out': 'left out',
  'setup.studio.link_retired': 'Links no longer run. Money moves by a money rule and stock by a quantity rule, so take this link away.',
  'setup.studio.edit.line.new_card': 'a new $t(glossary.insight_one) on {{record}}',
  'setup.studio.edit.line.card_removed': 'taken off the top of {{record}}',
  'setup.studio.edit.line.money_new': 'counts in money',
  'setup.studio.edit.line.money_changed': 'counts money a new way',
  'setup.studio.edit.line.money_off': 'stops counting in money',
  'setup.studio.edit.line.charge_plan': 'bills on a schedule',
  'setup.studio.edit.line.approvals': '$t(glossary.entry_other) wait for approval',
  'setup.studio.edit.line.approvals_off': 'no approval needed',
  'setup.studio.edit.line.tasks_one': 'raises {{count, number}} new task',
  'setup.studio.edit.line.tasks_other': 'raises {{count, number}} new tasks',
  'setup.studio.edit.keeps_money_changed_one': 'New saves count the new way; the {{count, number}} $t(glossary.entry_one) already saved keeps what it counted.',
  'setup.studio.edit.keeps_money_changed_other': 'New saves count the new way; the {{count, number}} $t(glossary.entry_other) already saved keep what they counted.',
  'setup.studio.edit.keeps_money_new_one': 'New saves count in money; the {{count, number}} $t(glossary.entry_one) already saved stays as it is.',
  'setup.studio.edit.keeps_money_new_other': 'New saves count in money; the {{count, number}} $t(glossary.entry_other) already saved stay as they are.',
  'setup.studio.edit.keeps_money_off': 'Built only while no saved $t(glossary.entry_one) counts in money. Otherwise turn Counts in money off in its settings.',
  'setup.studio.edit.not_yet_head': 'Stays as it is',
  'setup.studio.edit.rule': 'Nothing is deleted. A field you take out is hidden, and its values are kept.',
  'setup.studio.edit.open_record': 'Open {{name}}',
  'setup.studio.edit.close_body': 'Your changes are kept until {{day}}. Nothing changes in {{name}} until you build again.',
  'setup.studio.edit.door_help': 'Add or change records, lists, links and cards. Your entries are kept.',
  'setup.studio.edit.loading': 'Reading the setup of {{name}}',
  'setup.studio.edit.stale': 'Someone changed {{name}} in Customize while you were editing. Close, then open Edit the setup again to start from it as it is now.',
  'setup.studio.edit.stale_any': 'Someone changed this setup in Customize while you were editing. Close, then open Edit the setup again to start from it as it is now.',
  'setup.studio.build.next.first': 'Write your first {{noun}}',
  'setup.studio.landed.example': '{{name}} took 2 on credit',
  'setup.studio.landed.meta': '{{role}} in {{name}}',
  'setup.studio.landed.new': 'New {{noun}}',
  'setup.studio.landed.note_entry': 'Note a {{noun}}',
  'setup.studio.landed.only_sees': 'You can look at {{sees}}. Ask {{name}} if you need to write in one.',
  'setup.studio.landed.os_sub': 'Say what happened, like “{{example}}”, and OS writes it down.',
  'setup.studio.landed.os_sub_plain': 'Say what happened, and OS writes it down.',
  'setup.studio.landed.title': 'Welcome, {{name}}',
  'setup.studio.landed.what': 'You can write in {{adds}}, and see {{sees}}. Ask {{name}} if you need more.',
  'setup.studio.landed.what_adds': 'You can write in {{adds}}. Ask {{name}} if you need more.',
  'setup.studio.landed.what_title': '{{name}} set up this workspace for you',
  'setup.studio.invite.language': 'Which language should OS talk to you in?',
  'setup.studio.invite.language_note': 'OS talks with you in it. The app’s own words are in English for now.',
  'setup.studio.landed.os': 'OS can do it for you',
  'setup.studio.landed.start': 'Where to start',
  'setup.studio.invite.no_setup': '{{name}} set it all up. There is nothing for you to set up.',
  'setup.studio.invite.preview_failed': 'This invite could not be opened',
  'setup.studio.leave.unsent': 'Not sent yet',
  'setup.studio.leave.unsent_n_one': '{{count, number}} message, not kept if you leave',
  'setup.studio.leave.unsent_n_other': '{{count, number}} messages, not kept if you leave',
  // A member who may look at the setup but not change it, and the owner's side of their suggestions (9.18, board 9.12 edges-not-owner).
  'setup.studio.owner.bar': 'Only {{name}} can change this setup',
  'setup.studio.owner.head': 'Suggest a change',
  'setup.studio.owner.head_sub': '{{name}} decides. Nothing changes until they build it.',
  'setup.studio.owner.title': 'You can look, not change',
  'setup.studio.owner.desc': 'Only {{owner}}, who owns {{name}}, can change its setup. Say what you need: it reaches them with the part you picked.',
  'setup.studio.owner.sent': 'Sent before',
  'setup.studio.owner.done': '{{name}} built it · {{when}}',
  'setup.studio.owner.waiting': 'Waiting for {{name}}',
  'setup.studio.owner.put_away': '{{name}} put it away',
  'setup.studio.owner.goes_to': 'Goes to {{name}}, with the part you picked',
  'setup.studio.owner.canvas_sub': 'How {{name}} is set up. You can look; {{owner}} can change it.',
  'setup.studio.owner.placeholder': 'Say what should change',
  'setup.studio.owner.not_shown': 'This setup is not shown to your role',
  'setup.studio.owner.from_others_one': '{{count, number}} suggestion from your team',
  'setup.studio.owner.from_others_other': '{{count, number}} suggestions from your team',
  'setup.studio.owner.use': 'Use it',
  'setup.studio.owner.dismiss': 'Put away',
  'setup.studio.owner.look': 'See how it is set up',
  'setup.studio.edit.link_field': 'A link uses a field you took out of {{name}}. Take the link out first, or keep the field.',
  'setup.studio.edit.link_uses': 'A link posts into or from {{name}}, so it cannot be left out while that link runs. Take the link out first.',
  'setup.studio.links.drawer.delete': 'Delete this $t(glossary.link_one)',
  'setup.studio.links.drawer.delete_body': 'It comes off the map and adds nothing more to {{to}}.',
  'setup.studio.links.drawer.delete_sub': 'It comes off the map and adds nothing more',
  'setup.studio.links.drawer.delete_title': 'Delete {{name}}?',
  'setup.studio.links.drawer.delete_yes': 'Delete',
  'setup.studio.links.drawer.manage': 'Manage this $t(glossary.link_one)',
  'setup.studio.links.drawer.on_line': 'Every {{from}} saved adds to {{to}}',
  'setup.studio.links.drawer.paused_line': 'Nothing is added to {{to}} until you turn it on',
  // A drawer's foot on the Business map (M4 of docs/plans/business-map-canvas.md): to the record's or list's
  // own page, and to where the link is changed.
  'setup.studio.drawer.open_part': 'Open {{name}}',
  'setup.studio.drawer.open_link': 'Open $t(glossary.link_one)',
  'setup.studio.queued.call': 'Finishing what you said on the call. Your message goes next.',
  'setup.studio.queued.ran_out': 'Still finishing what you said on the call. Try again in a moment.',
  'setup.studio.voice.finishing': 'Finishing what you said on the call.',

  // ------------------------------------------------------- the business map
  // The setup sketch kept as the workspace's map, with live numbers on it
  // (docs/plans/business-map.md). Vivek chose the name "Business map" on
  // 7 Oct 2026. It is a named exception to Never 9 of writing.md, which says
  // "workspace" in the product and keeps "business" for routes and files.
  // "Workspace map" is the one-key alternative: `business_map.title` names
  // the page everywhere it is named (head, sidebar, ⌘K, Workspace details);
  // the two sentences that say it (`business_map.label`,
  // `business_map.pointer.body`) follow it.
  'business_map.title': 'Business map',
  'business_map.label': 'Business map of {{name}}',
  'business_map.sub': 'How the $t(glossary.record_other) and $t(glossary.list_other) of {{name}} fit together. Set up on {{date}}.',
  'business_map.sub_undated': 'How the $t(glossary.record_other) and $t(glossary.list_other) of {{name}} fit together.',
  // A record's live line: "412 this month · the last at 6:40 pm", or "None this
  // month · the last on 3 Oct" (`record.live` joins the two halves), or "No
  // entries yet". The time and the day come formatted.
  'business_map.record.month_one': '{{count, number}} this month',
  'business_map.record.month_other': '{{count, number}} this month',
  'business_map.record.none_month': 'None this month',
  'business_map.record.last_time': 'the last at {{time}}',
  'business_map.record.last_day': 'the last on {{day}}',
  'business_map.record.live': '{{month}} · {{last}}',
  'business_map.record.no_entries': 'No $t(glossary.entry_other) yet',
  'business_map.list.choose_one': '{{count, number}} to choose from',
  'business_map.list.choose_other': '{{count, number}} to choose from',
  'business_map.list.empty': 'Empty for now',
  // Before the names of the parts the map's draft cannot hold (a record no field can name, a worked-out field
  // that does not read back), each a link to where it is (M2 of docs/plans/business-map-canvas.md).
  'business_map.kept.lead': 'Not drawn on the map:',
  'business_map.empty.title': 'Nothing to map yet',
  'business_map.empty.line': 'Your $t(glossary.record_other) and $t(glossary.list_other) show here, joined by what each one chooses from.',
  'business_map.details.help': 'How your $t(glossary.record_other) and $t(glossary.list_other) fit together',
  'business_map.details.open': 'Open the map',
  'business_map.pointer.title': 'Your sketch lives here now',
  'business_map.pointer.body': 'The Business map shows how {{name}} fits together, and grows as you work.',
  'business_map.pointer.ok': 'Got it',
  // --- Business details (D10 of docs/plans/report-templates.md) ---
  // One label per registry key in packages/shared/src/business-details.ts, read
  // there as data (`labelKey`), and one per choice value. A dotted registry key
  // (`gst.einvoice`) takes an underscore here (`gst_einvoice`).
  'business_details.key.name': 'Business name',
  'business_details.key.legal_name': 'Legal name',
  'business_details.key.address': 'Address',
  'business_details.key.logo': 'Logo',
  'business_details.key.state': 'State',
  'business_details.key.phone': 'Phone',
  'business_details.key.email': 'Email',
  'business_details.key.website': 'Website',
  'business_details.key.gstin': 'GSTIN',
  'business_details.key.pan': 'PAN',
  'business_details.key.tax_regime': 'Tax registration',
  'business_details.key.gst_einvoice': 'E-invoicing',
  'business_details.key.gst_turnover_band': 'Yearly turnover',
  'business_details.key.bank_account': 'Bank account',
  'business_details.key.signature': 'Signature',
  'business_details.key.terms': 'Terms',
  'business_details.key.timezone': 'Time zone',
  'business_details.key.fssai': 'FSSAI licence number',
  'business_details.key.udyam': 'Udyam registration number',
  'business_details.key.cin': 'Company number (CIN)',
  'business_details.key.iec': 'Import export code',
  'business_details.key.drug_licence': 'Drug licence number',
  'business_details.key.trn': 'Tax registration number (TRN)',
  'business_details.key.school_affiliation_no': 'Affiliation number',
  'business_details.key.school_udise': 'UDISE code',
  'business_details.key.custom': 'Other detail',
  'business_details.choice.tax_regime.none': 'Not registered',
  'business_details.choice.tax_regime.gst_regular': 'GST, regular',
  'business_details.choice.tax_regime.gst_composition': 'GST, composition',
  'business_details.choice.tax_regime.vat': 'VAT',
  'business_details.choice.gst_einvoice.no': 'Not required',
  'business_details.choice.gst_einvoice.yes': 'Required',
  'business_details.choice.gst_einvoice.exempt': 'Exempt',
  'business_details.choice.gst_turnover_band.upto_5cr': 'Up to ₹5 crore',
  'business_details.choice.gst_turnover_band.above_5cr': 'Above ₹5 crore',
  'business_details.line': 'What the business is, as every template prints it: from setup, your files, your website and your chats, or typed here. Nothing is typed twice.',
  'business_details.removed': 'Detail removed',
  'business_details.saved': 'Detail saved',
  'business_details.title': 'Business details',
  'business_details.bank.account_name': 'Account holder',
  'business_details.bank.account_number': 'Account number',
  'business_details.bank.bank_name': 'Bank',
  'business_details.bank.branch': 'Branch',
  'business_details.bank.help': 'An account number, a UPI id, or both. A UPI id alone is enough.',
  'business_details.bank.ifsc': 'IFSC',
  'business_details.bank.label': 'Name for this account',
  'business_details.bank.upi': 'UPI id',
  'business_details.choose': 'Choose one',
  'business_details.custom.label': 'Name',
  'business_details.custom.label_hint': 'For example, shop act licence',
  'business_details.custom.text': 'Value',
  'business_details.gstin.gives': 'Fills in PAN {{pan}} and state {{state}}.',
  'business_details.gstin.help': 'Fifteen characters, as on your GST certificate. The PAN and the state are filled in from it.',
  'business_details.edit': 'Edit',
  'business_details.edit_named': 'Edit {{name}}',
  'business_details.remove_named': 'Remove {{name}}',
  'business_details.bank.ifsc_is': 'IFSC {{ifsc}}',
  'business_details.bank.upi_is': 'UPI {{upi}}',
  'business_details.from.a_file': 'From a file',
  'business_details.from.chat': 'You said in chat',
  'business_details.from.named': 'From {{name}}',
  'business_details.from.screen': 'Typed here',
  'business_details.from.setup': 'From setup',
  'business_details.from.website': 'From your website',
  'business_details.problem.account': 'Check the account number: 6 to 34 letters and digits.',
  'business_details.problem.account_or_upi': 'Add an account number or a UPI id.',
  'business_details.problem.empty': 'Add a value, or cancel.',
  'business_details.problem.gstin_check': 'The last character does not match the rest. Check the GSTIN against your certificate.',
  'business_details.problem.gstin_shape': 'A GSTIN has 15 characters, like 27AAPFU0939F1ZV.',
  'business_details.problem.ifsc': 'An IFSC has 11 characters, like HDFC0001234.',
  'business_details.problem.invalid': 'Check this value and try again.',
  'business_details.problem.label_taken': 'That name is already a detail on this page. Use that row.',
  'business_details.problem.pan_mismatch': 'This PAN is not the one in your GSTIN. Change the GSTIN first.',
  'business_details.problem.state': 'Choose a state from the list.',
  'business_details.problem.too_long': 'Make it shorter.',
  'business_details.problem.upi': 'A UPI id looks like name@bank.',
  'business_details.who_can.admin': 'Only an admin of this workspace can add business details.',
  'business_details.who_can.named': 'Only {{names}} can add business details.',
  'business_details.add': 'Add',
  'business_details.group.bank': 'Bank and UPI',
  'business_details.group.bank_line': 'Where you are paid. A bill prints the default account and its UPI code.',
  'business_details.group.custom': 'Other details',
  'business_details.group.custom_line': 'Anything else a bill or a letter should carry, under your own name for it.',
  'business_details.group.identity': 'Identity',
  'business_details.group.identity_line': 'Who the business is, as a document heads it.',
  'business_details.group.print': 'Printing',
  'business_details.group.print_line': 'What goes at the foot of a document.',
  'business_details.group.registrations': 'Registrations',
  'business_details.group.registrations_line': 'Licence and registration numbers a document may need to show.',
  'business_details.group.tax': 'Tax',
  'business_details.group.tax_line': 'Your GSTIN and how you are registered. Tax invoices are offered once a GSTIN is here.',
  'business_details.none': 'Not added',
  'business_details.signature.change': 'Change signature',
  'business_details.signature.none': 'No signature yet',
  'business_details.signature.remove': 'Remove signature',
  'business_details.signature.upload': 'Upload a signature',
  'business_details.bank.default': 'Print this account on documents',
  'business_details.bank.is_default': 'Printed on documents',
  'business_details.setup.line': 'Saved with your workspace when you build. Remove any you do not want kept.',
  'business_details.setup.remove': 'Remove {{detail}}',
  'business_details.setup.title': 'Business details OS will keep',
  'business_details.setup.from_files': 'From your files',
  'business_details.setup.from_said': 'You said this',
  'business_details.setup.from_source': 'From {{source}}',
  'business_details.setup.outranked': 'Not kept: only one {{detail}} is kept. Remove the other to keep this one.',
  'business_details.card.from_gstin': 'From your GSTIN',
  'business_details.card.was': 'Was {{value}}',
  // --- Report templates (Phase 8 of docs/plans/report-templates.md) ---
  // The record's Report templates tab, Generate new template, and the Generate, Issue and Cancel sheets
  // on an entry. A kind's name and a block's are read as data (`report_templates.kind.<kind>`,
  // `report_templates.block.<type>`); a document's own printed labels are the server's `doc.*`.
  'report_templates.tab': 'Report templates',
  'report_templates.generate_new': 'Generate new template',
  'report_templates.caption': 'Templates make documents from your $t(glossary.entry_other). No AI runs when you use one: every total is added up from the $t(glossary.entry_one) itself.',
  'report_templates.list_label': 'Templates',
  'report_templates.load_failed': 'The templates could not be read',
  'report_templates.archived': 'Archived',
  'report_templates.drift': 'Fields changed since it was saved',
  'report_templates.version_line': 'Version {{version, number}} · changed {{day}}',
  'report_templates.thumb_label': '{{name}}, first page',
  'report_templates.thumb_failed': 'The page could not be drawn now',
  'report_templates.empty.title': 'No templates yet',
  'report_templates.empty.body': 'A template turns an $t(glossary.entry_one) into a document to print or send: an invoice, a receipt, a quotation. OS designs it with you once, filled with one of your own $t(glossary.entry_other). After that it is made with no AI.',
  'report_templates.empty.hint': 'Or say what you need, in your own words.',
  'report_templates.empty.not_yours': 'Whoever may change this $t(glossary.record_one)’s $t(glossary.field_other) can make its templates.',
  'report_templates.starters_title': 'These fit your $t(glossary.business_one)',
  'report_templates.start_from_this': 'Start from this',
  'report_templates.kind.tax_invoice': 'GST tax invoice',
  'report_templates.kind.bill_of_supply': 'Bill of supply',
  'report_templates.kind.sale_bill': 'Bill',
  'report_templates.kind.payment_receipt': 'Payment receipt',
  'report_templates.kind.quotation': 'Quotation',
  'report_templates.starter.tax_invoice': 'Uses HSN code, GST rate and the party’s GSTIN; code works out CGST and SGST',
  'report_templates.starter.bill_of_supply': 'For a composition dealer or exempt goods: no tax charged on it',
  'report_templates.starter.sale_bill': 'Items, rates and the total, with your details at the top; never a tax invoice',
  'report_templates.starter.payment_receipt': 'What a party paid, in figures and words, and the balance left',
  'report_templates.starter.quotation': 'Items, rates and tax before an order; never titled a tax invoice',
  'report_templates.starter_ask.tax_invoice': 'Make a GST tax invoice from my sales',
  'report_templates.starter_ask.bill_of_supply': 'Make a bill of supply from my sales',
  'report_templates.starter_ask.sale_bill': 'Make a bill from my sales',
  'report_templates.starter_ask.payment_receipt': 'Make a payment receipt from my entries',
  'report_templates.starter_ask.quotation': 'Make a quotation from my entries',
  'report_templates.issued.title': 'Issued',
  'report_templates.issued.caption': 'Every document given a number, newest first. A cancelled one keeps its number.',
  'report_templates.issued.number': 'Number',
  'report_templates.issued.issued_on': 'Issued on',
  'report_templates.issued.party': 'Party',
  'report_templates.issued.total': 'Total',
  'report_templates.issued.status': 'Status',
  'report_templates.issued.issued': 'Issued',
  'report_templates.issued.cancelled': 'Cancelled',
  'report_templates.os.question': 'What document do you need from {{record}}? Say it in your own words, or pick one.',
  'report_templates.os.question_edit': 'What should change on {{name}}? Version 1 stays as it is until you save the change.',
  'report_templates.os.chip_invoice': 'A GST tax invoice for my sales',
  'report_templates.os.chip_receipt': 'A payment receipt',
  'report_templates.os.chip_quotation': 'A quotation',
  'report_templates.os.chip_teal': 'Make it teal',
  'report_templates.os.chip_logo': 'Put the logo on the right',
  'report_templates.os.draft': 'Draft, not saved yet',
  'report_templates.os.saved': 'Saved',
  'report_templates.os.meta_new': 'Made from {{record}} · nothing is saved until you save it',
  'report_templates.os.meta_edit': 'Version {{version, number}} is kept until you save the change',
  'report_templates.os.this_block': 'this block: {{block}}',
  'report_templates.saved.line': 'Saved as {{name}}. Open any entry and Generate it: no chat needed.',
  'report_templates.saved.open_tab': 'See all templates',
  'report_templates.preview.title': 'Preview',
  'report_templates.preview.sample': 'Sample data',
  'report_templates.preview.pages_one': '{{count, number}} page',
  'report_templates.preview.pages_other': '{{count, number}} pages',
  'report_templates.preview.page_of': 'Page {{page, number}} of {{pages, number}}',
  'report_templates.preview.previous_page': 'Previous page',
  'report_templates.preview.next_page': 'Next page',
  'report_templates.preview.zoom_in': 'Zoom in',
  'report_templates.preview.zoom_out': 'Zoom out',
  'report_templates.preview.zoom': '{{percent}}%',
  'report_templates.preview.busy': 'The page is being drawn. It shows in a moment.',
  'report_templates.preview.waiting': 'The page OS makes shows here, filled with your newest $t(glossary.entry_one).',
  'report_templates.preview.click_block': 'Click a part of the page to ask about it.',
  'report_templates.stepper.filled_with': 'Filled with {{entry}}',
  'report_templates.stepper.newest': 'Filled with your newest $t(glossary.entry_one)',
  'report_templates.stepper.sample': 'No $t(glossary.entry_other) yet: filled with sample data',
  'report_templates.stepper.newer': 'Newer $t(glossary.entry_one)',
  'report_templates.stepper.older': 'Older $t(glossary.entry_one)',
  'report_templates.uses.title': 'Fields this uses',
  'report_templates.uses.from': 'From {{record}}',
  'report_templates.uses.lines': 'On each line of {{record}}',
  'report_templates.uses.business': 'From your $t(glossary.business_one) details',
  'report_templates.basis.question': 'Do your prices include GST? The formulas do not say, so here is one $t(glossary.entry_one) drawn both ways.',
  'report_templates.basis.inclusive': 'Prices include GST',
  'report_templates.basis.exclusive': 'GST is added to prices',
  'report_templates.basis.inclusive_answer': 'My prices include GST',
  'report_templates.basis.exclusive_answer': 'Add GST to my prices',
  'report_templates.basis.kept': 'Kept on the template. OS asks again only if the $t(glossary.field_other) change.',
  'report_templates.card.save_template': 'Save template',
  'report_templates.card.save_changes': 'Save changes',
  'report_templates.card.parts': 'Parts of the page',
  'report_templates.card.name': 'Name',
  'report_templates.card.page_size': 'Page size',
  'report_templates.card.language': 'Language',
  'report_templates.card.needs_details': 'Needs your {{details}} before it can be issued',
  'report_templates.card.needs': 'Needs {{what}} before it can be issued',
  'report_templates.card.total_match': 'Every line matches the $t(glossary.entry_one), and so does its total.',
  'report_templates.card.total_rounding': 'Its total matches, rounded to the rupee.',
  'report_templates.card.total_mismatch': 'The $t(glossary.entry_one)’s total and its lines do not agree. Issue waits until they do.',
  'report_templates.card.total_not_checked': 'This $t(glossary.entry_one) holds no total to check against.',
  'report_templates.card.total_sample': 'Filled with sample data: there is no $t(glossary.entry_one) to check yet.',
  'report_templates.card.series_on_file': 'Takes the next number when issued: {{number}}',
  'report_templates.card.series_new': 'Saving makes a number series. The first one issued is {{number}}.',
  'report_templates.page.a4': 'A4',
  'report_templates.page.a5': 'A5',
  'report_templates.page.letter': 'Letter',
  'report_templates.block.header': 'Header',
  'report_templates.block.title': 'Title',
  'report_templates.block.meta': 'Number and dates',
  'report_templates.block.party': 'Party',
  'report_templates.block.items': 'Items',
  'report_templates.block.totals': 'Totals',
  'report_templates.block.tax_summary': 'Tax summary',
  'report_templates.block.amount_in_words': 'Amount in words',
  'report_templates.block.payment': 'Payment',
  'report_templates.block.text': 'Text',
  'report_templates.block.legal': 'Legal line',
  'report_templates.block.signature': 'Signature',
  'report_templates.block.divider': 'Divider',
  'report_templates.role.buyer_name': 'the party’s name',
  'report_templates.role.buyer_address': 'the party’s address',
  'report_templates.role.buyer_tax_id': 'the party’s GSTIN',
  'report_templates.role.buyer_state': 'the party’s state',
  'report_templates.role.line_hsn': 'an HSN or SAC code on each item',
  'report_templates.role.line_tax_rate': 'a GST rate on each item',
  'report_templates.role.total': 'the $t(glossary.entry_one)’s total',
  'report_templates.role.doc_place_of_supply': 'the place of supply',
  'report_templates.open': 'Open',
  'report_templates.edit_with_os': 'Edit with OS',
  'report_templates.template_page.missing': 'This template is not open to you',
  'report_templates.template_page.version': 'Version {{version, number}}',
  'report_templates.template_page.no_ai': 'No AI runs when you use it: the page is filled from the $t(glossary.entry_one), and every total is added up from its lines.',
  'report_templates.generate.named': 'Generate {{name}}',
  'report_templates.generate.menu': 'Generate',
  'report_templates.generate.opens_issue': 'Opens the issue sheet: the number is given there',
  'report_templates.generate.downloads': 'Downloads now, made from this $t(glossary.entry_one)',
  'report_templates.generate.saved': '{{name}} is downloaded',
  'report_templates.issue.title': 'Issue {{kind}}',
  'report_templates.issue.desc': 'This gives it number {{number}}. A number, once given, is kept.',
  'report_templates.issue.desc_no_number': 'It takes the next number of its series when it is issued. A number, once given, is kept.',
  'report_templates.issue.mismatch_title': 'This cannot be issued yet',
  'report_templates.issue.mismatch_body': 'The total on the $t(glossary.entry_one) and its lines do not agree. A document never prints a total its lines do not add up to.',
  'report_templates.issue.stored': 'Total on the $t(glossary.entry_one)',
  'report_templates.issue.computed': 'Its lines add up to',
  'report_templates.issue.apart': '{{amount}} apart',
  'report_templates.issue.cause_price_basis': 'Likely cause: the lines were added up with GST the other way. Check whether your prices include GST.',
  'report_templates.issue.cause_discount': 'Likely cause: the total is lower than the lines, as for a flat price agreed with the party. A bill discount brings the lines to the total; code spreads it over the lines and works out the tax again.',
  'report_templates.issue.cause_lines': 'Likely cause: a line’s amount does not match its quantity and rate.',
  'report_templates.issue.cause_unknown': 'Check the lines and the total of the $t(glossary.entry_one).',
  'report_templates.issue.add_discount': 'Add a bill discount of {{amount}}',
  'report_templates.issue.fix_sale': 'Fix the sale',
  'report_templates.issue.rounding_only': 'Only a difference from rounding is let through.',
  'report_templates.issue.gaps_one': '{{count, number}} thing the law asks for is missing. Add it before you issue.',
  'report_templates.issue.gaps_other': '{{count, number}} things the law asks for are missing. Add them before you issue.',
  'report_templates.issue.place_title': 'Place of supply',
  'report_templates.issue.place_body': 'The $t(glossary.entry_one) does not say where the goods go. Pick the state.',
  'report_templates.issue.place_pick': 'Pick a state',
  'report_templates.issue.state_line': '{{name}} ({{code}})',
  'report_templates.issue.late': 'The supply was on {{day}}, more than 30 days ago. Issue it with today’s date anyway.',
  'report_templates.issue.preview': 'Preview, with its number',
  'report_templates.issue.rights': 'Issuing needs Download $t(glossary.entry_other) and Add $t(glossary.entry_other), and sight of every $t(glossary.field_one) the document prints.',
  'report_templates.issue.issue_download': 'Issue and download',
  'report_templates.issue.issued': '{{number}} is issued',
  'report_templates.cancel.title': 'Cancel {{number}}',
  'report_templates.cancel.desc': 'Issued on {{day}}. A cancelled document keeps its number, and is never deleted.',
  'report_templates.cancel.why': 'Why is it cancelled?',
  'report_templates.cancel.reason.issued_in_error': 'Issued in error',
  'report_templates.cancel.reason.supply_not_made': 'The supply was not made',
  'report_templates.cancel.reason.duplicate': 'Issued twice',
  'report_templates.cancel.reason.other': 'Something else',
  'report_templates.cancel.same_day': 'Issued today: the reason is enough.',
  'report_templates.cancel.filed_question': 'Is this invoice in a GSTR-1 you have filed?',
  'report_templates.cancel.filed_no': 'No, not filed yet',
  'report_templates.cancel.filed_yes': 'Yes, it is in a filed return',
  'report_templates.cancel.filed_title': 'It cannot be cancelled here',
  'report_templates.cancel.filed_body': 'An invoice in a filed return is fixed with a credit note or a debit note. RecordOS does not make those yet; ask your accountant.',
  'report_templates.cancel.keeps_number': 'The number is kept: the next document takes the one after it.',
  'report_templates.cancel.keep': 'Keep it',
  'report_templates.cancel.confirm': 'Cancel invoice',
  'report_templates.cancel.done': '{{number}} is cancelled',
  'report_templates.issued_strip.label': 'Issued {{number}}',
  'report_templates.issued_strip.issued': 'Issued {{number}} · {{day}}',
  'report_templates.issued_strip.changed': 'Changed after {{number}} was issued',
  'report_templates.issued_strip.changed_body': 'The issued document still shows the $t(glossary.entry_one) as it was on {{day}}.',
  'report_templates.issued_strip.never_changes': 'An issued document never changes. A later edit to the $t(glossary.entry_one) or the template does not touch it.',
  'report_templates.issued_strip.download_again': 'Download again',
  'report_templates.issued_strip.cancel': 'Cancel invoice',
  'report_templates.issued_strip.downloaded': '{{number}} is downloaded',
  'report_templates.issued_strip.cancelled': '{{number}} was cancelled on {{day}}',
  'report_templates.issue.add_details': 'Add business details',
  // A template turn's steps on Generate new template and Edit with OS (os/doc-steps.ts), and the list that times them (coach/turn-steps.tsx).
  'report_templates.steps.adding_fields': 'Adding fields to {{list}}',
  'report_templates.steps.adding_fields_any': 'Adding fields',
  'report_templates.steps.asking_details': 'Asking for {{details}}',
  'report_templates.steps.asking_details_any': 'Asking for business details',
  'report_templates.steps.asking_you': 'Asking you',
  'report_templates.steps.blocks_one': '{{count, number}} block',
  'report_templates.steps.blocks_other': '{{count, number}} blocks',
  'report_templates.steps.changed': 'Changed: {{parts}}',
  'report_templates.steps.changing': 'Changing the layout',
  'report_templates.steps.checking': 'Checking the template',
  'report_templates.steps.drafting': 'Drafting the layout',
  'report_templates.steps.fields_one': '{{count, number}} field',
  'report_templates.steps.fields_other': '{{count, number}} fields',
  'report_templates.steps.needs': 'Needs: {{what}}',
  'report_templates.steps.previewing': 'Rendering the preview',
  'report_templates.steps.problem.entry': 'the sample entry could not be read',
  'report_templates.steps.problem.field': 'a field it names is not on the record',
  'report_templates.steps.problem.hidden': 'a field it prints is hidden from you',
  'report_templates.steps.problem.layout': 'the layout does not fit its shape',
  'report_templates.steps.problem.no_draft': 'there is no template to change',
  'report_templates.steps.problem.not_allowed': 'you may not change this record’s templates',
  'report_templates.steps.problem.number': 'the numbering is not set',
  'report_templates.steps.problem.record': 'the record could not be read',
  'report_templates.steps.problem.tax': 'the tax settings do not fit this kind',
  'report_templates.steps.problem.type': 'a field does not fit where it is printed',
  'report_templates.steps.reading': 'Reading {{record}}',
  'report_templates.steps.reading_record': 'Reading the record',
  'report_templates.steps.ready': 'Ready',
  'report_templates.steps.refused': 'Refused: {{reason}}',
  'report_templates.steps.uses': '{{blocks}} · {{fields}}',
  'report_templates.steps.count_one': '{{count, number}} step',
  'report_templates.steps.count_other': '{{count, number}} steps',
  'report_templates.steps.done': 'Done in {{time}} · {{steps}}',
  'report_templates.steps.done_mark': 'Done',
  'report_templates.steps.done_untimed': 'Done · {{steps}}',
  'report_templates.steps.label': 'Steps',
  'report_templates.steps.refused_mark': 'Refused',
  'report_templates.steps.running': 'Running',
  'report_templates.steps.seconds_one': '{{count, number}} s',
  'report_templates.steps.seconds_other': '{{count, number}} s',
  'report_templates.steps.stopped': 'Stopped',
  'report_templates.steps.thinking': 'Thinking',

  // A Gmail rule's schedule, in words (ruleWhenWords in connectors.ts).
  'connector.when.daily': 'Every day, {{time}}',
  'connector.when.weekly': 'Every {{weekday}}, {{time}}',
  // The schedule, and the zone it runs in when that is not the viewer's.
  'connector.when.in_zone': '{{when}} ({{zone}})',
  // One key a day of the month, so a translation writes its own ordinal.
  'connector.when.monthly.1': 'The 1st of each month, {{time}}',
  'connector.when.monthly.2': 'The 2nd of each month, {{time}}',
  'connector.when.monthly.3': 'The 3rd of each month, {{time}}',
  'connector.when.monthly.4': 'The 4th of each month, {{time}}',
  'connector.when.monthly.5': 'The 5th of each month, {{time}}',
  'connector.when.monthly.6': 'The 6th of each month, {{time}}',
  'connector.when.monthly.7': 'The 7th of each month, {{time}}',
  'connector.when.monthly.8': 'The 8th of each month, {{time}}',
  'connector.when.monthly.9': 'The 9th of each month, {{time}}',
  'connector.when.monthly.10': 'The 10th of each month, {{time}}',
  'connector.when.monthly.11': 'The 11th of each month, {{time}}',
  'connector.when.monthly.12': 'The 12th of each month, {{time}}',
  'connector.when.monthly.13': 'The 13th of each month, {{time}}',
  'connector.when.monthly.14': 'The 14th of each month, {{time}}',
  'connector.when.monthly.15': 'The 15th of each month, {{time}}',
  'connector.when.monthly.16': 'The 16th of each month, {{time}}',
  'connector.when.monthly.17': 'The 17th of each month, {{time}}',
  'connector.when.monthly.18': 'The 18th of each month, {{time}}',
  'connector.when.monthly.19': 'The 19th of each month, {{time}}',
  'connector.when.monthly.20': 'The 20th of each month, {{time}}',
  'connector.when.monthly.21': 'The 21st of each month, {{time}}',
  'connector.when.monthly.22': 'The 22nd of each month, {{time}}',
  'connector.when.monthly.23': 'The 23rd of each month, {{time}}',
  'connector.when.monthly.24': 'The 24th of each month, {{time}}',
  'connector.when.monthly.25': 'The 25th of each month, {{time}}',
  'connector.when.monthly.26': 'The 26th of each month, {{time}}',
  'connector.when.monthly.27': 'The 27th of each month, {{time}}',
  'connector.when.monthly.28': 'The 28th of each month, {{time}}',

  // The Connectors row of the sidebar (connectors-gmail Decided 19), and its dot.
  'connector.nav': 'Connectors',
  'connector.nav_mark.broken': 'Gmail stopped working',
  'connector.nav_mark.waiting_one': '{{count, number}} email waits for your yes',
  'connector.nav_mark.waiting_other': '{{count, number}} emails wait for your yes',

  // The Connectors page (connectors-gmail Decided 19; boards C1, C5): the apps OS sends through, in groups.
  'connector.title': 'Connectors',
  'connector.lede': 'Connect an app, and OS can send through it: a reorder to a supplier, a bill to a customer.',
  'connector.group.connected': 'Connected',
  'connector.group.mail': 'Email',
  'connector.group.messages': 'Messages',
  'connector.group.files': 'Files and sheets',
  'connector.connect': 'Connect',
  'connector.soon': 'Soon',
  'connector.connected': 'Connected',
  'connector.reconnect': 'Reconnect Gmail',
  'connector.open': 'Open {{app}}',
  'connector.app.gmail': 'Gmail',
  'connector.app.outlook': 'Outlook',
  'connector.app.whatsapp': 'WhatsApp Business',
  'connector.app.slack': 'Slack',
  'connector.app.sheets': 'Google Sheets',
  'connector.app.drive': 'Google Drive',
  'connector.what.gmail': 'Send emails and bills from your Gmail address',
  'connector.what.outlook': 'The same, from an Outlook address',
  'connector.what.whatsapp': 'Message a party on WhatsApp',
  'connector.what.slack': 'Post the day’s numbers and alerts in a channel',
  'connector.what.sheets': 'Keep a sheet in step with a record',
  'connector.what.drive': 'Keep the files your entries hold in a folder',

  // A connected Gmail's line on its card: the address, then how many rules it has.
  'connector.gmail.line': '{{address}} · {{rules}}',
  'connector.gmail.rules_one': '{{count, number}} rule',
  'connector.gmail.rules_other': '{{count, number}} rules',
  'connector.foot': 'Each person connects their own Gmail. OS sends nothing you have not seen, unless a rule you turned on says it may.',
  'connector.load_failed': 'Connectors did not load',

  // A person whose role does not let them ask OS: the page in place of the gallery.
  'connector.denied.title': 'Connectors work through OS',
  'connector.denied.body': 'Your role here does not let you ask OS, so nothing can be sent for you. An admin can change your role.',
  'connector.connect_app': 'Connect {{app}}',

  // Connect Gmail (board C2): what OS will and will not do with the connection, before Google asks.
  'connector.gmail.connect_title': 'Connect Gmail',
  'connector.gmail.connect_desc': 'Let OS write emails from your Gmail address. Nothing goes until you say so.',
  'connector.can': 'This connection lets OS',
  'connector.cannot': 'Not with this connection',
  'connector.gmail.can_send': 'Write drafts in your Gmail and send them',
  'connector.gmail.can_attach': 'Attach a file an entry already holds: a bill photo, an invoice PDF',
  'connector.gmail.cannot_read': 'Read your email. Bringing bills and replies in from email comes later, and asks you first',
  'connector.gmail.cannot_delete': 'Delete or change an email you sent',
  'connector.gmail.cannot_stranger': 'Write to an address that is not in your lists, unless you type it',
  'connector.google_next': 'Google asks you to allow this next. Disconnect at any time.',
  'connector.continue_google': 'Continue with Google',

  // Back from Google on the Gmail page: connected, or it did not connect (a cancel says nothing).
  'connector.connected_toast': 'Gmail is connected',
  'connector.connect_failed': 'Gmail did not connect',
  'connector.connect_failed_desc': 'Try again, and allow OS to write emails when Google asks.',

  // The Gmail page with no Gmail connected.
  'connector.gmail.none_title': 'Gmail is not connected',
  'connector.gmail.none_body': 'Connect your Gmail, and OS can write emails from your address.',

  // The Gmail page (connectors-gmail Decided 7, 8, 12, 13, 15 and 16; boards C4, C6, C7).
  'connector.connected_when': 'Connected {{when}}',
  'connector.ask_send': 'Ask OS to send',
  'connector.more': 'More for Gmail',
  'connector.disconnect': 'Disconnect',
  'connector.disconnect_title': 'Disconnect Gmail?',
  'connector.disconnect_desc': 'Your rules pause, and emails waiting for your yes are left out. What was sent stays in the log.',
  'connector.disconnected': 'Gmail is disconnected',

  // Gmail stopped working (board C7): when, and what waits; nothing was lost.
  'connector.broken_title': 'Gmail stopped working',
  'connector.broken_desc': 'Google stopped answering on {{when}}. Nothing was lost.',
  'connector.broken_desc_now': 'Google stopped answering. Nothing was lost.',
  'connector.rules.first_paused': 'Gmail stopped working, so a new rule can be drafted but not turned on until you reconnect it.',
  'connector.broken_rules_one': '{{count, number}} rule is paused',
  'connector.broken_rules_other': '{{count, number}} rules are paused',
  'connector.broken_waiting_one': '{{count, number}} email waits for your yes',
  'connector.broken_waiting_other': '{{count, number}} emails wait for your yes',
  'connector.old_address': 'Rules made with {{address}} stay paused. Connect that Gmail again to run them.',
  'connector.section.rules': 'Rules',
  'connector.section.waiting': 'Waiting for your yes',
  'connector.section.sent': 'Sent by OS',
  'connector.section.can': 'What OS may do',
  'connector.section.limits': 'Limits',
  'connector.new_rule': 'New rule',

  // No rules yet (board C4): the large OS input, and three to start from.
  'connector.rules.first': 'Set up your first rule',
  'connector.rules.first_line': 'Say what should happen, and when, as you would tell a person in the shop. OS then drafts the rule for you to check, and asks how the emails should go.',
  'connector.rules.placeholder': 'For example: when an item runs out, email its supplier to reorder it',
  'connector.rules.starter.reorder': 'Email a supplier when an item runs out',
  'connector.rules.starter.dues': 'Send each customer their dues on the 1st',
  'connector.rules.starter.bill': 'Email the bill when a sale on credit is saved',
  'connector.rules.failed': 'Rules did not load',
  'connector.col.rule': 'Rule',
  'connector.col.when': 'When',
  'connector.col.then': 'Then',
  'connector.col.last_ran': 'Last ran',
  'connector.col.on': 'On',

  // A rule's Then: how its emails go, always the person's pick (Decided 8).
  'connector.mode.draft': 'Save as drafts',
  'connector.mode.wait': 'Send after my yes',
  'connector.mode.own': 'Send on its own',
  'connector.mode.pick': 'Pick how',
  'connector.mode.label': 'How {{rule}} sends',
  'connector.rule.waiting_one': '{{count, number}} waiting',
  'connector.rule.waiting_other': '{{count, number}} waiting',
  'connector.rule.not_yet': 'Not yet',
  'connector.rule.next': 'Next {{when}}',
  'connector.rule.paused': 'Paused',
  'connector.rule.switch': '{{rule}} on',
  'connector.rule.menu': 'More for {{rule}}',
  'connector.rule.change': 'Change with OS',
  'connector.rule.remove_title': 'Remove {{rule}}?',
  'connector.rule.remove_desc': 'Its emails waiting for your yes are left out. What it sent stays in the log.',
  'connector.rule.removed': 'Rule removed',

  // Why a rule that is on does not run.
  'connector.paused.broken': 'Gmail stopped working',
  'connector.paused.disconnected': 'Gmail is not connected',
  'connector.paused.other_address': 'Made with {{address}}, not the address connected now',
  'connector.paused.no_access': 'You cannot open its record',
  'connector.paused.no_email_field': 'Its list has no email field',
  'connector.paused.field_gone': 'A field it uses is gone',

  // Waiting for your yes: each email, and the two answers to it.
  'connector.waiting.failed': 'Waiting emails did not load',
  'connector.waiting.look': 'Look',
  'connector.waiting.send': 'Send',
  'connector.waiting.leave': 'Leave out',
  'connector.waiting.leave_this': 'Leave this one out',
  'connector.waiting.send_all_one': 'Send {{count, number}} email',
  'connector.waiting.send_all_other': 'Send all {{count, number}} emails',
  'connector.waiting.sent_one': '{{count, number}} email sent',
  'connector.waiting.sent_other': '{{count, number}} emails sent',
  'connector.waiting.left_one': '{{count, number}} email left out',
  'connector.waiting.left_other': '{{count, number}} emails left out',
  'connector.waiting.to': 'To {{address}}',
  'connector.waiting.broken_note': 'Reconnect Gmail to send these',
  'connector.look.title': 'The email',
  'connector.look.from': 'From {{address}}',
  'connector.look.to': 'To {{address}}',
  'connector.look.attached': 'Attached: {{files}}',

  // Sent by OS: the log of every email it wrote.
  'connector.sent.label': 'Emails written through your Gmail',
  'connector.sent.empty': 'Nothing sent yet',
  'connector.sent.failed': 'Sent emails did not load',
  'connector.sent.col.when': 'When',
  'connector.sent.col.to': 'To',
  'connector.sent.col.subject': 'Subject',
  'connector.sent.col.because': 'Because',
  'connector.sent.col.state': 'State',
  'connector.sent.because_you': 'Asked by you',
  'connector.sent.open': 'Open in Gmail',
  'connector.sent.add_email': 'Add an email',
  'connector.sent.more': 'Show more',
  'connector.sent.no_to': 'No address',
  'connector.state.waiting': 'Waits for your yes',
  'connector.state.sending': 'Sending',
  'connector.state.retrying': 'Trying again shortly',
  'connector.state.drafted': 'Draft in Gmail',
  'connector.state.sent': 'Sent',
  'connector.state.failed': 'Failed',
  'connector.state.left_out': 'Left out',
  'connector.reason.by_you': 'Left out by you',
  'connector.reason.no_email': 'No email in the list',
  'connector.reason.no_party': 'No party named',
  'connector.reason.disconnected': 'Gmail was disconnected',
  'connector.reason.rule_removed': 'Its rule was removed',
  'connector.reason.not_yours': 'Out of your reach now',
  'connector.reason.field_hidden': 'Names a field hidden from you now',
  'connector.reason.bad_address': 'Gmail refused the address',

  // The aside: what OS may do, and the limits every email is held to (Decided 12).
  'connector.can.read': 'Bring in from email',
  'connector.limit.day': '{{used, number}} of {{limit, number}} emails today',
  'connector.limit.lists': 'Only to addresses in your lists',

  // The email card in the chat (connectors-gmail Decided 7 to 9, 12 and 19; boards A2 to A5).
  'connector.card.title_one': '{{count, number}} email',
  'connector.card.title_other': '{{count, number}} emails',
  'connector.card.title_party': 'Email {{party}}',
  'connector.card.title_list_one': 'Email {{count, number}} in {{list}}',
  'connector.card.title_list_other': 'Email {{count, number}} in {{list}}',
  'connector.card.connect_then': 'Then OS sends these from your address',
  'connector.card.not_sent': 'Not sent yet',
  'connector.card.from': 'From {{address}} · Gmail',
  'connector.card.typed': 'Typed by you',
  'connector.card.no_email': 'No email in {{list}}',
  'connector.card.note': 'Nothing is written to your Gmail until you pick one of these.',
  'connector.card.look': 'Look at each',
  'connector.card.send_one': 'Send {{count, number}} email',
  'connector.card.send_other': 'Send {{count, number}} emails',
  'connector.card.need_gmail': 'Gmail is not connected, so OS cannot send this yet.',
  'connector.card.need_gmail_body_one': 'The {{count, number}} email is written. Connect Gmail and it goes from your address, or copy it into any mail app.',
  'connector.card.need_gmail_body_other': 'The {{count, number}} emails are written. Connect Gmail and they go from your address, or copy them into any mail app.',
  'connector.card.copy_instead': 'Write the emails here to copy',
  'connector.card.unreached': 'Could not reach RecordOS. Nothing was sent twice.',
  'connector.card.sent_one': '{{count, number}} email sent from {{address}}',
  'connector.card.sent_other': '{{count, number}} emails sent from {{address}}',
  'connector.card.drafted_one': '{{count, number}} draft saved in your Gmail',
  'connector.card.drafted_other': '{{count, number}} drafts saved in your Gmail',
  'connector.card.none_went': 'No email went',
  'connector.card.kept': 'Each one is kept on its $t(glossary.row_one) in {{list}}.',
  'connector.card.problem': '{{to}} · {{why}}',

  // The emails, copied by hand when Gmail is not connected.
  'connector.copy.title': 'The emails, to copy',
  'connector.copy.desc': 'Copy each one into any mail app. Nothing is sent from here.',
  'connector.copy.copy': 'Copy',
  'connector.copy.copied': 'Copied',
  'connector.copy.to': 'To',
  'connector.copy.subject': 'Subject',

  // One email of the card, read and changed beside the chat (board A3).
  'connector.look.draft_of': 'Draft {{at, number}} of {{count, number}}',
  'connector.look.previous': 'Previous email',
  'connector.look.next': 'Next email',
  'connector.look.subject': 'Subject',
  'connector.look.body': 'Words',
  'connector.look.edit': 'Edit',
  'connector.look.done': 'Done',
  'connector.look.put_back': 'Put it back',
  'connector.look.left_out': 'Left out of this send',
  'connector.look.to_label': 'To',
  'connector.look.from_label': 'From',
  'connector.look.files': 'Files',

  // The rule card in the chat (connectors-gmail Decided 8 and 13; boards R1, R5).
  'connector.rule_card.title': 'Rule: {{name}}',
  'connector.rule_card.sub': 'Gmail · {{address}}',
  'connector.rule_card.not_on': 'Not on yet',
  'connector.rule_card.when': 'When',
  'connector.rule_card.to': 'To',
  'connector.rule_card.email': 'Email',
  'connector.rule_card.attach': 'Attach',
  'connector.rule_card.limit': 'Limit',
  'connector.rule_card.then': 'Then',
  'connector.rule_card.to_via': 'The {{field}} of each $t(glossary.entry_one), from {{list}}',
  'connector.rule_card.to_self': 'Each $t(glossary.row_one) of {{list}} it matches',
  'connector.rule_card.attach_from': 'The files in {{field}}',
  'connector.rule_card.email_more': 'Show the whole email',
  'connector.rule_card.email_less': 'Show less',
  'connector.rule_card.limit_line': 'One email a $t(glossary.row_one) a day; what matches on the same day goes in one email.',
  'connector.rule_card.mode_ask': 'How should the emails go?',
  'connector.rule_card.try_one': 'On today’s data it would write {{count, number}} email.',
  'connector.rule_card.try_other': 'On today’s data it would write {{count, number}} emails.',
  'connector.rule_card.missing_one': '{{count, number}} $t(glossary.row_one) has no email.',
  'connector.rule_card.missing_other': '{{count, number}} $t(glossary.row_other) have no email.',
  'connector.rule_card.waits_all': 'That is more than {{limit, number}} at once, so each one would wait for your yes.',
  'connector.rule_card.stopped': 'It stopped counting part way, so it may be more.',
  'connector.rule_card.change': 'Change',
  'connector.rule_card.change_prefix': 'Change this rule:',
  'connector.rule_card.turn_on': 'Turn on rule',
  'connector.rule_card.pick_first': 'Pick how the emails go, then turn it on',
  'connector.rule_card.is_on': '{{name}} is on',
  'connector.rule_card.is_on_next': 'Change how it sends on the Gmail page at any time.',
  'connector.rule_card.open_page': 'Open Gmail',
  'connector.rule_card.ph_party': 'the party’s name',
  'connector.rule_card.ph_party_of': 'the {{party}}’s name',
  'connector.rule_card.ph_items': 'one line for each $t(glossary.entry_one)',
  'connector.rule_card.ph_gone': 'a field that is gone',
  'connector.mode.draft_desc': 'You send them yourself, when you like',
  'connector.mode.wait_desc': 'Each one waits until you say yes',
  'connector.mode.own_desc': 'Each one goes when the rule fires',

  // A new rule, made with OS (connectors-gmail Decided 19; boards R0, R0b).
  'connector.rule_new.title': 'New rule',
  'connector.rule_new.meta': 'Gmail · {{address}}',
  'connector.rule_new.ask': 'What should happen, and when? Say it as you would tell a person in the shop.',
  'connector.rule_new.replaces': 'Change my rule “{{rule}}” (rule {{id}})',
  'connector.rule_new.replaces_with': 'Change my rule “{{rule}}” (rule {{id}}): {{ask}}',
  'connector.rule_new.preview': 'The rule',
  'connector.rule_new.preview_note': 'Fills in as you talk',
  'connector.rule_new.preview_empty': 'The rule appears here as you talk: when it fires, who it writes to, and what it says.',
  'connector.rule_new.today': 'On today’s data',
  'connector.rule_new.then_waiting': 'Waiting for your answer',

  // An entry's emails, and its way into OS (connectors-gmail Decided 15 and 19; board A6).
  'connector.entry.section': 'Emails from OS',
  'connector.entry.empty': 'No emails from OS yet',
  'connector.entry.mine': 'By you',
  'connector.entry.theirs': 'By someone else here',
  'connector.entry.subject_hidden': 'Subject not shown to you',
  'connector.entry.email_to': 'Email to {{party}}',
  'connector.entry.ask': 'Email the {{record}} $t(glossary.entry_one) to {{party}}, with the file in {{field}}',
  'connector.card.add_email_field': 'Add an email $t(glossary.field_one)',
  'connector.card.add_email_field_ask': 'Add an email $t(glossary.field_one) to {{list}}',
  'connector.card.no_email_field': '{{list}} has no email $t(glossary.field_one)',

  // A party's statement on its row page, and what a payment paid (4.3, 4.4 of docs/plans/ledger.md; board S4).
  'statement.title': 'Statement',
  'statement.billed': 'Billed',
  'statement.received': 'Received',
  'statement.pending': 'Pending',
  'statement.paid_in_advance': 'Paid in advance',
  'statement.date': 'Date',
  'statement.what': 'What',
  'statement.charged': 'Charged',
  'statement.paid': 'Paid',
  'statement.balance': 'Balance',
  'statement.paid_by': 'Paid by the payment of {{date}}',
  'statement.part_paid': '{{paid}} paid, {{due}} due',
  'statement.pays': 'Pays the sale of {{date}}',
  'statement.due': 'Due',
  // A bill's own state on its page, read from the ledger (D2): what is due on it now, and the payments that paid it.
  'statement.bill.title': 'Payments',
  'statement.bill.due': '{{amount}} due',
  'statement.bill.paid': 'Paid',
  // An entry that is its own party (L46): every line charged or paid on it, and the total.
  'statement.still_owed': 'Still owed',
  'statement.from': 'From',
  'statement.amount': 'Amount',
  // Pay from the statement: one place to settle a party's account, a button per account.
  'statement.pay.in': 'Payment received',
  'statement.pay.out': 'Payment made',
  'statement.pay.pick': 'Where should it go?',
  'statement.pay.lines_way': 'On the open bills of {{record}}',
  'statement.pay.failed': 'The payment did not save. Try again.',
  'statement.pay.in_title': 'Payment received from {{party}}',
  'statement.pay.out_title': 'Payment made to {{party}}',
  'statement.pay.desc': 'It pays the oldest bills first. Each bill takes a payment line, so its own balance goes down too.',
  'statement.pay.amount': 'Amount',
  'statement.pay.due_of_one': '{{amount}} is due on {{count, number}} bill.',
  'statement.pay.due_of_other': '{{amount}} is due on {{count, number}} bills.',
  'statement.pay.day': 'Paid on',
  'statement.pay.mode': 'Paid by',
  'statement.pay.mode_pick': 'Pick one',
  'statement.pay.spread': 'Goes on',
  'statement.pay.spread_line': '{{record}} of {{date}}: {{amount}}',
  'statement.pay.spread_more_one': 'and {{count, number}} more bill',
  'statement.pay.spread_more_other': 'and {{count, number}} more bills',
  'statement.pay.too_much': 'At most {{amount}} is due.',
  'statement.pay.none': 'Type an amount above nought.',
  'statement.pay.save': 'Save payment',
  'statement.pay.saved_one': 'Payment saved on {{count, number}} bill.',
  'statement.pay.saved_other': 'Payment saved on {{count, number}} bills.',
  'statement.pay.saved_waiting_one': 'Payment sent for approval on {{count, number}} bill.',
  'statement.pay.saved_waiting_other': 'Payment sent for approval on {{count, number}} bills.',

  // "Is this against the dues?" on a Money in (L24, 4.7 of docs/plans/ledger.md; board E3).
  'money.against_dues.q': 'Is this against {{party}}’s dues of {{amount}}?',
  'money.against_dues.q_plain': 'Is this against the dues?',
  'money.against_dues.yes': 'Yes, it pays the dues',
  'money.against_dues.no': 'No, it is new money',
  'money.against_dues.desc': 'Yes takes it off what they owe. No counts it as income.',
  // A kit's words (Phase 7 of docs/plans/field-relations.md): labels, choices,
  // nouns, plan names, cards and rule messages of the kits in
  // apps/server/src/agent/playbooks/kits/, put in the workspace's language when
  // a kit is built. A rule message names a field in single braces, which the
  // rule fills when it is broken, never i18next.
  'kit.payments.payments': 'Payments',
  'kit.payments.line': 'payment',
  'kit.payments.lines': 'payments',
  'kit.payments.mode': 'Mode',
  'kit.payments.mode_cash': 'Cash',
  'kit.payments.mode_upi': 'UPI',
  'kit.payments.mode_card': 'Card',
  'kit.payments.mode_bank': 'Bank',
  'kit.payments.paid': 'Paid',
  'kit.payments.paid_on': 'Paid on',
  'kit.payments.received': 'Received',
  'kit.payments.balance': 'Balance',
  'kit.payments.overpaid': 'Payments add up to ₹{received}, but the amount is ₹{amount}.',
  'kit.money.accounts': 'Accounts',
  'kit.money.account': 'account',
  'kit.money.name': 'Name',
  'kit.money.type': 'Type',
  'kit.money.notes': 'Notes',
  'kit.money.type_asset': 'Money you have',
  'kit.money.type_liability': 'Money you owe',
  'kit.money.type_equity': 'Owner\'s money',
  'kit.money.type_income': 'Income',
  'kit.money.type_expense': 'Expense',
  'kit.money.cash': 'Cash',
  'kit.money.bank': 'Bank',
  'kit.money.receivable': 'Customers owe you',
  'kit.money.payable': 'You owe suppliers',
  'kit.money.to_sort': 'To sort',
  'kit.money.drawings': 'For home',
  'kit.money.owner_capital': 'Owner\'s money put in',
  'kit.money.opening_equity': 'Opening balances',
  'kit.money.stock_loss': 'Stock loss',
  'kit.money.sales': 'Sales',
  'kit.money.discount_given': 'Discounts given',
  'kit.money.discount_received': 'Discounts received',
  'kit.money.round_off': 'Round off',
  'kit.money.tax_out': 'Tax you collected',
  'kit.money.tax_in': 'Tax you can claim back',
  'kit.money.tax_out_cgst': 'GST you collected: CGST',
  'kit.money.tax_out_sgst': 'GST you collected: SGST',
  'kit.money.tax_out_utgst': 'GST you collected: UTGST',
  'kit.money.tax_out_igst': 'GST you collected: IGST',
  'kit.money.tax_in_cgst': 'GST you can claim: CGST',
  'kit.money.tax_in_sgst': 'GST you can claim: SGST',
  'kit.money.tax_in_utgst': 'GST you can claim: UTGST',
  'kit.money.tax_in_igst': 'GST you can claim: IGST',
  'kit.money.rent': 'Rent',
  'kit.money.salaries': 'Salaries',
  'kit.money.electricity': 'Electricity',
  'kit.money.tea': 'Tea and snacks',
  'kit.money.transport': 'Transport',
  'kit.money.repairs': 'Repairs',
  'kit.money.other_income': 'Other income',
  'kit.money.amount': 'Amount',
  'kit.money.date': 'Date',
  'kit.money.note': 'Note',
  'kit.money.for': 'What was it for?',
  'kit.money.received_in': 'Received in',
  'kit.money.paid_from': 'Paid from',
  'kit.money.money_in': 'Money in',
  'kit.money.money_in_noun': 'money in',
  'kit.money.money_out': 'Money out',
  'kit.money.money_out_noun': 'money out',
  'kit.money.adjustment': 'Adjustment',
  'kit.money.adjustment_noun': 'adjustment',
  'kit.money.reason': 'Reason',
  'kit.money.account_moved': 'Account',
  'kit.money.against': 'Against',
  'kit.money.opening_balance': 'Opening balance',
  'kit.money.opening_balance_noun': 'opening balance',
  'kit.money.parties': 'Customers',
  'kit.money.party': 'customer',
  'kit.money.tax_id': 'Tax ID',
  'kit.money.tax_id_help': 'GSTIN, TRN or VAT number',
  'kit.money.state': 'State',
  'kit.money.who': 'Who',
  // A party list's three figures from the money it names (L33): what it was billed, what it paid, what is still due.
  'kit.money.billed': 'Billed',
  'kit.money.received': 'Received',
  'kit.money.pending': 'Pending',
  // One of the three when the list already has a field of that name, which keeps it: "Billed (accounts)".
  'kit.money.figure_apart': '{{label}} (accounts)',
  // The rows that hold quantities, not money (Phase 9, D1), made only when the business keeps stock.
  'kit.money.stock': 'Stock',
  'kit.money.stock_in': 'Came in',
  'kit.money.stock_out': 'Went out',
  'kit.money.stock_loss_qty': 'Wastage',
  // A stock item's two figures from what came in and went out (Phase 9, D4): what is left, and what was wasted.
  'kit.money.on_hand': 'On hand',
  'kit.money.wastage': 'Wastage',
  // The record a charge plan's runs write into (L23): one charge for each party, each period.
  'kit.money.charges': 'Charges',
  'kit.money.against_dues': 'Against dues',
  // The Discount kit (G3, G4 of docs/plans/gym-membership.md): a discount as an amount or a percent off a record's price.
  'kit.discount.discount_in': 'Discount in',
  'kit.discount.in_amount': 'Amount (₹)',
  'kit.discount.in_percent': 'Percent (%)',
  'kit.discount.discount': 'Discount',
  'kit.discount.discount_value': 'Discount value',
  'kit.discount.over_100': 'A discount cannot be more than 100%.',
  'kit.discount.over_price': 'The discount of ₹{discount_value} is more than the price of ₹{price}.',
  'kit.discount.discounts_month': 'Discounts this month',
  'kit.discount.discount_for': 'Discount for',
  'kit.discount.for_first': 'First cycle',
  'kit.discount.for_cycles': 'A number of cycles',
  'kit.discount.for_every': 'Every cycle',
  'kit.discount.cycles': 'Cycles',
  'kit.discount.cycles_help': 'Including this one: 3 is this one and the next two.',
  'kit.discount.cycle_number': 'Cycle number',
  'kit.discount.previous': 'Previous entry',
  'kit.discount.carried_cycles': 'Discount: {cycle_number} of {cycles}, carried from the previous entry.',
  'kit.discount.carried_every': 'Discount: cycle {cycle_number}, carried from the previous entry for every cycle.',
  'kit.discount.ended_plan': 'The discount on the previous entry ended because the plan changed. Give a new one if there is one.',
  'kit.discount.discount_from': 'Discount from',
  'kit.discount.discount_from_help': 'The first bill it comes off is the one that holds this day; the cycles count from there.',
  'kit.membership.plans': 'Plans',
  'kit.membership.plan_noun': 'plan',
  'kit.membership.plan': 'Plan',
  'kit.membership.length': 'Length',
  'kit.membership.unit': 'Unit',
  'kit.membership.unit_days': 'Days',
  'kit.membership.unit_weeks': 'Weeks',
  'kit.membership.unit_months': 'Months',
  'kit.membership.unit_years': 'Years',
  'kit.membership.price': 'Price',
  'kit.membership.plan_monthly': 'Monthly',
  'kit.membership.plan_quarterly': 'Quarterly',
  'kit.membership.plan_yearly': 'Yearly',
  'kit.membership.plan_custom': 'Custom',
  'kit.membership.members': 'Members',
  'kit.membership.member': 'member',
  'kit.membership.name': 'Name',
  'kit.membership.phone': 'Phone',
  'kit.membership.joined': 'Joined',
  'kit.membership.memberships': 'Memberships',
  'kit.membership.membership': 'membership',
  'kit.membership.member_pick': 'Member',
  'kit.membership.start': 'Start date',
  'kit.membership.end': 'End date',
  'kit.membership.amount': 'Amount',
  'kit.membership.end_after_start': 'The end date {end} is not after the start date {start}.',
  'kit.membership.current_expiry': 'Current expiry',
  'kit.membership.renewal': 'Renewal',
  'kit.membership.renewals_month': 'Renewals this month',
  'kit.membership.active': 'Active members',
  'kit.membership.expires_week': 'Expires this week',
  'kit.membership.remind_call': 'Call {member}: their plan ends on {end}',
  'kit.membership.monthly_fee': 'Monthly fee',
  'kit.membership.plan_not_monthly': 'A member billed every month takes a plan one month long, or a plan with no length.',
  'kit.membership.left': 'Left on',
  'kit.membership.charges': 'Membership fees',

  // ------------------------------------------------------- approvals (Phase 11 of docs/plans/ledger.md)
  'approval.menu': 'Approvals',
  'approval.title': 'Approvals',
  'approval.description': 'Which entries in this record wait for a yes before they count, and who says it.',
  'approval.conditions.label': 'Which entries need approval',
  'approval.conditions.none': 'No entry waits for approval.',
  'approval.condition.by_role': 'Entries by people whose role needs approval here',
  'approval.condition.above_label': 'Any entry above',
  'approval.condition.above': 'Any entry above {{amount}}',
  'approval.condition.every': 'Every entry',
  'approval.steps.label': 'Who approves, in order',
  'approval.steps.none': 'No step yet. Add who approves first.',
  'approval.steps.fairness': 'A member never approves their own entry, and one person approves at most one step. An owner’s or an admin’s own entry is approved when saved at the steps that are theirs. Nothing counts until the last step says yes.',
  'approval.step.label': 'Step {{index, number}}',
  'approval.step.every_amount': 'Every amount',
  'approval.step.only_above': 'Only above {{amount}}',
  'approval.step.only_above_pick': 'Only above an amount',
  'approval.step.amount_label': 'Which amounts go to {{who}}',
  'approval.step.remove': 'Take {{who}} out of the steps',
  'approval.step.add': 'Add a step',
  'approval.step.add_role': 'Anyone with a role',
  'approval.step.add_member': 'A named member',
  'approval.step.sentence': '{{index, number}}. {{who}} · {{amount}}',
  'approval.who.manager': 'The writer’s manager',
  'approval.who.manager_hint': 'From Reports to in Team. If they wrote it, the person above them.',
  'approval.who.role_hint': 'Anyone who holds this role',
  'approval.who.member_hint': 'A named member',
  'approval.who.owner_or_admin': 'The owner or an admin',
  'approval.who.owner_or_admin_hint': 'Anyone who runs the workspace. Their own entries count at once.',
  'approval.who.owner': '{{name}} · owner',
  'approval.who.role_gone': 'A role that was removed',
  'approval.who.member_gone': 'Someone who left the workspace',
  'approval.os.label': 'Tell OS who approves',
  'approval.os.placeholder': 'Or tell OS: “Expenses above ₹5,000 go to the manager, and above ₹50,000 to me as well.”',
  'approval.os.send': 'Send',
  'approval.os.ask': 'Set who approves entries in {{record}}: {{words}}',
  'approval.problem.no_condition': 'Tick which entries need approval.',
  'approval.problem.no_step': 'Add a step, so someone approves.',
  'approval.problem.amount': 'An amount must be above zero.',
  'approval.save': 'Save approvals',
  'approval.saved': 'Approvals saved',
  'approval.remove': 'Turn off approvals',
  'approval.removed': 'Approvals are off. New entries count at once.',
  'approval.approve': 'Approve',
  'approval.card.by': 'By {{name}}',
  'approval.card.reason': 'Why: {{reason}}',
  'approval.you': 'you',
  'approval.names_more_one': '{{name}} and {{count, number}} more',
  'approval.names_more_other': '{{name}} and {{count, number}} more',
  'approval.mark.for_you': 'Waiting for you',
  'approval.mark.for': 'Waiting for {{name}}',
  'approval.mark.for_more_one': 'Waiting for {{name}} and {{count, number}} more',
  'approval.mark.for_more_other': 'Waiting for {{name}} and {{count, number}} more',
  'approval.mark.waiting': 'Waiting for approval',
  'approval.mark.sent_back': 'Sent back',
  'approval.mark.sent_back_by': 'Sent back by {{name}}',
  'approval.mark.waits_row': 'Draft · waits for {{name}}',
  'approval.step.of_one': 'Step {{step, number}} of {{count, number}}',
  'approval.step.of_other': 'Step {{step, number}} of {{count, number}}',
  'approval.step.of_lower_one': 'step {{step, number}} of {{count, number}}',
  'approval.step.of_lower_other': 'step {{step, number}} of {{count, number}}',
  'approval.why.every': 'Every entry here needs approval',
  'approval.why.role': 'Your role needs approval here',
  'approval.why.above': 'Above {{amount}}, so it needs approval',
  'approval.history.title': 'Approval',
  'approval.history.sent': 'Sent by {{name}} at {{time}}',
  'approval.history.approved': '{{name}} approved at {{time}}',
  'approval.history.approved_at_save': 'Approved by {{name}} when saved',
  'approval.history.with_reason': '{{step}} · “{{reason}}”',
  'approval.history.sent_back': 'Sent back by {{name}}',
  'approval.history.step': 'Step {{index, number}}: {{who}}',
  'approval.history.waiting': 'Waiting',
  'approval.card.title': 'Waiting for you',
  'approval.card.line': 'Entries that count only after your yes',
  'approval.card.open_all_one': 'Open {{count, number}}',
  'approval.card.open_all_other': 'Open all {{count, number}}',
  'approval.card.open_in_one': 'Open {{count, number}} in {{record}}',
  'approval.card.open_in_other': 'Open {{count, number}} in {{record}}',
  'approval.nav.waiting_one': '{{count, number}} entry waits for you',
  'approval.nav.waiting_other': '{{count, number}} entries wait for you',
  'approval.filter.chip_one': 'Waiting for me · {{count, number}}',
  'approval.filter.chip_other': 'Waiting for me · {{count, number}}',
  'approval.filter.pick_all': 'Pick every entry that waits',
  'approval.filter.pick': 'Pick {{title}}',
  'approval.filter.date': 'Date',
  'approval.filter.for': 'For',
  'approval.filter.by': 'By',
  'approval.filter.amount': 'Amount',
  'approval.filter.where': 'Approval',
  'approval.filter.none': 'Nothing here waits for you.',
  'approval.filter.loading': 'Reading what waits for you',
  'approval.filter.failed': 'What waits for you could not be read. Try again in a moment.',
  'approval.bulk.selected_one': '{{count, number}} selected',
  'approval.bulk.selected_other': '{{count, number}} selected',
  'approval.bulk.selected_total_one': '{{count, number}} selected · {{amount}}',
  'approval.bulk.selected_total_other': '{{count, number}} selected · {{amount}}',
  'approval.bulk.approve_one': 'Approve {{count, number}}',
  'approval.bulk.approve_other': 'Approve {{count, number}}',
  'approval.bulk.approved_one': '{{count, number}} entry approved',
  'approval.bulk.approved_other': '{{count, number}} entries approved',
  'approval.bulk.sent_back_one': '{{count, number}} entry sent back',
  'approval.bulk.sent_back_other': '{{count, number}} entries sent back',
  'approval.bulk.partly_one': '{{count, number}} went through, then: {{reason}}',
  'approval.bulk.partly_other': '{{count, number}} went through, then: {{reason}}',
  'approval.bulk.all_count': 'These count as soon as you approve them.',
  'approval.bulk.all_go_on_one': '{{count, number}} entry goes on to the next step after your yes.',
  'approval.bulk.all_go_on_other': 'All {{count, number}} go on to the next step after your yes.',
  'approval.bulk.some_go_on_one': '{{count, number}} of these goes on to the next step after your yes. The others count as soon as you approve them.',
  'approval.bulk.some_go_on_other': '{{count, number}} of these go on to the next step after your yes. The others count as soon as you approve them.',
  'approval.send_back.button': 'Send back',
  'approval.send_back.title_one': 'Send back {{count, number}} entry',
  'approval.send_back.title_other': 'Send back {{count, number}} entries',
  'approval.send_back.line_one': 'It goes back to the person who wrote it, as a draft, with your reason.',
  'approval.send_back.line_other': 'Each goes back to the person who wrote it, as a draft, with your reason.',
  'approval.send_back.reason': 'Reason',
  'approval.send_back.placeholder': 'The bill says ₹58,600. Please attach it and fix the amount.',
  'approval.send_back.send': 'Send back',
  'approval.decided.approved': 'Approved. It counts now.',
  'approval.decided.next_step': 'Approved. It goes on to the next step.',
  'approval.decided.sent_back': 'Sent back to {{name}}',
  'approval.sent_back.quote': '“{{reason}}”',
  'approval.draft': 'Draft',
  'approval.draft_line': 'Counts in nothing until it is sent again and approved',
  'approval.send_again': 'Send again',
  'approval.sent_again': 'Sent again. It starts from the first step.',
  'approval.take_back': 'Take it back',
  'approval.taken_back': 'Taken back. It is a draft now and counts in nothing. Save it to send it again.',
  'record.settings.crumb': 'Settings',
  'record.settings.who_changes': 'The owner, admins and anyone who can customize this record change these',
  'record.settings.read_only': 'The owner, admins and anyone who can customize this record change these. You can read them.',
  // Routines (docs/plans/routines.md, R1 to R12): what RecordOS does by itself, on a date or a schedule.
  // Its sidebar row, after Records and Lists, and the count it says when something needs attention.
  'nav.routines': 'Routines',
  'nav.routines_attention_one': 'Routines, {{count, number}} needs attention',
  'nav.routines_attention_other': 'Routines, {{count, number}} need attention',
  // The Routines page (R5): the next thing to happen, what needs attention, then Bills, Tasks and Emails.
  'routines.title': 'Routines',
  'routines.desc': 'What RecordOS does by itself, on a date or a schedule.',
  'routines.new': 'New $t(glossary.routine_one)',
  'routines.next_label': 'Next · {{when}}',
  'routines.group.bills_one': 'Bills · {{count, number}}',
  'routines.group.bills_other': 'Bills · {{count, number}}',
  'routines.group.tasks_one': 'Tasks · {{count, number}}',
  'routines.group.tasks_other': 'Tasks · {{count, number}}',
  'routines.group.emails_one': 'Emails · {{count, number}}',
  'routines.group.emails_other': 'Emails · {{count, number}}',
  'routines.state.on': 'On',
  'routines.state.paused': 'Paused',
  'routines.state.attention': 'Needs attention',
  'routines.empty.title': 'Nothing runs by itself yet',
  'routines.empty.desc': 'A $t(glossary.routine_one) bills, reminds or emails on a date or a schedule. Tell OS what should happen. You see the $t(glossary.routine_one) before it is saved.',
  'routines.error.title': 'Routines could not be loaded',
  'routines.error.desc': 'Your $t(glossary.routine_other) still run. Check your connection, then try again.',
  // Two things to tell OS from the empty page, each sent to OS beside the page as it is.
  'routines.example.remind': 'Remind me 3 days before a plan ends',
  'routines.example.email': 'Email members who owe money every Monday',
  // When the next thing happens, on the Next card and a row: "tomorrow, 1 Nov", "Next 1 Nov".
  'routines.when_today': 'today, {{day}}',
  'routines.when_tomorrow': 'tomorrow, {{day}}',
  'routines.next_open': 'Open',
  'routines.open_label': 'Open {{name}}',
  'routines.row.next': 'Next {{when}}',
  'routines.row.open_one': '{{count, number}} open',
  'routines.row.open_other': '{{count, number}} open',
  'routines.row.none_open': 'None open',
  // An alert above the groups, one for each routine that needs attention.
  'routines.alert.email_paused': '{{name}} is paused',
  'routines.alert.email_paused_desc': '{{reason}}. Nothing is sent until it is fixed.',
  'routines.alert.run_failed_desc': 'Nothing was written into {{record}}. Open it to bill that month or skip it.',
  'routines.alert.open': 'Open',
  // One routine said as a sentence (R4, routineWords in routines.ts): its name, then when and where under it, joined by a dot.
  'routine.line': '{{left}} · {{right}}',
  // A monthly run: "Bill each member their Monthly fee", "On the 1st of every month · into Membership fees".
  'routine.bill.what': 'Bill each {{party}} their {{field}}',
  'routine.bill.what_plain': 'Bill each {{party}}',
  // The same, for a list with no word for one row of its own: "Bill everyone in Members their Monthly fee".
  'routine.bill.what_list': 'Bill everyone in {{list}} their {{field}}',
  'routine.bill.what_list_plain': 'Bill everyone in {{list}}',
  'routine.bill.when_monthly': 'On the 1st of every month',
  'routine.bill.when_quarterly': 'On the 1st of every quarter',
  'routine.bill.when_yearly': 'On the first day of every financial year',
  'routine.bill.into': 'into {{record}}',
  'routine.bill.ready_label': 'Can be billed now',
  'routine.bill.ready_note': '{{month}} started before this $t(glossary.routine_one) was made, so only you can bill it. {{next_month}} is billed by itself on {{day}}.',
  // A task rule: its task's words, then when it raises one, on which record, for whom.
  'routine.task.what': 'A task to {{title}}',
  'routine.task.when_added_a': 'When a {{entry}} is added',
  'routine.task.when_added_an': 'When an {{entry}} is added',
  'routine.task.when_approved_a': 'When a {{entry}} is approved',
  'routine.task.when_approved_an': 'When an {{entry}} is approved',
  'routine.task.due_on': 'On {{field}}',
  'routine.task.due_before_one': '{{count, number}} day before {{field}}',
  'routine.task.due_before_other': '{{count, number}} days before {{field}}',
  'routine.task.due_after_one': '{{count, number}} day after {{field}}',
  'routine.task.due_after_other': '{{count, number}} days after {{field}}',
  'routine.task.on': 'on {{record}}',
  'routine.task.for': 'for {{who}}',
  'routine.task.who.owner': 'the owner',
  'routine.task.who.writer': 'whoever added it',
  'routine.task.who.role': 'everyone in {{role}}',
  'routine.task.who.field': 'the $t(glossary.person_one) in {{field}}',
  'routine.task.open_title_one': 'Open tasks · {{count, number}}',
  'routine.task.open_title_other': 'Open tasks · {{count, number}}',
  // One open task on a task rule's page: "Due 2 Nov · for Ravi", and its link to the entry it is about.
  'routine.task.open_entry': 'Open entry',
  'routine.task.closed_note': 'Done and dismissed tasks are in Tasks.',
  // An email rule: its own name, its schedule (connector.when.*) and whose Gmail it sends from.
  'routine.email.from': 'from {{owner}}’s Gmail',
  'routine.email.edit': 'Edit in Gmail',
  // A routine's page (R6, R7).
  'routine.pause': 'Pause',
  'routine.resume': 'Resume',
  'routine.paused_title': 'Paused by {{who}} on {{day}}',
  'routine.paused_body': 'Nothing is billed while it is paused. When you resume, billing starts again from that month. The months it was paused stay unbilled.',
  'routine.failed_title': '{{month}} was not billed',
  'routine.what_title': 'What it does',
  'routine.ask_os_to_change': 'To change it, ask OS.',
  'routine.take_off_title': 'Take off this $t(glossary.routine_one)',
  'routine.take_off_body_one': 'No new tasks are made. The {{count, number}} open task stays until someone answers it.',
  'routine.take_off_body_other': 'No new tasks are made. The {{count, number}} open tasks stay until someone answers them.',
  // A routine's own page (R6, R7): what is not on the board's words, and the states every page shares.
  'routine.not_found': 'This $t(glossary.routine_one) is not here any more',
  'routine.not_found_body': 'It may have been taken off, or it is not yours to open.',
  'routine.bill.next_label': 'Next run · {{day}}',
  'routine.bill.waiting_note': 'Billed by itself on {{day}}.',
  'routine.bill.failed_label': 'Not billed · {{month}}',
  'routine.bill.parties_one': '{{count, number}} {{noun}}, {{amount}}',
  'routine.bill.parties_other': '{{count, number}} {{noun}}, {{amount}}',
  'routine.failed_body': 'Nothing was written into {{record}}. Bill {{month}} again, or skip it.',
  'routine.paused_done': 'Paused. Nothing is billed until you resume.',
  'routine.resumed_done': 'Resumed. Billing starts again from this month.',
  'routine.take_off_confirm': 'Take off {{name}}?',
  'routine.email.last_runs': 'Last runs',
  // Where the controls were keeps one line (R8): the bills record and record settings.
  'record.billed_by': 'Billed by the $t(glossary.routine_one) {{name}} · next run {{when}}',
  'record.billed_by_paused': 'Billed by the $t(glossary.routine_one) {{name}} · paused',
  'record.billed_by_plain': 'Billed by the $t(glossary.routine_one) {{name}}',
  'record.open_routine': 'Open $t(glossary.routine_one)',
  'record_settings.routines_title': 'Routines on this {{noun}}',
  'record_settings.routines_new': 'New $t(glossary.routine_one) for {{record}}',
  'record_settings.routines_desc': 'What RecordOS does by itself with this {{noun}}.',
  'record_settings.routines_empty': 'Nothing runs by itself on this {{noun}} yet.',
} as const;

/**
 * Every key exactly as it is written, plus every plural stem.
 *
 * A stem is the half of a pair a row stores and a call site says:
 * `t('noun.record', { count })` resolves `noun.record_one` or
 * `noun.record_other` and never a key called `noun.record`, which is why the
 * stem has to be added here rather than written into the object.
 */
type CatalogueKey = keyof typeof en;

type PluralStem<K extends string> = K extends `${infer Stem}_one`
  ? `${Stem}_other` extends CatalogueKey
    ? Stem
    : never
  : never;

export type AppKey = CatalogueKey | PluralStem<CatalogueKey>;

/**
 * The same object, widened to a plain map.
 *
 * `as const` is what gives `t` a typed key and the i18next augmentation its
 * resource shape, but it also means `en[someString]` no longer compiles. The
 * validator family — `validate-spec.ts`, `compose.ts`, `compose-prune.ts`
 * and the agent tools — reads keys that arrive as data and has nothing to
 * narrow them with, so it reads this view instead. One object, two types.
 */
export const enFlat: Readonly<Record<string, string>> = en;

/**
 * Does the catalogue hold both forms of this noun?
 *
 * The check a stored `noun_key` has to pass. The device says the noun with a
 * count, so a stem with only one form is a throw the first time a record
 * holds two entries — and `compose.ts` refuses a composed setup over it
 * rather than letting the crash reach a screen.
 */
export const nounShipped = (stem: string): boolean =>
  enFlat[`${stem}_one`] !== undefined && enFlat[`${stem}_other`] !== undefined;

/**
 * A catalogue value with every `$t()` expanded — for the few readers that take
 * the catalogue FLAT instead of through `t()`.
 *
 * `t()` does this itself and is the door a screen uses. Three places cannot
 * use it: `compose-prune.ts` resolves a word against a composed setup's own
 * overrides first, and the server's two `word()` helpers deliberately answer
 * in English whatever language a phone is in, because what they build is a
 * prompt for a model. All three used to read `enFlat[key]` and get a finished
 * sentence. Since the glossary landed they get "Find a $t(glossary.row_one)",
 * and a marker handed to a model — or drawn under a switch — is the raw key
 * name on a screen wearing brackets.
 *
 * So this is the expansion and nothing else: no language, no interpolation, no
 * engine. One level deep, which `nesting.enforcement.test.ts` proves is the
 * whole depth. A marker naming a key the catalogue does not hold is left as it
 * stands rather than thrown over, because these three callers all have a
 * fallback and none of them is a place to die.
 */
export const expandNested = (value: string): string =>
  value.replace(/\$t\(([^)]*)\)/g, (marker: string, named: string) => enFlat[named] ?? marker);